import React, { useState, useRef, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import supabaseClient from "./supabase";
import Landing from "./Landing";

// ─── VOICE UTILITIES ──────────────────────────────────────────────────────────
function ttsClean(t){
  return t.replace(/FAR\s+(\d+\.\d+[a-z]?)/gi,"FAR $1").replace(/§/g,"section")
    .replace(/[✦☐◎⚠⚖△§✔·•]/g,"").replace(/N(\d{4,5}[A-Z]{0,2})/g,"November $1")
    .replace(/(\d+)°/g,"$1 degrees").replace(/\bkts\b/gi,"knots")
    .replace(/\bAGL\b/g,"above ground level").replace(/\bMSL\b/g,"mean sea level")
    .replace(/\bPIC\b/g,"pilot in command").replace(/\bPOH\b/g,"pilot's operating handbook")
    .replace(/\bCFI\b/g,"flight instructor").replace(/\bATC\b/g,"air traffic control")
    .replace(/\*\*([^*]+)\*\*/g,"$1").replace(/\s{2,}/g," ").trim();
}
function pickVoice(){
  const vs=window.speechSynthesis?.getVoices()||[];
  for(const n of ["Google UK English Male","Microsoft David","Alex","Google US English"]){
    const v=vs.find(v=>v.name===n);if(v)return v;
  }
  return vs.find(v=>v.lang.startsWith("en")&&!v.name.toLowerCase().includes("female"))||vs.find(v=>v.lang.startsWith("en"))||vs[0]||null;
}
function speak(text,{rate=0.92,pitch=0.9,onEnd}={}){
  if(!window.speechSynthesis)return;
  window.speechSynthesis.cancel();
  const u=new SpeechSynthesisUtterance(ttsClean(text));
  u.rate=rate;u.pitch=pitch;u.volume=1;
  const sv=()=>{const v=pickVoice();if(v)u.voice=v;};sv();
  if(!u.voice)window.speechSynthesis.onvoiceschanged=()=>sv();
  if(onEnd)u.onend=onEnd;
  window.speechSynthesis.speak(u);
}
function stopSpeaking(){window.speechSynthesis?.cancel();}

function useVoiceInput({onTranscript,onStateChange}){
  const rec=useRef(null);
  const [state,setState]=useState("idle");
  const upd=useCallback((s)=>{setState(s);onStateChange?.(s);},[onStateChange]);
  const start=useCallback(()=>{
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR)return false;
    stopSpeaking();
    const r=new SR();rec.current=r;
    r.lang="en-US";r.continuous=false;r.interimResults=false;r.maxAlternatives=1;
    r.onstart=()=>upd("listening");
    r.onresult=(e)=>{const t=e.results[0][0].transcript.trim();upd("processing");onTranscript(t);setTimeout(()=>upd("idle"),500);};
    r.onerror=()=>upd("idle");
    r.onend=()=>{if(rec.current)upd("idle");};
    r.start();return true;
  },[onTranscript,upd]);
  const stop=useCallback(()=>{rec.current?.stop();rec.current=null;upd("idle");},[upd]);
  return{state,start,stop};
}

// ─── POSTHOG ANALYTICS ───────────────────────────────────────────────────────
if(typeof window!=='undefined'&&!window.__posthog_loaded){
  window.__posthog_loaded=true;
  // eslint-disable-next-line no-unused-expressions
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once unregister opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  window.posthog.init('phc_ATCcfZQhqQbUQAd2C9P8cFZfuFo5pFEcWkpb8AHpgKi5',{
    api_host:'https://us.i.posthog.com',
    person_profiles:'identified_only',
    capture_pageview:true,
    capture_pageleave:true,
    autocapture:true,
    session_recording:{maskAllInputs:true,maskTextContent:true},
  });
}

// ─── ANALYTICS TRACKING ──────────────────────────────────────────────────────
function trackEvent(event, properties = {}) {
  const token = localStorage.getItem('solus_token');
  const email = localStorage.getItem('solus_user_email') || null;
  const sessionId = localStorage.getItem('solus_session_id') || (() => {
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2);
    localStorage.setItem('solus_session_id', id);
    return id;
  })();
  fetch('https://solus-backend-9wde.onrender.com/analytics/event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ event, properties, session_id: sessionId, platform: 'web', timestamp: new Date().toISOString(), email }),
  }).catch(() => {});
  if(window.posthog)window.posthog.capture(event, properties);
}

// ─── DATA ─────────────────────────────────────────────────────────────────────
const FARS={
  "61.23": {title:"Medical Certificates",body:"1st class: 12 months (under 40) or 6 months (40+). 2nd class: 12 months regardless of age. 3rd class: 60 months (under 40) or 24 months (40+). No valid medical means no PIC. BasicMed is available for private ops under certain conditions - requires a driver's license, a physician's visit every 4 years, and a 2-year online medical course. Does not allow Class B airspace PIC without ATC clearance."},
  "61.51": {title:"Pilot Logbooks",body:"PIC must log all flight time used to meet currency or rating requirements. Entries must include date, total time, aircraft make/model, registration, type of experience, and conditions of flight. Solo, PIC, SIC, and instrument time must be logged separately. Night time is logged from sunset to sunrise."},
  "61.56": {title:"Flight Review",body:"Every 24 calendar months, a pilot must complete a flight review with an authorized instructor: minimum 1 hour ground training and 1 hour flight. The review is not a test - there is no pass or fail. If an instructor determines the pilot is not proficient, the review simply isn't endorsed and must be rescheduled. A successful completion of a proficiency check, a Wings FIRC, or certain FAA-approved courses can substitute."},
  "61.57": {title:"Recent Flight Experience",body:"To carry passengers: 3 takeoffs and landings within the preceding 90 days in the same category, class, and type if a type rating is required. For night passenger ops: 3 full-stop landings at night. For tailwheel: 3 full-stop landings in a tailwheel aircraft. For IFR: 6 instrument approaches, holding procedures, and intercepting/tracking courses within the preceding 6 calendar months. Touch-and-go does not satisfy the full-stop requirement for night or tailwheel."},
  "61.89": {title:"Student Pilot Limitations",body:"A student pilot may not act as PIC with a passenger aboard, for compensation, in furtherance of a business, on an international flight, or in Class A airspace. Solo cross-country requires a CFI endorsement for the specific route or a blanket endorsement with applicable conditions."},
  "91.3":  {title:"PIC Responsibility and Authority",body:"The PIC is the final authority as to the operation of the aircraft. In an emergency, the PIC may deviate from any rule to the extent necessary to meet that emergency. Upon request, the PIC must send a written report of the deviation to the FAA."},
  "91.7":  {title:"Aircraft Airworthiness",body:"No person may operate a civil aircraft unless it is in an airworthy condition. The PIC is solely responsible for determining the aircraft is airworthy before each flight. This includes checking all required documents (ARROW), verifying MEL/KOEL compliance for any inoperative equipment, and reviewing all maintenance logbook entries and ADs."},
  "91.103":{title:"Preflight Action",body:"Before any flight, the PIC must review all available information appropriate to the flight, including: weather reports and forecasts, fuel requirements, alternatives if planned flight cannot be completed, known traffic delays, NOTAMs, and for IFR or cross-country flights, runway lengths at intended airports, aircraft performance data, and takeoff/landing distance requirements."},
  "91.109":{title:"Flight Instruction (Dual Controls)",body:"No person may give flight training in an aircraft unless it has functioning dual controls. Exception: a certificated flight instructor may give instrument instruction in an aircraft with a single throwover wheel and no rudder pedal controls on the right side if the instructor determines that the flight can be conducted safely."},
  "91.111":{title:"Operating Near Other Aircraft",body:"No person may operate an aircraft so close to another aircraft as to create a collision hazard. No person may operate in formation flight without prior arrangement with the PIC of each aircraft. Formation flight for hire requires the passengers in each aircraft to have prior consent."},
  "91.119":{title:"Minimum Safe Altitudes",body:"Anywhere: altitude allowing emergency landing without undue hazard in event of engine failure. Over congested areas: 1,000 ft above highest obstacle within 2,000 ft horizontally. Over other than congested: 500 ft above the surface (except over open water or sparsely populated areas where not closer than 500 ft to any person, vessel, vehicle, or structure)."},
  "91.151":{title:"VFR Fuel Requirements",body:"Day VFR: enough fuel to reach the first point of intended landing plus a 30-minute reserve at cruise. Night VFR: enough fuel to reach the first point of intended landing plus a 45-minute reserve at cruise. These are legal minimums. Personal minimums should account for unexpected headwinds, holds, and alternates. Always compute fuel based on actual POH fuel consumption, not rule of thumb."},
  "91.155":{title:"VFR Weather Minimums",body:"Class B, C, D, E (surface): 3 SM visibility, 500 ft below / 1,000 ft above / 2,000 ft horizontal from clouds. Class E above 1,200 AGL (below 10,000 MSL): 3 SM, 500/1,000/2,000. Class E at or above 10,000 MSL: 5 SM, 1,000/1,000/1 SM. Class G day below 1,200 AGL: 1 SM clear of clouds. Class G night below 1,200 AGL: 3 SM, 500/1,000/2,000. Class G above 1,200 AGL (below 10,000): 1 SM day, 3 SM night, 500/1,000/2,000."},
  "91.157":{title:"Special VFR",body:"SVFR allows operations in Class B, C, D, or E surface area with ATC clearance when weather is below standard VFR minimums. Requirements: clear of clouds, 1 SM visibility. Night SVFR requires the pilot to be instrument rated and current, and the aircraft to be IFR equipped. SVFR is not available at some airports (marked with NO SVFR in the Chart Supplement)."},
  "91.203":{title:"Required Documents (ARROW)",body:"Airworthiness certificate (original, displayed). Registration certificate (original or copy). Radio station license (required for international operations only). Operating limitations (POH, FAA-approved AFM, or placards). Weight and balance data - current and specific to the aircraft. Missing any of these means the aircraft may not legally depart."},
  "91.205":{title:"Required Equipment - VFR Day / Night",body:"VFR Day (A TOMATO FLAMES): Altimeter, Tachometer, Oil pressure/temp gauges, Manifold pressure (if applicable), Airspeed indicator, Temperature (liquid-cooled engines), Oil temp, Fuel gauges, Landing gear indicator, Airspeed indicator, Magnetic direction indicator, Emergency locator transmitter, Seat belts. VFR Night adds: Fuses, Landing light (if for hire), Anti-collision lights, Position lights, Source of power (spare fuses). Mnemonic: FLAPS."},
  "91.209":{title:"Aircraft Lights",body:"From sunset to sunrise (and in some conditions during the day), all aircraft must display anti-collision lights and position lights. Anti-collision lights (rotating beacon or strobes) must be on whenever the engine is running. Position lights: red on left wing, green on right wing, white on tail. If landing light is required (for hire at night), it must be operational or the flight is not legal."},
  "91.213":{title:"Inoperative Equipment",body:"Equipment not required by the operating rules or the aircraft's KOEL/MEL may be inoperative if: it is not required by the KOEL or applicable rule, it is placarded INOP, and the PIC determines the aircraft is safe to fly. If an MEL exists, the MEL controls. If no MEL, use the POH/AFM equipment list. Document any deferred items in the logbook."},
  "91.409":{title:"Annual Inspection",body:"Every aircraft operated must have received an annual inspection within the preceding 12 calendar months by an A&P mechanic holding an Inspection Authorization (IA). After the annual, the IA must make a logbook entry. An aircraft out of annual is not airworthy and may not be flown. Exception: the owner may fly the aircraft for the purpose of flying it to a place where the inspection can be done, with a special flight permit."},
  "91.411":{title:"Altimeter and Static Tests",body:"For IFR operations: the altimeter and pitot-static system must have been tested and inspected per FAR Part 43 Appendix E within the preceding 24 calendar months. Checks are performed by certified repair stations. Failed inspection = not legal for IFR. Portable electronic devices may interfere with avionics - use caution."},
  "91.413":{title:"Transponder Tests",body:"All transponders (including ADS-B Out transmitters) used in any operation must have been tested within the preceding 24 calendar months per Appendix F of Part 43. Required in Class A, B, and C airspace and above 10,000 MSL (excluding Class G below 2,500 AGL). ADS-B Out required in these same airspaces as of January 1, 2020."},
  "91.417":{title:"Maintenance Records",body:"Owners must keep maintenance records for as long as the aircraft is owned. Records must include: description of work done, date, aircraft total time, signature and certificate number of person doing the work. Major repairs and alterations must be recorded on FAA Form 337. Records must be made available to the FAA upon request."},
  "61.3":  {title:"Requirement for Certificates, Ratings, and Authorizations",body:"No person may serve as a required pilot flight crewmember unless they have in their possession a valid pilot certificate, photo ID, and applicable medical certificate (or BasicMed). Operating without these documents is a violation even if the pilot holds the appropriate certificates. Certificates must be presented to the FAA, NTSB, or law enforcement upon request."},
  "61.31": {title:"Type Rating and Additional Training Requirements",body:"High-performance aircraft (>200 HP): requires a one-time endorsement from a CFI after ground and flight training. Complex aircraft (retractable gear, flaps, and controllable propeller): requires a one-time endorsement. High-altitude aircraft (service ceiling or max operating altitude >25,000 ft MSL): requires ground and flight training with endorsement. Tailwheel aircraft: requires endorsement for PIC. Pressurized aircraft capable of operating above FL250: requires high-altitude training."},
  "61.113":{title:"Private Pilot Privileges and Limitations",body:"A private pilot may not act as PIC of an aircraft for compensation or hire, with exceptions: may share operating expenses equally with passengers (fuel, oil, airport fees, rental fees). May act as PIC for a charitable, religious, or community event flight under specific conditions (FAR 91.146). May be reimbursed for aircraft operating expenses incidental to search and rescue operations. May not pay less than the pro rata share."},
  "61.195":{title:"Flight Instructor Limitations and Qualifications",body:"A CFI may not conduct more than 8 hours of flight training in any 24-consecutive-hour period. Must hold appropriate category, class, and instrument rating for the instruction given. Must have given at least 400 hours of flight training to renew a certificate. Must maintain records of all training given for at least 3 years."},
  "91.17": {title:"Alcohol and Drugs",body:"No person may act as a crewmember within 8 hours of consuming any alcoholic beverage, while under the influence of alcohol, while using any drug that affects the person's faculties contrary to safety, or with a blood alcohol concentration of 0.04% or greater. The 8-hour rule is a legal minimum; the AIM recommends 24 hours from bottle to throttle for any significant consumption. Violation may result in certificate suspension or revocation."},
  "91.21": {title:"Portable Electronic Devices",body:"No person may operate, or allow operation of, any portable electronic device on an aircraft operated by an air carrier unless the operator determines it will not interfere with navigation or communication systems. For Part 91 operations, PIC has authority to restrict or allow PEDs. Transmitting devices (cell phones, Bluetooth) should be evaluated for potential interference with avionics."},
  "91.123":{title:"Compliance with ATC Clearances",body:"When an ATC clearance is received, no pilot may deviate from that clearance unless an amended clearance is obtained, an emergency exists, or the deviation is in response to a traffic alert (TCAS RA). If given priority by ATC in an emergency, you may be asked to submit a report within 48 hours. If you deviate from a clearance, notify ATC as soon as possible."},
  "91.126":{title:"Operating in Class G Airspace",body:"At airports without a control tower in Class G airspace: all turns in the traffic pattern must be made to the left unless the airport displays right-turn indicators or the Chart Supplement specifies otherwise. Comply with any traffic pattern procedures established for that airport. Maintain situational awareness — no ATC separation services are provided."},
  "91.127":{title:"Operating in Class E Airspace",body:"Class E airspace requires compliance with basic VFR weather minimums appropriate to the altitude (FAR 91.155). No ATC clearance is required for VFR flight. Pilots must maintain appropriate cruising altitudes (odd+500 for eastbound, even+500 for westbound) above 3,000 AGL per FAR 91.159. Class E extends from the surface at some airports (dashed magenta line on sectional)."},
  "91.129":{title:"Operating in Class D Airspace",body:"Two-way radio communication must be established with the tower before entering Class D airspace. You must maintain communication while in Class D. The ceiling of Class D is typically 2,500 ft AGL (charted). Speed limit: 200 KIAS at or below 2,500 ft AGL within 4 NM of primary airport. Departing aircraft must comply with departure procedures."},
  "91.130":{title:"Operating in Class C Airspace",body:"Two-way radio communication required. Transponder with Mode C required. ATC provides separation between IFR and VFR traffic, and traffic advisories for VFR-to-VFR conflicts. Speed limit: 200 KIAS at or below 2,500 ft AGL within 4 NM. Outer area: participation encouraged but not required. Equipment: operating transponder with altitude encoding."},
  "91.131":{title:"Operating in Class B Airspace",body:"ATC clearance required before entry (must hear your callsign + 'cleared into Class Bravo'). Transponder with Mode C required. Student, sport, and recreational pilots need specific training and endorsement. Speed limit: 200 KIAS at or below 2,500 ft AGL within 4 NM of primary airport, 250 KIAS below 10,000 MSL. VFR: 3 SM visibility, clear of clouds."},
  "91.135":{title:"Operating in Class A Airspace",body:"Class A: 18,000 MSL to FL600. IFR flight plan required. ATC clearance required. Altimeter set to 29.92 at and above 18,000 MSL (flight levels). Transponder with Mode C required. No VFR operations permitted in Class A airspace. Minimum equipment: full IFR panel, two-way radio, transponder with altitude encoding."},
  "91.159":{title:"VFR Cruising Altitudes",body:"Above 3,000 ft AGL and below 18,000 MSL: magnetic course 0-179 degrees, fly odd thousands +500 (e.g., 3,500, 5,500). Magnetic course 180-359 degrees, fly even thousands +500 (e.g., 4,500, 6,500). This applies to level cruising flight. Below 3,000 AGL, no specific altitude requirement (but minimum safe altitude per 91.119 applies). Hemispheric rule does not apply to IFR flights."},
  "91.167":{title:"IFR Fuel Requirements",body:"No person may operate an IFR flight unless it carries enough fuel to complete the flight to the first airport of intended landing, fly from that airport to the alternate (if required), and fly for 45 minutes at normal cruising speed after that. An alternate is required if the destination weather forecast shows ceiling below 2,000 ft or visibility below 3 SM (1-2-3 rule) for ±1 hour of ETA."},
  "91.175":{title:"IFR Takeoff and Landing Minimums",body:"No pilot may begin an instrument approach unless the latest weather shows the destination airport meets at least the minimum visibility for the approach. At DA/DH or MDA: the pilot must have the runway environment in sight to continue below. The runway environment includes: approach light system, threshold, runway end identifier lights, VASI, touchdown zone, runway markings, or runway lights."},
  "91.185":{title:"IFR Operations: Two-way Radio Failure",body:"If radio failure occurs in VMC: continue VFR and land as soon as practicable. If in IMC, fly: Route — as assigned, vectored, expected, or filed (in that priority). Altitude — highest of assigned, expected, or MEA for each segment. Squawk 7600. Begin descent at expected approach time or, if none received, at the ETA from the filed flight plan. This is a memory item: AVE-F MEA."},
  "91.403":{title:"General Maintenance Responsibility",body:"The owner or operator is primarily responsible for maintaining the aircraft in airworthy condition. This includes compliance with all Airworthiness Directives (ADs) within the required timeframe. The owner must ensure that maintenance personnel make appropriate entries in maintenance records. Preventive maintenance may be performed by the pilot/owner for aircraft not used in air carrier operations."},
};

const CHECKLISTS={
  preflight:{label:"Pre-Flight",color:"var(--amber)",sections:[
    {name:"Documentation",items:["Aircraft documents (ARROW) verified on board","Weight & balance computed and within limits","POH/AFM on board","Maintenance logs - current annual verified","Pilot certificate, medical, and logbook current"]},
    {name:"Cockpit",items:["Control lock removed","All switches OFF","Fuel quantity gauges noted (verify visually)","Required equipment operative or documented inop","Fire extinguisher present and charged"]},
    {name:"Engine & Nose",items:["Engine oil checked per POH min/max","Engine compartment - no leaks or FOD","Propeller inspected for nicks and cracks","Pitot tube clear, cover removed","Static ports clear","Cowling latched secure"]},
    {name:"Wings & Tail",items:["Leading edges - no damage or ice","Fuel caps secure, quantity verified visually","Fuel sumps drained - check for water","Control surfaces free and correct","Tie-downs removed","All antennas secure"]},
    {name:"Final",items:["Chocks removed","Fuel type verified (100LL / Jet-A / MOGAS)","Weather reviewed (METAR / TAF / PIREPs)","NOTAMs checked - departure, en route, destination","TFRs checked","ATIS / AWOS obtained"]},
  ]},
  runup:{label:"Run-Up",color:"var(--sky)",sections:[
    {name:"Engine Start",items:["Prop area clear - call CLEAR PROP","Anti-collision light ON","Master switch ON","Throttle cracked 1/4 inch","Mixture RICH (or per altitude)","Ignition to START, release to BOTH on fire","Oil pressure green arc within 30 seconds","Avionics ON after engine stabilized"]},
    {name:"Run-Up",items:["Parking brake SET","Flight controls - full deflection, free and correct","DG set to compass, altimeter ±75ft field elevation","Trim set for takeoff","Fuel selector BOTH or fullest tank","Throttle to 1800 RPM","Left mag - max 125 RPM drop","Right mag - max 125 RPM drop","Differential - max 50 RPM between mags","Carb heat ON (note drop), then OFF","All engine instruments in green arcs","Primer IN and LOCKED","Transponder ALT"]},
  ]},
  takeoff:{label:"Takeoff",color:"var(--green)",sections:[
    {name:"Line Up",items:["Strobes and landing light ON","Transponder ALT","Time noted for fuel/nav log","Fuel, mixture, flaps, trim - final check","Runway visually clear"]},
    {name:"Takeoff Roll",items:["Throttle smoothly full forward","Engine instruments check green as power builds","Maintain centerline","Airspeed alive at 10-15 kts","Rotate at Vr per POH","Positive rate confirmed - VSI and altimeter","Establish Vx or Vy per conditions"]},
  ]},
  approach:{label:"Approach",color:"var(--sky)",sections:[
    {name:"Descent",items:["ATIS/AWOS obtained for destination","Altimeter reset to current setting","Mixture enriched with descent","Carb heat ON at reduced power (if equipped)","Power reduced smoothly - monitor CHT"]},
    {name:"Final",items:["Fuel selector fullest tank / BOTH","Fuel pump ON (if equipped)","Landing light ON","Mixture RICH","Flaps per POH","Airspeed Vapp (1.3 × Vso)","Gear down and locked (if retractable)","Go-around mentally briefed"]},
  ]},
  emergency:{label:"Emergency",color:"var(--red)",sections:[
    {name:"Engine Failure - In Flight",items:["BEST GLIDE SPEED - immediately","Select landing area within glide range","Switch fuel tanks","Fuel pump ON, mixture RICH, carb heat ON","Cycle ignition - attempt restart","Squawk 7700","MAYDAY on 121.5 MHz","Before touchdown: fuel off, mags off, master off","Unlatch doors before touchdown"]},
    {name:"Engine Failure - Takeoff < 500 ft",items:["Throttle IDLE immediately","Land AHEAD - do NOT return to runway below 500-1000 AGL","Minor turn only if field is visible","Before touchdown: fuel off, mags off, master off","Unlatch doors"]},
    {name:"Inadvertent IMC",items:["WINGS LEVEL - trust instruments, not your body","Standard rate 180 turn (3 deg/sec coordinated)","Hold altitude on VSI and altimeter only","Squawk 7700","Declare EMERGENCY on 121.5","Request ATC vectors immediately","Disorientation develops in minutes - act fast"]},
  ]},
  shutdown:{label:"Shutdown",color:"var(--muted)",sections:[
    {name:"After Landing",items:["Flaps UP","Transponder STBY","Strobes OFF, taxi lights ON","Carb heat OFF","Lean mixture for taxi (if applicable)","Obtain taxi clearance or clear runway at first available"]},
    {name:"Engine Shutdown",items:["Parking brake SET","Throttle to 1000 RPM","Avionics OFF","Mixture CUTOFF (pull to idle cutoff)","Magnetos OFF after engine stops","Master switch OFF","Fuel selector OFF (if recommended by POH)","Control lock installed","Tie-downs secured, chocks placed","Hobbs and tach time recorded"]},
  ]},
  ifr:{label:"IFR",color:"#66FCF1",sections:[
    {name:"IFR Departure",items:["Clearance received and read back (CRAFT)","Departure procedure reviewed","ODP reviewed if no SID assigned","NAV radios set and identified","Missed approach procedure briefed","Altimeter set to current setting","DG aligned with compass heading","Transponder ALT with assigned squawk","Pitot heat ON (if IMC or visible moisture)","Void time noted if clearance is void-time based"]},
    {name:"IFR En Route",items:["Position reports at compulsory reporting points (non-radar)","Altitude changes confirmed with ATC","MEA/MOCA/MRA checked on each segment","MORA verified on GPS-equipped aircraft","Icing conditions monitored - pireps checked","Fuel state recalculated at each waypoint","Alternate requirements reviewed (1-2-3 rule)","Weather at destination updated en route"]},
    {name:"IFR Approach",items:["ATIS obtained, approach briefed (PTAC)","Approach plate loaded and verified","Minimums identified: DA/DH or MDA","Missed approach point identified","Timer set if required (non-precision)","Final approach course intercepted","Gear down (if retractable)","At DA/MDA: runway environment in sight or go missed","Missed approach executed if environment lost"]},
  ]},
  crosscountry:{label:"X-Country",color:"var(--amber)",sections:[
    {name:"Planning",items:["Route plotted on sectional or EFB","NOTAMs checked: departure, en route, destination","TFRs checked along entire route","Fuel planning: total fuel required plus reserves","Weight and balance computed for departure AND landing","Alternate airport identified if weather marginal","Flight plan filed (VFR recommended, IFR required)","Pilot and passenger brief completed"]},
    {name:"En Route",items:["Checkpoints verified against time and fuel plan","Ground speed compared to planned - adjust ETE","Flight following requested or flight plan opened","Fuel state checked at each checkpoint","Weather ahead monitored (Flight Watch / ADS-B)","Diversion airports identified along route","Terrain clearance verified on each leg","Position reports if on VFR flight plan"]},
    {name:"Arrival",items:["ATIS/AWOS obtained 20 NM out","Descent planning: 500 FPM or 3:1 rule","Pattern entry planned per AIM 4-3-3","Traffic pattern altitude and direction confirmed","Landing checklist completed","Close VFR flight plan after landing","Fuel state recorded for logbook"]},
  ]},
  night:{label:"Night",color:"#9B59B6",sections:[
    {name:"Pre-Flight (Night)",items:["All position lights tested: red, green, white","Anti-collision lights operational","Landing light operational","Instrument lights and panel dimmer tested","Flashlight on board with fresh batteries","Backup flashlight or headlamp available","Dark adaptation: 30 minutes recommended","VFR night weather minimums reviewed"]},
    {name:"In Flight (Night)",items:["Instrument scan emphasized - spatial disorientation risk","Avoid staring at single light sources","Cruise altitude higher than day VFR minimum","Landing light ON below 1,500 AGL","Black hole approach awareness at unlit airports","VASI/PAPI guidance used when available","Go-around threshold lower than daytime"]},
  ]},
};

const INCIDENTS=[
  {id:"NTSB CEN23FA201",ac:"Beechcraft BE36",cat:"VFR Into IMC",phase:"Cruise",fatal:true,cause:"VFR-only pilot continued into deteriorating conditions after receiving a weather briefing that indicated marginal conditions en route. Entered IMC approximately 40 miles from destination, lost spatial orientation, and impacted terrain in a graveyard spiral.",lessons:["VFR into IMC is the leading killer of GA pilots with instrument time - the instinct to press on is fatal","A 180-degree turn at the first sign of deteriorating visibility is always the right call, even if it costs you the flight","Set hard personal minimums before you start the engine - do not negotiate with yourself in the air","Get a full weather briefing: METAR, TAF, PIREPs, SIGMETs, AIRMET Tango, and prog charts","If ATC offers a deviation around weather, accept it - it is not an admission of weakness"]},
  {id:"NTSB WPR21FA155",ac:"Cirrus SR22",cat:"Base-to-Final Stall",phase:"Pattern",fatal:true,cause:"Pilot overshot final, increased bank angle to correct rather than executing a go-around. Stalled at approximately 500 ft AGL with insufficient altitude to recover. CAPS parachute was not deployed.",lessons:["The base-to-final stall is the most common fatal pattern accident in GA - 400+ deaths in the last decade","Stall speed increases dramatically with bank angle: 41% higher at 60 degrees, 19% higher at 45 degrees","The only correct response to an overshoot is a go-around - never increase bank below 1,000 ft AGL","If you have a CAPS-equipped aircraft, use it - low altitude ballistic parachute deployment is survivable","The go-around decision must be made early; a stabilized final is a requirement, not a suggestion"]},
  {id:"TSB A23O0012",ac:"Piper PA-44",cat:"Black Hole Illusion",phase:"Night Approach",fatal:true,cause:"Crew descended below MDA on a visual approach over dark, featureless terrain. No ambient lighting to confirm altitude. Aircraft impacted trees 2.1 NM short of runway threshold. Both crew fatally injured.",lessons:["The black hole illusion: dark terrain at night creates a false sense of height - your eyes lie to you","Always fly a published instrument approach at night, even in clear VMC conditions","Not stabilized by 500 ft AGL is an automatic go-around - no exceptions, no negotiation","Cross-check the altimeter and VASI/PAPI on every approach, especially at night or over water","High terrain around an approach corridor amplifies the illusion - check sectional before night approaches"]},
  {id:"NTSB ERA23FA123",ac:"Cessna C172S",cat:"Fuel Exhaustion",phase:"Cruise",fatal:false,cause:"Pilot relied on fuel gauges indicating approximately one quarter tank per side. Aircraft ran out of fuel 12 miles from destination and made a forced landing in a farm field. Gauges were inaccurate due to a known float issue documented in maintenance logs.",lessons:["GA fuel gauges are legally required to be accurate only when reading empty - treat all other readings as advisory","Always verify fuel quantity visually before every flight - stick the tanks if unsure","Track actual fuel consumption against your plan at every checkpoint - 30-minute intervals minimum","The legal reserve is the absolute floor, not the target - file and fly with more","Check the maintenance logs: known gauge issues must not be ignored"]},
  {id:"NTSB ERA22FA301",ac:"Cessna C182T",cat:"Carburetor Icing",phase:"Descent",fatal:false,cause:"Pilot began a power-reduction descent in high-humidity conditions at 8 degrees Celsius without applying carburetor heat. Engine progressively roughened then quit. Forced landing in a field with gear damage but no injuries.",lessons:["Carb ice forms between minus 20 and plus 20 degrees Celsius - temperature alone does not determine risk","The most dangerous window: 0-15 degrees C at high relative humidity or visible moisture","Apply carb heat proactively anytime you reduce power significantly - before the descent, not after","A rough engine and RPM drop is the symptom of carb ice already forming - add full heat immediately","Carb heat on approach should be a checklist item, not an afterthought"]},
  {id:"NTSB WPR22FA089",ac:"Piper PA-28",cat:"Runway Incursion",phase:"Taxi",fatal:false,cause:"Pilot programming GPS on the ground at an unfamiliar airport. Missed a hold-short instruction, crossed an active runway without clearance during a commercial departure sequence. No collision due to the departing aircraft aborting.",lessons:["Finish all avionics programming before requesting taxi clearance - the cockpit is not a workspace while moving","Read back every hold-short instruction verbatim and verify the line against the airport diagram","At unfamiliar airports, request progressive taxi instructions - there is no shame in it","The FAA estimates 3 runway incursions per day at towered airports - most are head-down pilot error","Head-down time while taxiing is one of the most underrated risk factors in GA"]},
  {id:"NTSB CEN22FA088",ac:"Mooney M20J",cat:"Loss of Control on Takeoff",phase:"Takeoff",fatal:true,cause:"Pilot departed a short mountain strip at high density altitude with a rearward CG configuration. Aircraft became airborne at low airspeed, pitched up, rolled left, and impacted terrain off the departure end. Three fatalities.",lessons:["Density altitude performance loss is not linear - a 5,000 ft DA can double your takeoff roll over sea level","Always compute actual performance numbers from your POH charts, not rule-of-thumb estimates","CG affects stall speed and pitch authority - an aft CG provides less margin and slower recovery","Know your departure end terrain and the climb gradient required to clear it before you roll","Mountain and backcountry operations require specific training - treat each strip as a unique risk assessment"]},
  {id:"NTSB ERA21LA288",ac:"Cessna C150",cat:"Spatial Disorientation",phase:"Cruise",fatal:false,cause:"Student pilot on a night solo departed from assigned practice area and encountered IMC over unlit terrain. Declared emergency, received radar vectors. Recovered safely but aircraft sustained minor damage landing off-airport.",lessons:["Spatial disorientation can develop in as little as 20 seconds without visual horizon reference","Trust your instruments completely - the vestibular system will lie to you in low visibility conditions","The leans: you can feel wings level when banked - always cross-check and believe the attitude indicator","Night VFR minimums exist precisely because reduced visibility increases IMC encounter risk","Night solo requires demonstrated night proficiency - recency matters more at night than during the day"]},
  {id:"NTSB WPR23FA041",ac:"Beechcraft A36",cat:"CFIT - Controlled Flight Into Terrain",phase:"Descent",fatal:true,cause:"Pilot flying a non-precision approach in IMC descended below MDA prior to acquiring visual contact with the runway environment. Aircraft impacted mountainous terrain 600 ft below the MDA. No mechanical anomalies found.",lessons:["CFIT on non-precision approaches kills experienced pilots - complacency is the enemy","MDA is not a target altitude - do not descend to it and maintain it; it is a hard floor","The VDP (Visual Descent Point) exists to give you a safe 3-degree path to the threshold - use it","If you do not have the required visual reference at MDA, execute the missed approach immediately","Terrain awareness systems (TAWS/GPWS) are not a luxury in mountainous terrain - they are life-safety equipment"]},
  {id:"NTSB ERA23LA155",ac:"Piper PA-32",cat:"Gear Up Landing",phase:"Landing",fatal:false,cause:"Pilot distracted by passenger conversation during approach and forgot to lower landing gear. Aircraft landed on belly causing substantial damage but no injuries. GUMPS check was not completed. Gear warning horn had a known circuit issue noted in logs.",lessons:["GUMPS (Gas, Undercarriage, Mixture, Propeller, Switches) is not optional on a retractable gear aircraft","Use the GUMPS check on every approach, on every leg of the pattern - not just final","Sterile cockpit below 1,000 AGL: no non-essential conversation during approach and landing","Gear warning horn inoperative is a grounding issue - do not defer it and fly a retractable gear aircraft","If you hear the horn: go-around and reconfigure, then re-enter the pattern"]},
  {id:"NTSB CEN21FA201",ac:"Cessna C172N",cat:"Low-Altitude Maneuvering",phase:"Cruise",fatal:true,cause:"Pilot performing unauthorized low-altitude aerobatics over a lake. Lost control during an aggressive pull-up from a low pass, impacted water. Two fatalities. No aerobatic training on record.",lessons:["Low-altitude maneuvering is the second leading cause of GA fatalities - altitude is your only margin","A C172 is not an aerobatic aircraft and has no aerobatic certification - structural limits are unknown in maneuvers","Pulling up aggressively at low altitude combines low G margin with no altitude to recover in","Showing off is a statistical predictor of fatal accidents in GA - know the numbers","Film no maneuver below 1,500 AGL - your camera is not worth your life"]},
  {id:"NTSB WPR22LA200",ac:"Cessna C182Q",cat:"Fuel Contamination",phase:"Takeoff",fatal:false,cause:"Engine failed on rotation due to water contamination in fuel system. Aircraft overran the departure end into a ditch. Subsequent investigation found the fuel caps had not been properly sealed after a fuel stop in heavy rain, allowing water ingestion.",lessons:["Fuel contamination can appear colorless in avgas if water saturation is complete - always sump all drains","After refueling in rain: check sumps immediately after refueling and again before departure","A single sump check after a rain event is not sufficient - water migrates through the fuel system","Engine failure on rotation leaves you with one option: land straight ahead, do not attempt to turn back","The fuel sump check is not a formality - it is a life-safety check every single flight"]},
  {id:"NTSB ERA22FA089",ac:"Diamond DA40",cat:"Engine Failure - Fuel Mismanagement",phase:"Cruise",fatal:false,cause:"Pilot switched to empty main tank mid-flight without selecting crossfeed. Engine quit when selected tank ran dry. Forced landing on a highway with minor injuries. A full auxiliary tank was onboard.",lessons:["Always confirm fuel selector position with a physical check, not just a glance at the placard","Know your specific aircraft fuel system - DA40 and C172 selectors work very differently","Engine silence is a checklist event: fuel selector, fuel pump, mixture, ignition - in order","If an airport is within glide range, fly to it immediately - do not troubleshoot and fly simultaneously","Forced landing on a road: power lines are the primary threat, check for them on short final"]},
  {id:"NTSB CEN22LA310",ac:"Piper PA-28R",cat:"Loss of Control - Wind Shear",phase:"Approach",fatal:false,cause:"Pilot encountered unexpected wind shear and microburst on final approach during a convective weather event. Airspeed dropped 22 knots, aircraft landed hard and short. No PIREPs for the area had been filed in the preceding hour.",lessons:["Wind shear and microbursts are invisible - LLWAS and PIREP data is your primary warning system","A SIGMET or convective AIRMET active in the area means the approach is not safe until conditions improve","Airspeed below Vref on final approach should trigger an immediate go-around - no exceptions","If you see a virga (rain evaporating before reaching the ground), suspect microburst below the precipitation shaft","Fly stabilized approaches - if airspeed deviates more than 10 knots from Vref, go around"]},
  {id:"NTSB ERA23LA044",ac:"Cessna C172SP",cat:"Bird Strike",phase:"Climb",fatal:false,cause:"Bird strike at 400 ft AGL on departure shattered windscreen and caused partial engine power loss. Pilot declared emergency and returned for landing. Handled correctly with no injuries. Aircraft sustained substantial damage.",lessons:["Bird strikes peak at sunrise and sunset when birds are most active - file PIREP if you encounter flocks","A bird strike is an emergency even if initial systems check appears normal - return for inspection","Shattered windscreen in IMC or at altitude: aviate first, use side window if forward visibility is zero","Partial power loss: maintain best glide speed and declare emergency immediately - do not diagnose in the air","Report all bird strikes via the FAA Wildlife Strike Database - your data protects other pilots"]},
];

const WB_AIRCRAFT={
  "C172S":    {emptyWeight:1663,emptyArm:40.6,maxGross:2550,cgMin:35.0,cgMax:47.3,stations:[{name:"Pilot & Front Pax",arm:37.0},{name:"Rear Pax",arm:73.0},{name:"Fuel (gal)",arm:48.0,lbPerUnit:6.0},{name:"Baggage",arm:95.0}]},
  "C172N":    {emptyWeight:1393,emptyArm:39.2,maxGross:2300,cgMin:35.0,cgMax:47.3,stations:[{name:"Pilot & Front Pax",arm:37.0},{name:"Rear Pax",arm:73.0},{name:"Fuel (gal)",arm:48.0,lbPerUnit:6.0},{name:"Baggage",arm:95.0}]},
  "C182T":    {emptyWeight:1971,emptyArm:40.5,maxGross:3100,cgMin:35.0,cgMax:47.3,stations:[{name:"Pilot & Front Pax",arm:37.0},{name:"Rear Pax",arm:73.0},{name:"Fuel (gal)",arm:48.0,lbPerUnit:6.0},{name:"Baggage A",arm:84.0},{name:"Baggage B",arm:108.0}]},
  "PA28-161": {emptyWeight:1325,emptyArm:86.4,maxGross:2325,cgMin:82.0,cgMax:93.0,stations:[{name:"Pilot & Front Pax",arm:80.5},{name:"Rear Pax",arm:118.1},{name:"Fuel (gal)",arm:95.0,lbPerUnit:6.0},{name:"Baggage",arm:142.8}]},
  "PA28-181": {emptyWeight:1498,emptyArm:87.1,maxGross:2550,cgMin:82.0,cgMax:93.0,stations:[{name:"Pilot & Front Pax",arm:80.5},{name:"Rear Pax",arm:118.1},{name:"Fuel (gal)",arm:95.0,lbPerUnit:6.0},{name:"Baggage",arm:142.8}]},
  "DA40":     {emptyWeight:1632,emptyArm:55.7,maxGross:2535,cgMin:51.2,cgMax:58.9,stations:[{name:"Pilot & Front Pax",arm:48.0},{name:"Rear Pax",arm:93.0},{name:"Fuel (gal)",arm:60.0,lbPerUnit:6.0},{name:"Baggage",arm:101.0}]},
  "SR22":     {emptyWeight:2160,emptyArm:143.2,maxGross:3400,cgMin:135.0,cgMax:149.0,stations:[{name:"Pilot & Front Pax",arm:138.0},{name:"Rear Pax",arm:172.0},{name:"Fuel (gal)",arm:143.0,lbPerUnit:6.0},{name:"Baggage",arm:183.0}]},
};

function calcDA(elev,tempC,baro){const pa=elev+(29.92-baro)*1000;return Math.round(pa+120*(tempC-(15-1.98*elev/1000)));}

// Fetch route distance from backend (uses METAR station coordinates)
async function fetchRouteDistance(from,to){
  if(!from||!to||from.length<3||to.length<3)return null;
  try{
    const [r1,r2]=await Promise.all([
      fetch('https://aviationweather.gov/api/data/metar?ids='+from+'&format=json&hours=2').then(r=>r.json()),
      fetch('https://aviationweather.gov/api/data/metar?ids='+to+'&format=json&hours=2').then(r=>r.json()),
    ]);
    const s1=r1&&r1[0],s2=r2&&r2[0];
    if(!s1?.lat||!s2?.lat)return null;
    // Haversine in nautical miles
    const toRad=d=>d*Math.PI/180;
    const R=3440.065;
    const dLat=toRad(s2.lat-s1.lat),dLon=toRad(s2.lon-s1.lon);
    const a=Math.sin(dLat/2)**2+Math.cos(toRad(s1.lat))*Math.cos(toRad(s2.lat))*Math.sin(dLon/2)**2;
    return Math.round(R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a)));
  }catch{return null;}
}

function getQuickChips(fromIcao,toIcao){
  const apt=fromIcao&&fromIcao.length>=3?fromIcao:'my departure airport';
  const aptLabel=fromIcao&&fromIcao.length>=3?fromIcao:'my airport';
  const route=fromIcao&&toIcao&&fromIcao.length>=3&&toIcao.length>=3?fromIcao+' to '+toIcao:'my planned route';
  return[
  {label:"\u25C8 Mission Brief",q:"Generate a full Mission Brief for my current flight. Include threat assessment (environmental, organizational, personal), risk score, currency status, aircraft status, weather picture, NOTAM summary, mitigation checklist, and go/review/no-go recommendation."},
  {label:"Brief me on "+aptLabel,q:"Brief me on "+apt+". What do I need to know about the airspace, traffic patterns, terrain, and local operations?"},
  {label:"Airport intel",q:"Give me an airport intelligence brief for my destination airport. Cover airspace, local ops, threat environment, and environmental context."},
  {label:"Pre-flight risk brief",q:"Give me a full pre-flight risk brief for "+route+". What are the top 3 risk factors I should brief before departure?"},
  {label:"Weather risks",q:"What are the weather risks for my route today? Check METARs, TAFs, and any advisories."},
  {label:"Passenger currency",q:"My last 3 landings were 95 days ago and my last night full-stop was 110 days ago. Am I legal to carry passengers during the day? At night?"},
  {label:"NTSB pattern: C172",q:"What are the most common NTSB accident patterns for C172 pilots in the last 5 years? What should I be specifically vigilant about?"},
  {label:"Aft CG risk",q:"I loaded my C172 with a heavy pilot, rear passengers, and baggage. My CG is calculating at the aft limit. What does that mean for handling and what are the real risks?"},
  {label:"Medical options",q:"My 3rd class medical expired 30 days ago. What are my legal options to fly PIC right now? Walk me through BasicMed and sport pilot options."},
  {label:"CFIT approach brief",q:"I'm shooting a non-precision VOR approach to minimums. Walk me through MDA, VDP, and what a proper missed approach decision looks like."},
  {label:"Passenger Brief",q:"Generate a Passenger Brief for this flight. Write a short, plain-English summary for a non-pilot passenger. No aviation jargon. Cover where we are going and how long, weather in plain terms, any flags reviewed, the go decision and reasoning, what to expect during the flight (normal sounds and sensations), and a safety briefing (seatbelts, exits, what to do if pilot is incapacitated: call 121.5 on radio). Keep under 200 words. Conversational tone. End with: Your pilot has reviewed all available pre-flight information and made the decision to fly. All go/no-go decisions are made by the pilot in command."},
];}

const SYSTEM_PROMPT=(ac,logs,flightCtx)=>`You are SOLUS -- an AI flight intelligence copilot built for pilots who take safety seriously.

You serve pilots across multiple operation types. The risk picture for a student at a university-affiliated flight school is not the same as the risk picture for an aerial ag operator or a weekend VFR cross-country pilot. Know who you are talking to. Apply the right frame.

You serve two aircraft populations:
1. GA pilots flying traditional fixed-wing and rotary aircraft under FAA regulations
2. Jetson ONE pilots flying the Jetson ONE electric personal aerial vehicle (eVTOL) and using the Jetson ONE Flight Companion Android app

Detect which type of pilot you're talking to from context -- aircraft type, questions asked, terminology used. When unclear, ask. Apply the right knowledge base for the right pilot. Never mix FAA currency rules into a Jetson ONE conversation, and never apply Jetson ONE-specific procedures to a GA pilot.

Aircraft on file: ${ac.nNumber?`${ac.type||"Unknown"} N${ac.nNumber} - ${ac.totalTime||"?"} hrs total, engine ${ac.engineTime||"?"} SMOH, last annual ${ac.lastAnnual||"?"}, squawks: ${ac.squawks||"none"}`:"No aircraft loaded."}
Maintenance log: ${logs.length?logs.map(l=>`${l.date} - ${l.item} [${l.status}]`).join("; "):"No entries."}
${flightCtx&&flightCtx.wbResult?`W&B status: ${flightCtx.wbResult}. IMPORTANT: These W&B numbers are computed deterministically from pilot-entered data. Do NOT modify, estimate, or recalculate any weight, CG, moment, or performance number. Quote them exactly as provided. If W&B status shows INCOMPLETE, state that W&B was not evaluated and the pilot should complete their aircraft profile.`:"W&B: Not evaluated for this flight. State this in the AIRCRAFT section of any Mission Brief."}
${(()=>{try{const r=JSON.parse(localStorage.getItem('solus_readiness_today')||'null');if(!r)return'Pilot readiness: Not assessed. State this in the PILOT section of any Mission Brief.';if(r.skipped)return'Pilot readiness: Pilot chose to skip self-assessment.';const flags=[];if(r.sleepHours!=null&&r.sleepHours<5)flags.push('reported '+r.sleepHours+' hours sleep');if(r.alcohol8hr)flags.push('alcohol within 8 hours');if(r.illness)flags.push('illness symptoms');if(r.fatigue==='high')flags.push('high fatigue');if(r.stress==='high')flags.push('high stress');if(r.medication)flags.push('taking medication');if(r.sleepHours!=null&&r.sleepHours>=5&&r.sleepHours<=6)flags.push(r.sleepHours+' hours sleep');if(r.sleepQuality==='poor')flags.push('poor sleep quality');if(r.emotion==='upset')flags.push('elevated emotional state');return flags.length>0?'Pilot readiness flags: '+flags.join(', ')+'. IMPORTANT: Quote these flags exactly in the PILOT section. Never infer or add readiness states the pilot did not report. Use decision-support language only, never say fit/unfit.':'Pilot readiness: Self-assessment completed, no flags reported.';}catch{return'Pilot readiness: Not assessed.';}})()}
${(()=>{try{const m=JSON.parse(localStorage.getItem('solus_personal_minimums')||'null');if(!m)return'Personal minimums: Not configured.';const parts=[];if(m.ceilingDay!=null)parts.push('day ceiling '+m.ceilingDay+'ft');if(m.visibilityDay!=null)parts.push('day vis '+m.visibilityDay+'SM');if(m.ceilingNight!=null)parts.push('night ceiling '+m.ceilingNight+'ft');if(m.visibilityNight!=null)parts.push('night vis '+m.visibilityNight+'SM');if(m.crosswind!=null)parts.push('max crosswind '+m.crosswind+'kts');if(m.tailwind!=null)parts.push('max tailwind '+m.tailwind+'kts');if(m.maxDensityAlt!=null)parts.push('max DA '+m.maxDensityAlt+'ft');if(m.minRunwayLength!=null)parts.push('min runway '+m.minRunwayLength+'ft');if(m.maxFlightDuration!=null)parts.push('max duration '+m.maxFlightDuration+'hrs');return parts.length>0?'Personal minimums: '+parts.join(', ')+'. IMPORTANT: In every Mission Brief, compare current and forecast conditions against these personal minimums. If any condition exceeds a minimum, flag it explicitly in the enVIRONMENT or PILOT section with the specific value vs. the minimum. These are self-imposed limits, not regulatory.':'Personal minimums: Not configured.';}catch{return'Personal minimums: Not configured.';}})()}
${flightCtx&&(flightCtx.from||flightCtx.to)?`Flight plan: ${flightCtx.from||"?"} -> ${flightCtx.to||"?"}${flightCtx.deptTime?", departure "+flightCtx.deptTime+" local":""}${flightCtx.fuel?", "+flightCtx.fuel+" gal fuel":""}${flightCtx.burnRate?", burn rate "+flightCtx.burnRate+" gal/hr":""}${flightCtx.fuel&&flightCtx.burnRate?". Computed endurance: "+Math.round(parseFloat(flightCtx.fuel)/parseFloat(flightCtx.burnRate)*10)/10+" hrs ("+Math.round(parseFloat(flightCtx.fuel)/parseFloat(flightCtx.burnRate)*60)+" min). VFR day reserve (30 min): "+(parseFloat(flightCtx.fuel)-parseFloat(flightCtx.burnRate)*0.5).toFixed(1)+" gal usable. These are computed values — use them exactly.":""}`:""}

YOUR VOICE: Direct. No filler. You synthesize, you don't list. You surface risk -- you don't make decisions. Every response that involves risk assessment includes: "PIC reminder: You are pilot-in-command. All decisions are yours. SOLUS provides decision support, not authorization to fly."

DETERMINISTIC MATH RULE (NON-NEGOTIABLE): You must NEVER estimate, guess, or generate any numerical value for: distances between airports, fuel calculations, weight and balance figures, density altitude, takeoff/landing distances, wind components, runway lengths, or performance numbers. If a computed value is provided in this context (e.g., "Route distance: 1788 nautical miles"), use that exact number. If a value is NOT provided, say "not computed" or "verify with your EFB/POH." NEVER fill in a number from your training data. Distances between airports vary and your training data is unreliable for these. Fuel burn depends on the specific aircraft. Performance depends on actual conditions. State what you don't know. A wrong number is worse than no number.

Format: plain text and short paragraphs only. Numbered lists only for step-by-step procedures. No markdown of any kind: no asterisks, no bold, no headers, no bullet points, no backticks.

OPERATION TYPE AWARENESS:
Before assessing risk, identify the operation type. Each has different baseline risk and primary concerns.
GA recreational / cross-country: Personal safety, weather judgment, currency. Standard baseline.
Flight training (Part 61/141): Student safety + institutional exposure. Lower tolerance.
University-affiliated flight training: Lowest tolerance. A single accident headline is existential.
Aerial agriculture / banner tow / low-altitude work: Terrain, wire strike, fatigue. Inherently higher baseline.
Air ambulance / medevac (Part 135): Weather, duty time, pressure to launch. Highest stakes.
eVTOL / Part 103 (Jetson ONE): Battery, wind, airspace incursion, GPS. No FAR 61 currency applies.
Corporate / charter (Part 135): Crew currency, aircraft MEL, schedule pressure.
Ferry / one-off / non-standard: Unfamiliar aircraft, unfamiliar route.
Ask if unclear. "What type of operation are you flying?" is a legitimate first question.

RISK SCORING PHILOSOPHY (THREE-BAND FRAMEWORK):
Risk is not binary and not generic. The same conditions that are yellow for an experienced ATP are red for a student pilot 10 hours post-solo.
GREEN: Within normal parameters for this operation type and pilot profile. No mitigations beyond standard preflight.
YELLOW: Elevated above normal for this operation type. Not a stop -- a flag. Name what is elevated and why.
RED: Outside acceptable parameters for this operation type. Flight not authorized without mitigation or supervisory sign-off. Be specific about what makes it red and what would bring it back to yellow.
Critical rule: Never apply generic thresholds. Always anchor to operation type. A 15-knot crosswind is yellow for a 2,000-hour pilot in a Cherokee and red for a 60-hour student. Say so.

METHODOLOGY TRANSPARENCY:
Pilots and operators need to understand how a risk score is calculated. When you surface a risk assessment, show your reasoning.
For each factor: name it, state what you see, explain why it matters for this operation, and state the band contribution (GREEN/YELLOW/RED).
Then synthesize: "Given [operation type] and [pilot profile], the overall picture is [band] because [primary drivers]."
Never give a score without an explanation. A number or color with no reasoning is the failure mode -- pilots do not trust scores they cannot audit.

PILOT CURRENCY VS. PROFICIENCY:
Currency is the legal minimum (FAR 61.57). It is the floor, not the ceiling.
Proficiency is the actual safety question. A pilot can be current and not proficient. Flag this distinction explicitly.
Key currency requirements (all require three qualifying dates, not one):
VFR day: 3 T/Os and 3 landings in preceding 90 days, same category/class/type.
VFR night: 3 T/Os and 3 full-stop landings at night in preceding 90 days.
Instrument: 6 approaches + holding + tracking in preceding 6 calendar months.
Flight Review (FAR 61.56): every 24 calendar months, 1 hr flight + 1 hr ground minimum.
Medical: class-dependent; BasicMed as alternative for some operations.
Recency signals to probe: last flight in this aircraft type, last actual IMC, last flight in this area, time since last flight review, any gap 30+ days since last PIC time.
When currency is lapsed or close to lapse, name it clearly with the specific date gap. Do not soften it.

FLIGHT SCHOOL AND INSTITUTIONAL SEGMENT:
When a pilot identifies as a flight school operator, CFI, chief pilot, or university flight department:
Primary concern is risk management, not just accident prevention.
What they care about: media/reputational risk (a single accident generates national headlines), regulatory exposure (FAA scrutiny can ground an operation), student safety, operational efficiency.
Frame risk with institutional exposure first. Customizable thresholds matter. Supervisor visibility is a feature. Post-flight debrief as training tool.
Flight school-specific factors: student recency relative to lesson plan stage, dual vs. solo flight, new airport for student experience level, weather approaching student demonstrated limitations, instructor fatigue / high lesson count.

WEATHER INTELLIGENCE:
METAR: Decode into plain language. Flag for this operation: wind shear, gusts >15 kt, ceiling below 1,000 ft AGL, vis below 3 SM, icing, thunderstorms. For flight training, apply tighter thresholds relative to student experience.
TAF: Identify trend changes, PROB groups, TEMPO, BECMG. Flag windows where conditions could trap VFR flight or interrupt training.
PIREPs: High weight. Recent actual conditions beat forecast. Flag moderate/severe turbulence, icing, LLWS.
Density altitude: Flag high elevation + high temp + high humidity putting DA more than 1,000 ft above field elevation. Especially relevant for low-altitude ag ops and mountain flying.
GPS spoofing/jamming (2026): Eastern Europe, Middle East, East Africa, expanding U.S. Gulf Coast/Texas border. IATA: spoofing up 193%, jamming up 67% vs 2023. Cross-check with VOR/DME and dead reckoning.

AIRSPACE INTELLIGENCE:
TFRs: Always check. Stadium, VIP, temporary security TFRs most commonly violated.
Class B: Explicit ATC clearance required -- "cleared to enter Class B," not just contact.
Special use airspace: MOA, Restricted, Prohibited, Warning Areas. Active status from NOTAMs.
NOTAM translation: Parse into plain language. Surface what is operationally relevant to this route and operation type.

NTSB PATTERN AWARENESS:
LOC-I: #1 killer. Maneuvering flight, approach, inadvertent IMC.
CFIT: Disproportionately fatal. VFR into IMC -- 178 seconds average survival.
Fuel mismanagement: Exhaustion (ran out) vs. starvation (fuel available, not reaching engine).
Get-there-itis: Launching into marginal conditions expecting improvement.
Pressure to launch: Most acute in Part 135 (air ambulance, charter) and university flight departments.
When a pilot's scenario maps to a pattern, name it. Not to alarm -- to inform.

AIRCRAFT AIRWORTHINESS:
Annual (FAR 91.409): Every 12 calendar months for Part 91.
100-hour (FAR 91.409(b)): For aircraft used for hire or flight instruction for hire.
ADs: Mandatory. Non-compliant = not airworthy.
ARROW: Airworthiness, Registration, Radio license (international), Operating limitations, Weight and balance -- must be on board.
MEL: Without an approved MEL, all required equipment must be operative.

RISK FRAMEWORK (PAVE):
P -- Pilot: Currency, recency, fatigue, health, stress load, recent experience in aircraft type
A -- Aircraft: Airworthiness, MEL status, known squawks, fuel state, performance margins
V -- enVironment: Weather, airspace, terrain, NOTAMs, time of day, destination conditions
E -- External pressures: Schedule pressure, passenger expectations, get-there-itis, return-to-home pressure
Synthesize into a risk picture anchored to the operation type.

HUMAN FACTORS AND TEM:
Apply Threat and Error Management proactively.
Threats: Environmental, Organizational, Personal.
Errors: Proficiency, Decision, Perceptual.
When surfacing: "Human Factors: [concern]" then the countermeasure.

MISSION BRIEF FORMAT:
Structure using PAVE (FAA-H-8083-2): PILOT, AIRCRAFT, enVIRONMENT, EXTERNAL PRESSURES.
AIRCRAFT section MUST include W&B status. If W&B was computed, state: weight, CG, envelope status, and any flags verbatim from the W&B result. If W&B severity is HIGH, flag it as an explicit no-go factor. If MEDIUM, flag it as a caution. If INCOMPLETE, state: "W&B was not evaluated — complete your aircraft profile." Never silently omit W&B.
If density altitude or performance data is available, include it in enVIRONMENT: DA value, takeoff/landing distances with safety margin, and runway comparison.
After PAVE: concrete mitigation checklist. No go/no-go recommendation. End with flag count and highest flag.
End every Mission Brief with: "SOLUS applies the PAVE framework (FAA-H-8083-2) to your specific flight conditions."

AIRPORT INTELLIGENCE:
TL;DR brief: airspace class, traffic patterns, terrain, density altitude, local procedures. 4-6 action statements.
For unfamiliar regions: weather patterns, ground transport, cultural norms.

AVIONICS AND EFB CONTEXT:
SOLUS does not currently integrate natively with ForeFlight or Garmin Pilot. Acknowledge directly. The workaround: pilots can pull data from their EFB and paste into SOLUS for synthesis. ForeFlight integration is on the roadmap. SOLUS adds synthesis and risk framing, not data sourcing. Do not suggest SOLUS replaces an EFB.

MARKET CONTEXT:
ForeFlight acquired by Thoma Bravo PE for $10.55B (April 2025).
FAA projects 18,000 pilot deficit by 2030.
GPS spoofing/jamming now routine threat.
AAM eIPP flights begin summer 2026 in select cities.
GA insurance ($2.9B market) moving toward behavioral pricing.
Jetson ONE: $148K Part 103 eVTOL, 650+ units sold, no pilot certificate required, sold out through 2027.

INSURANCE SIGNAL FRAMING:
Currency lapses are highest-correlation factor in GA accidents. Recency gaps over 60 days increase incident probability. Night + instrument currency lapses are highest-risk combination. Pre-flight behavioral data enables usage-based insurance pricing.

SECTION B -- JETSON ONE PILOT SUPPORT

When a pilot is flying or operating the Jetson ONE, shift into Jetson ONE mode. Context cues: mentions of "sortie," "Jetson Link," "eVTOL," "Flight Companion app," serial number format "SN-###," or direct questions about the app.

AIRCRAFT CONTEXT:
Jetson ONE: single-seat electric personal aerial vehicle (eVTOL/PAV). 8 propellers on 4 folding motor arms -- arms must be unfolded and locked before every flight. Onboard parachute system -- safety pin must be verified SAFE every preflight. Built-in Primary Flight Display. Jetson Link: WiFi hotspot for downloading binary flight controller logs. Maximum wind limit: 12 knots. Typical environment: Class G uncontrolled airspace at low altitude.

FLIGHT COMPANION APP (Android-only, sideloaded):
Main screens: Logbook (home), Pre-Flight Brief (live weather + airspace from GPS), 11-Step Sortie Wizard, Live Flight HUD, Post-Flight Wizard + OCR, Debrief (GPS replay, charts, alerts, export), Jetson Link (WiFi log download), Risk Matrix (Go/No-Go), Performance (Pilot Score, Trends, Precision Exercises, Leaderboard), Flagging + Instructor Debrief.

PRE-FLIGHT BRIEF:
4 Go/No-Go status chips: TFRs (Green=none, Red=active), Airspace (Green=Class G, Red=B/C/D), Wind (Green=<8kt, Amber=8-12kt, Red=>12kt), Flight Wx (Green=VFR, Amber=MVFR, Red=IFR/LIFR).
Location card border: red=TFR overhead or Class B/C/D conflict, amber=Class D or wind caution, grey=clean.

11-STEP SORTIE WIZARD:
1. Pilot weight with helmet  2. Date and time  3. Serial number (e.g. SN-042)  4. Battery ID and state of charge (%)  5. Flight type (Recreational/Training/Test/Maintenance)  6. Training details (Training only)  7. Test flight type (Test only)  8. Test conductor name (Test only)  9. Flight location  10. Risk assessment (score each category 1-5)  11. Summary then Begin Flight.

RISK ASSESSMENT (Step 10 / standalone):
Scores 1-5 per category. 1-2=Green (proceed), 3=Yellow (attention required), 4=Orange (mitigation plan required), 5=Red (FOM authorization required).
Rule: 3 or more Red (score 5) scores = FOM authorization required. Do not fly without sign-off.
Recreational: 10 categories (Environment + Human). Test flights: 15 categories (adds MIX group).

PRE-FLIGHT CHECKLISTS (critical items -- never skip):
1. Motor Arms (4): REMOVE CHOCKS, UNFOLD and LOCK
2. MDCs (2): CONDITION, SECURELY ATTACHED
3. Batteries (8): SECURED, GET IN POSITION, DO NOT CONNECT YET
4. MDCs LED (8): VERIFY BLINKING: 1x BLUE, 1x WHITE
Also: Takeoff Area CLEAR FOD, Wind Strength 12 KTS MAXIMUM, Vehicle Position LEVEL GROUND (plus/minus 5 deg), Parachute Safety Pin SAFE, Battery Terminal Covers (2) FITTED and LATCHED, Propeller Guards (8) FITTED.

AUTO-DETECTION TIMERS (configurable):
Motor Start Confirmation: 5s. Motor Stop Warmup: 3s. Motor Stop Confirmation: 10s. Quick-Turnaround Window: 15 min. Debrief Prompt Delay: 2 min. Vibration RMS Threshold: 1.5.

VOICE COMMANDS (Android, on-device, no internet):
"Start logging," "Stop logging," "Next item," "Read brief."

LIVE FLIGHT HUD:
Left: Airspeed (kt), GPS satellites, RTH arrow, Heading (MAG), Vibration. Center: Flight time remaining, BAT/MDC/MOT temps (C), Stopwatch, Alert banners. Right: Altitude AGL (ft), Vertical Speed, Throttle, FCS stick, Yaw. Battery bar: blue to yellow to red. Alert banners flash for CRITICAL and WARNING.

POST-FLIGHT WIZARD + OCR:
After motor shutdown: confirm/edit duration, enter observed weather, flight notes, post-flight inspection status, capture aircraft flight display (OCR), tag precision exercises. OCR: camera at Jetson ONE display, fill frame, ML Kit extracts stopwatch time, remaining flight time, peak altitude, max speed, motor hours.

JETSON LINK (WiFi Log Download):
After each flight: Jetson ONE powered on with hotspot active, Connect to Box, Download Logs, review proposed sortie match, Confirm or Skip, Done then View Debrief. Status indicators: black=Not Connected, yellow=Searching, orange=Connecting, green=Connected, blue=Transferring, checkmark=Complete, red=Error.

PILOT SCORE COMPONENTS:
Positive: Green/Attention risk scores, all critical checklist items completed, brief acknowledged, VMC within limits, clean sortie, within weight+wind envelope, precision exercise passing grade.
Negative: Mitigate/FOM Required scores, skipped critical items, skipped brief, marginal weather or exceeded wind limit, incident flagged, outside approved limits.
Score visible to Jetson instructors. Strong score accelerates training progression.

PRECISION EXERCISES (7 types, auto-graded):
Coordinated Circles, S-Turns, Smooth Landing, Hover Stability, Altitude Control, Slow Flight, Traffic Pattern.
Grades: Excellent +15, Good +10, Satisfactory +5, Incomplete 0.

FLAGGING AN INCIDENT:
Any abnormal event: Logbook then sortie then Flag then select reason (Aircraft Behaviour / Alert Triggered / Abort+Emergency Stop / Airspace Incident / Injury+Near-Miss / Other) then description then Submit. Always flag if a health alert fired during flight even if flight completed normally.

CLICKUP INTEGRATION:
Per-sortie sync: pilot name, date, duration, flight type, serial number, location, risk level, notes, log sync status. Offline: queues and retries on reconnect.

CARDO AUDIO:
Phase 1 (current): Android TTS via phone speaker or Bluetooth. Reads checklists, briefs, alerts aloud.
Phase 2 (planned): native Cardo Packtalk SDK with priority-based alert queuing and helmet audio routing.

TROUBLESHOOTING:
Flight not auto-detected: Location=Always Allow, Auto-Detection=on, adjust Vibration RMS threshold.
Jetson Link won't connect: Verify SSID+password, aircraft powered, WiFi LED active.
OCR not reading: Good lighting, phone parallel, full display in frame, no glare.
Poor GPS in debrief: Download aircraft log via Jetson Link.
ClickUp sync failing: Re-enter Authorization Code, verify List ID, confirm internet.
Weather not loading: Verify Windy API key, confirm internet, GPS must be accurate.

SECTION C -- HARD RULES

No go/no-go language. Surface the risk picture. The pilot decides.
PIC disclaimer on every risk assessment.
Never apply generic thresholds. Always anchor to operation type and pilot profile.
Show methodology. A risk flag without reasoning is worse than no flag.
Do not conflate GA and Jetson ONE. FAR 61 currency does not apply to Part 103 operators.
Do not hallucinate regulations. If uncertain, say so and direct to FAA source.
Flag safety-critical information first. Lapsed currency, active TFRs, red-band conditions lead, they do not trail.
Brevity is safety. Pilots do not need essays. Synthesize. Cut what does not matter.

End every response with exactly this line on its own:
SOLUS surfaces data. All go/no-go decisions rest with the PIC per FAR 91.7.`;

// ─── THEME CSS ────────────────────────────────────────────────────────────────
const CSS=`
@import url('https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Barlow+Condensed:wght@500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
ol.blog-ol li::marker{color:#FFB000;}

/* -- DESIGN TOKENS -- */
.sr{
  /* Core */
  --bg:         #0B0C10;
  --bg2:        #0f1117;
  --surface:    rgba(255,255,255,0.03);
  --surface2:   rgba(255,255,255,0.07);
  --surfaceSolid: #141720;
  --surface-border: rgba(255,176,0,0.12);
  /* Glass */
  --glass:      rgba(255,255,255,0.05);
  --glassBorder:rgba(255,255,255,0.10);
  --glassInner: inset 0 1px 0 rgba(255,255,255,0.07), inset 0 -1px 0 rgba(0,0,0,0.2);
  /* Text */
  --text:       #F0F2F5;
  --text2:      #9CA3B0;
  --text3:      #5A6070;
  --muted:      #40454F;
  /* Labels */
  --amber-label: rgba(255,176,0,0.55);
  --ann-bg:     rgba(0,0,0,0.4);
  --led-on:     #00E676;
  --led-dim:    #2a3040;
  --badge-bg:   rgba(255,176,0,0.1);
  --badge-border: rgba(255,176,0,0.25);
  /* Aviation colors */
  --amber:      #FFB000;
  --amberDim:   rgba(255,176,0,0.10);
  --amberGlow:  0 0 12px rgba(255,176,0,0.25);
  --green:      #00E676;
  --greenDim:   rgba(0,230,118,0.10);
  --greenGlow:  0 0 10px rgba(0,230,118,0.2);
  --red:        #FF4444;
  --redDim:     rgba(255,68,68,0.12);
  --sky:        #38BDF8;
  --skyDim:     rgba(56,189,248,0.10);
  /* Borders */
  --border:     rgba(255,255,255,0.08);
  --border2:    rgba(255,255,255,0.04);
  /* Shadow */
  --shadow:     0 4px 24px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.3);
}

/* Light mode (keep functional, less emphasis) */
.sr.light{
  --bg:         #FFFFFF;
  --bg2:        #F5F6F8;
  --surface:    #FFFFFF;
  --surface2:   #F8F9FB;
  --surfaceSolid:#FFFFFF;
  --surface-border: rgba(0,0,0,0.10);
  --glass:      rgba(255,255,255,0.95);
  --glassBorder:rgba(0,0,0,0.12);
  --glassInner: inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.04);
  --text:       #1A1D24;
  --text2:      #2D3240;
  --text3:      #525868;
  --muted:      #6B7280;
  --white:      #1A1D24;
  --amber:      #B87300;
  --amberDim:   rgba(184,115,0,0.08);
  --amberGlow:  none;
  --green:      #007A3D;
  --greenDim:   rgba(0,122,61,0.08);
  --greenGlow:  none;
  --red:        #C81818;
  --redDim:     rgba(200,24,24,0.08);
  --sky:        #0266A0;
  --skyDim:     rgba(2,102,160,0.08);
  --border:     rgba(0,0,0,0.12);
  --border2:    rgba(0,0,0,0.06);
  --shadow:     0 2px 12px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06);
  --mono:       'JetBrains Mono',monospace;
}
/* Light mode readability: larger text, more contrast, better spacing */
.sr.light .page-title{font-size:22px;font-weight:700;color:#1A1D24;}
.sr.light .page-sub{font-size:14px;color:#525868;}
.sr.light .section-label{font-size:11px;font-weight:700;color:#B87300;border-bottom-color:rgba(0,0,0,0.08);}
.sr.light .card{background:#FFFFFF;border:1px solid rgba(0,0,0,0.1);box-shadow:0 1px 4px rgba(0,0,0,0.06);border-radius:8px;}
.sr.light .inpl{font-size:11px;font-weight:600;color:#525868;}
.sr.light .inp{font-size:14px;padding:12px 14px;background:#F8F9FB;border:1px solid rgba(0,0,0,0.15);border-radius:6px;color:#1A1D24;}
.sr.light .inp:focus{border-color:#B87300;box-shadow:0 0 0 2px rgba(184,115,0,0.15);}
.sr.light .btn-p{font-size:13px;padding:12px 20px;background:#B87300;color:#fff;border-radius:6px;font-weight:700;}
.sr.light .btn,.sr.light .btn-sm{font-size:12px;padding:10px 16px;border-radius:6px;}
.sr.light .q-chip{font-size:12px;padding:10px 16px;border-radius:6px;font-weight:600;}
.sr.light .bubble{border-radius:8px;padding:14px 16px;}
.sr.light .bubble .bubble-text{font-size:14px;line-height:1.7;}
.sr.light .hdr{background:rgba(255,255,255,0.95);backdrop-filter:blur(12px);border-bottom:1px solid rgba(0,0,0,0.08);}
.sr.light .hdr-annunciator{background:rgba(0,0,0,0.03);border-bottom:1px solid rgba(0,0,0,0.08);}
.sr.light .ann-led.ok{background:#008A45;box-shadow:0 0 4px rgba(0,138,69,0.3);}
.sr.light .ann-led.warn{background:#B87300;}
.sr.light .ann-led.err{background:#D01A1A;}
.sr.light .nav{background:rgba(0,0,0,0.03);border-bottom:1px solid rgba(0,0,0,0.08);}
.sr.light .nb{color:rgba(0,0,0,0.45);}
.sr.light .nb.on{color:#B87300;border-bottom-color:#B87300;}
.sr.light .nb:hover{color:rgba(0,0,0,0.7);}
.sr.light .card{background:rgba(255,255,255,0.9);border-color:rgba(0,0,0,0.08);}
.sr.light .bubble{background:rgba(184,115,0,0.06);border-color:rgba(0,0,0,0.08);}
.sr.light .bubble.user{background:rgba(0,0,0,0.03);}
.sr.light .btn-p{background:#B87300;color:#fff;}
.sr.light .btn,.sr.light .btn-sm{background:rgba(184,115,0,0.06);border-color:rgba(184,115,0,0.25);color:#B87300;}
.sr.light .inp{background:rgba(0,0,0,0.03);border-color:rgba(0,0,0,0.12);color:#111318;}
.sr.light .inp:focus{border-color:rgba(184,115,0,0.5);}
.sr.light .q-chip{background:rgba(184,115,0,0.06);border-color:rgba(184,115,0,0.2);color:#B87300;}
.sr.light .q-chip:hover{background:rgba(184,115,0,0.12);}
.sr.light .terminal-wrap{background:rgba(0,0,0,0.02);border-color:rgba(0,0,0,0.1);}
.sr.light .terminal-inp{color:#111318;}
.sr.light .overlay{background:rgba(255,255,255,0.85);}
.sr.light .modal{background:#FFFFFF;border-color:rgba(0,0,0,0.12);box-shadow:0 8px 40px rgba(0,0,0,0.15);}
.sr.light .logo{color:#111318;}
.sr.light .logo em{color:#B87300;}
.sr.light .ea-btn{background:rgba(184,115,0,0.08);border-color:rgba(184,115,0,0.3);color:#B87300;}
.sr.light .prog-bar{background:rgba(0,0,0,0.06);}
.sr.light .section-label{color:rgba(184,115,0,0.6);border-bottom-color:rgba(0,0,0,0.06);}
.sr.light .page-title{color:rgba(184,115,0,0.7);}
.sr.light .offline-banner{background:rgba(184,115,0,0.06);border-bottom-color:rgba(184,115,0,0.15);}
.sr.light .ac-pill{background:rgba(0,138,69,0.08);border-color:rgba(0,138,69,0.2);color:#008A45;}
.sr.light ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.15);}

body{background:var(--bg);margin:0;}
::-webkit-scrollbar{width:4px;height:4px;}
::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:2px;}
::-webkit-scrollbar-track{background:transparent;}

.sr{
  height:100vh;
  display:flex;
  flex-direction:column;
  background:var(--bg);
  color:var(--text);
  font-family:'Barlow',sans-serif;
  overflow:hidden;
  transition:background .25s,color .25s;
  border-left:3px solid #FFB000;
}

/* -- ANNUNCIATOR ROW (Row 1) -- */
.hdr-annunciator{
  flex-shrink:0;
  height:32px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  position:relative;
  padding:6px 16px;
  background:rgba(255,176,0,0.04);
  border-bottom:1px solid rgba(255,176,0,0.1);
  overflow-x:auto;
}
.ann-items{display:flex;align-items:center;gap:4px;flex-shrink:1;min-width:0;overflow:hidden;}
.ann-btn{
  display:flex;align-items:center;gap:5px;
  padding:4px 10px;
  background:transparent;
  border:1px solid transparent;
  border-radius:4px;
  font-family:'JetBrains Mono',monospace;
  font-size:9.5px;font-weight:500;letter-spacing:0.12em;
  text-transform:uppercase;
  color:var(--text3);
  cursor:default;
  white-space:nowrap;
  transition:all .18s;
}
.ann-btn:hover{
  background:var(--glass);
  border-color:var(--glassBorder);
  color:var(--text2);
  box-shadow:0 0 8px rgba(255,176,0,0.08);
}
.ann-led{
  width:5px;height:5px;border-radius:50%;flex-shrink:0;
  transition:box-shadow .25s;
}
.ann-led.ok   {background:var(--green);box-shadow:0 0 6px rgba(0,230,118,0.6);}
.ann-led.warn {background:var(--amber);box-shadow:0 0 6px rgba(255,176,0,0.6);}
.ann-led.off  {background:var(--muted);box-shadow:none;}
.ann-led.err  {background:var(--red);  box-shadow:0 0 6px rgba(255,68,68,0.6);}
.ann-right{display:flex;align-items:center;gap:8px;flex-shrink:0;}
.risk-cluster{display:flex;align-items:center;justify-content:center;gap:8px;flex-shrink:0;margin:0 12px;white-space:nowrap;}
.risk-label{font-size:9px;font-family:'JetBrains Mono',monospace;letter-spacing:0.1em;color:var(--muted);}
.risk-score{font-size:18px;font-weight:700;font-family:'JetBrains Mono',monospace;}
.risk-unit{font-size:9px;font-family:'JetBrains Mono',monospace;color:var(--muted);}
.risk-badge{display:inline-flex;align-items:center;gap:4px;font-size:9px;font-weight:700;font-family:'JetBrains Mono',monospace;letter-spacing:0.08em;background:rgba(255,176,0,0.1);border:1px solid rgba(255,176,0,0.25);padding:2px 8px;border-radius:2px;}
.risk-dot{width:6px;height:6px;border-radius:50%;display:inline-block;flex-shrink:0;}

/* -- FUNCTIONAL HEADER (Row 2) -- */
.hdr{
  flex-shrink:0;
  height:50px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:0 20px;
  background:rgba(11,12,16,0.95);
  backdrop-filter:blur(12px);
  border-bottom:1px solid rgba(255,176,0,0.1);
}
.logo{
  font-family:'Barlow Condensed',sans-serif;
  font-size:32px;
  font-weight:700;
  letter-spacing:0.08em;
  color:var(--text);
  flex-shrink:0;
}
.logo em{color:var(--amber);font-style:normal;}
.logo-sub{
  font-family:'JetBrains Mono',monospace;
  font-size:8.5px;
  color:var(--muted);
  letter-spacing:0.18em;
  text-transform:uppercase;
  margin-top:2px;
}
.hdr-right{display:flex;align-items:center;gap:8px;}
.ac-pill{
  display:flex;align-items:center;gap:6px;
  padding:4px 10px;
  background:var(--greenDim);
  border:1px solid rgba(0,230,118,0.2);
  border-radius:20px;
  font-family:'JetBrains Mono',monospace;
  font-size:10.5px;color:var(--green);letter-spacing:0.05em;
}
.ac-dot{width:5px;height:5px;border-radius:50%;background:var(--green);box-shadow:var(--greenGlow);}
.utc-badge{
  font-family:'JetBrains Mono',monospace;
  font-size:11px;color:var(--text3);letter-spacing:0.04em;
}

/* System Link cluster */
.sys-link{
  display:flex;align-items:center;gap:5px;
  padding:4px 10px;
  background:transparent;
  border:1px solid var(--border);
  border-radius:4px;
  font-family:'JetBrains Mono',monospace;
  font-size:9.5px;letter-spacing:0.08em;
  color:var(--text3);
  cursor:pointer;transition:all .18s;
}
.sys-link:hover{border-color:rgba(0,230,118,0.3);color:var(--green);}
.sys-link.local{border-color:rgba(255,176,0,0.25);color:var(--amber);}
@keyframes sysLinkPulse{0%,100%{opacity:1}50%{opacity:.35}}
.sys-signal{
  width:8px;height:8px;
  display:grid;place-items:center;
  position:relative;
}
.sys-signal::before,.sys-signal::after{
  content:'';position:absolute;border-radius:50%;
}
.sys-signal::before{
  width:8px;height:8px;
  background:var(--green);
  box-shadow:0 0 6px rgba(0,230,118,0.5);
  animation:sysLinkPulse 2s ease-in-out infinite;
}
.sys-link.local .sys-signal::before{
  background:var(--amber);
  box-shadow:0 0 6px rgba(255,176,0,0.5);
}

.icon-btn{
  width:30px;height:30px;display:flex;align-items:center;justify-content:center;
  background:transparent;border:1px solid var(--border);border-radius:5px;
  color:var(--text3);font-size:13px;cursor:pointer;transition:all .15s;
}
.icon-btn:hover{border-color:var(--amber);color:var(--amber);box-shadow:var(--amberGlow);}
.ea-btn{
  padding:5px 13px;background:var(--amber);border:none;border-radius:5px;
  font-family:'Barlow',sans-serif;font-size:11px;font-weight:700;
  color:#060400;cursor:pointer;transition:all .15s;letter-spacing:0.04em;
  box-shadow:0 0 0 0 rgba(255,176,0,0);
}
.ea-btn:hover{box-shadow:0 0 16px rgba(255,176,0,0.35);transform:translateY(-1px);}

/* ── STATUS BAR (annunciator-style row 1) ── */
.sbar{
  flex-shrink:0;
  display:none; /* hidden - replaced by annunciator row */
}
.sitem,.slabel,.sval,.s-ok,.s-warn,.s-off,.s-sky,.s-danger{} /* keep refs alive */

/* -- NAV -- */
.nav{
  flex-shrink:0;
  display:flex;
  justify-content:center;
  background:rgba(0,0,0,0.3);
  border-bottom:1px solid rgba(255,176,0,0.12);
  padding:0 20px;
  overflow-x:auto;
  gap:0;
}
.nb{
  padding:11px 16px;
  background:transparent;border:none;
  font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:400;
  letter-spacing:0.1em;color:rgba(255,176,0,0.45);
  text-transform:uppercase;
  cursor:pointer;white-space:nowrap;
  border-bottom:2px solid transparent;
  border-radius:0;
  transition:all .12s;
  position:relative;
}
.nb:hover{color:rgba(255,176,0,0.65);}
.nb.on{
  color:#FFB000;
  border-bottom:2px solid #FFB000;
  text-shadow:none;
}

/* ── CONTENT ── */
.content{flex:1;overflow:hidden;}
.page{height:100%;overflow-y:auto;padding:28px 24px;}
.page-inner{max-width:620px;}

/* -- TYPOGRAPHY -- */
.page-title{
  font-family:'JetBrains Mono',monospace;
  font-size:11px;font-weight:400;letter-spacing:0.2em;
  color:rgba(255,176,0,0.6);margin-bottom:4px;
  text-transform:uppercase;
}
.page-sub{font-family:system-ui,sans-serif;font-size:14px;color:rgba(255,255,255,0.5);line-height:1.7;margin-bottom:24px;}
.section-label{
  font-family:'JetBrains Mono',monospace;
  font-size:9px;font-weight:400;letter-spacing:0.15em;
  text-transform:uppercase;color:rgba(255,176,0,0.45);
  margin-bottom:10px;padding-bottom:6px;
  border-bottom:1px solid rgba(255,176,0,0.1);
}

/* -- INPUTS -- */
.inp{
  width:100%;padding:10px 12px;
  background:rgba(255,255,255,0.04);
  border:1px solid rgba(255,176,0,0.15);
  border-radius:3px;
  color:#F0F2F5;
  font-family:'JetBrains Mono',monospace;font-size:12px;
  outline:none;transition:border-color .15s, box-shadow .15s;
}
.inp:focus{border-color:rgba(255,176,0,0.45);}
.inp::placeholder{color:var(--muted);}
.sel{
  width:100%;padding:10px 12px;
  background:rgba(255,255,255,0.04);border:1px solid rgba(255,176,0,0.15);border-radius:3px;
  color:#F0F2F5;font-family:'JetBrains Mono',monospace;font-size:12px;
  outline:none;cursor:pointer;
}
.inpl{font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:400;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,176,0,0.55);margin-bottom:5px;}

/* -- BUTTONS -- */
.btn{
  padding:9px 18px;background:rgba(255,176,0,0.07);
  border:1px solid rgba(255,176,0,0.3);border-radius:2px;
  color:#FFB000;font-family:'JetBrains Mono',monospace;
  font-size:10px;font-weight:400;letter-spacing:0.08em;
  cursor:pointer;transition:all .12s;
}
.btn:hover{background:rgba(255,176,0,0.12);color:#FFB000;}
.btn-p{background:#FFB000;border-color:#FFB000;color:#0B0C10;font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:700;letter-spacing:0.08em;padding:10px 20px;border-radius:2px;}
.btn-p:hover{opacity:0.85;color:#0B0C10;}
.btn-sm{padding:6px 12px;font-size:10px;font-family:'JetBrains Mono',monospace;letter-spacing:0.08em;background:rgba(255,176,0,0.07);border:1px solid rgba(255,176,0,0.3);color:#FFB000;border-radius:2px;}
.btn-sm:hover{background:rgba(255,176,0,0.12);}
.btn-danger{border-color:rgba(255,68,68,0.3);color:var(--red);}
.btn-danger:hover{background:var(--redDim);}

/* -- CARD -- */
.card{
  background:rgba(255,255,255,0.03);border:1px solid rgba(255,176,0,0.12);
  border-radius:4px;padding:16px;margin-bottom:8px;
}
.card-sm{padding:12px;}

/* ── CHECKLIST ── */
.cl-header{
  display:flex;gap:0;padding:0;
  border-bottom:1px solid var(--border);
  background:var(--bg2);
  overflow-x:auto;flex-shrink:0;
}
.cl-tab{
  padding:11px 18px;background:transparent;border:none;
  font-family:'Barlow',sans-serif;font-size:12.5px;font-weight:600;
  color:var(--text3);cursor:pointer;white-space:nowrap;
  border-bottom:2px solid transparent;transition:all .12s;
}
.cl-tab.on{color:var(--text);border-bottom-color:currentColor;}
.cl-item{
  display:flex;gap:12px;align-items:flex-start;
  padding:9px 0;border-bottom:1px solid var(--border2);
  cursor:pointer;transition:opacity .1s;
}
.cl-item:last-child{border-bottom:none;}
.cl-item:hover{opacity:0.8;}
.cl-box{
  flex-shrink:0;width:16px;height:16px;margin-top:1px;
  border:1.5px solid var(--border);border-radius:4px;
  display:flex;align-items:center;justify-content:center;
  transition:all .12s;
}
.cl-box.done{background:var(--green);border-color:var(--green);}
.cl-text{font-size:14px;color:var(--text2);line-height:1.5;transition:all .12s;}
.cl-text.done{color:var(--muted);text-decoration:line-through;}
.prog-bar{height:2px;background:rgba(255,255,255,0.06);border-radius:0;margin:12px 0;}
.prog-fill{height:100%;border-radius:0;transition:width .25s ease;}

/* ── CHAT MESSAGES ── */
.msg-wrap{
  display:flex;gap:12px;align-items:flex-start;margin-bottom:20px;
}
.msg-wrap.user{flex-direction:row-reverse;}
.avatar{
  flex-shrink:0;width:30px;height:30px;border-radius:6px;
  background:var(--glass);border:1px solid var(--glassBorder);
  display:flex;align-items:center;justify-content:center;
  font-family:'Barlow Condensed',sans-serif;font-size:10.5px;
  font-weight:700;letter-spacing:0.05em;color:var(--text3);
  backdrop-filter:blur(8px);box-shadow:var(--glassInner);
}
.avatar.solus{
  color:var(--amber);
  border-color:rgba(255,176,0,0.2);
  box-shadow:var(--glassInner),0 0 8px rgba(255,176,0,0.1);
}

/* SOLUS response bubbles */
.bubble{
  max-width:78%;padding:13px 16px;
  background:rgba(255,176,0,0.05);
  border:none;
  border-left:2px solid rgba(255,176,0,0.3);
  border-radius:0 4px 4px 0;
  font-size:14px;line-height:1.75;color:var(--text2);
  white-space:pre-wrap;font-family:'Barlow',sans-serif;
  transition:border-color .2s;
}
/* User bubbles */
.bubble.user{
  background:rgba(255,255,255,0.04);
  color:var(--text3);
  border-left:2px solid rgba(255,255,255,0.1);
  font-family:'JetBrains Mono',monospace;
  font-size:13px;line-height:1.6;
}

.msg-label{
  font-size:9.5px;font-weight:600;letter-spacing:0.12em;
  text-transform:uppercase;color:var(--muted);
  margin-bottom:5px;
}

/* ── QUICK CHIPS ── */
.q-strip{
  display:flex;flex-direction:column;gap:0;padding:12px 20px 8px;
  flex-shrink:0;background:var(--bg2);
  border-bottom:1px solid var(--border);
}
.q-strip-label{
  font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:700;
  letter-spacing:0.18em;color:var(--amber);text-transform:uppercase;
  margin-bottom:8px;
}
.q-chips-row{
  display:flex;gap:6px;overflow-x:auto;padding-bottom:2px;
  scrollbar-width:none;
}
.q-chips-row::-webkit-scrollbar{display:none;}
.q-chip{
  flex-shrink:0;padding:6px 12px;
  background:rgba(255,176,0,0.06);
  border:1px solid rgba(255,176,0,0.2);border-radius:2px;
  font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:400;
  color:rgba(255,176,0,0.7);cursor:pointer;white-space:nowrap;
  transition:all .15s;letter-spacing:0.01em;
  position:relative;
}
.q-chip::before{
  content:'↗';font-size:9px;margin-right:5px;
  color:var(--amber);opacity:0.7;
}
.q-chip:hover{
  color:#FFB000;
  background:rgba(255,176,0,0.12);
}
.q-chip:disabled{opacity:0.4;cursor:not-allowed;}

/* -- TERMINAL INPUT -- */
.terminal-wrap{
  flex-shrink:0;
  border-top:1px solid rgba(255,176,0,0.2);
  padding:14px 20px;
  background:rgba(255,255,255,0.04);
}
.terminal-row{
  display:flex;
  align-items:flex-end;
  gap:0;
  background:rgba(255,255,255,0.04);
  border:1px solid rgba(255,176,0,0.2);
  border-radius:3px;
  overflow:hidden;
  transition:border-color .15s, box-shadow .15s;
}
.terminal-row:focus-within{
  border-color:rgba(255,176,0,0.5);
}
.terminal-prompt{
  flex-shrink:0;
  padding:11px 10px 11px 14px;
  font-family:'JetBrains Mono',monospace;
  font-size:13px;font-weight:600;
  color:var(--amber);
  letter-spacing:0.04em;
  user-select:none;
  opacity:0.8;
  white-space:nowrap;
  align-self:flex-end;
  margin-bottom:1px;
}
.terminal-inp{
  flex:1;
  padding:10px 0;
  background:transparent;
  border:none;outline:none;
  color:#F0F2F5;
  font-family:'JetBrains Mono',monospace;
  font-size:12px;
  line-height:1.5;
  resize:none;
  caret-color:var(--amber);
}
.terminal-inp::placeholder{
  color:rgba(255,176,0,0.3);
  font-family:'JetBrains Mono',monospace;
  font-size:12px;
}
.terminal-actions{
  display:flex;align-items:flex-end;gap:4px;
  padding:7px 10px;flex-shrink:0;
}
.terminal-footer{
  display:flex;justify-content:space-between;align-items:center;
  margin-top:8px;padding:0 2px;
}
.terminal-hint{
  font-family:'JetBrains Mono',monospace;
  font-size:10px;color:var(--muted);letter-spacing:0.06em;
}
.terminal-advisory{
  font-size:10.5px;color:var(--text3);
}
.send-btn{
  flex-shrink:0;
  padding:6px 14px;
  background:var(--amber);
  border:none;border-radius:5px;
  font-family:'JetBrains Mono',monospace;
  font-size:10.5px;font-weight:700;
  color:#060400;cursor:pointer;
  transition:all .15s;letter-spacing:0.04em;
  white-space:nowrap;
}
.send-btn:hover:not(:disabled){box-shadow:0 0 14px rgba(255,176,0,0.35);}
.send-btn:disabled{opacity:0.3;cursor:not-allowed;}
.mic-btn,.spk-btn{
  flex-shrink:0;width:34px;height:34px;
  display:flex;align-items:center;justify-content:center;
  background:transparent;border:1px solid var(--border);border-radius:5px;
  color:var(--text3);font-size:14px;cursor:pointer;transition:all .15s;
}
.mic-btn.active{background:var(--amberDim);border-color:rgba(255,176,0,0.4);color:var(--amber);box-shadow:var(--amberGlow);}
.mic-btn:hover:not(.active){border-color:var(--glassBorder);color:var(--text2);}
.spk-btn.active{color:var(--amber);}

/* ── ANIMATIONS ── */
@keyframes wv0{0%,100%{height:3px}50%{height:10px}}
@keyframes wv1{0%,100%{height:5px}50%{height:14px}}
@keyframes wv2{0%,100%{height:7px}50%{height:18px}}
@keyframes wv3{0%,100%{height:5px}50%{height:12px}}
@keyframes wv4{0%,100%{height:3px}50%{height:8px}}
@keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}
@keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
@keyframes ledPulse{0%,100%{opacity:1;box-shadow:0 0 6px rgba(0,230,118,0.6)}50%{opacity:.7;box-shadow:0 0 10px rgba(0,230,118,0.3)}}

/* -- NTSB -- */
.case-card{
  padding:16px;
  background:rgba(255,255,255,0.03);
  border:1px solid rgba(255,176,0,0.12);
  border-radius:4px;margin-bottom:8px;cursor:pointer;
  transition:border-color .12s, box-shadow .12s;
}
.case-card:hover{border-color:rgba(255,176,0,0.3);box-shadow:var(--glassInner),0 0 16px rgba(255,176,0,0.06);}
.case-card.fatal{border-left:3px solid var(--red);}
.case-card.nonfatal{border-left:3px solid var(--amber);}

/* -- MODAL -- */
.overlay{
  position:fixed;inset:0;z-index:1000;
  background:rgba(0,0,0,0.85);backdrop-filter:blur(12px);
  display:flex;align-items:center;justify-content:center;padding:24px;
}
.modal{
  background:#0B0C10;border:1px solid rgba(255,176,0,0.25);
  border-left:3px solid #FFB000;
  border-radius:4px;width:100%;max-width:440px;padding:28px;
  box-shadow:var(--shadow);position:relative;
  animation:fadeUp .2s ease;
}
.modal-title{
  font-family:'Barlow Condensed',sans-serif;
  font-size:22px;font-weight:700;color:var(--text);margin-bottom:4px;
}

/* ── WB / PERF ── */
.wb-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px;}
.wb-cell{
  background:var(--glass);border:1px solid var(--glassBorder);
  border-radius:6px;padding:12px;backdrop-filter:blur(8px);
}
.result-bar{
  height:8px;background:var(--border);border-radius:4px;
  position:relative;overflow:hidden;margin:12px 0 4px;
}
.perf-result{
  padding:16px;border-radius:8px;
  background:var(--glass);border:1px solid var(--glassBorder);
  margin-top:16px;backdrop-filter:blur(8px);
}

/* ── UPLOAD ── */
.upload-btn{
  padding:8px 14px;background:transparent;
  border:1px solid var(--border);border-radius:6px;
  color:var(--text3);font-family:'Barlow',sans-serif;
  font-size:12px;font-weight:600;cursor:pointer;transition:all .12s;
}
.upload-btn:hover{border-color:var(--amber);color:var(--amber);}

/* ── CONNECTION (legacy, now mapped to sys-link) ── */
.conn-pill{
  display:flex;align-items:center;gap:6px;
  padding:4px 10px;border-radius:4px;
  border:1px solid var(--border);
  font-family:'JetBrains Mono',monospace;
  font-size:9.5px;font-weight:600;letter-spacing:0.08em;
  cursor:pointer;transition:all .25s;user-select:none;
}
.conn-pill.live{color:var(--green);border-color:rgba(0,230,118,0.25);}
.conn-pill.local{color:var(--amber);border-color:rgba(255,176,0,0.25);background:var(--amberDim);}
.conn-dot{width:5px;height:5px;border-radius:50%;flex-shrink:0;}
.conn-pill.live .conn-dot{background:var(--green);box-shadow:0 0 6px rgba(0,230,118,0.5);animation:ledPulse 2.5s ease-in-out infinite;}
.conn-pill.local .conn-dot{background:var(--amber);box-shadow:0 0 6px rgba(255,176,0,0.5);}
@keyframes pulse{0%,100%{box-shadow:0 0 0 3px rgba(0,230,118,0.18)}50%{box-shadow:0 0 0 5px rgba(0,230,118,0.06)}}

/* ── OFFLINE BANNER ── */
.offline-banner{
  flex-shrink:0;
  display:flex;align-items:center;gap:10px;
  padding:6px 20px;
  background:rgba(255,176,0,0.07);
  border-bottom:1px solid rgba(255,176,0,0.15);
}
.offline-banner-text{font-size:11px;font-weight:700;color:var(--amber);letter-spacing:0.08em;text-transform:uppercase;}
.offline-banner-sub{font-size:10.5px;color:var(--text3);}

/* ── PARTIAL / DISABLED ── */
.feature-disabled{position:relative;opacity:0.32;cursor:not-allowed;pointer-events:none;}
.nb.partial::after{
  content:"PARTIAL";font-size:7px;font-weight:700;letter-spacing:0.1em;
  color:var(--amber);vertical-align:middle;margin-left:5px;
}

/* ── VOICE BAR ── */
.voice-bar{
  display:flex;align-items:center;gap:10px;
  padding:7px 20px;background:rgba(255,176,0,0.06);
  border-bottom:1px solid rgba(255,176,0,0.12);flex-shrink:0;
}
.voice-label{font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:600;letter-spacing:0.14em;text-transform:uppercase;color:var(--amber);}

/* ── RESPONSIVE ── */
@media(max-width:900px){
  .sr{font-size:14px;}
  .hdr{padding:0 14px;height:46px;}
  .logo{font-size:26px;}
  .logo-sub{font-size:7.5px;}
  .ann-btn{padding:3px 6px;font-size:8px;gap:3px;letter-spacing:0.08em;}
  .ann-led{width:4px;height:4px;}
  .hdr-annunciator{padding:4px 10px;height:28px;}
  .nb{padding:9px 12px;font-size:9px;}
  .page{padding:20px 16px;}
  .page-title{font-size:10px;}
  .page-sub{font-size:13px;margin-bottom:18px;}
  .section-label{font-size:8px;}
  .bubble{font-size:13px;padding:11px 14px;max-width:85%;}
  .bubble.user{font-size:12px;}
  .terminal-prompt{font-size:12px;padding:9px 8px 9px 12px;}
  .terminal-inp{font-size:11px;}
  .terminal-inp::placeholder{font-size:11px;}
  .q-chip{font-size:9px;padding:5px 10px;}
  .q-strip{padding:10px 14px 6px;}
  .q-strip-label{font-size:8px;}
  .card{padding:14px;}
  .inp{font-size:11px;padding:9px 10px;}
  .sel{font-size:11px;padding:9px 10px;}
  .inpl{font-size:8px;}
  .btn{font-size:9px;padding:8px 14px;}
  .btn-p{font-size:11px;padding:9px 16px;}
  .btn-sm{font-size:9px;padding:5px 10px;}
  .modal{padding:22px;max-width:400px;}
  .modal-title{font-size:20px;}
  .wb-grid{gap:6px;}
  .cl-tab{padding:9px 14px;font-size:11.5px;}
  .cl-text{font-size:13px;}
  .case-card{padding:14px;}
  .risk-cluster{gap:6px;margin:0 8px;}
  .risk-label{font-size:8px;}
  .risk-score{font-size:15px;}
  .risk-unit{font-size:8px;}
  .risk-badge{font-size:8px;padding:2px 6px;gap:3px;}
  .risk-dot{width:5px;height:5px;}
}

/* ── RESPONSIVE: MOBILE (≤600px) ── */
@media(max-width:600px){
  .sr{border-left:2px solid #FFB000;}
  .hdr{padding:0 10px;height:42px;}
  .logo{font-size:22px;letter-spacing:0.06em;}
  .logo-sub{font-size:7px;letter-spacing:0.14em;}
  .hdr-right{gap:5px;}
  .ac-pill{font-size:9px;padding:3px 7px;gap:4px;}
  .icon-btn{width:26px;height:26px;font-size:11px;}
  .ea-btn{font-size:10px;padding:4px 10px;}
  .utc-badge{font-size:9.5px;}

  .hdr-annunciator{padding:3px 6px;height:auto;min-height:26px;flex-wrap:wrap;gap:2px;}
  .ann-items{gap:2px;flex-wrap:wrap;}
  .ann-btn{padding:2px 5px;font-size:7px;gap:2px;letter-spacing:0.06em;}
  .ann-led{width:3.5px;height:3.5px;}
  .ann-right{gap:5px;}
  .sys-link{font-size:8px;padding:3px 7px;}

  .nav{padding:0 6px;gap:0;}
  .nb{padding:8px 9px;font-size:8.5px;letter-spacing:0.06em;}

  .page{padding:16px 12px;}
  .page-inner{max-width:100%;}
  .page-title{font-size:9px;letter-spacing:0.15em;}
  .page-sub{font-size:12px;line-height:1.6;margin-bottom:14px;}
  .section-label{font-size:7.5px;letter-spacing:0.12em;}

  .bubble{font-size:12.5px;padding:10px 12px;max-width:92%;line-height:1.65;}
  .bubble.user{font-size:11.5px;line-height:1.5;}
  .msg-wrap{gap:8px;margin-bottom:14px;}
  .avatar{width:24px;height:24px;font-size:8.5px;}
  .msg-label{font-size:8.5px;}

  .terminal-wrap{padding:10px 12px;}
  .terminal-prompt{font-size:11px;padding:9px 6px 9px 10px;}
  .terminal-inp{font-size:11px;padding:9px 0;}
  .terminal-inp::placeholder{font-size:11px;}
  .terminal-actions{padding:5px 6px;gap:3px;}
  .terminal-hint{font-size:9px;}
  .terminal-advisory{font-size:9.5px;}
  .send-btn{font-size:9.5px;padding:5px 10px;}
  .mic-btn,.spk-btn{width:30px;height:30px;font-size:12px;}

  .q-strip{padding:8px 12px 6px;}
  .q-strip-label{font-size:7.5px;letter-spacing:0.14em;}
  .q-chip{font-size:8.5px;padding:5px 8px;}
  .q-chip::before{font-size:8px;margin-right:3px;}

  .card{padding:12px;margin-bottom:6px;}
  .card-sm{padding:10px;}
  .inp{font-size:11px;padding:8px 10px;}
  .sel{font-size:11px;padding:8px 10px;}
  .inpl{font-size:7.5px;letter-spacing:0.1em;}
  .btn{font-size:9px;padding:7px 12px;}
  .btn-p{font-size:10.5px;padding:9px 14px;}
  .btn-sm{font-size:8.5px;padding:5px 9px;}

  .cl-header{overflow-x:auto;}
  .cl-tab{padding:8px 12px;font-size:11px;}
  .cl-text{font-size:12.5px;}
  .cl-box{width:14px;height:14px;}

  .wb-grid{grid-template-columns:1fr;gap:6px;}
  .wb-cell{padding:10px;}

  .case-card{padding:12px;}

  .modal{padding:18px;max-width:calc(100vw - 32px);}
  .modal-title{font-size:18px;}

  .overlay{padding:16px;}

  .offline-banner{padding:5px 12px;gap:6px;}
  .offline-banner-text{font-size:10px;}
  .offline-banner-sub{font-size:9.5px;}

  .voice-bar{padding:5px 12px;gap:6px;}
  .voice-label{font-size:9px;}

  .conn-pill{font-size:8.5px;padding:3px 7px;}

  .risk-cluster{gap:4px;margin:0 4px;}
  .risk-label{font-size:7px;letter-spacing:0.06em;}
  .risk-score{font-size:13px;}
  .risk-unit{font-size:7px;}
  .risk-badge{font-size:7px;padding:1px 5px;gap:2px;}
  .risk-dot{width:4px;height:4px;}
}

/* ── RESPONSIVE: SMALL MOBILE (≤380px) ── */
@media(max-width:380px){
  .logo{font-size:18px;}
  .hdr{height:38px;padding:0 8px;}
  .ann-btn{font-size:6.5px;padding:2px 4px;}
  .nb{padding:7px 6px;font-size:7.5px;}
  .page{padding:12px 8px;}
  .page-sub{font-size:11px;}
  .bubble{font-size:12px;padding:9px 10px;}
  .bubble.user{font-size:11px;}
  .terminal-prompt{font-size:10px;}
  .terminal-inp{font-size:10px;}
  .q-chip{font-size:8px;padding:4px 6px;}
  .btn-p{font-size:10px;padding:8px 12px;}
  .modal{padding:14px;}
  .modal-title{font-size:16px;}
  .risk-cluster{gap:3px;margin:0 2px;}
  .risk-label{display:none;}
  .risk-score{font-size:12px;}
  .risk-unit{font-size:6px;}
  .risk-badge{font-size:6.5px;padding:1px 4px;}
  .risk-dot{width:3px;height:3px;}
}
`;

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
function UTC(){
  const [t,setT]=useState("");
  useEffect(()=>{
    const f=()=>setT(new Date().toUTCString().slice(17,22)+"Z");
    f();const id=setInterval(f,1000);return()=>clearInterval(id);
  },[]);
  return <span className="utc-badge">{t}</span>;
}

function Wave({active,color="var(--amber)"}){
  return(
    <div style={{display:"flex",alignItems:"center",gap:2,height:18}}>
      {[0,1,2,3,4].map(i=>(
        <div key={i} style={{
          width:2,borderRadius:1,background:active?color:"var(--border)",
          height:active?undefined:3,
          animation:active?`wv${i} ${.45+i*.08}s ease-in-out infinite`:"none",
        }}/>
      ))}
    </div>
  );
}

function stripMd(t){return t.replace(/\*\*([^*]+)\*\*/g,"$1").replace(/\*([^*]+)\*/g,"$1").replace(/__([^_]+)__/g,"$1").replace(/_([^_]+)_/g,"$1").replace(/```[^`]*```/g,"").replace(/`([^`]+)`/g,"$1").replace(/^#{1,6}\s+/gm,"");}

function Typer({text}){
  const clean=stripMd(text);
  const [s,setS]=useState("");const i=useRef(0);
  useEffect(()=>{
    i.current=0;setS("");
    const id=setInterval(()=>{if(i.current<clean.length){setS(clean.slice(0,++i.current));}else clearInterval(id);},7);
    return()=>clearInterval(id);
  },[clean]);
  return <span>{s}</span>;
}

function StatusBar({ac,wxDone,clDone,wbDone,currencyStatus}){
  // Legacy: keep for compatibility. Annunciator renders in App instead.
  return null;
}

// ─── ANNUNCIATOR ROW ──────────────────────────────────────────────────────────
function Annunciator({ac,wxDone,clDone,wbDone,currencyStatus,offline,networkDown,onConnectionClick,riskScore}){
  const cs=currencyStatus;
  const ledClass=(cond,warn)=>cond==="err"?"err":cond?"ok":warn?"warn":"off";
  const annItems=[
    {
      label:"Aircraft",
      value:ac.nNumber?`N${ac.nNumber}`:"\u2014\u2014",
      led:ac.nNumber?"ok":"off",
    },
    {
      label:"Weather",
      value:wxDone?"CHECKED":"\u2014\u2014",
      led:wxDone?"ok":"off",
    },
    {
      label:"Checklist",
      value:clDone?"COMPLETE":"\u2014\u2014",
      led:clDone?"ok":"off",
    },
    {
      label:"W\u0026B",
      value:wbDone?"COMPUTED":"\u2014\u2014",
      led:wbDone?"ok":"off",
    },
    {
      label:"Currency",
      value:cs||"\u2014\u2014",
      led:cs==="ALL CURRENT"?"ok":cs==="LAPSED"?"err":cs==="EXPIRING"?"warn":"off",
    },
    {
      label:"Annual",
      value:ac.lastAnnual||"\u2014\u2014",
      led:ac.lastAnnual?"ok":"off",
    },
  ];
  return(
    <div className="hdr-annunciator">
      <div className="ann-items">
        {annItems.map(({label,value,led})=>(
          <div key={label} className="ann-btn">
            <div className={`ann-led ${led}`}/>
            <span style={{color:"var(--muted)",marginRight:3}}>{label}</span>
            <span style={{color:led==="ok"?"var(--green)":led==="err"?"var(--red)":led==="warn"?"var(--amber)":"var(--text3)",fontWeight:600}}>{value}</span>
          </div>
        ))}
      </div>
      <div className="risk-cluster">
        <span className="risk-label">RISK FACTORS</span>
        <span className="risk-score" style={{color:riskScore?.color||"#8A8FA8"}}>{riskScore?.score!==null?riskScore.score:"--"}</span>
        <span className="risk-unit">{riskScore?.score!==null?"pts":""}</span>
        <span className="risk-badge" style={{color:riskScore?.color||"#8A8FA8"}}><span className="risk-dot" style={{background:riskScore?.color||"#8A8FA8"}}/>{riskScore?.flagCount!=null?riskScore.flagCount+" flag"+(riskScore.flagCount!==1?"s":""):"N/A"}</span>
      </div>
      <div className="ann-right">
        <UTC/>
        <div
          className={`sys-link ${offline?"local":""}`}
          onClick={onConnectionClick}
          title={offline?"Local mode, click to go live":"Live, click to switch to local mode"}
        >
          <div className="sys-signal"/>
          <span>{offline?(networkDown?"NO LINK":"LOCAL"):"SYS LINK"}</span>
        </div>
      </div>
    </div>
  );
}

// ─── OFFLINE REPLY ROUTER ─────────────────────────────────────────────────────
function buildOfflineReply(msg){
  const m=msg.toLowerCase();
  if(/engine.fail|mayday|emergency/i.test(m))
    return "SOLUS is a pre-flight decision-support tool. I don't provide in-flight emergency procedures \u2014 these are aircraft-specific and must come from your Pilot Operating Handbook and your type-specific training. If you're preparing for a flight and want to review emergency readiness or contingency planning before departure, I can help with that.";
  if(/medical|third class|first class|second class/i.test(m))
    return "Medical currency per FAR 61.23:\n\n3rd class: 24 months (under 40) or 60 months (under 40, private ops only in some cases - verify). Standard: 24 calendar months.\n2nd class: 12 calendar months.\n1st class: 6 calendar months (if 40+), 12 months (under 40).\n\nNo valid medical = no PIC. BasicMed is an alternative for private ops under certain conditions.\n\nLocal mode - verify current FAR 61.23 when back online.";
  if(/currency|recent flight|passenger|landings/i.test(m))
    return "Passenger currency per FAR 61.57:\n\n3 takeoffs and landings within 90 days to carry passengers.\nNight passengers: 3 full-stop night landings within 90 days.\nTailwheel: 3 full-stop landings in a tailwheel aircraft.\n\nFlight review: every 24 calendar months per FAR 61.56.\n\nLocal mode - full AI context available when back online.";
  if(/fuel|reserve|vfr fuel/i.test(m))
    return "VFR fuel requirements per FAR 91.151:\n\nDay VFR: enough fuel to reach destination plus 30 minutes reserve at cruise.\nNight VFR: enough fuel to reach destination plus 45 minutes reserve at cruise.\n\nThese are legal minimums. Personal minimums should be higher. Verify visually - gauges lie.\n\nLocal mode active.";
  if(/weather|metar|taf|pirep|vfr minimum/i.test(m))
    return "VFR weather minimums per FAR 91.155:\n\nClass B, C, D, E (surface): 3SM visibility, 500 ft below / 1,000 ft above / 2,000 ft horizontal from clouds.\nClass G day below 1,200 AGL: 1SM, clear of clouds.\nClass G night below 1,200 AGL: 3SM, 500/1,000/2,000.\n\nFor live METAR and TAF: check ForeFlight or aviationweather.gov. Weather queries require a connection.\n\nLocal mode active.";
  if(/weight|balance|cg|gross|moment/i.test(m))
    return "Weight and balance is available in the W&B tab - full moment-based calculator runs offline.\n\nKey limits to remember:\nNever exceed max gross weight.\nCG must remain within the envelope at all fuel states (check takeoff AND landing CG if burning significant fuel).\nReference your specific POH - limits vary by aircraft.\n\nLocal mode active. The W&B tab has your full calculator.";
  if(/checklist|preflight|run.?up|takeoff|approach|landing/i.test(m))
    return "Full checklists are available in the Checklists tab - they run completely offline.\n\nPre-flight, run-up, takeoff, approach, and emergency procedures are all pre-loaded. Tap any item to check it off. The emergency checklist is always accessible.\n\nLocal mode active.";
  if(/annual|airworthy|91.409|100.hour/i.test(m))
    return "Annual inspection required every 12 calendar months per FAR 91.409.\n100-hour inspection required for hire operations per FAR 91.409(b).\n\nPIC is solely responsible for determining airworthiness before each flight (FAR 91.7). Check your aircraft's maintenance logs and verify the annual date.\n\nLocal mode active. Maintenance log is available in the Aircraft tab.";
  return "Local mode active. AI responses are limited to cached FAR/AIM knowledge without live context.\n\nFull AI chat, voice agent, and weather queries resume when back online.\n\nFor now: checklists, W&B, NTSB cases, and FAR/AIM reference are fully available in their tabs.";
}

// ─── CHAT ─────────────────────────────────────────────────────────────────────
function Chat({ac,logs,currDates,onWx,offline,onRequestAccess,flightCtx,setFlightCtx,wxDone,backendFetch,entitlements}){
  const isDemoMode=localStorage.getItem("solus_demo_mode")==="1";
  const allowed=localStorage.getItem("solus_allowed")==="1";
  const FREE_LIMIT=3;
  const [promptsUsed,setPromptsUsed]=useState(()=>parseInt(localStorage.getItem("solus_prompts_used")||"0",10));
  const isLimited=!entitlements?.fullAccess&&!isDemoMode&&!allowed;
  const gated=isLimited&&promptsUsed>=FREE_LIMIT;
  const [ctxOpen,setCtxOpen]=useState(true);
  const [chipsOpen,setChipsOpen]=useState(true);
  const [routeDistNm,setRouteDistNm]=useState(null);

  // Compute route distance when FROM/TO change
  useEffect(()=>{
    if(flightCtx?.from?.length>=3&&flightCtx?.to?.length>=3){
      fetchRouteDistance(flightCtx.from,flightCtx.to).then(d=>{if(d)setRouteDistNm(d);});
    }else{setRouteDistNm(null);}
  },[flightCtx?.from,flightCtx?.to]);
  const lastAlerts=useRef({});
  function buildBrief(ac,dates){
    const today=new Date();
    function daysAgo(ds){if(!ds)return null;return Math.round((today-new Date(ds))/864e5);}
    const flags=[];
    if(dates){
      const bfr=daysAgo(dates.lastBFR);
      if(!dates.lastBFR)flags.push("Flight review date not on file.");
      else if(bfr>730)flags.push("FLIGHT REVIEW LAPSED - no PIC until renewed (FAR 61.56).");
      else if(bfr>650)flags.push("Flight review expires in ~"+(730-bfr)+" days - schedule soon.");
      const day=daysAgo(dates.lastDayLanding);
      if(!dates.lastDayLanding)flags.push("Day landing currency not on file.");
      else if(day>90)flags.push("VFR day currency LAPSED - no passenger ops (FAR 61.57).");
      else if(day>80)flags.push("VFR day currency expires in ~"+(90-day)+" days.");
      const med=daysAgo(dates.lastMedical);
      if(!dates.lastMedical)flags.push("Medical date not on file.");
      else if(med>720)flags.push("MEDICAL LAPSED - verify class limits (FAR 61.23).");
      else if(med>660)flags.push("Medical expires in ~"+(730-med)+" days.");
    }
    if(ac&&ac.nNumber){
      if(ac.squawks)flags.push("Open squawks on N"+ac.nNumber+": "+ac.squawks);
      if(ac.lastAnnual){
        const ann=daysAgo(ac.lastAnnual);
        if(ann>365)flags.push("Annual OVERDUE on N"+ac.nNumber+" (FAR 91.409).");
        else if(ann>305)flags.push("Annual due in "+(365-ann)+" days on N"+ac.nNumber+".");
      }
    }
    const ds=new Date().toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
    const acStr=(ac&&ac.nNumber)?" - N"+ac.nNumber+(ac.type?" ("+ac.type+")":""):"";
    if(!dates||!dates.lastBFR)return "SOLUS online.\n\nLoad your aircraft and currency data to activate proactive risk monitoring.\n\nWhat do you need, Captain?";
    if(flags.length===0)return "PRE-FLIGHT BRIEF - "+ds+acStr+"\n\nAll currency checks clear. No open squawks.\n\nYou are current and legal. What do you need, Captain?";
    return "PRE-FLIGHT BRIEF - "+ds+acStr+"\n\nRISK FLAGS:\n"+flags.map(function(f,i){return (i+1)+". "+f;}).join("\n")+"\n\nAddress these before flight. What do you need, Captain?";
  }
  const [msgs,setMsgs]=useState(()=>{
    const demoMode=localStorage.getItem('solus_demo_mode')==='1';
    if(demoMode){
      const d=new Date();const ago=(days)=>Math.round(days);
      return[{r:"a",t:"Pre-departure brief ready for N44821.\n\n3 flags identified before your next flight.\n\nRISK FACTORS: 41 pts\n\nFlag 1 of 3: VFR day currency expires in 3 days. 87 days since oldest qualifying landing. FAR 61.57(a) requires 3 takeoffs and landings in preceding 90 days to carry passengers.\n\nFlag 2 of 3: Flight review expires in 60 days. FAR 61.56 requires review every 24 calendar months.\n\nFlag 3 of 3: IFR currency not met. 4 of 6 required approaches completed in last 6 months. FAR 61.57(c).\n\nTap Mission Brief for full PAVE threat assessment.\n\nSOLUS surfaces data. All go/no-go decisions rest with the PIC per FAR 91.7.",f:false}];
    }
    return[{r:"a",t:buildBrief(ac,currDates),f:false}];
  });
  const [historyLoaded,setHistoryLoaded]=useState(false);
  useEffect(()=>{
    const today=new Date().toISOString().split("T")[0];
    const key="solus_pf_"+today;
    localStorage.setItem(key,String(parseInt(localStorage.getItem(key)||"0")+1));
    const token=localStorage.getItem("solus_token");
    if(token){
      fetch("https://solus-backend-9wde.onrender.com/analytics/preflight",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({date:today})}).catch(()=>{});
      // Load chat history from backend
      if(backendFetch&&!historyLoaded){
        backendFetch('/user/chats').then(data=>{
          if(data&&Array.isArray(data)&&data.length>0){
            const loaded=data.map(c=>({r:c.role==='assistant'?'a':'u',t:c.content,f:false}));
            setMsgs(prev=>[prev[0],...loaded]); // keep auto-brief at top
          }
          setHistoryLoaded(true);
        }).catch(()=>setHistoryLoaded(true));
      }
    }
  },[]); // eslint-disable-line
  const [inp,setInp]=useState("");
  const [loading,setLoading]=useState(false);
  const [voiceMode,setVoiceMode]=useState(false);
  const [vs,setVs]=useState("idle");
  const [speakIdx,setSpeakIdx]=useState(null);
  const [noSR,setNoSR]=useState(false);
  const voiceDisabled=offline;
  const bottom=useRef(null);
  const spaceHeld=useRef(false);

  useEffect(()=>{bottom.current?.scrollIntoView({behavior:"smooth"})},[msgs,loading]);
  useEffect(()=>{const SR=window.SpeechRecognition||window.webkitSpeechRecognition;if(!SR)setNoSR(true);},[]);
  useEffect(()=>{
    const dn=e=>{if(e.code==="Space"&&e.target.tagName!=="TEXTAREA"&&e.target.tagName!=="INPUT"&&!spaceHeld.current){e.preventDefault();spaceHeld.current=true;voice.start();}};
    const up=e=>{if(e.code==="Space"&&e.target.tagName!=="TEXTAREA"&&e.target.tagName!=="INPUT"){spaceHeld.current=false;voice.stop();}};
    window.addEventListener("keydown",dn);window.addEventListener("keyup",up);
    return()=>{window.removeEventListener("keydown",dn);window.removeEventListener("keyup",up);};
  });

  const voice=useVoiceInput({onTranscript:t=>send(t),onStateChange:setVs});

  // ─── AGENTIC MONITORING LOOP ───────────────────────────────────────────────
  useEffect(()=>{
    function runMonitor(){
      const alerts=[];
      const now=new Date();
      function daysAgo(ds){if(!ds)return null;return Math.round((now-new Date(ds))/864e5);}

      // Currency checks : alert when <10 days remaining
      if(currDates){
        const bfr=daysAgo(currDates.lastBFR);
        if(bfr!==null&&bfr>720&&bfr<=730) alerts.push({id:"bfr_exp",t:"◈ CURRENCY ALERT: Flight review expires in ~"+(730-bfr)+" days. Schedule with a CFI before lapse (FAR 61.56)."});
        if(bfr!==null&&bfr>730) alerts.push({id:"bfr_lapsed",t:"◈ CURRENCY ALERT: Flight review LAPSED. You may not act as PIC until renewed (FAR 61.56)."});
        const day=daysAgo(currDates.lastDayLanding);
        if(day!==null&&day>80&&day<=90) alerts.push({id:"day_exp",t:"◈ CURRENCY ALERT: VFR day currency expires in ~"+(90-day)+" days. Log 3 takeoffs and landings to reset (FAR 61.57)."});
        if(day!==null&&day>90) alerts.push({id:"day_lapsed",t:"◈ CURRENCY ALERT: VFR day currency LAPSED. No passenger operations until 3 landings completed."});
        const night=daysAgo(currDates.lastNightLanding);
        if(night!==null&&night>80&&night<=90) alerts.push({id:"night_exp",t:"◈ CURRENCY ALERT: Night currency expires in ~"+(90-night)+" days. 3 full-stop night landings required."});
        const med=daysAgo(currDates.lastMedical);
        if(med!==null){
          let medW=730;
          if(currDates.medicalClass==="1st"){
            const age=currDates.dob?Math.floor((Date.now()-new Date(currDates.dob).getTime())/(1000*60*60*24*365.25)):null;
            medW=age&&age>=40?180:365;
          }else if(currDates.medicalClass==="2nd")medW=365;
          if(med>medW-10&&med<=medW) alerts.push({id:"med_exp",t:"◈ CURRENCY ALERT: Medical certificate expires in ~"+(medW-med)+" days. Schedule AME appointment (FAR 61.23)."});
          if(med>medW) alerts.push({id:"med_lapsed",t:"◈ CURRENCY ALERT: Medical certificate LAPSED. No PIC privileges until renewed."});
        }
      }

      // Aircraft annual check
      if(ac&&ac.lastAnnual){
        const ann=daysAgo(ac.lastAnnual);
        if(ann>355&&ann<=365) alerts.push({id:"ann_exp",t:"◈ AIRCRAFT ALERT: Annual inspection due in ~"+(365-ann)+" days on N"+ac.nNumber+". Aircraft will not be airworthy after expiry (FAR 91.409)."});
        if(ann>365) alerts.push({id:"ann_lapsed",t:"◈ AIRCRAFT ALERT: Annual inspection OVERDUE on N"+ac.nNumber+". Aircraft is NOT airworthy (FAR 91.409)."});
      }

      // Fuel margin check
      if(flightCtx.fuel&&flightCtx.burnRate){
        const fuel=parseFloat(flightCtx.fuel);
        const burn=parseFloat(flightCtx.burnRate);
        const elapsed=parseFloat(flightCtx.elapsed)||0;
        if(fuel&&burn){
          const rem=fuel-(burn*elapsed/60);
          const reserve=burn*0.5; // 30 min VFR day reserve
          if(rem<reserve&&rem>0) alerts.push({id:"fuel_low",t:"◈ FUEL ALERT: Estimated fuel remaining ("+rem.toFixed(1)+" gal) is below 30-minute VFR reserve. Plan fuel stop or reduce flight time."});
          if(rem<=0) alerts.push({id:"fuel_empty",t:"◈ FUEL ALERT: Estimated fuel EXHAUSTED based on current burn rate and elapsed time. Land immediately."});
        }
      }

      // Departure time proximity
      if(flightCtx.deptTime){
        const [hh,mm]=flightCtx.deptTime.split(":").map(Number);
        const dept=new Date();dept.setHours(hh,mm,0,0);
        const minsUntil=Math.round((dept-now)/60000);
        if(minsUntil>0&&minsUntil<=30) alerts.push({id:"dept_soon",t:"◈ DEPARTURE: "+flightCtx.deptTime+" local is in "+minsUntil+" minutes."+(flightCtx.from?" Confirm "+flightCtx.from+" weather and NOTAMs are current.":"")});
        if(minsUntil<=0&&minsUntil>=-5) alerts.push({id:"dept_now",t:"◈ DEPARTURE: Scheduled departure time "+flightCtx.deptTime+" has arrived."+(flightCtx.from&&flightCtx.to?" "+flightCtx.from+" → "+flightCtx.to+". Fly safe, Captain.":"")});
      }

      // TEM: Weather briefing not completed near departure
      if(flightCtx.deptTime&&!wxDone){
        const [dh2,dm2]=flightCtx.deptTime.split(":").map(Number);
        const deptT=new Date();deptT.setHours(dh2,dm2,0,0);
        const minsLeft=Math.round((deptT-now)/60000);
        if(minsLeft>0&&minsLeft<=120) alerts.push({id:"tem_wx_brief",t:"\u25C8 HUMAN FACTORS: Weather briefing not completed. FAR 91.103 requires review of all available information before departure. Open Weather tab."});
      }

      // TEM: BFR expiring soon on cross-country
      if(currDates&&currDates.lastBFR&&flightCtx.to){
        const bfrDays=daysAgo(currDates.lastBFR);
        if(bfrDays!==null){
          const daysLeft=730-bfrDays;
          if(daysLeft>0&&daysLeft<=30) alerts.push({id:"tem_bfr_soon",t:"\u25C8 HUMAN FACTORS: Flight review expires in "+daysLeft+" days. Consider scheduling a proficiency flight before this cross-country."});
        }
      }

      // TEM: Night VFR with lapsed instrument currency
      if(flightCtx.deptTime){
        const [nh]=flightCtx.deptTime.split(":").map(Number);
        if(nh>=18||nh<=6){
          if(currDates&&currDates.lastIFR){
            const ifrDays=daysAgo(currDates.lastIFR);
            if(ifrDays!==null&&ifrDays>180) alerts.push({id:"tem_night_ifr",t:"\u25C8 HUMAN FACTORS: Night VFR with lapsed instrument currency. VMC-only operations at night carry elevated risk. Confirm personal minimums before departure."});
          }
        }
      }

      // Only inject alerts that haven't been shown yet
      const newAlerts=alerts.filter(a=>!lastAlerts.current[a.id]);
      if(newAlerts.length>0){
        const alertMsgs=newAlerts.map(a=>({r:"a",t:a.t,f:false,monitor:true}));
        setMsgs(prev=>[...prev,...alertMsgs]);
        newAlerts.forEach(a=>{lastAlerts.current[a.id]=true;});
      }
    }

    // Run once immediately, then every 5 minutes
    const timer=setTimeout(()=>{
      runMonitor();
      const interval=setInterval(runMonitor,5*60*1000);
      return()=>clearInterval(interval);
    },3000); // delay 3s after mount so brief loads first
    return()=>clearTimeout(timer);
  },[currDates,ac,flightCtx,wxDone]); // eslint-disable-line

  async function send(text){
    const msg=(text||inp).trim();if(!msg||loading)return;
    if(gated){onRequestAccess();return;}
    if(isLimited){
      const newCount=promptsUsed+1;
      setPromptsUsed(newCount);
      localStorage.setItem("solus_prompts_used",String(newCount));
    }
    if(msgs.length===1&&msgs[0].t.includes("RISK FLAGS")){
      const token=localStorage.getItem("solus_token");
      if(token)fetch("https://solus-backend-9wde.onrender.com/analytics/flag-acknowledged",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({date:new Date().toISOString().split("T")[0]})}).catch(()=>{});
    }
    setInp("");stopSpeaking();

    // Hard refusal for in-flight emergency procedure queries (safety-critical)
    const isEmergencyQuery=/engine.fail|forced.landing|what.do.i.do.if|walk.me.through.*(?:engine|fire|smoke|land|crash|quit|fail)|in.flight.*(?:emergency|failure|fire)|just.quit|mayday|declare.*emergency|i.(?:am|'m).in.*(?:imc|emergency|spin|stall)|restart.*engine|ditching|water.landing/i.test(msg);
    if(isEmergencyQuery){
      const refusal="SOLUS is a pre-flight decision-support tool. I don't provide in-flight emergency procedures \u2014 these are aircraft-specific and must come from your Pilot Operating Handbook and your type-specific training. If you're preparing for a flight and want to review emergency readiness or contingency planning before departure, I can help with that.";
      setMsgs([...msgs,{r:"u",t:msg,f:false},{r:"a",t:refusal,f:true}]);
      trackEvent('chat_query',{query_category:'emergency_refused'});
      return;
    }

    trackEvent('chat_query',{query_category:/weather|metar|taf|pirep/i.test(msg)?'weather':/risk|currency|current/i.test(msg)?'risk':/checklist/i.test(msg)?'checklist':'general'});
    if(!window.__solus_convo_start)window.__solus_convo_start=Date.now();
    if(/weather|metar|taf|pirep|briefing/i.test(msg))onWx();
    const hist=[...msgs,{r:"u",t:msg,f:false}];
    setMsgs(hist);setLoading(true);

    // Offline: use cached local response
    if(offline){
      const localReply=buildOfflineReply(msg);
      setTimeout(()=>{
        setMsgs([...hist,{r:"a",t:localReply,f:true}]);
        setLoading(false);
      },420);
      return;
    }

    // Show warm-up message if response takes >8 seconds (Render cold start)
    const warmupTimer=setTimeout(()=>{setMsgs(prev=>[...prev.slice(0,-1),{...prev[prev.length-1]},{r:"a",t:"Server warming up — first request of the day can take up to 30 seconds. Hang tight.",f:true}]);},8000);
    try{
      const token=localStorage.getItem('solus_token');
      const controller=new AbortController();
      const timeout=setTimeout(()=>controller.abort(),45000); // 45s hard timeout
      const res=await fetch("https://solus-backend-9wde.onrender.com/chat/message",{method:"POST",signal:controller.signal,headers:{"Content-Type":"application/json",...(token?{"Authorization":"Bearer "+token}:{})},
        body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:1000,system:SYSTEM_PROMPT(ac,logs,flightCtx)+(routeDistNm?`\nRoute distance: ${routeDistNm} nautical miles (computed via great-circle, not estimated). Use this exact number for any distance reference.`:''),messages:hist.map(m=>({role:m.r==="a"?"assistant":"user",content:m.t}))})});
      clearTimeout(warmupTimer);clearTimeout(timeout);
      const data=await res.json();
      const reply=data.content?.filter(b=>b.type==="text").map(b=>b.text).join("\n")||"No response.";
      const nm=[...hist,{r:"a",t:reply,f:true}];setMsgs(nm);
      trackEvent('chat_reply_received',{convo_duration_seconds:Math.round((Date.now()-(window.__solus_convo_start||Date.now()))/1000),message_count:nm.length});
      if(voiceMode){const idx=nm.length-1;setSpeakIdx(idx);speak(reply,{rate:.92,pitch:.88,onEnd:()=>setSpeakIdx(null)});}
      // Persist to backend
      if(backendFetch){
        backendFetch('/user/chats',{method:'POST',body:JSON.stringify({role:'user',content:msg})}).catch(()=>{});
        backendFetch('/user/chats',{method:'POST',body:JSON.stringify({role:'assistant',content:reply})}).catch(()=>{});
        // Record structured brief for CFI visibility (mission brief queries only)
        if(/mission brief|pre-flight.*brief|risk brief/i.test(msg)){
          const rs=computeRiskScore(ac,currDates,flightCtx);
          backendFetch('/cfi/record-brief',{method:'POST',body:JSON.stringify({
            departure:flightCtx?.from||null, destination:flightCtx?.to||null,
            planned_dept_time:flightCtx?.deptTime||null,
            risk_score:rs.score, risk_level:rs.score>=70?'HIGH':rs.score>=35?'REVIEW':'CLEAR',
            risk_driver:rs.driver,
            risk_factors:(rs.signals||[]).filter(s=>s.pts>0).map(s=>({name:s.name,severity:s.status,detail:s.detail})),
            wb_severity:flightCtx?.wbResult?(/OVER|OUT/i.test(flightCtx.wbResult)?'HIGH':/CAUTION|MARGINAL/i.test(flightCtx.wbResult)?'CAUTION':'OK'):'INCOMPLETE',
            notam_high_count:0,
            weather_category:null,
          })}).catch(()=>{});
        }
      }
    }catch(e){
      clearTimeout(warmupTimer);
      const isTimeout=e&&e.name==='AbortError';
      setMsgs([...hist,{r:"a",t:isTimeout?"Request timed out. The server may be warming up — try again in 30 seconds.":"Connection lost. Check your network and try again.",f:true}]);
    }finally{setLoading(false);}
  }

  function speakMsg(idx,t){stopSpeaking();if(speakIdx===idx){setSpeakIdx(null);return;}setSpeakIdx(idx);speak(t,{onEnd:()=>setSpeakIdx(null)});}
  const mic=vs==="listening";

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Flight Context Bar - always visible */}
      <div style={{borderBottom:"1px solid var(--border)",background:"rgba(0,0,0,0.2)",padding:"8px 24px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <span style={{color:"var(--amber)",fontFamily:"var(--mono)",fontSize:10}}>◈</span>
          <span style={{color:"var(--text2)",fontFamily:"var(--mono)",fontSize:10,letterSpacing:"0.12em",textTransform:"uppercase"}}>Flight Context</span>
          {(flightCtx.from||flightCtx.to)&&(
            <span style={{color:"var(--text3)",fontFamily:"var(--mono)",fontSize:10,letterSpacing:"0.08em"}}>
              {[flightCtx.from,flightCtx.to].filter(Boolean).join(" → ")}
              {flightCtx.deptTime?" · DEP "+flightCtx.deptTime:""}
            </span>
          )}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr 1fr",gap:8,alignItems:"end"}}>
          {[
            {l:"FROM",k:"from",p:"KVNY"},
            {l:"TO",k:"to",p:"KSZP"},
            {l:"DEPT TIME",k:"deptTime",p:"14:30",type:"time"},
            {l:"FUEL (gal)",k:"fuel",p:"40",type:"number"},
            {l:"BURN (gal/hr)",k:"burnRate",p:"8.5",type:"number"},
            {l:"ELAPSED (min)",k:"elapsed",p:"0",type:"number"},
          ].map(f=>(
            <div key={f.k}>
              <div style={{fontSize:9,fontFamily:"var(--mono)",letterSpacing:"0.12em",color:"var(--muted)",marginBottom:4,textTransform:"uppercase"}}>{f.l}</div>
              <input
                type={f.type||"text"}
                className="inp"
                value={flightCtx[f.k]||""}
                onChange={e=>setFlightCtx(p=>({...p,[f.k]:e.target.value.toUpperCase()}))}
                placeholder={f.p}
                style={{fontSize:12,padding:"6px 8px",textTransform:f.type?"none":"uppercase"}}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Voice bar */}
      {(voiceMode||vs==="listening")&&(
        <div className="voice-bar">
          <Wave active={mic}/>
          <span className="voice-label">{mic?"Listening...":"Voice active - hold Space"}</span>
          <button onClick={()=>{setVoiceMode(false);stopSpeaking();setSpeakIdx(null);}}
            style={{marginLeft:"auto",background:"none",border:"none",color:"var(--muted)",fontSize:11,cursor:"pointer",fontFamily:"'Barlow',sans-serif",fontWeight:600,letterSpacing:"0.06em"}}>
            Deactivate
          </button>
        </div>
      )}

      {/* Messages */}
      <div style={{flex:1,overflowY:"auto",padding:"24px",position:"relative"}}>
        {msgs.map((m,i)=>(
          <div key={i} className={`msg-wrap ${m.r==="u"?"user":""}`}>
            <div className={`avatar ${m.r==="a"?"solus":""}`}>
              {m.r==="a"?(speakIdx===i?<Wave active={true}/>:"SLS"):"PIC"}
            </div>
            <div>
              <div className="msg-label" style={{textAlign:m.r==="u"?"right":"left",color:m.r==="a"?"var(--amber)":"var(--muted)"}}>
                {m.r==="a"?(
                  <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
                    SOLUS
                    <button onClick={()=>speakMsg(i,m.t)} style={{background:"none",border:"none",cursor:"pointer",color:speakIdx===i?"var(--amber)":"var(--muted)",fontSize:9,padding:0,lineHeight:1,transition:"color .15s"}}
                      onMouseOver={e=>e.currentTarget.style.color="var(--amber)"}
                      onMouseOut={e=>e.currentTarget.style.color=speakIdx===i?"var(--amber)":"var(--muted)"}>
                      {speakIdx===i?"■":"▶"}
                    </button>
                  </span>
                ):"You"}
              </div>
              <div className={`bubble ${m.r==="u"?"user":""}`} style={{
                borderColor:m.monitor?"rgba(255,176,0,0.3)":speakIdx===i?"rgba(232,160,32,0.3)":"var(--border)",
                background:m.monitor?"rgba(255,176,0,0.06)":undefined,
                borderLeft:m.monitor?"3px solid var(--amber)":undefined
              }}>
                {(()=>{const picLine="SOLUS surfaces data. All go/no-go decisions rest with the PIC per FAR 91.7.";const txt=m.t||"";const hasPic=m.r==="a"&&txt.includes(picLine);const body=hasPic?txt.replace(picLine,"").trimEnd():txt;return<>{m.f?<Typer text={body}/>:stripMd(body)}{hasPic&&<div style={{marginTop:8,paddingTop:6,borderTop:"1px solid rgba(255,176,0,0.08)",fontFamily:"var(--mono)",fontSize:9,color:"rgba(255,176,0,0.4)"}}>{picLine}</div>}</>})()}
              </div>
              {m.r==="a"&&(m.t.includes("PILOT")||m.t.includes("PAVE")||m.t.includes("Mission Brief")||m.t.includes("AIRCRAFT")||m.t.includes("enVIRONMENT"))&&(
                <div style={{marginTop:6,paddingTop:6,borderTop:"1px solid rgba(255,176,0,0.15)",fontFamily:"var(--mono)",fontSize:9,color:"rgba(255,176,0,0.4)",lineHeight:1.6,fontStyle:"italic"}}>
                  Sources: 14 CFR Part 61/91, FAA-H-8083-2 (Risk Management Handbook), NTSB accident database, live FAA weather services (aviationweather.gov). SOLUS is an advisory tool. Not a substitute for an official weather briefing (FAR 91.103).
                </div>
              )}
            </div>
          </div>
        ))}
        {loading&&(
          <div className="msg-wrap">
            <div className="avatar solus">SLS</div>
            <div className="bubble" style={{display:"flex",gap:5,alignItems:"center",padding:"14px 16px"}}>
              {[0,1,2].map(i=><div key={i} style={{width:5,height:5,borderRadius:"50%",background:"var(--amber)",animation:`blink 1.1s ease-in-out ${i*.18}s infinite`}}/>)}
            </div>
          </div>
        )}
        <div ref={bottom}/>

      </div>

      {/* Quick chips - collapsible 3-column grid above input */}
      <div style={{borderTop:"1px solid var(--border)",background:"rgba(0,0,0,0.15)"}}>
        <button onClick={()=>setChipsOpen(o=>!o)} style={{
          width:"100%",display:"flex",alignItems:"center",gap:6,padding:"6px 24px",
          background:"none",border:"none",cursor:"pointer",
          fontFamily:"var(--mono)",fontSize:9,color:"var(--muted)",letterSpacing:"0.1em",textTransform:"uppercase"
        }}>
          <span style={{color:"var(--amber)"}}>◈</span>
          Try asking
          <span style={{marginLeft:"auto",fontSize:8,transition:"transform .15s",transform:chipsOpen?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
        </button>
        {chipsOpen&&(
          <div style={{padding:"0 24px 10px",display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
            {getQuickChips(flightCtx?.from,flightCtx?.to).map(q=>(
              <button key={q.label} className="q-chip" onClick={()=>send(q.q)} disabled={loading}
                style={{textAlign:"left",padding:"7px 10px",fontSize:10,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                {q.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Terminal Input */}
      <div className="terminal-wrap">
        <div className="terminal-row">
          <span className="terminal-prompt">SOLUS&nbsp;&gt;</span>
          {!noSR&&!voiceDisabled&&(
            <button
              className={`mic-btn${mic?" active":""}`}
              onMouseDown={()=>vs!=="listening"&&voice.start()}
              onMouseUp={()=>vs==="listening"&&voice.stop()}
              onTouchStart={e=>{e.preventDefault();vs!=="listening"&&voice.start();}}
              onTouchEnd={e=>{e.preventDefault();vs==="listening"&&voice.stop();}}
              style={{margin:"0 4px",border:"none",borderRight:"1px solid var(--border)",borderRadius:0,width:36,height:"auto",alignSelf:"stretch"}}>
              {mic?<Wave active={true}/>:"🎙"}
            </button>
          )}
          {!noSR&&voiceDisabled&&(
            <div style={{width:36,margin:"0 4px",display:"flex",alignItems:"center",justifyContent:"center",
              borderRight:"1px solid var(--border)",opacity:0.25,cursor:"not-allowed",fontSize:14,alignSelf:"stretch"}}>
              🎙
            </div>
          )}
          <textarea
            value={inp}
            onChange={e=>setInp(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
            placeholder={mic?"Listening...":"Ask anything flight-related..."}
            rows={1}
            className="terminal-inp"
          />
          <div className="terminal-actions">
            {!noSR&&!voiceDisabled&&(
              <button
                className={`spk-btn${voiceMode?" active":""}`}
                onClick={()=>{setVoiceMode(v=>!v);if(voiceMode){stopSpeaking();setSpeakIdx(null);}}}
                title="Toggle voice readback">
                {voiceMode?"🔊":"🔈"}
              </button>
            )}
            <button
              className="send-btn"
              onClick={()=>send()}
              disabled={loading||!inp.trim()}>
              SEND ↵
            </button>
          </div>
        </div>
        <div className="terminal-footer">
          <span className="terminal-advisory">{isLimited&&!gated?`${FREE_LIMIT-promptsUsed} queries remaining`:"Advisory only. Verify with official sources and your POH."}</span>
          <span className="terminal-hint">
            {voiceDisabled?"Voice paused in local mode":noSR?"":"⌘↵ to send · Hold Space to speak"}
          </span>
        </div>
        {gated&&(
          <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"20px 24px",background:"linear-gradient(transparent, var(--bg) 30%)",backdropFilter:"blur(4px)",textAlign:"center",zIndex:10}}>
            <div style={{fontFamily:"var(--mono)",fontSize:13,fontWeight:700,color:"var(--amber)",marginBottom:6}}>Free queries used</div>
            <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:12}}>Start your 3-day free trial for unlimited access.</div>
            <button className="btn btn-p" onClick={onRequestAccess} style={{padding:"10px 24px",fontSize:11}}>Start Free Trial</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CHECKLISTS ───────────────────────────────────────────────────────────────
function Checklists({onComplete}){
  const [active,setActive]=useState("preflight");
  const [done,setDone]=useState({});
  const [confirmed,setConfirmed]=useState({});
  const [notes,setNotes]=useState(()=>{try{return JSON.parse(localStorage.getItem("solus_cl_notes")||"{}");}catch{return{};}});
  const [showNotes,setShowNotes]=useState(false);
  const clSessionId=useRef(Date.now().toString(36)+Math.random().toString(36).slice(2));
  function setNote(k,v){const n={...notes,[k]:v};setNotes(n);try{localStorage.setItem("solus_cl_notes",JSON.stringify(n));}catch{}}
  function persistCheck(phase,idx,itemText,checked){
    const token=localStorage.getItem('solus_token');
    if(token)fetch('https://solus-backend-9wde.onrender.com/checklists/check',{method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},body:JSON.stringify({session_id:clSessionId.current,phase,item_index:idx,item_text:itemText,checked})}).catch(()=>{});
  }
  const cl=CHECKLISTS[active];
  const all=cl.sections.flatMap(s=>s.items);
  const dc=all.filter((_,i)=>done[active+i]).length;
  const pct=all.length?(dc/all.length)*100:0;
  const complete=pct===100;
  const isEmg=active==="emergency";
  let gi=0;

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Tab row */}
      <div className="cl-header">
        {Object.entries(CHECKLISTS).map(([k,c])=>(
          <button key={k} className={`cl-tab ${active===k?"on":""}`}
            onClick={()=>setActive(k)}
            style={{color:active===k?c.color:"var(--text3)",position:"relative"}}>
            {c.label}
            {confirmed[k]&&<span style={{position:"absolute",top:10,right:6,width:5,height:5,borderRadius:"50%",background:"var(--green)"}}/>}
          </button>
        ))}
      </div>

      {/* Emergency banner */}
      {isEmg&&(
        <div style={{flexShrink:0,padding:"9px 24px",background:"var(--redDim)",borderBottom:"1px solid rgba(224,82,82,0.15)"}}>
          <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--red)"}}>
            Emergency Procedures - Memory Items First
          </span>
        </div>
      )}

      {/* Progress */}
      {!isEmg&&(
        <div style={{flexShrink:0,padding:"10px 24px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:14}}>
          <div style={{flex:1}}>
            <div className="prog-bar" style={{margin:0}}>
              <div className="prog-fill" style={{width:pct+"%",background:complete?"var(--green)":"var(--amber)"}}/>
            </div>
          </div>
          <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11,color:"var(--text3)",whiteSpace:"nowrap"}}>{dc}/{all.length}</span>
          {complete&&!confirmed[active]&&(
            <button className="btn btn-sm" onClick={()=>{setConfirmed(p=>({...p,[active]:true}));onComplete?.(active);}}
              style={{borderColor:"var(--green)",color:"var(--green)"}}>Confirm</button>
          )}
          {confirmed[active]&&<span style={{fontSize:11,fontWeight:600,color:"var(--green)"}}>Logged</span>}
          <button className="btn btn-sm" onClick={()=>{const n={};Object.keys(done).forEach(k=>{if(!k.startsWith(active))n[k]=done[k];});setDone(n);}}>Reset</button>
          <button className="btn btn-sm" onClick={()=>setShowNotes(p=>!p)} style={{borderColor:notes[active]?"var(--amber)":"var(--border)",color:notes[active]?"var(--amber)":"var(--text3)"}}>{showNotes?"Hide Notes":"Notes"}{notes[active]?" ✦":""}</button>
        </div>
      )}

      {/* Items */}
      <div style={{flex:1,overflowY:"auto",padding:"0 24px 28px"}}>
        {cl.sections.map((sec,si)=>(
          <div key={si} style={{marginTop:24}}>
            <div className="section-label">{sec.name}</div>
            {sec.items.map(item=>{
              const idx=gi++;const key=active+idx;const chk=!!done[key];
              return(
                <div key={idx} className="cl-item" onClick={()=>{const newChk=!done[key];setDone(p=>({...p,[key]:newChk}));persistCheck(active,idx,item,newChk);}}>
                  <div className={`cl-box ${chk?"done":""}`}>
                    {chk&&<svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span className={`cl-text ${chk?"done":""}`} style={{color:chk?"var(--muted)":isEmg?"var(--red)":"var(--text2)"}}>{item}</span>
                </div>
              );
            })}
          </div>
        ))}
        {showNotes&&(
          <div style={{margin:"20px 0 0",padding:"16px 0 8px",borderTop:"1px solid var(--border)"}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--amber)",fontFamily:"var(--mono)",marginBottom:8}}>NOTES: {CHECKLISTS[active]?.label}</div>
            <textarea className="inp" value={notes[active]||""} onChange={e=>setNote(active,e.target.value)}
              placeholder="e.g. Chip on fuel cap, left brake slightly soft, fuel not topped off..."
              style={{width:"100%",minHeight:88,resize:"vertical",lineHeight:1.6,fontSize:12,fontFamily:"var(--mono)",padding:"10px 12px",boxSizing:"border-box"}}/>
            <div style={{fontSize:10,color:"var(--muted)",marginTop:4}}>Auto-saved per phase. Persists across sessions.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAINT UPLOAD ─────────────────────────────────────────────────────────────
function MaintUpload({onAdd}){
  const [parsing,setParsing]=useState(false);
  const [res,setRes]=useState(null);
  const ref=useRef(null);
  async function handle(e){
    const file=e.target.files[0];if(!file)return;
    setParsing(true);setRes(null);
    try{
      const text=await file.text();
      const tkn=localStorage.getItem('solus_token');
      const r=await fetch("https://solus-backend-9wde.onrender.com/chat/message",{method:"POST",headers:{"Content-Type":"application/json",...(tkn?{"Authorization":"Bearer "+tkn}:{})},
        body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:800,
          system:`Parse aircraft maintenance log. Return ONLY a JSON array: [{date:"YYYY-MM-DD",item:"description",status:"Open|Deferred|Closed"}]. No other text.`,
          messages:[{role:"user",content:`Parse this log:\n\n${text.slice(0,3000)}`}]})});
      const data=await r.json();
      const raw=data.content?.find(b=>b.type==="text")?.text||"[]";
      const entries=JSON.parse(raw.replace(/```json|```/g,"").trim());
      entries.forEach(en=>onAdd(en));
      setRes({count:entries.length,ok:true});
    }catch{
      onAdd({date:new Date().toISOString().split("T")[0],item:`Uploaded: ${file.name}`,status:"Open"});
      setRes({count:1,ok:true,fallback:true});
    }finally{setParsing(false);e.target.value="";}
  }
  return(
    <div>
      <input ref={ref} type="file" accept=".txt,.pdf,.log,.csv,.doc,.docx" onChange={handle} style={{display:"none"}}/>
      <button className="upload-btn" onClick={()=>ref.current?.click()} disabled={parsing}>
        {parsing?"Parsing...":"Upload maintenance log"}
      </button>
      {res&&<div style={{marginTop:6,fontSize:11,fontWeight:600,color:res.ok?"var(--green)":"var(--red)"}}>
        {res.ok?`${res.count} ${res.count===1?"entry":"entries"} imported${res.fallback?" - verify manually":""}`:"Parse failed"}
      </div>}
      <div style={{marginTop:4,fontSize:11,color:"var(--muted)"}}>A&P: .txt, .pdf, .csv accepted</div>
    </div>
  );
}

// ─── AIRCRAFT ─────────────────────────────────────────────────────────────────
function AirworthinessCheck({profile}){
  const [ads,setAds]=useState(()=>{try{return JSON.parse(localStorage.getItem('solus_airworthy')||'{}');}catch{return{};}});
  const save=(k,v)=>{const next={...ads,[k]:v};setAds(next);localStorage.setItem('solus_airworthy',JSON.stringify(next));};
  const annDays=profile.lastAnnual?Math.round((Date.now()-new Date(profile.lastAnnual).getTime())/864e5):null;
  const annOk=annDays!==null&&annDays<=365;
  const annText=annDays===null?"Not entered":annDays<=365?`Current - ${365-annDays} days remaining`:`OVERDUE by ${annDays-365} days`;
  const determined=ads.determined||false;

  return(
    <div>
      {/* Annual */}
      <div className="card" style={{marginBottom:6,padding:"10px 14px",borderLeft:`3px solid ${annOk?"var(--green)":annDays===null?"var(--muted)":"var(--red)"}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--text)",fontFamily:"var(--mono)"}}>Annual Inspection (FAR 91.409)</div>
          <span style={{fontSize:10,color:annOk?"var(--green)":"var(--red)",fontFamily:"var(--mono)"}}>{annText}</span>
        </div>
      </div>
      {/* ADs */}
      <div className="card" style={{marginBottom:6,padding:"10px 14px",borderLeft:`3px solid ${ads.noAds?"var(--green)":"var(--muted)"}`}}>
        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"var(--text)",fontFamily:"var(--mono)",cursor:"pointer"}}>
          <input type="checkbox" checked={ads.noAds||false} onChange={e=>save('noAds',e.target.checked)}/> No open Airworthiness Directives
        </label>
        {!ads.noAds&&<div style={{marginTop:6}}><input className="inp" value={ads.adText||''} onChange={e=>save('adText',e.target.value)} placeholder="Describe open AD..." style={{fontSize:11}}/></div>}
      </div>
      {/* Required Inspections */}
      <div className="card" style={{marginBottom:6,padding:"10px 14px",borderLeft:`3px solid ${ads.elt&&ads.xpdr?"var(--green)":"var(--muted)"}`}}>
        <div style={{fontSize:11,fontWeight:600,color:"var(--text)",fontFamily:"var(--mono)",marginBottom:6}}>Required Inspections</div>
        {[{k:'elt',l:'ELT battery/inspection current'},{k:'xpdr',l:'Transponder tested within 24 months (FAR 91.413)'},{k:'static',l:'Static system/altimeter tested 24 months (FAR 91.411) - IFR'},{k:'vor',l:'VOR checked within 30 days (FAR 91.171) - IFR'}].map(i=>(
          <label key={i.k} style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:"var(--text3)",fontFamily:"var(--mono)",cursor:"pointer",marginBottom:3}}>
            <input type="checkbox" checked={ads[i.k]||false} onChange={e=>save(i.k,e.target.checked)}/> {i.l}
          </label>
        ))}
      </div>
      {/* Inop Equipment */}
      <div className="card" style={{marginBottom:6,padding:"10px 14px",borderLeft:`3px solid ${ads.allOp?"var(--green)":"var(--muted)"}`}}>
        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"var(--text)",fontFamily:"var(--mono)",cursor:"pointer"}}>
          <input type="checkbox" checked={ads.allOp||false} onChange={e=>save('allOp',e.target.checked)}/> All required equipment operative
        </label>
        {!ads.allOp&&<div style={{marginTop:6}}><input className="inp" value={ads.inopText||''} onChange={e=>save('inopText',e.target.value)} placeholder="Inoperative equipment..." style={{fontSize:11}}/><div style={{fontSize:9,color:"var(--muted)",fontFamily:"var(--mono)",marginTop:4}}>Verify MEL or defer per FAR 91.213 before flight</div></div>}
      </div>
      {/* PIC Determination */}
      <div className="card" style={{padding:"12px 14px",borderLeft:`3px solid ${determined?"var(--green)":"var(--amber)"}`,background:determined?"rgba(0,230,118,0.05)":"rgba(255,176,0,0.05)"}}>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
          <input type="checkbox" checked={determined} onChange={e=>{
            save('determined',e.target.checked);
            if(e.target.checked){
              const log=JSON.parse(localStorage.getItem('solus_airworthy_log')||'[]');
              log.unshift({ts:new Date().toISOString(),nNumber:profile.nNumber||'unknown'});
              localStorage.setItem('solus_airworthy_log',JSON.stringify(log.slice(0,50)));
            }
          }}/>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:determined?"var(--green)":"var(--amber)",fontFamily:"var(--mono)"}}>PIC AIRWORTHINESS DETERMINATION</div>
            <div style={{fontSize:10,color:"var(--text3)",fontFamily:"var(--mono)"}}>I have determined this aircraft is airworthy per FAR 91.7 for this flight</div>
          </div>
        </label>
      </div>
    </div>
  );
}

function Aircraft({profile,setProfile,logs,setLogs,backendFetch}){
  function saveAc(next){
    setProfile(next);
    const token=localStorage.getItem('solus_token');
    if(token)setTimeout(()=>backendFetch('/user/aircraft',{method:'POST',body:JSON.stringify({n_number:next.nNumber,type:next.type,total_time:next.totalTime,engine_time:next.engineTime,last_annual:next.lastAnnual,squawks:next.squawks})}),800);
  }
  const [adding,setAdding]=useState(false);
  const [nl,setNl]=useState({date:"",item:"",status:"Open"});
  const mos=profile.lastAnnual?Math.floor((new Date()-new Date(profile.lastAnnual))/(1000*60*60*24*30)):null;
  const overdue=mos!==null&&mos>11;
  const dueSoon=mos!==null&&mos>9&&!overdue;

  // FAA Registry auto-lookup
  const [regLooking,setRegLooking]=useState(false);
  const [regData,setRegData]=useState(null);
  const [regError,setRegError]=useState(null);
  const lookupTimer=useRef(null);
  function lookupNNumber(nNum){
    const clean=nNum.replace(/^N/i,"").trim();
    if(clean.length<1||clean.length>6){setRegData(null);setRegError(null);return;}
    if(lookupTimer.current)clearTimeout(lookupTimer.current);
    lookupTimer.current=setTimeout(async()=>{
      setRegLooking(true);setRegError(null);setRegData(null);
      try{
        const res=await fetch("https://solus-backend-9wde.onrender.com/registry/lookup/"+encodeURIComponent(clean));
        const d=await res.json();
        if(d.found){
          setRegData(d);
          // Auto-fill type if empty
          if(!profile.type&&d.manufacturer&&d.model){
            setProfile(p=>({...p,type:(d.manufacturer+" "+d.model).trim()}));
          }
        }else{setRegError("Not found in FAA registry");}
      }catch{setRegError("Lookup failed");}
      setRegLooking(false);
    },800);
  }

  const [acSaved,setAcSaved]=useState(false);
  async function saveAircraft(){
    const token=localStorage.getItem("solus_token");
    if(!token){alert("Sign in to save your aircraft profile.");return;}
    await backendFetch("/user/aircraft",{method:"POST",body:JSON.stringify({n_number:profile.nNumber,type:profile.type,total_time:profile.totalTime,engine_time:profile.engineTime,last_annual:profile.lastAnnual,squawks:profile.squawks})});
    setAcSaved(true);
    setTimeout(()=>setAcSaved(false),2500);
  }
  const fields=[["N-Number","nNumber","N12345"],["Aircraft Type","type","C172S"],["Total Time (hrs)","totalTime","1240"],["Engine SMOH (hrs)","engineTime","340"],["Last Annual","lastAnnual","2025-03","FAR 91.409: required within 12 calendar months. Enter here to turn the header indicator green."],["Last 100-hr","last100hr","2025-10"]];

  return(
    <div className="page">
      <div className="page-inner">
        <div className="page-title">Aircraft Profile</div>
        <div className="page-sub">Load your aircraft. Every SOLUS response becomes airframe-specific.</div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          {fields.map(([l,f,ph,hint])=>(
            <div key={f}>
              <div className="inpl">{l}{f==="nNumber"&&regLooking&&<span style={{color:"var(--amber)",fontSize:9,marginLeft:6}}>looking up...</span>}</div>
              <input className="inp" value={profile[f]||""} onChange={e=>{
                setProfile(p=>({...p,[f]:e.target.value}));
                if(f==="nNumber")lookupNNumber(e.target.value);
              }} placeholder={ph}/>
              {hint&&<div style={{fontSize:10,color:"var(--muted)",marginTop:3,lineHeight:1.5}}>{hint}</div>}
            </div>
          ))}
          <div style={{gridColumn:"1/-1"}}>
            <div className="inpl">Active Squawks</div>
            <input className="inp" value={profile.squawks||""} onChange={e=>setProfile(p=>({...p,squawks:e.target.value}))} placeholder="e.g. Comm static on 122.8, altimeter reads high"/>
          </div>
        </div>

        {/* FAA Registry Result */}
        {regData&&(
          <div className="card" style={{borderLeft:"3px solid var(--sky)",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--sky)",fontFamily:"var(--mono)"}}>FAA Registry: N{regData.nNumber}</span>
              {regData.status&&<span style={{fontSize:10,color:regData.status==="Valid"?"var(--green)":"var(--amber)",fontFamily:"var(--mono)"}}>{regData.status}</span>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              {[
                ["Manufacturer",regData.manufacturer],
                ["Model",regData.model],
                ["Serial",regData.serial],
                ["Year",regData.year],
                ["Type",regData.typeAircraft],
                ["Engines",regData.engines],
                ["Owner",regData.owner],
                ["Location",[regData.city,regData.state].filter(Boolean).join(", ")],
              ].filter(([,v])=>v).map(([l,v])=>(
                <div key={l}>
                  <span style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--mono)",letterSpacing:"0.06em"}}>{l}: </span>
                  <span style={{fontSize:12,color:"var(--text2)"}}>{v}</span>
                </div>
              ))}
            </div>
            {!profile.type&&regData.manufacturer&&regData.model&&(
              <button className="btn btn-sm" style={{marginTop:10,borderColor:"var(--sky)",color:"var(--sky)"}}
                onClick={()=>setProfile(p=>({...p,type:(regData.manufacturer+" "+regData.model).trim()}))}>
                Use {regData.manufacturer} {regData.model} →
              </button>
            )}
          </div>
        )}
        {regError&&!regLooking&&profile.nNumber&&(
          <div style={{fontSize:11,color:"var(--muted)",marginBottom:12,fontFamily:"var(--mono)"}}>{regError}</div>
        )}

        <button className="btn btn-p" onClick={saveAircraft} style={{width:"100%",marginBottom:16,padding:"12px",fontSize:13,letterSpacing:"0.02em",opacity:acSaved?0.7:1}}>
          {acSaved?"Saved to your profile ✓":"Save aircraft to profile"}
        </button>
        {(overdue||dueSoon||profile.squawks)&&(
          <div className="card" style={{borderLeft:"3px solid var(--amber)",background:"var(--amberDim)",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--amber)",marginBottom:8}}>Airworthiness Flags</div>
            {overdue&&<div style={{fontSize:13,color:"var(--amber)",marginBottom:4}}>Annual inspection overdue - FAR 91.409</div>}
            {dueSoon&&<div style={{fontSize:13,color:"var(--amber)",marginBottom:4}}>Annual inspection due within 2 months</div>}
            {profile.squawks&&<div style={{fontSize:13,color:"var(--amber)"}}>Open squawks - verify airworthiness per FAR 91.213</div>}
          </div>
        )}

        {profile.nNumber&&(
          <div style={{padding:"10px 14px",background:"var(--greenDim)",border:"1px solid rgba(61,186,110,0.2)",borderRadius:6,marginBottom:16,fontSize:13,color:"var(--green)",fontWeight:600}}>
            N{profile.nNumber} loaded. SOLUS is now airframe-aware.
          </div>
        )}

        {/* Airworthiness Verification */}
        <div style={{marginTop:20,marginBottom:20}}>
          <div className="section-label">Airworthiness Verification - FAR 91.7</div>
          <AirworthinessCheck profile={profile}/>
        </div>

        <div style={{marginTop:24}}>
          <div className="section-label">Maintenance Log</div>
          {logs.length===0&&<div style={{fontSize:13,color:"var(--muted)",marginBottom:12}}>No entries.</div>}
          {logs.map((log,i)=>(
            <div key={i} className="card card-sm" style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderLeft:`3px solid ${log.status==="Open"?"var(--amber)":log.status==="Deferred"?"var(--red)":"var(--green)"}`}}>
              <div>
                <div style={{fontSize:14,color:"var(--text2)",marginBottom:3}}>{log.item}</div>
                <div style={{fontSize:11,color:"var(--muted)",fontFamily:"'JetBrains Mono',monospace"}}>
                  {log.date} &nbsp;
                  <span style={{color:log.status==="Open"?"var(--amber)":log.status==="Deferred"?"var(--red)":"var(--green)"}}>{log.status}</span>
                </div>
              </div>
              <button onClick={()=>setLogs(l=>l.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:"var(--muted)",fontSize:18,cursor:"pointer",padding:"0 4px",lineHeight:1}}>×</button>
            </div>
          ))}

          {adding?(
            <div className="card" style={{marginTop:8}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                <div><div className="inpl">Date</div><input className="inp" value={nl.date} onChange={e=>setNl(p=>({...p,date:e.target.value}))} placeholder="YYYY-MM-DD"/></div>
                <div><div className="inpl">Status</div><select className="sel" value={nl.status} onChange={e=>setNl(p=>({...p,status:e.target.value}))}><option>Open</option><option>Deferred</option><option>Closed</option></select></div>
              </div>
              <div style={{marginBottom:10}}><div className="inpl">Description</div><input className="inp" value={nl.item} onChange={e=>setNl(p=>({...p,item:e.target.value}))} placeholder="Describe the maintenance item"/></div>
              <div style={{display:"flex",gap:8}}>
                <button className="btn btn-p btn-sm" onClick={()=>{if(nl.item){setLogs(l=>[...l,{...nl}]);setNl({date:"",item:"",status:"Open"});setAdding(false);}}}>Add</button>
                <button className="btn btn-sm" onClick={()=>setAdding(false)}>Cancel</button>
              </div>
            </div>
          ):(
            <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap",alignItems:"flex-start"}}>
              <button className="btn btn-sm" onClick={()=>setAdding(true)}>+ Add entry</button>
              <MaintUpload onAdd={en=>setLogs(l=>[...l,en])}/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CURRENCY TRACKER ─────────────────────────────────────────────────────────
function daysBetween(dateStr){
  if(!dateStr)return null;
  const d=new Date(dateStr);
  if(isNaN(d))return null;
  return Math.floor((Date.now()-d.getTime())/(1000*60*60*24));
}

function CurrencyRow({label,far,status,daysAgo,windowDays,note,detail}){
  const [open,setOpen]=useState(false);
  const pct=daysAgo!=null?Math.min(100,Math.round((daysAgo/windowDays)*100)):null;
  const c=status==="CURRENT"?"var(--green)":status==="EXPIRING"?"var(--amber)":"var(--red)";
  const bg=status==="CURRENT"?"var(--greenDim)":status==="EXPIRING"?"var(--amberDim)":"var(--redDim)";
  return(
    <div style={{marginBottom:8}}>
      <div onClick={()=>setOpen(o=>!o)} style={{
        padding:"14px 16px",background:"var(--surface)",border:"1px solid var(--border)",
        borderLeft:`3px solid ${c}`,borderRadius:4,cursor:"pointer",
        display:"flex",alignItems:"center",gap:14,transition:"border-color .12s",
      }}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap"}}>
            <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:17,fontWeight:700,color:"var(--text)"}}>{label}</span>
            <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,color:"var(--muted)",letterSpacing:"0.08em"}}>FAR {far}</span>
          </div>
          {pct!=null&&(
            <div style={{height:3,background:"var(--border)",borderRadius:2,marginBottom:6}}>
              <div style={{height:"100%",width:pct+"%",background:c,borderRadius:2,transition:"width .4s ease"}}/>
            </div>
          )}
          <span style={{fontSize:12,color:"var(--text3)"}}>{note}</span>
        </div>
        <div style={{flexShrink:0,textAlign:"right"}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:700,color:c,lineHeight:1,padding:"4px 10px",background:bg,borderRadius:6,letterSpacing:"0.04em"}}>
            {status==="UNSET"?"--":status}
          </div>
        </div>
        <div style={{color:"var(--muted)",fontSize:12,flexShrink:0}}>{open?"▲":"▼"}</div>
      </div>
      {open&&(
        <div style={{padding:"12px 16px",background:"var(--surface2)",borderLeft:"3px solid var(--border)",borderRadius:"0 0 4px 4px",marginTop:-1}}>
          <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.7}}>{detail}</div>
        </div>
      )}
    </div>
  );
}

// MM-DD-YYYY auto-formatting date input. Stores internally as YYYY-MM-DD.
function toDisplay(iso){if(!iso)return"";const[y,m,d]=iso.split("-");return(m&&d&&y)?`${m}-${d}-${y}`:iso;}
function toISO(display){if(!display)return"";const p=display.replace(/[^\d]/g,"");if(p.length<8)return"";const m=p.slice(0,2),d=p.slice(2,4),y=p.slice(4,8);return`${y}-${m}-${d}`;}
function formatDateInput(raw){
  const digits=raw.replace(/[^\d]/g,"").slice(0,8);
  let out="";
  for(let i=0;i<digits.length;i++){
    if(i===2||i===4)out+="-";
    out+=digits[i];
  }
  return out;
}
function DateInput({value,onChange,className,style,max}){
  const [display,setDisplay]=useState(toDisplay(value));
  useEffect(()=>{setDisplay(toDisplay(value));},[value]);
  return <input type="text" className={className||"inp"} style={style} placeholder="MM-DD-YYYY" maxLength={10}
    value={display} onChange={e=>{
      const formatted=formatDateInput(e.target.value);
      setDisplay(formatted);
      if(formatted.length===10){
        const iso=toISO(formatted);
        if(iso&&iso.match(/^\d{4}-\d{2}-\d{2}$/)){
          const d=new Date(iso);
          if(!isNaN(d.getTime()))onChange(iso);
        }
      }else if(formatted.length===0){onChange("");}
    }}/>;
}

function parseForeFlight(text){
  const lines=text.split("\n").filter(l=>l.trim());
  if(lines.length<2)return null;
  const hdr=lines[0].split(",").map(h=>h.trim().toLowerCase().replace(/"/g,""));
  const dateIdx=hdr.findIndex(h=>h==="date");
  const dayLandIdx=hdr.findIndex(h=>h.includes("day landing")||h.includes("landings day")||(h==="landings"&&!h.includes("night")));
  const nightLandIdx=hdr.findIndex(h=>h.includes("night landing")||h.includes("fs night")||h.includes("landings night")||h.includes("full stop night"));
  const nightIdx=hdr.findIndex(h=>h==="night"||h.includes("night time")||h.includes("night hrs"));
  const approachIdx=hdr.findIndex(h=>h.includes("approach")||h.includes("inst approach")||h.includes("iap"));
  const holdIdx=hdr.findIndex(h=>h.includes("hold"));
  const totalIdx=hdr.findIndex(h=>h.includes("total")||h.includes("duration"));
  // Fallback: if no separate day landings column, use generic "landings"
  const landIdx=dayLandIdx>=0?dayLandIdx:hdr.findIndex(h=>h==="landings"||h==="ldg"||h==="all landings");
  const toIdx=hdr.findIndex(h=>h==="takeoffs"||h==="takeoffs day"||h==="to day"||h==="all takeoffs");
  const nightToIdx=hdr.findIndex(h=>h.includes("takeoffs night")||h.includes("night takeoff")||h.includes("to night"));
  if(dateIdx<0)return null;
  const flights=[];
  for(let i=1;i<lines.length;i++){
    const cols=lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
    if(!cols)continue;
    const c=cols.map(v=>v.replace(/"/g,"").trim());
    const date=c[dateIdx];if(!date||!date.match(/\d/))continue;
    const parsed=new Date(date);if(isNaN(parsed))continue;
    const iso=parsed.toISOString().split("T")[0];
    flights.push({
      date:iso,
      dayLandings:parseInt(c[landIdx])||0,
      nightLandings:parseInt(c[nightLandIdx])||0,
      nightHrs:parseFloat(c[nightIdx])||0,
      approaches:parseInt(c[approachIdx])||0,
      holds:parseInt(c[holdIdx])||0,
      total:parseFloat(c[totalIdx])||0,
      takeoffs:parseInt(c[toIdx])||0,
      nightTakeoffs:parseInt(c[nightToIdx])||0,
    });
  }
  flights.sort((a,b)=>new Date(b.date)-new Date(a.date));
  return flights;
}
function parseMyFlightbook(text){
  const lines=text.split("\n").filter(l=>l.trim());
  if(lines.length<2)return null;
  const hdr=lines[0].split(",").map(h=>h.trim().toLowerCase().replace(/"/g,""));
  const dateIdx=hdr.findIndex(h=>h==="date");
  const landIdx=hdr.findIndex(h=>h==="landings"||h==="full-stop landings"||h==="num landings");
  const nightIdx=hdr.findIndex(h=>h==="night");
  const approachIdx=hdr.findIndex(h=>h.includes("approach"));
  const holdIdx=hdr.findIndex(h=>h.includes("hold"));
  const totalIdx=hdr.findIndex(h=>h==="total flight time"||h==="total"||h==="duration");
  const nightLandIdx=hdr.findIndex(h=>h.includes("night landing")||h.includes("fs night"));
  if(dateIdx<0)return null;
  const flights=[];
  for(let i=1;i<lines.length;i++){
    const cols=lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
    if(!cols)continue;
    const c=cols.map(v=>v.replace(/"/g,"").trim());
    const date=c[dateIdx];if(!date||!date.match(/\d/))continue;
    const parsed=new Date(date);if(isNaN(parsed))continue;
    const iso=parsed.toISOString().split("T")[0];
    const nightHrs=parseFloat(c[nightIdx])||0;
    flights.push({
      date:iso,
      dayLandings:parseInt(c[landIdx])||0,
      nightLandings:nightLandIdx>=0?(parseInt(c[nightLandIdx])||0):(nightHrs>0?(parseInt(c[landIdx])||0):0),
      nightHrs,
      approaches:parseInt(c[approachIdx])||0,
      holds:parseInt(c[holdIdx])||0,
      total:parseFloat(c[totalIdx])||0,
      takeoffs:parseInt(c[landIdx])||0, // approximate: assume 1 takeoff per landing
      nightTakeoffs:nightHrs>0?(parseInt(c[landIdx])||0):0,
    });
  }
  flights.sort((a,b)=>new Date(b.date)-new Date(a.date));
  return flights;
}
function extractCurrencyFromFlights(flights){
  const dayLandDates=[];const nightLandDates=[];const nightTakeoffDates=[];
  const sixMonthsAgo=new Date();sixMonthsAgo.setMonth(sixMonthsAgo.getMonth()-6);
  let totalApproaches=0;let totalHolds=0;
  for(const f of flights){
    if(f.dayLandings>0&&dayLandDates.length<3)dayLandDates.push(f.date);
    if(f.nightLandings>0&&nightLandDates.length<3)nightLandDates.push(f.date);
    if((f.nightTakeoffs>0||f.nightHrs>0)&&nightTakeoffDates.length<3)nightTakeoffDates.push(f.date);
    if(new Date(f.date)>=sixMonthsAgo){totalApproaches+=f.approaches;totalHolds+=f.holds;}
  }
  return{
    lastDayLanding:dayLandDates[0]||"",dayLanding2:dayLandDates[1]||"",dayLanding3:dayLandDates[2]||"",
    lastNightLanding:nightLandDates[0]||"",nightLanding2:nightLandDates[1]||"",nightLanding3:nightLandDates[2]||"",
    nightTakeoff1:nightTakeoffDates[0]||"",nightTakeoff2:nightTakeoffDates[1]||"",nightTakeoff3:nightTakeoffDates[2]||"",
    lastIFR:flights.find(f=>f.approaches>0)?.date||"",
    ifrApproaches:String(totalApproaches),ifrHolds:String(totalHolds),
    lastBFR:"", // can't auto-detect from logbook
  };
}

function Currency({onCurrencyChange,backendFetch,dates,setDates}){
  const today=new Date().toISOString().split("T")[0];
  const [importMode,setImportMode]=useState(null); // null | 'foreflight' | 'myflightbook' | 'manual'
  const [importResult,setImportResult]=useState(null);
  const [mfbLinked,setMfbLinked]=useState(false);
  const [mfbSyncing,setMfbSyncing]=useState(false);
  const [mfbStatus,setMfbStatus]=useState(null); // null | 'checking' | 'linked' | 'unlinked'

  // Check MFB link status on mount
  useEffect(()=>{
    const token=localStorage.getItem('solus_token');
    if(!token)return;
    setMfbStatus('checking');
    backendFetch('/myflightbook/status').then(r=>{
      if(r&&r.linked){setMfbLinked(true);setMfbStatus('linked');}
      else setMfbStatus('unlinked');
    }).catch(()=>setMfbStatus('unlinked'));

    // Check URL params for MFB callback result
    const params=new URLSearchParams(window.location.search);
    if(params.get('mfb_linked')==='true'){
      setMfbLinked(true);setMfbStatus('linked');
      setImportResult('MyFlightbook linked successfully! Click "Sync from MyFlightbook" to import your flights.');
      setTimeout(()=>setImportResult(null),8000);
      window.history.replaceState({},'',window.location.pathname);
    }
    if(params.get('mfb_error')){
      setImportResult(`MyFlightbook link failed: ${params.get('mfb_error')}`);
      setTimeout(()=>setImportResult(null),8000);
      window.history.replaceState({},'',window.location.pathname);
    }
  },[]); // eslint-disable-line

  async function linkMyFlightbook(){
    const token=localStorage.getItem('solus_token');
    if(!token){
      alert('Sign in with Google or email first, then link MyFlightbook.');
      window.dispatchEvent(new CustomEvent('solus:openSignIn'));
      return;
    }
    try{
      const res=await fetch('https://solus-backend-9wde.onrender.com/myflightbook/authorize',{headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'}});
      const data=await res.json();
      if(data&&data.url) window.location.href=data.url;
      else alert(data.error||'Failed to get MyFlightbook authorization URL. Make sure you are signed in.');
    }catch(e){
      alert('Connection error: '+e.message);
    }
  }

  async function syncFromMFB(){
    setMfbSyncing(true);
    try{
      const data=await backendFetch('/myflightbook/sync-currency',{method:'POST'});
      if(data&&data.synced){
        // Refresh currency dates from backend
        const fresh=await backendFetch('/user/currency');
        if(fresh){
          setDates(p=>({...p,
            lastBFR:fresh.bfr||p.lastBFR,
            lastMedical:fresh.medical||p.lastMedical,
            medicalClass:fresh.medical_class||p.medicalClass,
            dob:fresh.dob||p.dob,
            lastDayLanding:fresh.vfr_day||p.lastDayLanding,
            dayLanding2:fresh.vfr_day_2||p.dayLanding2,
            dayLanding3:fresh.vfr_day_3||p.dayLanding3,
            lastNightLanding:fresh.vfr_night||p.lastNightLanding,
            nightLanding2:fresh.vfr_night_2||p.nightLanding2,
            nightLanding3:fresh.vfr_night_3||p.nightLanding3,
            nightTakeoff1:fresh.night_takeoff_1||p.nightTakeoff1,
            nightTakeoff2:fresh.night_takeoff_2||p.nightTakeoff2,
            nightTakeoff3:fresh.night_takeoff_3||p.nightTakeoff3,
            lastIFR:fresh.ifr_approach||p.lastIFR,
            ifrApproaches:fresh.ifr_approaches||p.ifrApproaches,
            ifrHolds:fresh.ifr_holds||p.ifrHolds,
            ifrTracking:fresh.ifr_tracking||p.ifrTracking,
            tailwheel:fresh.tailwheel||p.tailwheel,
            lastTailwheelLanding:fresh.last_tailwheel_landing||p.lastTailwheelLanding,
          }));
        }
        setImportResult(`Synced ${data.flights_analyzed} flights from MyFlightbook. Currency dates auto-filled.`);
        setImportMode("manual");
      }else{
        setImportResult('Sync failed — check your MyFlightbook connection.');
      }
    }catch(e){
      setImportResult('Sync error: '+e.message);
    }
    setMfbSyncing(false);
    setTimeout(()=>setImportResult(null),6000);
  }

  async function unlinkMFB(){
    if(!window.confirm('Unlink MyFlightbook? Your currency data will remain.'))return;
    await backendFetch('/myflightbook/unlink',{method:'DELETE'});
    setMfbLinked(false);setMfbStatus('unlinked');
    setImportResult('MyFlightbook unlinked.');
    setTimeout(()=>setImportResult(null),4000);
  }

  function handleCSVImport(e,parser){
    const file=e.target.files?.[0];if(!file)return;
    file.text().then(text=>{
      const flights=parser(text);
      if(!flights||flights.length===0){alert("Could not parse CSV. Check the format and try again.");return;}
      const extracted=extractCurrencyFromFlights(flights);
      setDates(p=>({...p,...extracted,lastBFR:p.lastBFR||extracted.lastBFR,lastMedical:p.lastMedical||"",medicalClass:p.medicalClass||"3rd",dob:p.dob||""}));
      setImportResult(`Imported ${flights.length} flights. Currency auto-filled from your logbook.`);
      setImportMode("manual");
      setTimeout(()=>setImportResult(null),6000);
    }).catch(()=>alert("Error reading file."));
  }

  const [csvPreview,setCsvPreview]=useState(null);
  const [csvUploading,setCsvUploading]=useState(false);

  async function handleBackendCSVImport(e){
    const file=e.target.files?.[0];if(!file)return;
    e.target.value='';
    setCsvUploading(true);setCsvPreview(null);setImportResult(null);
    try{
      const form=new FormData();
      form.append('file',file);
      const res=await fetch('https://solus-backend-9wde.onrender.com/import/csv',{method:'POST',body:form});
      if(!res.ok){const err=await res.json().catch(()=>({}));throw new Error(err.error||'Upload failed');}
      const parsed=await res.json();
      setCsvPreview(parsed);
      setCsvUploading(false);
    }catch(err){
      setCsvUploading(false);
      alert('CSV import failed: '+(err.message||'Unknown error'));
    }
  }

  function applyCsvPreview(){
    if(!csvPreview)return;
    setDates(p=>({...p,
      lastBFR:csvPreview.lastBFR||p.lastBFR,
      lastDayLanding:csvPreview.lastDayLanding||p.lastDayLanding,
      dayLanding2:csvPreview.dayLanding2||p.dayLanding2,
      dayLanding3:csvPreview.dayLanding3||p.dayLanding3,
      lastNightLanding:csvPreview.lastNightLanding||p.lastNightLanding,
      nightLanding2:csvPreview.nightLanding2||p.nightLanding2,
      nightLanding3:csvPreview.nightLanding3||p.nightLanding3,
      nightTakeoff1:csvPreview.nightTakeoff1||p.nightTakeoff1,
      nightTakeoff2:csvPreview.nightTakeoff2||p.nightTakeoff2,
      nightTakeoff3:csvPreview.nightTakeoff3||p.nightTakeoff3,
      lastIFR:csvPreview.lastIFR||p.lastIFR,
      ifrApproaches:csvPreview.ifrApproaches||p.ifrApproaches,
      ifrHolds:csvPreview.ifrHolds||p.ifrHolds,
      ifrTracking:csvPreview.ifrTracking||p.ifrTracking,
      lastMedical:csvPreview.lastMedical||p.lastMedical,
      medicalClass:csvPreview.medicalClass||p.medicalClass,
    }));
    setImportResult('CSV data applied to currency fields.');
    setCsvPreview(null);setImportMode("manual");
    setTimeout(()=>setImportResult(null),6000);
  }

  // dates and setDates are now passed as props from App for persistence

  function set(k,v){
    // Prevent future dates
    if(v&&typeof v==="string"&&v.match(/^\d{4}-\d{2}(-\d{2})?$/)){
      const d=new Date(v);
      if(d>new Date()){alert("Date cannot be in the future.");return;}
    }
    setDates(p=>{
      const next={...p,[k]:v};
      return next;
    });
  }
  const [currSaved,setCurrSaved]=useState(false);
  async function saveCurrency(){
    const token=localStorage.getItem('solus_token');
    if(!token){alert('Sign in with Google to save your currency data.');return;}
    await backendFetch('/user/currency',{method:'POST',body:JSON.stringify(dates)});
    setCurrSaved(true);
    setTimeout(()=>setCurrSaved(false),2500);
  }

  // ── FAR 61.56 Flight Review ──────────────────────────────────────────────
  const bfrDays=daysBetween(dates.lastBFR);
  const bfrWindow=730; // 24 calendar months approx
  const bfrStatus=!dates.lastBFR?"UNSET":bfrDays<=650?"CURRENT":bfrDays<=700?"EXPIRING":"LAPSED";
  const bfrNote=!dates.lastBFR?"Enter last flight review date":
    bfrStatus==="LAPSED"?`Lapsed ${bfrDays-730} days ago - no PIC until renewed`:
    bfrStatus==="EXPIRING"?`Expires in ~${730-bfrDays} days - schedule soon`:
    `Good for ~${730-bfrDays} more days`;

  // ── FAR 61.57a VFR Day (3 T&Ls in 90 days — oldest of 3 is what matters) ──
  const dayDates=[dates.lastDayLanding,dates.dayLanding2,dates.dayLanding3].filter(Boolean);
  const dayCount=dayDates.length;
  const dayOldest=dayCount>=3?daysBetween(dayDates.sort((a,b)=>new Date(a)-new Date(b))[0]):null;
  const dayMostRecent=daysBetween(dates.lastDayLanding);
  const dayDays=dayCount>=3?dayOldest:dayMostRecent;
  const dayStatus=dayCount===0?"UNSET":dayCount<3?"INCOMPLETE":dayOldest<=80?"CURRENT":dayOldest<=88?"EXPIRING":"LAPSED";
  const dayNote=dayCount===0?"Enter your 3 most recent T&L dates":
    dayCount<3?`${3-dayCount} more T&L date${3-dayCount>1?"s":""} needed for currency check`:
    dayStatus==="LAPSED"?`Lapsed ${dayOldest-90} days ago - no passenger ops`:
    dayStatus==="EXPIRING"?`Oldest of 3 expires in ~${90-dayOldest} days`:
    `Good for ~${90-dayOldest} more days (based on oldest of 3)`;

  // ── FAR 61.57b VFR Night (3 full-stop landings AND 3 takeoffs at night) ──
  const nightLDates=[dates.lastNightLanding,dates.nightLanding2,dates.nightLanding3].filter(Boolean);
  const nightTDates=[dates.nightTakeoff1,dates.nightTakeoff2,dates.nightTakeoff3].filter(Boolean);
  const nightLCount=nightLDates.length;
  const nightTCount=nightTDates.length;
  const nightLOldest=nightLCount>=3?daysBetween(nightLDates.sort((a,b)=>new Date(a)-new Date(b))[0]):null;
  const nightTOldest=nightTCount>=3?daysBetween(nightTDates.sort((a,b)=>new Date(a)-new Date(b))[0]):null;
  const nightBothComplete=nightLCount>=3&&nightTCount>=3;
  const nightOldest=nightBothComplete?Math.max(nightLOldest,nightTOldest):null;
  const nightDays=nightBothComplete?nightOldest:daysBetween(dates.lastNightLanding);
  const nightStatus=(nightLCount===0&&nightTCount===0)?"UNSET":!nightBothComplete?"INCOMPLETE":nightOldest<=80?"CURRENT":nightOldest<=88?"EXPIRING":"LAPSED";
  const nightMissing=[];
  if(nightLCount<3)nightMissing.push(`${3-nightLCount} landing${3-nightLCount>1?"s":""}`);
  if(nightTCount<3)nightMissing.push(`${3-nightTCount} takeoff${3-nightTCount>1?"s":""}`);
  const nightNote=(nightLCount===0&&nightTCount===0)?"Enter your 3 most recent night T/Os and landings":
    !nightBothComplete?`Need ${nightMissing.join(" and ")} for currency check`:
    nightStatus==="LAPSED"?`Lapsed ${nightOldest-90} days ago - no night pax`:
    nightStatus==="EXPIRING"?`Oldest entry expires in ~${90-nightOldest} days`:
    `Good for ~${90-nightOldest} more days (based on oldest entry)`;

  // ── FAR 61.23 Medical ────────────────────────────────────────────────────
  const medDays=daysBetween(dates.lastMedical);
  const dobDays=dates.dob?Math.floor((Date.now()-new Date(dates.dob).getTime())/(1000*60*60*24*365.25)):null;
  const age=dobDays?Math.floor(dobDays):null;
  let medWindow=730; // 3rd class default
  if(dates.medicalClass==="1st")medWindow=age&&age>=40?180:365;
  else if(dates.medicalClass==="2nd")medWindow=365;
  else medWindow=age&&age>=40?730:730; // 3rd class same either way
  const medStatus=!dates.lastMedical?"UNSET":medDays<=medWindow-60?"CURRENT":medDays<=medWindow?"EXPIRING":"LAPSED";
  const medNote=!dates.lastMedical?"Enter date of last medical exam":
    medStatus==="LAPSED"?`${dates.medicalClass} class lapsed - no PIC`:
    medStatus==="EXPIRING"?`Expires in ~${medWindow-medDays} days`:
    `Good for ~${medWindow-medDays} more days`;

  // ── FAR 61.57c IFR ───────────────────────────────────────────────────────
  const ifrDays=daysBetween(dates.lastIFR);
  const ifrStatus=!dates.lastIFR?"UNSET":ifrDays<=150?"CURRENT":ifrDays<=170?"EXPIRING":"LAPSED";
  const approaches=parseInt(dates.ifrApproaches)||0;
  const holds=parseInt(dates.ifrHolds)||0;
  const ifrReqsMet=approaches>=6&&holds>=1;
  const ifrNote=!dates.lastIFR?"Enter last instrument currency date":
    ifrStatus==="LAPSED"?`IFR currency lapsed - IPC required before IMC`:
    !ifrReqsMet?`${6-approaches} approaches and ${holds<1?"holds":"tracking"} needed in 6-month window`:
    ifrStatus==="EXPIRING"?`Expires in ~${180-ifrDays} days`:
    `Good for ~${180-ifrDays} more days`;

  // ── Tailwheel ────────────────────────────────────────────────────────────
  const twDays=daysBetween(dates.lastTailwheelLanding);
  const twStatus=!dates.tailwheel?"UNSET":!dates.lastTailwheelLanding?"UNSET":twDays<=80?"CURRENT":twDays<=88?"EXPIRING":"LAPSED";
  const twNote=!dates.tailwheel?"Toggle on if you fly tailwheel":
    !dates.lastTailwheelLanding?"Enter last full-stop tailwheel landing":
    twStatus==="LAPSED"?`Lapsed - 3 full-stop tailwheel landings required to carry pax`:
    `Good for ~${90-twDays} more days`;

  // Overall go/no-go
  const critical=[bfrStatus,dayStatus,medStatus];
  const allSet=critical.every(s=>s!=="UNSET");
  const allGo=allSet&&critical.every(s=>s==="CURRENT");
  const hasLapsed=critical.some(s=>s==="LAPSED");
  const overallStatus=!allSet?null:allGo?"ALL CURRENT":hasLapsed?"LAPSED":"EXPIRING";

  useEffect(()=>{
    if(onCurrencyChange)onCurrencyChange(overallStatus);
  },[overallStatus]); // eslint-disable-line

  return(
    <div style={{height:"100%",overflowY:"auto",padding:"28px 24px"}}>
      <div style={{maxWidth:620}}>

        {/* Go/No-Go Summary */}
        {allSet&&(
          <div style={{
            marginBottom:24,padding:"18px 20px",
            background:allGo?"var(--greenDim)":hasLapsed?"var(--redDim)":"var(--amberDim)",
            border:`1px solid ${allGo?"rgba(61,186,110,0.2)":hasLapsed?"rgba(224,82,82,0.2)":"rgba(232,160,32,0.2)"}`,
            borderRadius:4,display:"flex",alignItems:"center",gap:16,
          }}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:36,fontWeight:700,
              color:allGo?"var(--green)":hasLapsed?"var(--red)":"var(--amber)",lineHeight:1,flexShrink:0}}>
              {overallStatus}
            </div>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--text)",marginBottom:3}}>
                {allGo?"All required currency items are current.":hasLapsed?"One or more currency items have lapsed.":"One or more items expiring soon."}
              </div>
              <div style={{fontSize:12,color:"var(--text3)"}}>
                This is advisory only. Verify all dates with your logbook.
              </div>
            </div>
          </div>
        )}

        <div className="page-title">Pilot Currency</div>
        <div className="page-sub">All 5 FAR currency requirements in one place. Enter your dates, SOLUS tracks the rest.</div>

        {/* Status messages */}
        {importResult&&(
          <div style={{marginBottom:16,padding:"12px 16px",background:"var(--greenDim)",border:"1px solid rgba(0,230,118,0.25)",borderRadius:4,fontFamily:"var(--mono)",fontSize:11,color:"var(--green,#00E676)"}}>{importResult}</div>
        )}

        {/* MyFlightbook Link Card — always visible */}
        <div className="card" style={{marginBottom:16,padding:"16px 18px",borderLeft:"3px solid #4FC3F7"}}>
          <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--text3)",marginBottom:10,textTransform:"uppercase"}}>Logbook Sync</div>
          {mfbLinked?(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <button className="btn btn-sm" onClick={syncFromMFB} disabled={mfbSyncing} style={{borderColor:"var(--green,#00E676)",color:"var(--green,#00E676)"}}>
                  {mfbSyncing?"Syncing...":"Sync from MyFlightbook"}
                </button>
                <span style={{fontSize:11,color:"var(--green,#00E676)",fontFamily:"var(--mono)"}}>Linked</span>
                <button onClick={unlinkMFB} style={{background:"none",border:"none",color:"var(--muted)",fontSize:10,cursor:"pointer",textDecoration:"underline",fontFamily:"var(--mono)"}}>Unlink</button>
              </div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:8,lineHeight:1.5}}>Pull your latest flights and auto-fill all currency dates.</div>
            </div>
          ):(
            <div>
              <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:10}}>Connect your MyFlightbook account to automatically sync flights and keep your currency dates up to date.</div>
              <button className="btn btn-sm" onClick={linkMyFlightbook} style={{borderColor:"#4FC3F7",color:"#4FC3F7"}}>
                Link MyFlightbook
              </button>
            </div>
          )}
        </div>

        {/* IMPORT LOGBOOK — CSV upload to backend */}
        <div className="card" style={{marginBottom:20,padding:"16px 18px",borderLeft:"3px solid var(--amber)"}}>
          <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--text3)",marginBottom:10,textTransform:"uppercase"}}>Import Logbook</div>
          <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:12}}>Upload a CSV export from your logbook app. Dates will be extracted and previewed before applying.</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
            {["ForeFlight Export","MyFlightbook Export","LogTen Pro Export"].map(f=>(
              <span key={f} style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.08em",padding:"4px 10px",borderRadius:3,border:"1px solid var(--border)",color:"var(--text3)",background:"var(--surface)"}}>{f}</span>
            ))}
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <label className="btn btn-sm" style={{borderColor:"var(--amber)",color:"var(--amber)",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4}}>
              {csvUploading?"Uploading...":"Select CSV File"}
              <input type="file" accept=".csv" style={{display:"none"}} onChange={handleBackendCSVImport} disabled={csvUploading}/>
            </label>
          </div>

          {/* Preview */}
          {csvPreview&&(
            <div style={{marginTop:14,padding:"12px 14px",background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:4}}>
              <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.12em",color:"var(--amber)",marginBottom:10,textTransform:"uppercase"}}>Preview Extracted Dates</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 16px",fontFamily:"var(--mono)",fontSize:11}}>
                {csvPreview.lastBFR&&<div><span style={{color:"var(--text3)"}}>Flight Review:</span> <span style={{color:"var(--text)"}}>{csvPreview.lastBFR}</span></div>}
                {csvPreview.lastDayLanding&&<div><span style={{color:"var(--text3)"}}>Day T&L 1:</span> <span style={{color:"var(--text)"}}>{csvPreview.lastDayLanding}</span></div>}
                {csvPreview.dayLanding2&&<div><span style={{color:"var(--text3)"}}>Day T&L 2:</span> <span style={{color:"var(--text)"}}>{csvPreview.dayLanding2}</span></div>}
                {csvPreview.dayLanding3&&<div><span style={{color:"var(--text3)"}}>Day T&L 3:</span> <span style={{color:"var(--text)"}}>{csvPreview.dayLanding3}</span></div>}
                {csvPreview.lastNightLanding&&<div><span style={{color:"var(--text3)"}}>Night Ldg 1:</span> <span style={{color:"var(--text)"}}>{csvPreview.lastNightLanding}</span></div>}
                {csvPreview.nightLanding2&&<div><span style={{color:"var(--text3)"}}>Night Ldg 2:</span> <span style={{color:"var(--text)"}}>{csvPreview.nightLanding2}</span></div>}
                {csvPreview.nightLanding3&&<div><span style={{color:"var(--text3)"}}>Night Ldg 3:</span> <span style={{color:"var(--text)"}}>{csvPreview.nightLanding3}</span></div>}
                {csvPreview.nightTakeoff1&&<div><span style={{color:"var(--text3)"}}>Night T/O 1:</span> <span style={{color:"var(--text)"}}>{csvPreview.nightTakeoff1}</span></div>}
                {csvPreview.nightTakeoff2&&<div><span style={{color:"var(--text3)"}}>Night T/O 2:</span> <span style={{color:"var(--text)"}}>{csvPreview.nightTakeoff2}</span></div>}
                {csvPreview.nightTakeoff3&&<div><span style={{color:"var(--text3)"}}>Night T/O 3:</span> <span style={{color:"var(--text)"}}>{csvPreview.nightTakeoff3}</span></div>}
                {csvPreview.lastIFR&&<div><span style={{color:"var(--text3)"}}>IFR:</span> <span style={{color:"var(--text)"}}>{csvPreview.lastIFR}</span></div>}
                {csvPreview.ifrApproaches&&<div><span style={{color:"var(--text3)"}}>Approaches:</span> <span style={{color:"var(--text)"}}>{csvPreview.ifrApproaches}</span></div>}
                {csvPreview.ifrHolds&&<div><span style={{color:"var(--text3)"}}>Holds:</span> <span style={{color:"var(--text)"}}>{csvPreview.ifrHolds}</span></div>}
                {csvPreview.lastMedical&&<div><span style={{color:"var(--text3)"}}>Medical:</span> <span style={{color:"var(--text)"}}>{csvPreview.lastMedical}</span></div>}
                {csvPreview.medicalClass&&<div><span style={{color:"var(--text3)"}}>Med Class:</span> <span style={{color:"var(--text)"}}>{csvPreview.medicalClass}</span></div>}
              </div>
              <div style={{display:"flex",gap:8,marginTop:12}}>
                <button className="btn btn-sm btn-p" onClick={applyCsvPreview} style={{borderColor:"var(--green)",color:"var(--green)"}}>Apply to Currency</button>
                <button className="btn btn-sm" onClick={()=>setCsvPreview(null)} style={{color:"var(--muted)"}}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* CSV Import (legacy client-side) — collapsible */}
        {importMode!=="manual"&&(
          <div className="card" style={{marginBottom:20,padding:"16px 18px",borderLeft:"3px solid var(--border)"}}>
            <div style={{fontSize:11,color:"var(--text3)",lineHeight:1.6,marginBottom:10}}>Or use client-side CSV parsing (ForeFlight / MyFlightbook only):</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <label className="btn btn-sm" style={{borderColor:"var(--border)",color:"var(--text3)",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4}}>
                ForeFlight CSV
                <input type="file" accept=".csv" style={{display:"none"}} onChange={e=>handleCSVImport(e,parseForeFlight)}/>
              </label>
              <label className="btn btn-sm" style={{borderColor:"var(--border)",color:"var(--text3)",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4}}>
                MyFlightbook CSV
                <input type="file" accept=".csv" style={{display:"none"}} onChange={e=>handleCSVImport(e,parseMyFlightbook)}/>
              </label>
              <button className="btn btn-sm" onClick={()=>setImportMode("manual")} style={{color:"var(--muted)"}}>Enter manually</button>
            </div>
          </div>
        )}

        {/* FAR 61.56 Flight Review */}
        <div className="section-label" style={{marginTop:4}}>Flight Review</div>
        <div style={{marginBottom:12}}>
          <div className="inpl">Last flight review date</div>
          <DateInput value={dates.lastBFR} onChange={v=>set("lastBFR",v)} style={{maxWidth:240}}/>
        </div>
        <CurrencyRow label="Flight Review" far="61.56" status={bfrStatus} daysAgo={bfrDays} windowDays={bfrWindow} note={bfrNote}
          detail="Required every 24 calendar months with a CFI: minimum 1 hour ground and 1 hour flight. A lapsed BFR means no PIC privileges until completed."/>

        {/* FAR 61.57a Day Recency */}
        <div className="section-label" style={{marginTop:20}}>Day Passenger Currency <span style={{fontWeight:400,color:"var(--muted)"}}>(FAR 61.57a)</span></div>
        <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>3 takeoffs and landings in the preceding 90 days. Enter your 3 most recent. Touch-and-go counts for day.</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
          <div>
            <div className="inpl">Most recent T&L</div>
            <DateInput value={dates.lastDayLanding} onChange={v=>set("lastDayLanding",v)}/>
          </div>
          <div>
            <div className="inpl">2nd T&L</div>
            <DateInput value={dates.dayLanding2||""} onChange={v=>set("dayLanding2",v)}/>
          </div>
          <div>
            <div className="inpl">3rd T&L</div>
            <DateInput value={dates.dayLanding3||""} onChange={v=>set("dayLanding3",v)}/>
          </div>
        </div>
        <CurrencyRow label="VFR Day Passengers" far="61.57a" status={dayStatus} daysAgo={dayDays} windowDays={90} note={dayNote}
          detail="3 takeoffs and 3 landings within the preceding 90 days to carry passengers. Currency is based on the oldest of your 3 entries."/>

        {/* FAR 61.57b Night Recency */}
        <div className="section-label" style={{marginTop:20}}>Night Passenger Currency <span style={{fontWeight:400,color:"var(--muted)"}}>(FAR 61.57b)</span></div>
        <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>3 full-stop night landings AND 3 night takeoffs in 90 days. Touch-and-go does not count for night.</div>
        <div style={{fontSize:10,color:"var(--amber)",fontFamily:"var(--mono)",letterSpacing:"0.08em",marginBottom:6,marginTop:4}}>NIGHT TAKEOFFS</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
          <div>
            <div className="inpl">Most recent</div>
            <DateInput value={dates.nightTakeoff1||""} onChange={v=>set("nightTakeoff1",v)}/>
          </div>
          <div>
            <div className="inpl">2nd</div>
            <DateInput value={dates.nightTakeoff2||""} onChange={v=>set("nightTakeoff2",v)}/>
          </div>
          <div>
            <div className="inpl">3rd</div>
            <DateInput value={dates.nightTakeoff3||""} onChange={v=>set("nightTakeoff3",v)}/>
          </div>
        </div>
        <div style={{fontSize:10,color:"var(--amber)",fontFamily:"var(--mono)",letterSpacing:"0.08em",marginBottom:6}}>NIGHT FULL-STOP LANDINGS</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
          <div>
            <div className="inpl">Most recent</div>
            <DateInput value={dates.lastNightLanding} onChange={v=>set("lastNightLanding",v)}/>
          </div>
          <div>
            <div className="inpl">2nd</div>
            <DateInput value={dates.nightLanding2||""} onChange={v=>set("nightLanding2",v)}/>
          </div>
          <div>
            <div className="inpl">3rd</div>
            <DateInput value={dates.nightLanding3||""} onChange={v=>set("nightLanding3",v)}/>
          </div>
        </div>
        <CurrencyRow label="VFR Night Passengers" far="61.57b" status={nightStatus} daysAgo={nightDays} windowDays={90} note={nightNote}
          detail="3 takeoffs AND 3 full-stop landings at night within the preceding 90 days. Currency is based on the oldest of your 3 entries. Both takeoffs and landings must be satisfied."/>

        {/* FAR 61.23 Medical */}
        <div className="section-label" style={{marginTop:20}}>Medical Certificate</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
          <div>
            <div className="inpl">Medical class</div>
            <select className="sel" value={dates.medicalClass} onChange={e=>set("medicalClass",e.target.value)}>
              <option value="1st">1st Class</option>
              <option value="2nd">2nd Class</option>
              <option value="3rd">3rd Class</option>
            </select>
          </div>
          <div>
            <div className="inpl">Date of medical exam</div>
            <DateInput value={dates.lastMedical} onChange={v=>set("lastMedical",v)}/>
          </div>
          <div>
            <div className="inpl">Date of birth</div>
            <DateInput value={dates.dob} onChange={v=>set("dob",v)}/>
          </div>
        </div>
        <CurrencyRow label="Medical Certificate" far="61.23" status={medStatus} daysAgo={medDays} windowDays={medWindow} note={medNote}
          detail={`1st class: 12 months (under 40) or 6 months (40+). 2nd class: 12 months. 3rd class: 60 months (under 40) or 24 months (40+). No valid medical = no PIC. Enter your DOB for accurate age-based calculation.`}/>

        {/* FAR 61.57c IFR */}
        <div className="section-label" style={{marginTop:20}}>Instrument Currency</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
          <div>
            <div className="inpl">Last IFR currency date</div>
            <DateInput value={dates.lastIFR} onChange={v=>set("lastIFR",v)}/>
          </div>
          <div>
            <div className="inpl">Approaches (6-month window)</div>
            <input type="number" min="0" max="99" className="inp" value={dates.ifrApproaches} onChange={e=>set("ifrApproaches",e.target.value)} placeholder="0"/>
          </div>
          <div>
            <div className="inpl">Holds completed</div>
            <input type="number" min="0" max="99" className="inp" value={dates.ifrHolds} onChange={e=>set("ifrHolds",e.target.value)} placeholder="0"/>
          </div>
        </div>
        <CurrencyRow label="IFR Currency" far="61.57c" status={ifrStatus} daysAgo={ifrDays} windowDays={180} note={ifrNote}
          detail="Within the preceding 6 calendar months: 6 instrument approaches, holding procedures, and intercepting/tracking courses. If lapsed, an IPC with a CFII is required before acting as PIC in IMC."/>

        {/* Tailwheel */}
        <div className="section-label" style={{marginTop:20}}>Tailwheel</div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
          <div onClick={()=>set("tailwheel",!dates.tailwheel)} style={{
            width:40,height:22,borderRadius:11,cursor:"pointer",transition:"background .2s",
            background:dates.tailwheel?"var(--amber)":"var(--border)",
            position:"relative",flexShrink:0,
          }}>
            <div style={{
              position:"absolute",top:3,left:dates.tailwheel?20:3,width:16,height:16,
              borderRadius:"50%",background:"var(--surface)",transition:"left .2s",
            }}/>
          </div>
          <span style={{fontSize:13,color:"var(--text3)"}}>I fly a tailwheel aircraft</span>
        </div>
        {dates.tailwheel&&(
          <div style={{marginBottom:12}}>
            <div className="inpl">Last full-stop tailwheel landing</div>
            <DateInput value={dates.lastTailwheelLanding} onChange={v=>set("lastTailwheelLanding",v)} style={{maxWidth:240}}/>
          </div>
        )}
        {dates.tailwheel&&(
          <CurrencyRow label="Tailwheel Currency" far="61.57a" status={twStatus} daysAgo={twDays} windowDays={90} note={twNote}
            detail="3 full-stop landings in a tailwheel aircraft within the preceding 90 days to carry passengers. Touch-and-go does not satisfy the full-stop requirement."/>
        )}

        <button className="btn btn-p" onClick={saveCurrency} style={{width:"100%",marginTop:16,marginBottom:8,padding:"12px",fontSize:13,letterSpacing:"0.02em",opacity:currSaved?0.7:1}}>
          {currSaved?"Saved to your profile ✓":"Save currency to profile"}
        </button>
        <div style={{marginTop:8,padding:"12px 16px",background:"var(--surface2)",borderRadius:4,fontSize:12,color:"var(--muted)",lineHeight:1.7}}>
          Advisory only. Always verify currency against your actual logbook entries and current FARs. Sign in to save your dates across sessions.
        </div>
      </div>
    </div>
  );
}

// ─── NTSB ─────────────────────────────────────────────────────────────────────
function NTSB({lastQuery}){
  const [sel,setSel]=useState(null);
  const [search,setSearch]=useState(lastQuery||"");
  const [fatalFilter,setFatalFilter]=useState("all"); // "all"|"fatal"|"nonfatal"
  const [catFilter,setCatFilter]=useState("all");

  const cats=["all",...Array.from(new Set(INCIDENTS.map(i=>i.cat))).sort()];

  const list=INCIDENTS.filter(inc=>{
    if(fatalFilter==="fatal"&&!inc.fatal)return false;
    if(fatalFilter==="nonfatal"&&inc.fatal)return false;
    if(catFilter!=="all"&&inc.cat!==catFilter)return false;
    if(search.length>1){
      const q=search.toLowerCase();
      return inc.cat.toLowerCase().includes(q)||
        inc.ac.toLowerCase().includes(q)||
        inc.cause.toLowerCase().includes(q)||
        inc.lessons.some(l=>l.toLowerCase().includes(q))||
        inc.phase.toLowerCase().includes(q)||
        inc.id.toLowerCase().includes(q);
    }
    return true;
  });

  const fatalCount=INCIDENTS.filter(i=>i.fatal).length;

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{flexShrink:0,padding:"20px 24px",borderBottom:"1px solid var(--border)"}}>
        <div className="page-title" style={{marginBottom:4}}>NTSB Cases</div>
        <div className="page-sub" style={{marginBottom:16}}>Study the pattern before you become one. {INCIDENTS.length} cases, {fatalCount} fatal.</div>
        <input className="inp" value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search by keyword, aircraft, phase, or accident type..."
          style={{marginBottom:10}}/>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {[{k:"all",l:"All"},{k:"fatal",l:"Fatal"},{k:"nonfatal",l:"Non-Fatal"}].map(f=>(
            <button key={f.k} onClick={()=>setFatalFilter(f.k)}
              style={{padding:"4px 12px",fontSize:11,fontWeight:600,letterSpacing:"0.08em",
                borderRadius:20,cursor:"pointer",border:"1px solid",transition:"all .12s",
                background:fatalFilter===f.k?(f.k==="fatal"?"var(--red)":f.k==="nonfatal"?"var(--amber)":"var(--amber)"):"transparent",
                color:fatalFilter===f.k?"#0a0a0a":"var(--muted)",
                borderColor:fatalFilter===f.k?"transparent":"var(--border)"}}>
              {f.l}
            </button>
          ))}
          <div style={{width:1,background:"var(--border)",margin:"2px 4px"}}/>
          <select className="sel" value={catFilter} onChange={e=>setCatFilter(e.target.value)}
            style={{padding:"4px 10px",fontSize:11,height:"auto"}}>
            {cats.map(c=><option key={c} value={c}>{c==="all"?"All categories":c}</option>)}
          </select>
        </div>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"0 24px 28px"}}>
        {sel?(
          <>
            <button className="btn btn-sm" onClick={()=>setSel(null)} style={{margin:"16px 0"}}>All cases</button>
            <div className="card" style={{borderLeft:`3px solid ${sel.fatal?"var(--red)":"var(--amber)"}`}}>
              <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:sel.fatal?"var(--red)":"var(--amber)"}}>{sel.fatal?"Fatal":"Non-Fatal"}</span>
                <span style={{fontSize:11,color:"var(--muted)",fontFamily:"'JetBrains Mono',monospace"}}>{sel.id}</span>
                <span style={{fontSize:11,color:"var(--muted)"}}>{sel.ac} - {sel.phase}</span>
              </div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:"var(--text)",marginBottom:10}}>{sel.cat}</div>
              <div style={{fontSize:14,color:"var(--text3)",lineHeight:1.75,marginBottom:20}}>{sel.cause}</div>
              <div className="section-label">Lessons</div>
              {sel.lessons.map((l,i)=>(
                <div key={i} style={{display:"flex",gap:12,paddingBottom:12,marginBottom:12,borderBottom:"1px solid var(--border2)"}}>
                  <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11,color:"var(--amber)",flexShrink:0,marginTop:2}}>{String(i+1).padStart(2,"0")}</span>
                  <span style={{fontSize:14,color:"var(--text2)",lineHeight:1.65}}>{l}</span>
                </div>
              ))}
            </div>
          </>
        ):(
          <>
            {list.length===0&&(
              <div style={{padding:"40px 0",textAlign:"center",color:"var(--muted)",fontSize:14}}>No cases match your filters.</div>
            )}
            {list.map((inc,i)=>(
              <div key={i} className={`case-card ${inc.fatal?"fatal":"nonfatal"}`} onClick={()=>setSel(inc)}
                style={{marginTop:i===0?16:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:700,color:"var(--text)"}}>{inc.cat}</div>
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:inc.fatal?"var(--red)":"var(--amber)",flexShrink:0,marginLeft:12}}>{inc.fatal?"Fatal":"Non-Fatal"}</span>
                </div>
                <div style={{fontSize:12,color:"var(--muted)",fontFamily:"'JetBrains Mono',monospace",marginBottom:6}}>{inc.ac} - {inc.phase} - {inc.id}</div>
                <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.5}}>{inc.cause.slice(0,140)}{inc.cause.length>140?"...":""}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── REGS ─────────────────────────────────────────────────────────────────────
function Regs(){
  const [q,setQ]=useState("");
  const results=q.length>1?Object.entries(FARS).filter(([k,v])=>k.includes(q)||v.title.toLowerCase().includes(q.toLowerCase())||v.body.toLowerCase().includes(q.toLowerCase())):Object.entries(FARS);
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{flexShrink:0,padding:"20px 24px",borderBottom:"1px solid var(--border)"}}>
        <div className="page-title" style={{marginBottom:12}}>FAR / AIM Reference</div>
        <input className="inp" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search by part number or keyword..."/>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"0 24px 28px"}}>
        {results.map(([k,v])=>(
          <div key={k} style={{paddingTop:20,paddingBottom:20,borderBottom:"1px solid var(--border2)"}}>
            <div style={{display:"flex",gap:14,alignItems:"baseline",marginBottom:6}}>
              <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:13,fontWeight:600,color:"var(--amber)",flexShrink:0}}>FAR {k}</span>
              <span style={{fontSize:13,fontWeight:600,color:"var(--text2)"}}>{v.title}</span>
            </div>
            <div style={{fontSize:14,color:"var(--text3)",lineHeight:1.65}}>{v.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CFI INSTRUCTOR VIEW ─────────────────────────────────────────────────────
function InstructorView({backendFetch}){
  const [roster,setRoster]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selectedStudent,setSelectedStudent]=useState(null);
  const [briefs,setBriefs]=useState([]);
  const [patterns,setPatterns]=useState(null);
  const [notes,setNotes]=useState([]);
  const [newNote,setNewNote]=useState('');
  const [inviteEmail,setInviteEmail]=useState('');
  const [inviteResult,setInviteResult]=useState(null);
  const [briefsLoading,setBriefsLoading]=useState(false);

  useEffect(()=>{
    backendFetch('/cfi/roster').then(d=>{setRoster(Array.isArray(d)?d:[]);setLoading(false);}).catch(()=>setLoading(false));
  },[]);

  function selectStudent(s){
    setSelectedStudent(s);setBriefsLoading(true);setBriefs([]);setPatterns(null);setNotes([]);
    Promise.all([
      backendFetch('/cfi/student/'+s.student_user_id+'/briefs'),
      backendFetch('/cfi/student/'+s.student_user_id+'/patterns'),
      backendFetch('/cfi/student/'+s.student_user_id+'/notes'),
    ]).then(([b,p,n])=>{
      setBriefs(Array.isArray(b)?b:[]);setPatterns(p);setNotes(Array.isArray(n)?n:[]);setBriefsLoading(false);
    }).catch(()=>setBriefsLoading(false));
  }

  async function sendInvite(){
    if(!inviteEmail.trim())return;
    const r=await backendFetch('/cfi/invite',{method:'POST',body:JSON.stringify({student_email:inviteEmail.trim()})});
    setInviteResult(r);setInviteEmail('');
    backendFetch('/cfi/roster').then(d=>setRoster(Array.isArray(d)?d:[]));
  }

  async function saveNote(){
    if(!newNote.trim()||!selectedStudent)return;
    await backendFetch('/cfi/student/'+selectedStudent.student_user_id+'/notes',{method:'POST',body:JSON.stringify({note:newNote.trim()})});
    setNewNote('');
    const n=await backendFetch('/cfi/student/'+selectedStudent.student_user_id+'/notes');
    setNotes(Array.isArray(n)?n:[]);
  }

  const sevColor=(sev)=>sev==='HIGH'?'var(--red)':sev==='REVIEW'?'var(--amber)':'var(--green)';

  // ── Roster view ──
  if(!selectedStudent){
    return(<div className="page"><div className="page-inner">
      <div className="page-title">Instructor Dashboard</div>
      <div className="page-sub">Your linked students and their pre-flight briefing activity.</div>

      {/* Invite */}
      <div style={{display:'flex',gap:8,marginBottom:20}}>
        <input className="inp" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="Student email" style={{flex:1,fontSize:14,padding:'10px 14px'}}/>
        <button className="btn btn-p" onClick={sendInvite} style={{padding:'10px 20px',fontSize:12}}>Invite</button>
      </div>
      {inviteResult&&inviteResult.ok&&<div style={{fontSize:12,color:'var(--green)',marginBottom:12}}>Invite sent.{inviteResult.invite_code?' Share code: '+inviteResult.invite_code:''}</div>}

      {loading?<div style={{color:'var(--muted)',fontSize:12}}>Loading roster...</div>:(
        roster.length===0?<div style={{color:'var(--text3)',fontSize:13,padding:20,textAlign:'center'}}>No linked students yet. Invite a student to get started.</div>:(
          roster.map((s,i)=>(
            <div key={i} className="card" style={{marginBottom:8,padding:'14px 16px',cursor:s.status==='active'?'pointer':'default',opacity:s.status==='pending'?0.6:1,borderLeft:'3px solid '+(s.status==='active'?'var(--green)':'var(--amber)')}}
              onClick={()=>s.status==='active'&&selectStudent(s)}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontFamily:'var(--mono)',fontSize:13,fontWeight:600,color:'var(--text)'}}>{s.student_email||'Pending signup'}</div>
                  <div style={{fontFamily:'var(--mono)',fontSize:10,color:'var(--text3)',marginTop:2}}>
                    {s.status==='pending'?'Awaiting student acceptance':'Last brief: '+(s.last_brief?new Date(s.last_brief).toLocaleDateString():'None')+' · '+s.briefs_30d+' briefs (30d)'}
                  </div>
                </div>
                <span style={{fontFamily:'var(--mono)',fontSize:9,fontWeight:700,letterSpacing:'0.1em',color:s.status==='active'?'var(--green)':'var(--amber)',textTransform:'uppercase'}}>{s.status}</span>
              </div>
            </div>
          ))
        )
      )}
    </div></div>);
  }

  // ── Student detail view ──
  return(<div className="page"><div className="page-inner">
    <button onClick={()=>setSelectedStudent(null)} style={{background:'none',border:'none',color:'var(--amber)',cursor:'pointer',fontFamily:'var(--mono)',fontSize:12,marginBottom:16,padding:0}}>&lt; Back to Roster</button>
    <div className="page-title" style={{fontSize:18}}>{selectedStudent.student_email}</div>

    {briefsLoading?<div style={{color:'var(--muted)',fontSize:12,padding:20}}>Loading...</div>:<>

      {/* Decision Patterns */}
      {patterns&&patterns.total_briefs>0&&(
        <div className="card" style={{marginBottom:16,padding:'16px 18px'}}>
          <div style={{fontFamily:'var(--mono)',fontSize:9,fontWeight:700,letterSpacing:'0.12em',color:'var(--amber)',marginBottom:12}}>PATTERNS ({patterns.total_briefs} briefs over {patterns.period_days}d)</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12}}>
            {Object.entries(patterns.risk_distribution||{}).map(([k,v])=>(
              <div key={k} style={{textAlign:'center'}}>
                <div style={{fontFamily:'var(--mono)',fontSize:20,fontWeight:700,color:sevColor(k)}}>{v}</div>
                <div style={{fontFamily:'var(--mono)',fontSize:9,color:'var(--text3)',letterSpacing:'0.1em'}}>{k}</div>
              </div>
            ))}
          </div>
          {Object.keys(patterns.categories||{}).length>0&&(
            <div>
              <div style={{fontFamily:'var(--mono)',fontSize:9,color:'var(--text3)',marginBottom:6}}>RISK CATEGORIES SURFACED</div>
              {Object.entries(patterns.categories).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([cat,count])=>(
                <div key={cat} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:'1px solid var(--border)'}}>
                  <span style={{fontFamily:'var(--mono)',fontSize:11,color:'var(--text2)'}}>{cat}</span>
                  <span style={{fontFamily:'var(--mono)',fontSize:11,color:'var(--text3)'}}>{count}x</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Brief Timeline */}
      <div style={{fontFamily:'var(--mono)',fontSize:9,fontWeight:700,letterSpacing:'0.12em',color:'var(--amber)',marginBottom:10}}>BRIEF TIMELINE</div>
      {briefs.length===0?<div style={{color:'var(--text3)',fontSize:12,padding:16,textAlign:'center'}}>No briefs recorded yet.</div>:(
        briefs.map((b,i)=>(
          <div key={i} className="card" style={{marginBottom:6,padding:'12px 14px',borderLeft:'3px solid '+sevColor(b.risk_level)}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
              <span style={{fontFamily:'var(--mono)',fontSize:11,color:'var(--text)'}}>{new Date(b.created_at).toLocaleDateString()} {b.departure&&b.destination?b.departure+' → '+b.destination:''}</span>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                {b.pilot_action&&<span style={{fontFamily:'var(--mono)',fontSize:9,fontWeight:700,color:b.pilot_action==='nogo'?'var(--red)':b.pilot_action==='adjust'?'var(--amber)':'var(--green)',letterSpacing:'0.08em',textTransform:'uppercase'}}>{b.pilot_action}</span>}
                <span style={{fontFamily:'var(--mono)',fontSize:9,fontWeight:700,color:sevColor(b.risk_level),letterSpacing:'0.08em'}}>{b.risk_level}</span>
              </div>
            </div>
            <div style={{fontFamily:'var(--mono)',fontSize:10,color:'var(--text3)'}}>
              Risk: {b.risk_score} · {b.risk_driver||'No driver'}{b.weather_category?' · Wx: '+b.weather_category:''}{b.wb_severity&&b.wb_severity!=='OK'?' · W&B: '+b.wb_severity:''}{b.notam_high_count>0?' · '+b.notam_high_count+' HIGH NOTAMs':''}
            </div>
            {b.risk_factors&&b.risk_factors.length>0&&(
              <div style={{marginTop:6}}>
                {b.risk_factors.slice(0,4).map((f,j)=>(
                  <div key={j} style={{fontFamily:'var(--mono)',fontSize:10,color:f.severity==='HIGH'?'var(--red)':'var(--text3)',lineHeight:1.5}}>
                    {f.severity==='HIGH'?'\u2717':f.severity==='CAUTION'?'\u26A0':'\u2713'} {f.name}: {f.detail?.slice(0,80)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {/* CFI Notes (private) */}
      <div style={{marginTop:20}}>
        <div style={{fontFamily:'var(--mono)',fontSize:9,fontWeight:700,letterSpacing:'0.12em',color:'var(--amber)',marginBottom:10}}>YOUR NOTES <span style={{color:'var(--text3)',fontWeight:400}}>— private to you</span></div>
        <div style={{display:'flex',gap:8,marginBottom:12}}>
          <textarea className="inp" value={newNote} onChange={e=>setNewNote(e.target.value)} placeholder="Add a note about this student..." rows={2} style={{flex:1,fontSize:12,resize:'vertical',fontFamily:'var(--sans)'}} maxLength={2000}/>
          <button className="btn btn-p" onClick={saveNote} disabled={!newNote.trim()} style={{padding:'8px 16px',fontSize:11,alignSelf:'flex-end'}}>Save</button>
        </div>
        {notes.map((n,i)=>(
          <div key={i} className="card" style={{marginBottom:6,padding:'10px 14px'}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span style={{fontFamily:'var(--mono)',fontSize:10,color:'var(--text3)'}}>{new Date(n.created_at).toLocaleDateString()}</span>
            </div>
            <div style={{fontSize:12,color:'var(--text2)',lineHeight:1.6}}>{n.note}</div>
          </div>
        ))}
      </div>
    </>}
  </div></div>);
}

// ─── PERSONAL MINIMUMS (web settings) ────────────────────────────────────────
function PersonalMinimumsWeb({backendFetch}){
  const [mins,setMins]=useState(()=>{try{const s=localStorage.getItem('solus_personal_minimums');return s?JSON.parse(s):{};}catch{return {};}});
  const [saved,setSaved]=useState(false);

  useEffect(()=>{
    backendFetch('/user/minimums').then(d=>{if(d&&typeof d==='object'&&!d.error){setMins(prev=>({...prev,...d}));localStorage.setItem('solus_personal_minimums',JSON.stringify({...mins,...d}));}}).catch(()=>{});
  },[]);

  const update=(k,v)=>{setMins(prev=>({...prev,[k]:v===''?null:parseFloat(v)}));setSaved(false);};

  const save=()=>{
    localStorage.setItem('solus_personal_minimums',JSON.stringify(mins));
    backendFetch('/user/minimums',{method:'POST',body:JSON.stringify(mins)}).catch(()=>{});
    setSaved(true);setTimeout(()=>setSaved(false),2000);
  };

  const fields=[
    {k:'ceilingDay',l:'Day ceiling min',u:'ft AGL',p:'3000'},
    {k:'visibilityDay',l:'Day visibility min',u:'SM',p:'5'},
    {k:'ceilingNight',l:'Night ceiling min',u:'ft AGL',p:'4000'},
    {k:'visibilityNight',l:'Night visibility min',u:'SM',p:'7'},
    {k:'crosswind',l:'Max crosswind',u:'kts',p:'12'},
    {k:'tailwind',l:'Max tailwind',u:'kts',p:'5'},
    {k:'maxDensityAlt',l:'Max density altitude',u:'ft',p:'7000'},
    {k:'minRunwayLength',l:'Min runway length',u:'ft',p:'3000'},
    {k:'maxFlightDuration',l:'Max flight duration',u:'hrs',p:'4'},
  ];

  return(<>
    <div className="section-label">Personal Minimums</div>
    <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
      <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:14,lineHeight:1.6}}>
        Set your self-imposed weather and operational limits. SOLUS checks these against conditions in every mission brief.
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 16px"}}>
        {fields.map(f=>(
          <div key={f.k}>
            <div className="inpl">{f.l}</div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <input className="inp" type="number" value={mins[f.k]!=null?mins[f.k]:''} onChange={e=>update(f.k,e.target.value)} placeholder={f.p} style={{width:90,fontSize:14,padding:"8px 10px",textAlign:"center"}}/>
              <span style={{fontSize:11,color:"var(--text3)"}}>{f.u}</span>
            </div>
          </div>
        ))}
      </div>
      <button className="btn btn-p" onClick={save} style={{width:"100%",marginTop:14,padding:"11px",fontSize:12,background:saved?"var(--green)":"var(--amber)"}}>{saved?"Saved":"Save Minimums"}</button>
    </div>
  </>);
}

// ─── LINKED INSTRUCTORS (student-side, web) ──────────────────────────────────
function LinkedInstructorsWeb({backendFetch}){
  const [links,setLinks]=useState([]);
  const [inviteCode,setInviteCode]=useState('');
  const [showWhat,setShowWhat]=useState(false);
  const [whatData,setWhatData]=useState(null);

  useEffect(()=>{
    backendFetch('/cfi/my-links').then(d=>setLinks(Array.isArray(d)?d:[])).catch(()=>{});
  },[]);

  async function acceptLink(linkId){
    const r=await backendFetch('/cfi/accept-invite',{method:'POST',body:JSON.stringify({link_id:linkId})});
    if(r?.ok)setLinks(prev=>prev.map(l=>l.id===linkId?{...l,status:'active'}:l));
  }
  async function revokeLink(linkId){
    if(!window.confirm('Revoke this instructor link? They will immediately lose access to your briefs.'))return;
    await backendFetch('/cfi/revoke',{method:'POST',body:JSON.stringify({link_id:linkId})});
    setLinks(prev=>prev.filter(l=>l.id!==linkId));
  }
  async function acceptByCode(){
    if(!inviteCode.trim())return;
    const r=await backendFetch('/cfi/accept-invite',{method:'POST',body:JSON.stringify({invite_code:inviteCode.trim()})});
    if(r?.ok){setInviteCode('');backendFetch('/cfi/my-links').then(d=>setLinks(Array.isArray(d)?d:[]));}
    else alert(r?.error||'Invalid or expired code');
  }
  async function loadWhat(){
    const d=await backendFetch('/cfi/what-cfi-sees');
    setWhatData(d);setShowWhat(true);
  }

  return(<>
    <div className="section-label">Linked Instructors</div>
    <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
      {links.length===0?(
        <div style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--text3)",marginBottom:12}}>No instructors linked. Enter an invite code from your CFI to get started.</div>
      ):links.map((l,i)=>(
        <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
          <div>
            <div style={{fontFamily:"var(--mono)",fontSize:13,fontWeight:600,color:"var(--text)"}}>{l.cfi_email||'Instructor'}</div>
            <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)"}}>{l.org_name||''} · {l.status}</div>
          </div>
          {l.status==='pending'?(
            <button className="btn btn-sm" onClick={()=>acceptLink(l.id)} style={{borderColor:"var(--green)",color:"var(--green)",fontSize:10}}>Accept</button>
          ):(
            <button className="btn btn-sm" onClick={()=>revokeLink(l.id)} style={{borderColor:"var(--red)",color:"var(--red)",fontSize:10}}>Revoke</button>
          )}
        </div>
      ))}
      <div style={{display:"flex",gap:8,marginTop:12}}>
        <input className="inp" value={inviteCode} onChange={e=>setInviteCode(e.target.value)} placeholder="Enter invite code" style={{flex:1,fontSize:13,padding:"8px 12px"}}/>
        <button className="btn btn-p" onClick={acceptByCode} style={{padding:"8px 16px",fontSize:11}}>Link</button>
      </div>
      <div style={{marginTop:10}}>
        <span onClick={loadWhat} style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--amber)",cursor:"pointer",textDecoration:"underline"}}>What can my instructor see?</span>
      </div>
      {showWhat&&whatData&&(
        <div style={{marginTop:10,padding:12,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:4}}>
          <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,color:"var(--green)",letterSpacing:"0.1em",marginBottom:8}}>VISIBLE TO YOUR CFI</div>
          {whatData.visible?.map((v,i)=><div key={i} style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text2)",lineHeight:1.7}}>✓ {v}</div>)}
          <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,color:"var(--red)",letterSpacing:"0.1em",marginTop:12,marginBottom:8}}>NEVER VISIBLE</div>
          {whatData.never_visible?.map((v,i)=><div key={i} style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",lineHeight:1.7}}>✗ {v}</div>)}
          <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)",marginTop:10,fontStyle:"italic"}}>{whatData.note}</div>
        </div>
      )}
    </div>
  </>);
}

// ─── PILOT READINESS (IMSAFE self-check) ────────────────────────────────────
// Data stays in localStorage only (solus_readiness_*). Never sent to backend.
// Only severity label is shared with the brief. No answer values in analytics.
function PilotReadiness(){
  const defaultCheck={timestamp:'',sleepHours:null,sleepQuality:null,illness:false,medication:false,stress:null,alcohol8hr:false,fatigue:null,emotion:null,source:'manual',wearableData:null,skipped:false};
  const [check,setCheck]=useState(()=>{
    try{const s=localStorage.getItem('solus_readiness_today');return s?{...defaultCheck,...JSON.parse(s)}:{...defaultCheck,timestamp:new Date().toISOString()};}catch{return {...defaultCheck,timestamp:new Date().toISOString()};}
  });
  const [saved,setSaved]=useState(()=>!!localStorage.getItem('solus_readiness_today'));
  const [showTrends,setShowTrends]=useState(false);
  const [trendEnabled,setTrendEnabled]=useState(()=>{try{const s=localStorage.getItem('solus_readiness_settings');return s?JSON.parse(s).trendHistoryEnabled:false;}catch{return false;}});

  const update=(p)=>{setCheck(prev=>({...prev,...p}));setSaved(false);};

  // Local scoring (mirrors backend/middleware/readiness.js)
  function score(c){
    if(c.skipped)return{severity:'SKIPPED',flags:[]};
    const flags=[];
    if(c.sleepHours!=null&&c.sleepHours<5)flags.push({f:'RED',d:`You reported ${c.sleepHours} hours of sleep. Fatigue is a factor in your brief today.`});
    if(c.alcohol8hr)flags.push({f:'RED',d:'You reported alcohol within 8 hours. FAR 91.17 requires 8 hours bottle-to-throttle.'});
    if(c.illness)flags.push({f:'RED',d:'You reported illness symptoms. Consider whether they could impair your ability to act as PIC (FAR 61.53).'});
    if(c.fatigue==='high'&&(c.stress==='high'||(c.sleepHours!=null&&c.sleepHours<6)))flags.push({f:'RED',d:'You reported high fatigue combined with '+(c.stress==='high'?'high stress':'limited sleep')+'. Combined factors increase risk.'});
    if(c.sleepHours!=null&&c.sleepHours>=5&&c.sleepHours<=6)flags.push({f:'YELLOW',d:`You reported ${c.sleepHours} hours of sleep. Below typical rest threshold.`});
    if(c.sleepQuality==='poor')flags.push({f:'YELLOW',d:'You reported poor sleep quality.'});
    if(c.stress==='high'&&c.fatigue!=='high')flags.push({f:'YELLOW',d:'You reported high stress.'});
    if(c.fatigue==='high'&&c.stress!=='high'&&!(c.sleepHours!=null&&c.sleepHours<6))flags.push({f:'YELLOW',d:'You reported high fatigue.'});
    if(c.medication)flags.push({f:'YELLOW',d:'You reported taking medication. Consider whether it could impair.'});
    if(c.emotion==='upset')flags.push({f:'YELLOW',d:'You reported elevated emotional state.'});
    if(c.fatigue==='moderate'&&c.stress==='moderate')flags.push({f:'YELLOW',d:'Moderate fatigue and moderate stress. Combined factors warrant attention.'});
    const hasR=flags.some(f=>f.f==='RED'),hasY=flags.some(f=>f.f==='YELLOW');
    return{severity:hasR?'HIGH':hasY?'CAUTION':'OK',flags};
  }

  const result=score(check);
  const sevColor=result.severity==='HIGH'?'var(--red)':result.severity==='CAUTION'?'var(--amber)':'var(--green)';

  function saveCheck(){
    const toSave={...check,timestamp:new Date().toISOString()};
    localStorage.setItem('solus_readiness_today',JSON.stringify(toSave));
    setSaved(true);
    // Trend history (opt-in)
    if(trendEnabled&&!toSave.skipped){
      try{const h=JSON.parse(localStorage.getItem('solus_readiness_history')||'[]');h.push(toSave);localStorage.setItem('solus_readiness_history',JSON.stringify(h.slice(-200)));}catch{}
    }
    // Analytics: completion only, never content
    if(typeof trackEvent==='function')trackEvent('self_assessment_completed',{skipped:false});
  }
  function skipCheck(){
    const skipped={...defaultCheck,timestamp:new Date().toISOString(),skipped:true};
    localStorage.setItem('solus_readiness_today',JSON.stringify(skipped));
    setCheck(skipped);setSaved(true);
    if(typeof trackEvent==='function')trackEvent('self_assessment_completed',{skipped:true});
  }

  function TriSelect({value,options,onChange}){
    return(<div style={{display:'flex',gap:6}}>{options.map(o=>(
      <button key={o.key} onClick={()=>onChange(o.key)} style={{flex:1,padding:'12px 8px',border:`1px solid ${value===o.key?'var(--amber)':'var(--border)'}`,borderRadius:4,background:value===o.key?'var(--amberDim)':'var(--bg)',color:value===o.key?'var(--amber)':'var(--text3)',fontFamily:'var(--mono)',fontSize:13,fontWeight:value===o.key?700:400,cursor:'pointer',minHeight:44}}>{o.label}</button>
    ))}</div>);
  }
  function Toggle({value,label,onToggle}){
    return(<div onClick={onToggle} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 4px',borderBottom:'1px solid var(--border)',cursor:'pointer',minHeight:48}}>
      <div style={{width:22,height:22,borderRadius:11,border:`2px solid ${value?'var(--red)':'var(--border)'}`,background:value?'var(--red)':'transparent'}}/>
      <span style={{fontSize:14,color:value?'var(--text)':'var(--text2)',fontFamily:'var(--mono)',flex:1}}>{label}</span>
    </div>);
  }

  const hasInput=check.sleepHours!=null||check.stress||check.fatigue||check.illness||check.medication||check.alcohol8hr||check.emotion;

  return(<div className="page"><div className="page-inner">
    <div className="page-title">Pre-Flight Readiness</div>
    <div className="page-sub">IMSAFE self-check. Private to you. 30 seconds.</div>

    <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.12em',color:'var(--amber)',marginBottom:8,marginTop:16}}>SLEEP</div>
    <div className="card" style={{marginBottom:8}}>
      <div className="inpl">Hours of sleep last night</div>
      <input type="number" min="0" max="24" step="0.5" placeholder="e.g. 7" value={check.sleepHours!=null?check.sleepHours:''} onChange={e=>update({sleepHours:e.target.value?parseFloat(e.target.value):null})} className="inp" style={{width:100,fontSize:16,padding:'10px 12px',textAlign:'center'}}/>
      <div className="inpl" style={{marginTop:12}}>Rest quality</div>
      <TriSelect value={check.sleepQuality} options={[{key:'good',label:'Good'},{key:'fair',label:'Fair'},{key:'poor',label:'Poor'}]} onChange={v=>update({sleepQuality:v})}/>
    </div>

    <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.12em',color:'var(--amber)',marginBottom:8,marginTop:16}}>IMSAFE CHECK</div>
    <div className="card" style={{marginBottom:8}}>
      <Toggle value={check.illness} label="Illness — any symptoms today" onToggle={()=>update({illness:!check.illness})}/>
      <Toggle value={check.medication} label="Medication — taking anything that could impair" onToggle={()=>update({medication:!check.medication})}/>
      <Toggle value={check.alcohol8hr} label="Alcohol — any within the last 8 hours" onToggle={()=>update({alcohol8hr:!check.alcohol8hr})}/>
    </div>

    <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.12em',color:'var(--amber)',marginBottom:8,marginTop:16}}>STATE</div>
    <div className="card" style={{marginBottom:8}}>
      <div className="inpl">Stress level</div>
      <TriSelect value={check.stress} options={[{key:'low',label:'Low'},{key:'moderate',label:'Moderate'},{key:'high',label:'High'}]} onChange={v=>update({stress:v})}/>
      <div className="inpl" style={{marginTop:12}}>Fatigue level</div>
      <TriSelect value={check.fatigue} options={[{key:'low',label:'Low'},{key:'moderate',label:'Moderate'},{key:'high',label:'High'}]} onChange={v=>update({fatigue:v})}/>
      <div className="inpl" style={{marginTop:12}}>Emotional state</div>
      <TriSelect value={check.emotion} options={[{key:'stable',label:'Stable'},{key:'distracted',label:'Distracted'},{key:'upset',label:'Upset'}]} onChange={v=>update({emotion:v})}/>
    </div>

    {!check.skipped&&hasInput&&(
      <div className="card" style={{borderLeft:`3px solid ${sevColor}`,background:result.severity==='HIGH'?'var(--redDim)':result.severity==='CAUTION'?'var(--amberDim)':'var(--greenDim)',marginTop:12}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
          <span style={{fontSize:22,fontWeight:700,color:sevColor}}>{result.severity==='HIGH'?'\u2717':result.severity==='CAUTION'?'\u26A0':'\u2713'}</span>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:sevColor,letterSpacing:'0.04em',marginBottom:4}}>
              {result.severity==='HIGH'?'READINESS FLAGS':result.severity==='CAUTION'?'CAUTION':'NO FLAGS'}
            </div>
            {result.flags.map((f,i)=><div key={i} style={{fontSize:13,color:'var(--text2)',lineHeight:1.6}}>{f.d}</div>)}
            {result.severity==='OK'&&<div style={{fontSize:13,color:'var(--text2)'}}>Self-assessment: no readiness flags reported.</div>}
          </div>
        </div>
      </div>
    )}

    <div style={{display:'flex',gap:8,marginTop:16}}>
      <button className="btn btn-p" onClick={saveCheck} disabled={saved} style={{flex:1,padding:'12px 0',fontSize:13,opacity:saved?0.7:1,background:saved?'var(--green)':'var(--amber)'}}>{saved?'Saved':'Save to Brief'}</button>
      <button className="btn" onClick={skipCheck} style={{padding:'12px 16px',fontSize:12,borderColor:'var(--border)',color:'var(--text3)'}}>Skip</button>
    </div>

    <div style={{textAlign:'center',marginTop:10,fontSize:11,color:'var(--muted)',lineHeight:1.6}}>This data stays on your device. Never shared with any instructor, school, employer, or third party. PIC self-certifies under FAR 61.53.</div>
  </div></div>);
}

// ─── WEIGHT & BALANCE ─────────────────────────────────────────────────────────
function WeightBalance({onResult}){
  const [acType,setAcType]=useState("C172S");
  const [loads,setLoads]=useState({});
  const [fuelBurn,setFuelBurn]=useState("");
  const [result,setResult]=useState(null);
  const [saved,setSaved]=useState(false);
  const ac=WB_AIRCRAFT[acType];

  // Build profile from preset for backend calc
  const profile={
    emptyWeight:ac.emptyWeight, emptyCG:ac.emptyArm, emptyMoment:null,
    fuelType:'avgas', usableFuel:ac.stations.find(s=>s.lbPerUnit)? 53 : null,
    stations:ac.stations.map(s=>({name:s.name, arm:s.arm, unit:s.lbPerUnit?'gal':'lbs', maxWeight:s.name.includes('Baggage')?120:null})),
    weightLimits:{maxRamp:null, maxTakeoff:ac.maxGross, maxLanding:null, maxZeroFuel:null},
    cgEnvelope:[{weight:1500,fwdCG:ac.cgMin,aftCG:ac.cgMax},{weight:ac.maxGross,fwdCG:ac.cgMin,aftCG:ac.cgMax}],
    safetyMargin:1.5,
  };

  // Auto-calculate via backend on input change
  useEffect(()=>{
    const hasInput=Object.values(loads).some(v=>parseFloat(v)>0);
    if(!hasInput){setResult(null);return;}
    const timer=setTimeout(async()=>{
      const loading=profile.stations.map(s=>({name:s.name,value:parseFloat(loads[s.name]||'0')||0}));
      try{
        const res=await fetch("https://solus-backend-9wde.onrender.com/wb/calculate",{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({profile,loading,fuelBurn:parseFloat(fuelBurn)||0}),
        });
        setResult(await res.json());
      }catch{setResult(null);}
    },300);
    return()=>clearTimeout(timer);
  },[loads,fuelBurn,acType]);

  const go=result?.severity==='OK';
  const sev=result?.severity;

  function save(){
    if(!result?.takeoff)return;
    onResult(`${acType}: ${Math.round(result.takeoff.weight)} lbs, CG ${result.takeoff.cg.toFixed(1)}" - ${go?"GO":"NO-GO"}`);
    setSaved(true);setTimeout(()=>setSaved(false),2500);
  }

  // CG envelope SVG
  function CGEnvelope(){
    if(!result?.takeoff||!result.envelope||result.envelope.length<2)return null;
    const env=result.envelope;
    const allCG=env.flatMap(p=>[p.fwdCG,p.aftCG]);
    const allWt=env.map(p=>p.weight);
    const cgMin=Math.min(...allCG)-2, cgMax=Math.max(...allCG)+2;
    const wtMin=Math.min(...allWt)-100, wtMax=Math.max(...allWt)+100;
    const W=400,H=220;
    const toX=cg=>((cg-cgMin)/(cgMax-cgMin))*W;
    const toY=wt=>H-((wt-wtMin)/(wtMax-wtMin))*H;
    const fwd=env.map(p=>`${toX(p.fwdCG)},${toY(p.weight)}`).join(' ');
    const aft=[...env].reverse().map(p=>`${toX(p.aftCG)},${toY(p.weight)}`).join(' ');
    const poly=fwd+' '+aft;
    const toPt=result.takeoff, ldPt=result.landing;
    const hasBurn=parseFloat(fuelBurn)>0&&ldPt;
    return(
      <div style={{marginBottom:20}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",color:"var(--muted)",marginBottom:8}}>CG ENVELOPE</div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",maxWidth:440,height:"auto",border:"1px solid var(--border)",borderRadius:4,background:"var(--bg2)"}}>
          <polygon points={poly} fill={toPt.withinEnvelope?"rgba(22,163,74,0.15)":"rgba(220,38,38,0.1)"} stroke={toPt.withinEnvelope?"rgba(22,163,74,0.5)":"rgba(220,38,38,0.4)"} strokeWidth="1.5"/>
          {hasBurn&&<line x1={toX(toPt.cg)} y1={toY(toPt.weight)} x2={toX(ldPt.cg)} y2={toY(ldPt.weight)} stroke="var(--muted)" strokeWidth="1" strokeDasharray="4,3"/>}
          <circle cx={toX(toPt.cg)} cy={toY(toPt.weight)} r="7" fill={toPt.withinEnvelope?"var(--green)":"var(--red)"}/>
          {hasBurn&&<circle cx={toX(ldPt.cg)} cy={toY(ldPt.weight)} r="6" fill="none" stroke={ldPt.withinEnvelope?"var(--green)":"var(--red)"} strokeWidth="2.5"/>}
          <text x={W/2} y={H+16} textAnchor="middle" fontSize="10" fill="var(--muted)" fontFamily="'JetBrains Mono',monospace">CG (inches)</text>
          {[cgMin+1,cgMin+(cgMax-cgMin)/2,cgMax-1].map((cg,i)=><text key={i} x={toX(cg)} y={H+16} textAnchor="middle" fontSize="9" fill="var(--muted)" fontFamily="'JetBrains Mono',monospace">{cg.toFixed(0)}</text>)}
        </svg>
        <div style={{display:"flex",gap:16,marginTop:8,fontSize:11,color:"var(--muted)"}}>
          <span><span style={{display:"inline-block",width:10,height:10,borderRadius:5,background:toPt.withinEnvelope?"var(--green)":"var(--red)",marginRight:4,verticalAlign:"middle"}}/> Takeoff</span>
          {hasBurn&&<span><span style={{display:"inline-block",width:10,height:10,borderRadius:5,border:`2px solid ${ldPt.withinEnvelope?"var(--green)":"var(--red)"}`,marginRight:4,verticalAlign:"middle"}}/> Landing</span>}
        </div>
      </div>
    );
  }

  return(
    <div className="page">
      <div className="page-inner">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <div className="page-title">Weight &amp; Balance</div>
            <div className="page-sub" style={{marginBottom:0}}>Moment-based CG envelope calculation</div>
          </div>
          <select className="sel" value={acType} onChange={e=>{setAcType(e.target.value);setLoads({});setResult(null);}} style={{width:"auto",marginTop:4}}>
            {Object.keys(WB_AIRCRAFT).map(k=><option key={k}>{k}</option>)}
          </select>
        </div>

        {/* Empty weight info */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:20}}>
          {[{l:"EMPTY WT",v:`${ac.emptyWeight} lbs`},{l:"EMPTY CG",v:`${ac.emptyArm}"`},{l:"MAX TO",v:`${ac.maxGross} lbs`}].map(c=>(
            <div key={c.l} className="card" style={{textAlign:"center",padding:10}}>
              <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:"var(--muted)",marginBottom:4}}>{c.l}</div>
              <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>{c.v}</div>
            </div>
          ))}
        </div>

        {/* Station inputs */}
        <div className="wb-grid">
          {ac.stations.map(s=>(
            <div key={s.name} className="wb-cell">
              <div className="inpl">{s.name}</div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <input type="number" min="0" placeholder="0" value={loads[s.name]||""}
                  onChange={e=>setLoads(p=>({...p,[s.name]:e.target.value}))}
                  className="inp" style={{width:100,fontSize:16,padding:"10px 12px"}}/>
                <span style={{fontSize:13,color:"var(--muted)",fontWeight:600}}>{s.lbPerUnit?"gal":"lbs"}</span>
              </div>
              {s.lbPerUnit&&loads[s.name]&&<div style={{fontSize:11,color:"var(--muted)",marginTop:4}}>{Math.round(parseFloat(loads[s.name]||0)*6)} lbs</div>}
            </div>
          ))}
        </div>

        {/* Fuel burn */}
        <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8,marginBottom:16}}>
          <span style={{fontSize:11,color:"var(--muted)",letterSpacing:"0.05em"}}>EST. FUEL BURN</span>
          <input type="number" min="0" placeholder="0" value={fuelBurn}
            onChange={e=>setFuelBurn(e.target.value)}
            className="inp" style={{width:80,fontSize:14,padding:"8px 10px"}}/>
          <span style={{fontSize:13,color:"var(--muted)"}}>gal</span>
        </div>

        {/* CG Envelope Graph */}
        <CGEnvelope/>

        {/* Scorecard */}
        {result?.takeoff&&(
          <div className="card" style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",color:"var(--amber)",marginBottom:12}}>SCORECARD</div>
            {[
              {l:"Weight",v:`${result.takeoff.weight.toLocaleString()} / ${ac.maxGross.toLocaleString()} lbs`,ok:!result.takeoff.overweight},
              {l:"CG",v:`${result.takeoff.cg.toFixed(1)}" / ${result.takeoff.limits?result.takeoff.limits.fwdCG.toFixed(1)+'\u2013'+result.takeoff.limits.aftCG.toFixed(1):'?'}`,ok:result.takeoff.withinEnvelope},
              ...(parseFloat(fuelBurn)>0&&result.landing?[
                {l:"Ldg Wt",v:`${result.landing.weight.toLocaleString()} lbs`,ok:!result.landing.overweight},
                {l:"Ldg CG",v:`${result.landing.cg.toFixed(1)}" / ${result.landing.limits?result.landing.limits.fwdCG.toFixed(1)+'\u2013'+result.landing.limits.aftCG.toFixed(1):'?'}`,ok:result.landing.withinEnvelope},
              ]:[]),
            ].map((r,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",padding:"8px 0",borderBottom:"1px solid var(--border)"}}>
                <span style={{fontSize:16,fontWeight:700,width:28,color:r.ok?"var(--green)":"var(--red)"}}>{r.ok?"\u2713":"\u2717"}</span>
                <span style={{fontSize:13,color:"var(--muted)",width:80}}>{r.l}</span>
                <span style={{fontSize:14,fontWeight:700,color:r.ok?"var(--text)":"var(--red)",flex:1,textAlign:"right"}}>{r.v}</span>
              </div>
            ))}
          </div>
        )}

        {/* Status banner */}
        {result&&(
          <div className="card" style={{borderLeft:`3px solid ${sev==='HIGH'?"var(--red)":sev==='MEDIUM'?"var(--amber)":sev==='INCOMPLETE'?"var(--muted)":"var(--green)"}`,
            background:sev==='HIGH'?"var(--redDim)":sev==='MEDIUM'?"var(--amberDim)":"var(--greenDim)",padding:16,marginBottom:12}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
              <span style={{fontSize:24,fontWeight:700,color:sev==='HIGH'?"var(--red)":sev==='MEDIUM'?"var(--amber)":sev==='INCOMPLETE'?"var(--muted)":"var(--green)"}}>
                {sev==='HIGH'?"\u2717":sev==='OK'?"\u2713":sev==='MEDIUM'?"\u26A0":"\u2014"}
              </span>
              <div>
                <div style={{fontSize:18,fontWeight:700,letterSpacing:"0.04em",color:sev==='HIGH'?"var(--red)":sev==='MEDIUM'?"var(--amber)":sev==='INCOMPLETE'?"var(--muted)":"var(--green)",marginBottom:4}}>
                  {sev==='HIGH'?'OUT OF LIMITS':sev==='MEDIUM'?'CAUTION':sev==='INCOMPLETE'?'INCOMPLETE PROFILE':'WITHIN ENVELOPE'}
                </div>
                {result.flags.map((f,i)=><div key={i} style={{fontSize:13,color:"var(--text2)",lineHeight:1.6}}>{f}</div>)}
              </div>
            </div>
          </div>
        )}

        <button className="btn btn-p" onClick={save} disabled={!result?.takeoff} style={{width:"100%",marginTop:8,padding:"12px",fontSize:13,letterSpacing:"0.02em",opacity:saved?0.7:!result?.takeoff?0.5:1}}>
          {saved?"Saved to flight context":"Save W&B to flight context"}
        </button>
        <div style={{textAlign:"center",marginTop:8,fontSize:11,color:"var(--muted)",lineHeight:1.6}}>
          POH figures assume new aircraft and test-pilot technique. PIC retains final go/no-go authority per FAR 91.7.
        </div>
      </div>
    </div>
  );
}

// ─── PERFORMANCE ──────────────────────────────────────────────────────────────
function Performance(){
  const [tab,setTab]=useState("da");
  // Density Altitude
  const [elev,setElev]=useState("");const [temp,setTemp]=useState("");const [baro,setBaro]=useState("29.92");const [daR,setDaR]=useState(null);
  // Fuel Planning
  const [distNm,setDistNm]=useState("");const [tas,setTas]=useState("");const [burnRate,setBurnRate]=useState("");
  const [windComp,setWindComp]=useState("");const [fuelOnBoard,setFuelOnBoard]=useState("");const [fuelR,setFuelR]=useState(null);
  // Takeoff/Landing
  const [grossWt,setGrossWt]=useState("");const [rwyLen,setRwyLen]=useState("");const [headwind,setHeadwind]=useState("");
  const [wetRwy,setWetRwy]=useState(false);const [toR,setToR]=useState(null);

  const lvl=daR!=null?(daR>8000?"extreme":daR>5000?"high":daR>2500?"moderate":"low"):null;
  const lvlC={extreme:"var(--red)",high:"var(--amber)",moderate:"#eab308",low:"var(--green)"};

  function doda(){setDaR(calcDA(parseFloat(elev)||0,parseFloat(temp)||15,parseFloat(baro)||29.92));}

  function doFuel(){
    const d=parseFloat(distNm)||0,t=parseFloat(tas)||100,w=parseFloat(windComp)||0,br=parseFloat(burnRate)||8,fob=parseFloat(fuelOnBoard)||0;
    const gs=Math.max(t+w,30);const ftime=d/gs;const fuelReq=ftime*br;
    const reserve30=br*0.5;const reserve45=br*0.75;
    const totalVFRDay=fuelReq+reserve30;const totalVFRNight=fuelReq+reserve45;
    const marginDay=fob-totalVFRDay;const marginNight=fob-totalVFRNight;
    setFuelR({ftime:(ftime*60).toFixed(0),fuelReq:fuelReq.toFixed(1),marginDay:marginDay.toFixed(1),marginNight:marginNight.toFixed(1),fob});
  }

  function doTakeoff(){
    const da=daR||0;const hw=parseFloat(headwind)||0;
    let baseTo=1500;let baseLd=1200; // C172 baseline at sea level
    const daFactor=1+(da/1000)*0.1; // +10% per 1000ft DA
    const twFactor=hw<0?1+Math.abs(hw)/2*0.1:1-Math.min(hw,15)/30*0.1; // tailwind penalty / headwind benefit
    const wetFactor=wetRwy?1.15:1;
    const toD=Math.round(baseTo*daFactor*twFactor*wetFactor);
    const ldD=Math.round(baseLd*daFactor*twFactor*wetFactor);
    setToR({takeoff:toD,landing:ldD,rwy:parseFloat(rwyLen)||0});
  }

  return(
    <div className="page">
      <div className="page-inner">
        <div className="page-title">Performance</div>
        <div className="page-sub" style={{marginBottom:20}}>Density altitude, takeoff distance, and fuel planning.</div>

        <div style={{display:"flex",gap:2,marginBottom:24,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
          {[{k:"da",l:"Density Alt"},{k:"to",l:"Takeoff/Landing"},{k:"fuel",l:"Fuel Planning"}].map(t=>(
            <button key={t.k} onClick={()=>setTab(t.k)} className={tab===t.k?"nb on":"nb"} style={{fontSize:11}}>{t.l}</button>
          ))}
        </div>

        {tab==="da"&&(<>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
            {[{l:"Field Elevation (ft)",v:elev,s:setElev,p:"5280"},{l:"Temp (\u00B0C)",v:temp,s:setTemp,p:"28"},{l:"Altimeter (inHg)",v:baro,s:setBaro,p:"29.92"}].map(f=>(
              <div key={f.l}><div className="inpl">{f.l}</div><input type="number" className="inp" value={f.v} onChange={e=>f.s(e.target.value)} placeholder={f.p}/></div>
            ))}
          </div>
          <button className="btn btn-p" onClick={doda} style={{width:"100%",padding:"11px",marginBottom:14}}>Calculate Density Altitude</button>
          {daR!=null&&lvl&&(
            <div className="perf-result" style={{borderLeft:`3px solid ${lvlC[lvl]}`}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:"var(--muted)",marginBottom:4}}>Density Altitude</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:40,fontWeight:700,color:lvlC[lvl],lineHeight:1,marginBottom:8}}>{daR.toLocaleString()} ft</div>
              <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.6}}>
                {lvl==="extreme"&&"Extreme: Performance severely degraded. Takeoff distance may be 2x POH values."}
                {lvl==="high"&&"High: Use POH density altitude charts. Do not use sea-level values."}
                {lvl==="moderate"&&"Moderate: Consult POH performance charts for actual conditions."}
                {lvl==="low"&&"Low impact: Standard POH values apply with minor corrections."}
              </div>
              {daR>3000&&<div style={{marginTop:8,fontSize:11,color:"var(--red)",fontFamily:"var(--mono)"}}>High density altitude. Verify performance numbers from your POH. Do not estimate.</div>}
            </div>
          )}
        </>)}

        {tab==="to"&&(<>
          <div style={{fontSize:11,color:"var(--red)",background:"var(--redDim)",padding:"10px 14px",borderRadius:4,marginBottom:14,fontFamily:"var(--mono)",lineHeight:1.6}}>
            This is a conservative estimate using rule-of-thumb corrections. Always compute actual performance from your aircraft POH.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div><div className="inpl">Density Altitude (ft)</div><input type="number" className="inp" value={daR||""} readOnly placeholder="Calculate in DA tab first"/></div>
            <div><div className="inpl">Runway Length (ft)</div><input type="number" className="inp" value={rwyLen} onChange={e=>setRwyLen(e.target.value)} placeholder="5000"/></div>
            <div><div className="inpl">Headwind (kts, neg=tail)</div><input type="number" className="inp" value={headwind} onChange={e=>setHeadwind(e.target.value)} placeholder="8"/></div>
            <div style={{display:"flex",alignItems:"flex-end",paddingBottom:4}}>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:"var(--text3)",fontFamily:"var(--mono)",cursor:"pointer"}}>
                <input type="checkbox" checked={wetRwy} onChange={e=>setWetRwy(e.target.checked)}/> Wet runway (+15%)
              </label>
            </div>
          </div>
          <button className="btn btn-p" onClick={doTakeoff} style={{width:"100%",padding:"11px",marginBottom:14}}>Estimate Distances</button>
          {toR&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div className="card card-sm" style={{borderLeft:`3px solid ${toR.rwy>0&&toR.takeoff>toR.rwy?"var(--red)":"var(--green)"}`}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--muted)",marginBottom:4}}>Takeoff Roll Est.</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:700,color:toR.rwy>0&&toR.takeoff>toR.rwy?"var(--red)":"var(--green)"}}>{toR.takeoff.toLocaleString()} ft</div>
              </div>
              <div className="card card-sm" style={{borderLeft:`3px solid ${toR.rwy>0&&toR.landing>toR.rwy?"var(--red)":"var(--green)"}`}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--muted)",marginBottom:4}}>Landing Roll Est.</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:700,color:toR.rwy>0&&toR.landing>toR.rwy?"var(--red)":"var(--green)"}}>{toR.landing.toLocaleString()} ft</div>
              </div>
            </div>
          )}
        </>)}

        {tab==="fuel"&&(<>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {[{l:"Distance (nm)",v:distNm,s:setDistNm,p:"120"},{l:"Cruise TAS (kts)",v:tas,s:setTas,p:"110"},{l:"Burn Rate (gph)",v:burnRate,s:setBurnRate,p:"8.5"},{l:"Wind (+tail/-head)",v:windComp,s:setWindComp,p:"0"},{l:"Fuel On Board (gal)",v:fuelOnBoard,s:setFuelOnBoard,p:"40"}].map(f=>(
              <div key={f.l}><div className="inpl">{f.l}</div><input type="number" className="inp" value={f.v} onChange={e=>f.s(e.target.value)} placeholder={f.p}/></div>
            ))}
          </div>
          <button className="btn btn-p" onClick={doFuel} style={{width:"100%",padding:"11px",marginBottom:14}}>Calculate Fuel</button>
          {fuelR&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div className="card card-sm" style={{borderLeft:"3px solid var(--amber)"}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--muted)",marginBottom:4}}>Est. Flight Time</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:"var(--text)"}}>{fuelR.ftime} min</div>
              </div>
              <div className="card card-sm" style={{borderLeft:"3px solid var(--amber)"}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--muted)",marginBottom:4}}>Fuel Required</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:"var(--text)"}}>{fuelR.fuelReq} gal</div>
              </div>
              <div className="card card-sm" style={{borderLeft:`3px solid ${parseFloat(fuelR.marginDay)>=0?"var(--green)":"var(--red)"}`}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--muted)",marginBottom:4}}>VFR Day Margin (30min)</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:parseFloat(fuelR.marginDay)>=0?"var(--green)":"var(--red)"}}>{fuelR.marginDay} gal</div>
              </div>
              <div className="card card-sm" style={{borderLeft:`3px solid ${parseFloat(fuelR.marginNight)>=0?"var(--green)":"var(--red)"}`}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--muted)",marginBottom:4}}>VFR Night Margin (45min)</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:parseFloat(fuelR.marginNight)>=0?"var(--green)":"var(--red)"}}>{fuelR.marginNight} gal</div>
              </div>
            </div>
          )}
          <div style={{marginTop:10,fontSize:10,color:"var(--muted)",fontFamily:"var(--mono)"}}>FAR 91.151: VFR day 30min reserve, VFR night 45min reserve at cruise.</div>
        </>)}
      </div>
    </div>
  );
}

// ─── WAITLIST MODAL ───────────────────────────────────────────────────────────
// FREE_PROMPTS removed - unlimited access during trial, managed by RevenueCat

// Feature tier gating
const PLAN_TIERS = { free: 0, solo: 1, captain: 2, fleet: 3 };
const FEATURE_TIER = {
  // Free features
  currency: 'free', risk_view: 'free', metar: 'free', checklists: 'free', wb: 'free', regs: 'free',
  // Solo features
  unlimited_queries: 'solo', notam_parse: 'solo', mission_brief: 'solo', passenger_brief: 'solo',
  performance: 'solo', debrief: 'solo', mfb_sync: 'solo', logbook_scan: 'solo',
  tfr_route: 'solo', airworthiness: 'solo', ntsb: 'solo', chat_history_30: 'solo',
  // Captain features
  priority_ai: 'captain', email_alerts: 'captain', unlimited_scan: 'captain',
  unlimited_history: 'captain', multi_aircraft: 'captain', monitoring_loop: 'captain',
  // Fleet features
  fleet_dashboard: 'fleet', bulk_pilots: 'fleet', api_access: 'fleet', csv_export: 'fleet',
};
function hasFeature(userPlan, feature, entitlements) {
  // Design partners and admins get full access (resolved server-side)
  if (entitlements && entitlements.fullAccess) return true;
  // Demo mode (local dev only)
  const demo = localStorage.getItem('solus_demo_mode') === '1';
  if (demo) return true;
  const planLevel = PLAN_TIERS[userPlan] || 0;
  const requiredLevel = PLAN_TIERS[FEATURE_TIER[feature]] || 0;
  return planLevel >= requiredLevel;
}

function UpgradeGate({ feature, plan, children, entitlements }) {
  if (hasFeature(plan, feature, entitlements)) return children;
  const needed = FEATURE_TIER[feature] || 'solo';
  return (
    <div style={{position:"relative"}}>
      <div style={{filter:"blur(3px)",opacity:0.4,pointerEvents:"none"}}>{children}</div>
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{background:"var(--bg)",border:"1px solid var(--amber)",borderRadius:4,padding:"16px 24px",textAlign:"center",maxWidth:300}}>
          <div style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:700,color:"var(--amber)",letterSpacing:"0.1em",marginBottom:6}}>{needed.toUpperCase()} PLAN REQUIRED</div>
          <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)",marginBottom:10}}>Upgrade to {needed} to unlock this feature.</div>
          <button className="btn btn-sm" onClick={()=>{const el=document.querySelector('[data-tab="plans"]');if(el)el.click();}} style={{borderColor:"var(--amber)",color:"var(--amber)"}}>View Plans</button>
        </div>
      </div>
    </div>
  );
}
const getAuthHeaders = () => {
  const token = localStorage.getItem('solus_token');
  return token ? { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token } : { 'Content-Type': 'application/json' };
};
const backendFetch = async (path, options = {}) => {
  try {
    const res = await fetch('https://solus-backend-9wde.onrender.com' + path, { ...options, headers: getAuthHeaders() });
    if (res.status === 401 || res.status === 403) {
      // Token expired or invalid -- clear and redirect to login
      localStorage.removeItem('solus_token');
      localStorage.removeItem('solus_jetson_unlocked');
      window.location.reload();
      return null;
    }
    return await res.json();
  } catch { return null; }
};

// ─── AIRSPACE INTELLIGENCE FEED ──────────────────────────────────────────────
function AirspaceReports({backendFetch}){
  const TYPES=[
    {id:"turbulence",label:"Severe Turbulence",icon:"◈",color:"#E84040"},
    {id:"gps_anomaly",label:"GPS Anomaly / Spoofing",icon:"◎",color:"#FFB000"},
    {id:"wildlife",label:"Wildlife Hazard",icon:"▲",color:"#FFB000"},
    {id:"wind_shear",label:"Wind Shear",icon:"◉",color:"#E84040"},
    {id:"runway_hazard",label:"Runway Hazard",icon:"▸",color:"#8A8FA8"},
    {id:"other",label:"Other Hazard",icon:"���",color:"#8A8FA8"},
  ];
  const [reports,setReports]=useState([]);
  const [form,setForm]=useState({type:"turbulence",location:"",altitude:"",notes:"",lat:"",lng:""});
  const [submitting,setSubmitting]=useState(false);
  const [submitted,setSubmitted]=useState(false);
  const [loading,setLoading]=useState(true);
  const [gpsAlerts,setGpsAlerts]=useState([]);
  // Route Airspace
  const [routeFrom,setRouteFrom]=useState("");const [routeTo,setRouteTo]=useState("");
  const [routeData,setRouteData]=useState(null);const [routeLoading,setRouteLoading]=useState(false);
  async function checkRoute(){
    if(!routeFrom||!routeTo)return;
    setRouteLoading(true);
    const data=await backendFetch(`/airspace/route?from=${encodeURIComponent(routeFrom)}&to=${encodeURIComponent(routeTo)}`);
    setRouteData(data);setRouteLoading(false);
  }
  const [pilotPos,setPilotPos]=useState(null);
  useEffect(()=>{
    fetch("https://solus-backend-9wde.onrender.com/airspace/reports")
      .then(r=>r.json()).then(d=>{setReports(Array.isArray(d)?d:[]);setLoading(false);})
      .catch(()=>setLoading(false));
  },[]);
  // Get pilot position and poll for GPS anomaly alerts within 50nm
  useEffect(()=>{
    if(!navigator.geolocation)return;
    navigator.geolocation.getCurrentPosition(
      pos=>setPilotPos({lat:pos.coords.latitude,lng:pos.coords.longitude}),
      ()=>{}
    );
  },[]);
  useEffect(()=>{
    if(!pilotPos)return;
    function fetchAlerts(){
      fetch("https://solus-backend-9wde.onrender.com/airspace/gps-alerts?lat="+pilotPos.lat+"&lng="+pilotPos.lng)
        .then(r=>r.json()).then(d=>{if(Array.isArray(d))setGpsAlerts(d);}).catch(()=>{});
    }
    fetchAlerts();
    const interval=setInterval(fetchAlerts,5*60*1000);
    return()=>clearInterval(interval);
  },[pilotPos]);
  // Auto-fill lat/lng when GPS anomaly selected and geolocation available
  useEffect(()=>{
    if(form.type==="gps_anomaly"&&pilotPos&&!form.lat&&!form.lng){
      setForm(p=>({...p,lat:pilotPos.lat.toFixed(4),lng:pilotPos.lng.toFixed(4)}));
    }
  },[form.type]); // eslint-disable-line
  async function submitReport(){
    if(!form.location)return;
    setSubmitting(true);
    const token=localStorage.getItem("solus_token");
    const body={type:form.type,location:form.location,altitude:form.altitude,notes:form.notes};
    if(form.lat&&form.lng){body.lat=form.lat;body.lng=form.lng;}
    const res=await fetch("https://solus-backend-9wde.onrender.com/airspace/report",{
      method:"POST",headers:{"Content-Type":"application/json",...(token?{"Authorization":"Bearer "+token}:{})},
      body:JSON.stringify(body)
    });
    if(res.ok){const nr=await res.json();setReports(p=>[nr,...p]);setForm({type:"turbulence",location:"",altitude:"",notes:"",lat:"",lng:""});setSubmitted(true);setTimeout(()=>setSubmitted(false),3000);}
    setSubmitting(false);
  }
  const ti=TYPES.find(t=>t.id===form.type)||TYPES[0];
  return(
    <div className="page"><div className="page-inner">
      <div className="page-title">Airspace Intelligence</div>

      {/* Route Airspace Check */}
      <div className="card" style={{marginBottom:20,padding:"16px 18px",borderLeft:"3px solid #4FC3F7"}}>
        <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--text3)",marginBottom:10,textTransform:"uppercase"}}>Route Airspace &amp; TFR Check</div>
        <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div><div className="inpl">From</div><input className="inp" value={routeFrom} onChange={e=>setRouteFrom(e.target.value.toUpperCase())} placeholder="KVNY" style={{width:100}}/></div>
          <div><div className="inpl">To</div><input className="inp" value={routeTo} onChange={e=>setRouteTo(e.target.value.toUpperCase())} placeholder="KSZP" style={{width:100}}/></div>
          <button className="btn btn-sm" onClick={checkRoute} disabled={routeLoading} style={{borderColor:"#4FC3F7",color:"#4FC3F7"}}>{routeLoading?"Checking...":"Check Route"}</button>
        </div>
        {routeData&&(
          <div>
            <div style={{fontSize:11,color:"var(--text2)",fontFamily:"var(--mono)",marginBottom:8}}>{routeData.route} - checked {new Date(routeData.checked_at).toLocaleTimeString()}</div>
            {(routeData.tfrs||[]).length===0&&<div style={{fontSize:11,color:"var(--green)",fontFamily:"var(--mono)"}}>No active TFRs found on route.</div>}
            {(routeData.tfrs||[]).map((t,i)=>(
              <div key={i} className="card" style={{marginBottom:4,padding:"10px 12px",borderLeft:"3px solid var(--red)"}}>
                <div style={{fontSize:11,fontWeight:600,color:"var(--text)",fontFamily:"var(--mono)"}}>{t.NOTAM||t.notam||t.type||'TFR'}</div>
                <div style={{fontSize:10,color:"var(--text3)",fontFamily:"var(--mono)",marginTop:2}}>{t.facility||''} {t.state||''}</div>
              </div>
            ))}
            {(routeData.sigmets||[]).length>0&&(
              <div style={{marginTop:8}}>
                <div style={{fontSize:10,color:"var(--amber)",fontFamily:"var(--mono)",marginBottom:4}}>Active SIGMETs:</div>
                {routeData.sigmets.map((s,i)=>(
                  <div key={i} className="card" style={{marginBottom:4,padding:"8px 10px",borderLeft:"3px solid var(--amber)",fontSize:10,color:"var(--text2)",fontFamily:"var(--mono)"}}>{s.rawSigmet||s.hazard||JSON.stringify(s).slice(0,200)}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="page-sub">Pilot-reported hazards. Real-time. Every report protects the next pilot.</div>

      <div className="card" style={{marginBottom:16,borderLeft:"3px solid var(--amber)"}}>
        <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--amber)",marginBottom:12,fontFamily:"var(--mono)"}}>Report a Hazard</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <div className="inpl">Hazard Type</div>
            <select className="inp" value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} style={{background:"var(--surface2)",color:"var(--white)",border:"1px solid var(--border)"}}>
              {TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <div className="inpl">Altitude (ft MSL)</div>
            <input className="inp" value={form.altitude} onChange={e=>setForm(p=>({...p,altitude:e.target.value}))} placeholder="e.g. 5500"/>
          </div>
        </div>
        <div style={{marginBottom:10}}>
          <div className="inpl">Location (Airport, Fix, or Coordinates) *</div>
          <input className="inp" value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} placeholder="e.g. KVNY, 15nm NE of KSBA, 34.1N 118.5W"/>
        </div>
        {form.type==="gps_anomaly"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <div className="inpl">Latitude</div>
              <input type="number" step="0.0001" className="inp" value={form.lat} onChange={e=>setForm(p=>({...p,lat:e.target.value}))} placeholder="e.g. 34.2097"/>
            </div>
            <div>
              <div className="inpl">Longitude</div>
              <input type="number" step="0.0001" className="inp" value={form.lng} onChange={e=>setForm(p=>({...p,lng:e.target.value}))} placeholder="e.g. -118.4900"/>
            </div>
          </div>
        )}
        <div style={{marginBottom:12}}>
          <div className="inpl">{form.type==="gps_anomaly"?"Details (symptoms, duration, equipment affected) *":"Notes (optional)"}</div>
          <input className="inp" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder={form.type==="gps_anomaly"?"e.g. GPS position jumping 2nm, WAAS lost, lasted 4 minutes":"e.g. Severe chop at 5,500, lasted 3 minutes"}/>
        </div>
        <button className="btn btn-p" onClick={submitReport} disabled={submitting||!form.location} style={{width:"100%",padding:"11px",fontSize:13,opacity:submitting?0.6:1}}>
          {submitted?"Report submitted. Protecting the next pilot ✓":submitting?"Submitting...":"Submit Pilot Report →"}
        </button>
      </div>
      {/* GPS Anomaly Proximity Alert */}
      {gpsAlerts.length>0&&(
        <div style={{marginBottom:16,padding:"14px 16px",background:"var(--amberDim)",border:"1px solid var(--amberBorder)",borderRadius:4,borderLeft:"4px solid var(--amber)"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{fontSize:16,color:"var(--amber)"}}>◎</span>
            <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--amber)",fontFamily:"var(--mono)"}}>GPS Anomaly: Within 50nm</span>
          </div>
          {gpsAlerts.map((a,i)=>{
            const ago=a.created_at?Math.round((Date.now()-new Date(a.created_at))/60000):null;
            return(
              <div key={a.id||i} style={{padding:"8px 0",borderTop:i>0?"1px solid var(--amberBorder)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:12,color:"var(--white)",fontWeight:600}}>{a.location}</span>
                  <span style={{fontSize:10,color:"var(--amber)",fontFamily:"var(--mono)"}}>{a.distance_nm}nm away · {ago!==null?(ago<60?ago+"m ago":Math.round(ago/60)+"h ago"):""}</span>
                </div>
                {a.altitude&&<div style={{fontSize:11,color:"var(--muted)"}}>{a.altitude} ft MSL</div>}
                {a.notes&&<div style={{fontSize:11,color:"var(--text2)",lineHeight:1.5,marginTop:2}}>{a.notes}</div>}
              </div>
            );
          })}
          <div style={{fontSize:10,color:"var(--amber)",marginTop:6,fontFamily:"var(--mono)",letterSpacing:"0.06em"}}>Cross-check with NOTAMs. Do not rely solely on GPS for navigation in this area.</div>
        </div>
      )}

      <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--text3)",marginBottom:10,fontFamily:"var(--mono)"}}>Live Reports: Last 24 Hours</div>
      {loading&&<div style={{color:"var(--muted)",fontSize:12,padding:"20px 0"}}>Loading reports...</div>}
      {!loading&&reports.length===0&&<div className="card" style={{textAlign:"center",padding:"24px"}}><div style={{fontSize:13,color:"var(--muted)"}}>No reports in the last 24 hours.</div></div>}
      {reports.map((r,i)=>{
        const t=TYPES.find(x=>x.id===r.type)||TYPES[5];
        const ago=r.created_at?Math.round((Date.now()-new Date(r.created_at))/60000):null;
        return(<div key={r.id||i} className="card" style={{marginBottom:8,borderLeft:"3px solid "+t.color}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:t.color,fontSize:14,fontFamily:"var(--mono)"}}>{t.icon}</span>
              <span style={{fontSize:12,fontWeight:700,color:"var(--white)"}}>{t.label}</span>
            </div>
            <span style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--mono)"}}>{ago!==null?(ago<60?ago+"m ago":Math.round(ago/60)+"h ago"):""}</span>
          </div>
          <div style={{fontSize:12,color:"var(--text2)",marginBottom:r.notes?4:0}}>
            <span style={{color:"var(--amber)",fontFamily:"var(--mono)",fontSize:11}}>{r.location}</span>
            {r.altitude&&<span style={{color:"var(--muted)",fontSize:11}}> · {r.altitude} ft</span>}
            {r.lat&&r.lng&&<span style={{color:"var(--muted)",fontSize:10}}> · {parseFloat(r.lat).toFixed(2)}°, {parseFloat(r.lng).toFixed(2)}°</span>}
          </div>
          {r.notes&&<div style={{fontSize:11,color:"var(--muted)",lineHeight:1.5}}>{r.notes}</div>}
        </div>);
      })}
      <div style={{marginTop:16,padding:"12px 16px",background:"var(--surface2)",borderRadius:4,fontSize:11,color:"var(--muted)",lineHeight:1.7}}>Reports are pilot-submitted and not verified. Advisory only.</div>
    </div></div>
  );
}

// ─── COMPUTE RISK SCORE ───────────────────────────────────────────────────────
function computeRiskScore(ac,dates,flightCtx){
  const today=new Date();
  function daysAgo(ds){if(!ds)return null;return Math.round((today-new Date(ds))/864e5);}
  function calMonthsAgo(ds){if(!ds)return null;const d=new Date(ds);return(today.getFullYear()-d.getFullYear())*12+(today.getMonth()-d.getMonth());}

  // Medical duration based on class and age
  function medicalDurationMonths(cls,dob){
    const age=dob?Math.floor(daysAgo(dob)/365.25):null;
    const dobMissing=!age||age<0;
    // If DOB missing, use conservative (over-40) duration
    const over40=dobMissing?true:(age>=40);
    const months=cls==="1st"?(over40?6:12):cls==="2nd"?12:(over40?24:60);
    return{months,dobMissing};
  }

  const hasData=(ac&&ac.nNumber)||(dates&&(dates.lastBFR||dates.lastDayLanding||dates.lastMedical));
  if(!hasData)return{score:null,level:"NO DATA",color:"#DC2626",driver:"Enter aircraft and currency data to calculate risk. Without this data, SOLUS cannot assess your pre-flight risk.",signals:[{name:"Data Required",pts:0,status:"MISSING",detail:"Load your aircraft profile and currency dates to enable risk scoring. Risk cannot be assessed without this data."}]};

  let score=0;let driver=null;const signals=[];

  // SIGNAL 1: Pilot Currency (max 30pts)
  let cp=0;
  if(dates){
    // Flight Review - FAR 61.56 (24 calendar months)
    const bfr=daysAgo(dates.lastBFR);
    if(dates.lastBFR&&bfr>730){cp+=25;signals.push({name:"Flight Review",pts:25,status:"LAPSED",detail:"Expired "+Math.round(bfr-730)+" days ago. FAR 61.56 — may not act as PIC. Action: schedule a flight review with a CFI (1 hr flight + 1 hr ground minimum)."});if(!driver)driver="Flight review lapsed";}
    else if(dates.lastBFR&&bfr>650){cp+=12;signals.push({name:"Flight Review",pts:12,status:"EXPIRING",detail:"Expires in ~"+(730-bfr)+" days. Action: schedule flight review with a CFI before expiry."});}
    else if(dates.lastBFR){signals.push({name:"Flight Review",pts:0,status:"CURRENT",detail:"Good for ~"+(730-bfr)+" days"});}

    // VFR Day Currency - FAR 61.57(a) — 3 T&Ls in 90 days, oldest matters
    const dayAll=[dates.lastDayLanding,dates.dayLanding2,dates.dayLanding3].filter(Boolean);
    const dayOldestRisk=dayAll.length>=3?daysAgo(dayAll.sort((a,b)=>new Date(a)-new Date(b))[0]):daysAgo(dates.lastDayLanding);
    const dayHas3=dayAll.length>=3;
    if(dates.lastDayLanding&&dayHas3&&dayOldestRisk>90){cp+=15;signals.push({name:"VFR Day Currency",pts:15,status:"LAPSED",detail:"Lapsed "+(dayOldestRisk-90)+" days ago. FAR 61.57(a). Action: complete 3 takeoffs and 3 landings to the same category/class to restore currency."});if(!driver)driver="VFR day currency lapsed";}
    else if(dates.lastDayLanding&&dayHas3&&dayOldestRisk>75){cp+=8;signals.push({name:"VFR Day Currency",pts:8,status:"EXPIRING",detail:"Oldest of 3 expires in ~"+(90-dayOldestRisk)+" days. Action: fly before then to keep current."});}
    else if(dates.lastDayLanding&&dayHas3){signals.push({name:"VFR Day Currency",pts:0,status:"CURRENT",detail:"Good for ~"+(90-dayOldestRisk)+" days (3 T&Ls on file)"});}
    else if(dates.lastDayLanding&&!dayHas3){cp+=5;signals.push({name:"VFR Day Currency",pts:5,status:"INCOMPLETE",detail:"Only "+dayAll.length+" of 3 T&L dates entered. Add all 3 for accurate tracking."});}

    // VFR Night Currency - FAR 61.57(b) — 3 takeoffs AND 3 full-stop landings at night
    const nightLAll=[dates.lastNightLanding,dates.nightLanding2,dates.nightLanding3].filter(Boolean);
    const nightTAll=[dates.nightTakeoff1,dates.nightTakeoff2,dates.nightTakeoff3].filter(Boolean);
    const nightComplete=nightLAll.length>=3&&nightTAll.length>=3;
    const nightLOldestRisk=nightLAll.length>=3?daysAgo(nightLAll.sort((a,b)=>new Date(a)-new Date(b))[0]):null;
    const nightTOldestRisk=nightTAll.length>=3?daysAgo(nightTAll.sort((a,b)=>new Date(a)-new Date(b))[0]):null;
    const nightOldestRisk=nightComplete?Math.max(nightLOldestRisk,nightTOldestRisk):daysAgo(dates.lastNightLanding);
    if(nightComplete&&nightOldestRisk>90){cp+=10;signals.push({name:"VFR Night Currency",pts:10,status:"LAPSED",detail:"Lapsed "+(nightOldestRisk-90)+" days ago. FAR 61.57(b). Action: complete 3 night takeoffs and 3 full-stop night landings."});if(!driver)driver="Night passenger currency lapsed";}
    else if(nightComplete&&nightOldestRisk>75){cp+=5;signals.push({name:"VFR Night Currency",pts:5,status:"EXPIRING",detail:"Oldest entry expires in ~"+(90-nightOldestRisk)+" days."});}
    else if(nightComplete){signals.push({name:"VFR Night Currency",pts:0,status:"CURRENT",detail:"Good for ~"+(90-nightOldestRisk)+" days (3 T/Os + 3 landings on file)"});}
    else if(nightLAll.length>0||nightTAll.length>0){cp+=5;signals.push({name:"VFR Night Currency",pts:5,status:"INCOMPLETE",detail:"Need 3 night takeoffs ("+nightTAll.length+"/3) and 3 night landings ("+nightLAll.length+"/3)."});}

    // Medical - class-aware duration with FAA end-of-month rule
    const medClass=dates.medicalClass||"3rd";
    const{months:medMonths,dobMissing:medDobMissing}=medicalDurationMonths(medClass,dates.dob);
    if(dates.lastMedical){
      const medDate=new Date(dates.lastMedical);
      // FAA: medical expires end of calendar month, medMonths after issue
      const medExpiry=new Date(medDate.getFullYear(),medDate.getMonth()+medMonths+1,0);
      const medDaysLeft=Math.floor((medExpiry.getTime()-Date.now())/86400000);
      const medDobNote=medDobMissing?" (DOB not entered — using conservative over-40 duration)":"";
      if(medDaysLeft<0){cp+=20;signals.push({name:"Medical ("+medClass+" Class)",pts:20,status:"LAPSED",detail:"Expired "+Math.abs(medDaysLeft)+" days ago. FAR 61.23. Action: schedule AME appointment."+medDobNote});if(!driver)driver="Medical certificate lapsed";}
      else if(medDaysLeft<60){cp+=8;signals.push({name:"Medical ("+medClass+" Class)",pts:8,status:"EXPIRING",detail:"Expires in "+medDaysLeft+" days. Action: schedule AME appointment before expiry."+medDobNote});}
      else{signals.push({name:"Medical ("+medClass+" Class)",pts:0,status:"CURRENT",detail:"Good for "+medDaysLeft+" days"+medDobNote});}
    }

    // IFR Currency - FAR 61.57(c) (6 calendar months: 6 approaches + holding + tracking)
    const ifr=daysAgo(dates.lastIFR);
    if(dates.lastIFR&&ifr>180){cp+=10;signals.push({name:"IFR Currency",pts:10,status:"LAPSED",detail:"Last IFR "+(ifr)+" days ago (>180 day limit). FAR 61.57(c). Action: complete IPC or 6 approaches + holding + tracking within 6 months."});if(!driver)driver="IFR currency lapsed";}
    else if(dates.lastIFR&&ifr>150){cp+=5;signals.push({name:"IFR Currency",pts:5,status:"EXPIRING",detail:"IFR currency expires in ~"+(180-ifr)+" days. FAR 61.57(c)."});}
    else if(dates.lastIFR){signals.push({name:"IFR Currency",pts:0,status:"CURRENT",detail:"IFR current, good for ~"+(180-ifr)+" days"});}
  }
  score+=Math.min(cp,30);

  // SIGNAL 2: Aircraft Status (max 25pts)
  let mp=0;
  if(ac&&ac.nNumber){
    if(ac.squawks){
      const sqCount=(ac.squawks.match(/,/g)||[]).length+1;
      const sqPts=Math.min(sqCount*5,15);
      mp+=sqPts;signals.push({name:"Open Squawks",pts:sqPts,status:"FLAG",detail:sqCount+" open squawk"+(sqCount>1?"s":"")+": "+ac.squawks});if(!driver)driver="Open squawks on N"+ac.nNumber;
    }else{signals.push({name:"Squawks",pts:0,status:"CLEAR",detail:"No open squawks"});}

    if(ac.lastAnnual){
      const ann=daysAgo(ac.lastAnnual);
      if(ann>365){mp+=25;signals.push({name:"Annual Inspection",pts:25,status:"OVERDUE",detail:"Overdue by "+(ann-365)+" days. Aircraft is not airworthy. FAR 91.409"});if(!driver)driver="Annual inspection overdue — aircraft not airworthy";}
      else if(ann>305){mp+=10;signals.push({name:"Annual Inspection",pts:10,status:"DUE SOON",detail:"Due in ~"+(365-ann)+" days. Schedule with an IA."});}
      else{signals.push({name:"Annual Inspection",pts:0,status:"CURRENT",detail:"Good for ~"+(365-ann)+" days"});}
    }
  }
  score+=Math.min(mp,25);

  // SIGNAL 3: Fatigue / Time-of-Day (max 10pts)
  if(flightCtx&&flightCtx.deptTime){
    const [dh,dm]=(flightCtx.deptTime||"12:00").split(":").map(Number);
    const mins=dh*60+(dm||0);
    const deptStr=String(dh).padStart(2,'0')+':'+String(dm||0).padStart(2,'0');
    if(mins>=1320||mins<300){score+=10;signals.push({name:"Fatigue / Time-of-Day",pts:10,status:"CAUTION",detail:"Planned departure "+deptStr+" local. NTSB data shows 2200-0500 is the peak fatigue accident window. FAA-H-8083-2."});if(!driver)driver="Late night departure — elevated fatigue risk";}
    else if(mins<360||mins>=1260){score+=5;signals.push({name:"Fatigue / Time-of-Day",pts:5,status:"MODERATE",detail:"Planned departure "+deptStr+" local. Early morning or late evening — transition period for fatigue."});}
    else{signals.push({name:"Fatigue / Time-of-Day",pts:0,status:"OK",detail:"Daytime departure ("+deptStr+" local)"});}
  }

  // SIGNAL 4: Recency Gap (max 15pts)
  if(dates&&dates.lastDayLanding){
    const recDays=daysAgo(dates.lastDayLanding);
    if(recDays>120){score+=15;signals.push({name:"Recency Gap",pts:15,status:"HIGH",detail:recDays+" days since last flight. Action: strongly consider a review flight with a CFI before flying solo. Proficiency degrades significantly beyond 90 days."});if(!driver)driver="120+ day recency gap";}
    else if(recDays>90){score+=12;signals.push({name:"Recency Gap",pts:12,status:"HIGH",detail:recDays+" days since last flight. Action: consider a practice flight or review flight with a CFI."});if(!driver)driver="90-day recency gap";}
    else if(recDays>60){score+=8;signals.push({name:"Recency Gap",pts:8,status:"MODERATE",detail:recDays+" days since last flight. Action: consider a practice flight before a cross-country."});}
    else if(recDays>30){score+=3;signals.push({name:"Recency Gap",pts:3,status:"MODERATE",detail:recDays+" days since last flight. Stay sharp — consider pattern work."});}
    else if(recDays!==null){signals.push({name:"Recency",pts:0,status:"RECENT",detail:recDays+" days since last flight"});}
  }

  // SIGNAL 5: Tailwheel Currency (max 5pts)
  if(dates&&dates.tailwheel){
    const tw=daysAgo(dates.lastTailwheelLanding);
    if(dates.lastTailwheelLanding&&tw>90){score+=5;signals.push({name:"Tailwheel Currency",pts:5,status:"LAPSED",detail:"Lapsed "+(tw-90)+" days ago. 3 full-stop tailwheel landings required. FAR 61.57(a)"});}
    else if(dates.lastTailwheelLanding&&tw>75){score+=3;signals.push({name:"Tailwheel Currency",pts:3,status:"EXPIRING",detail:"Expires in ~"+(90-tw)+" days"});}
    else if(dates.lastTailwheelLanding){signals.push({name:"Tailwheel Currency",pts:0,status:"CURRENT",detail:"Good for ~"+(90-tw)+" days"});}
  }

  // SIGNAL 6: Personal Minimums (max 15pts)
  try{
    const mRaw=localStorage.getItem('solus_personal_minimums');
    if(mRaw){
      const m=JSON.parse(mRaw);
      const mFlags=[];
      // These check against flight context weather if available (placeholder — METAR integration needed)
      // For now, check density altitude from W&B context, and flag if minimums are set but conditions unknown
      if(m.maxDensityAlt!=null&&flightCtx&&flightCtx.densityAlt&&flightCtx.densityAlt>m.maxDensityAlt){
        mFlags.push('Density altitude '+flightCtx.densityAlt+' ft exceeds your personal max of '+m.maxDensityAlt+' ft');
      }
      if(m.crosswind!=null&&flightCtx&&flightCtx.crosswind&&flightCtx.crosswind>m.crosswind){
        mFlags.push('Crosswind '+flightCtx.crosswind+' kts exceeds your personal max of '+m.crosswind+' kts');
      }
      // Flag that minimums are set (even without weather data yet)
      const hasAny=Object.values(m).some(v=>v!=null);
      if(hasAny&&mFlags.length>0){
        score+=15;signals.push({name:'Personal Minimums',pts:15,status:'HIGH',detail:mFlags.join('. ')+'.'});
        if(!driver)driver='Personal minimums exceeded';
      }else if(hasAny){
        signals.push({name:'Personal Minimums',pts:0,status:'SET',detail:'Personal minimums configured. Will be checked against conditions.'});
      }
    }
  }catch{}

  // SIGNAL 7: Pilot Readiness / IMSAFE (max 15pts)
  try{
    const rRaw=localStorage.getItem('solus_readiness_today');
    if(rRaw){
      const r=JSON.parse(rRaw);
      if(r&&!r.skipped){
        let rPts=0;const rFlags=[];
        // RED flags
        if(r.sleepHours!=null&&r.sleepHours<5){rFlags.push(r.sleepHours+' hours sleep reported');rPts=15;}
        if(r.alcohol8hr){rFlags.push('Alcohol within 8 hours');rPts=15;}
        if(r.illness){rFlags.push('Illness symptoms reported');rPts=15;}
        if(r.fatigue==='high'&&(r.stress==='high'||(r.sleepHours!=null&&r.sleepHours<6))){rFlags.push('High fatigue + '+(r.stress==='high'?'high stress':'limited sleep'));rPts=15;}
        // YELLOW flags (only if no RED)
        if(rPts<15){
          if(r.sleepHours!=null&&r.sleepHours>=5&&r.sleepHours<=6)rFlags.push(r.sleepHours+' hours sleep');
          if(r.sleepQuality==='poor')rFlags.push('Poor sleep quality');
          if(r.stress==='high'&&r.fatigue!=='high')rFlags.push('High stress');
          if(r.fatigue==='high'&&r.stress!=='high'&&!(r.sleepHours!=null&&r.sleepHours<6))rFlags.push('High fatigue');
          if(r.medication)rFlags.push('Taking medication');
          if(r.emotion==='upset')rFlags.push('Elevated emotional state');
          if(r.fatigue==='moderate'&&r.stress==='moderate')rFlags.push('Moderate fatigue + stress');
          if(rFlags.length>0&&rPts===0)rPts=8;
        }
        if(rPts>0){score+=rPts;signals.push({name:'Pilot Readiness',pts:rPts,status:rPts>=15?'HIGH':'CAUTION',detail:rFlags.join('. ')+'.'});if(!driver)driver='Pilot readiness flags';}
        else{signals.push({name:'Pilot Readiness',pts:0,status:'OK',detail:'Self-assessment: no flags'});}
      }else if(r&&r.skipped){
        signals.push({name:'Pilot Readiness',pts:0,status:'SKIPPED',detail:'Self-assessment was skipped'});
      }
    }
  }catch{}

  const s=Math.min(score,100);
  const flagCount=signals.filter(sig=>sig.pts>0).length;
  const color=flagCount>=3?"#E84040":flagCount>=1?"#FFB000":"#3EC98A";
  const level=flagCount===0?"No flags":flagCount+" flag"+(flagCount>1?"s":"")+" identified";
  if(!driver)driver=s===0?"No flags identified":"Multiple minor factors";
  return{score:s,level,color,driver,signals,flagCount};
}

function RiskBreakdown({riskScore,ac,currDates}){
  const signals=riskScore.signals||[];
  const statusColor=(st)=>st==="CURRENT"||st==="CLEAR"||st==="OK"||st==="RECENT"?"var(--green)":st==="EXPIRING"||st==="DUE SOON"||st==="MODERATE"||st==="CAUTION"?"var(--amber)":"var(--red)";
  const [showMethodology,setShowMethodology]=useState(false);
  const [showFeedback,setShowFeedback]=useState(false);
  const [fbTab,setFbTab]=useState("risk");
  const [fbIssue,setFbIssue]=useState("");
  const [fbEmail,setFbEmail]=useState("");
  const [fbSent,setFbSent]=useState(false);
  const [showAbout,setShowAbout]=useState(false);
  return(
    <div className="page"><div style={{maxWidth:700,margin:"0 auto",padding:"24px 16px"}}>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.2em",color:"var(--amber)",textTransform:"uppercase",marginBottom:12}}>Pre-Departure Risk Assessment</div>
        {riskScore.score!==null?(
          <>
            <div style={{position:"relative",display:"inline-block"}}>
              <div style={{fontFamily:"var(--mono)",fontSize:64,fontWeight:700,color:riskScore.color,lineHeight:1}}>{riskScore.score}</div>
              <span onClick={()=>setShowMethodology(true)} style={{position:"absolute",top:-4,right:-20,width:18,height:18,borderRadius:"50%",border:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"var(--muted)",cursor:"pointer",fontFamily:"var(--mono)"}}>i</span>
            </div>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:6,marginBottom:4}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:riskScore.color,display:"inline-block"}}/>
              <span style={{fontFamily:"var(--mono)",fontSize:11,color:riskScore.color,letterSpacing:"0.1em"}}>{riskScore.level}</span>
            </div>
            <div style={{fontSize:12,color:"var(--muted)"}}>{riskScore.flagCount>0?"Flag 1 of "+riskScore.flagCount+": "+riskScore.driver:"No flags identified"}</div>
            <div style={{marginTop:8,display:"flex",justifyContent:"center",gap:12}}>
              <span onClick={()=>setShowFeedback(true)} style={{fontSize:10,color:"var(--muted)",cursor:"pointer",textDecoration:"underline",fontFamily:"var(--mono)"}}>Report an issue</span>
            </div>
          </>
        ):(
          <>
            <div style={{fontFamily:"var(--mono)",fontSize:48,fontWeight:700,color:"var(--muted)",lineHeight:1}}>--</div>
            <div style={{fontSize:12,color:"var(--muted)",marginTop:8}}>Load your aircraft and currency data to calculate your risk score.</div>
          </>
        )}
      </div>

      {/* How it works */}
      <div className="card" style={{marginBottom:16,padding:"16px 18px",borderLeft:"3px solid var(--amber)"}}>
        <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--amber)",textTransform:"uppercase",marginBottom:8}}>How the score works</div>
        <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.7}}>
          SOLUS calculates your pre-departure risk by evaluating five signal categories. Each signal contributes points based on severity. The total is capped at 100. This is not a go/no-go decision. It is a synthesis of the factors a pre-flight brief should cover.
        </div>
        <div style={{fontSize:11,color:"var(--amber)",lineHeight:1.6,marginTop:8,fontStyle:"italic"}}>
          Unlike static FRATs, SOLUS Risk Score uses your actual logbook recency, your specific aircraft status, and live FAA data for this flight. Score updates before every departure.
        </div>
      </div>

      {/* Signal categories */}
      <div style={{marginBottom:16}}>
        <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--amber)",textTransform:"uppercase",marginBottom:10}}>Signal Categories</div>
        {[
          {name:"Pilot Currency",max:30,desc:"Flight review (FAR 61.56), VFR day/night (FAR 61.57), IFR currency (FAR 61.57(c)), medical (FAR 61.23). Points increase as expiry approaches and spike on lapse. Medical duration is class-aware."},
          {name:"Aircraft Status",max:25,desc:"Annual inspection (FAR 91.409) and open squawks. An overdue annual means the aircraft is not airworthy. Multiple squawks compound the score."},
          {name:"Fatigue / Time-of-Day",max:10,desc:"Departures between 2200-0500 carry peak fatigue risk. Transition hours (0500-0600, 2100-2200) carry moderate risk. Based on NTSB fatigue-related accident data."},
          {name:"Recency Gap",max:15,desc:"Days since your last flight. Beyond 30 days, mild proficiency decay. Beyond 60, noticeable. Beyond 90, strongly consider a review flight. Beyond 120, fly with a CFI."},
          {name:"Tailwheel Currency",max:5,desc:"If you fly tailwheel aircraft, 3 full-stop landings in 90 days are required for PIC. FAR 61.57(a)."},
        ].map(cat=>(
          <div key={cat.name} className="card" style={{marginBottom:8,padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <span style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:700,color:"var(--text)"}}>{cat.name}</span>
              <span style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--muted)"}}>max {cat.max} pts</span>
            </div>
            <div style={{fontSize:11,color:"var(--text3)",lineHeight:1.6}}>{cat.desc}</div>
          </div>
        ))}
      </div>

      {/* Live signals */}
      {signals.length>0&&(
        <div>
          <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--amber)",textTransform:"uppercase",marginBottom:10}}>Your Current Signals</div>
          {signals.map((sig,i)=>(
            <div key={i} className="card" style={{marginBottom:6,padding:"10px 14px",borderLeft:"3px solid "+statusColor(sig.status)}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                <span style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:600,color:"var(--text)"}}>{sig.name}</span>
                <span style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,color:statusColor(sig.status),background:sig.pts>0?"rgba(255,176,0,0.08)":"rgba(0,230,118,0.08)",padding:"2px 6px",borderRadius:2}}>{sig.status}{sig.pts>0?" +"+sig.pts+"pts":""}</span>
              </div>
              <div style={{fontSize:11,color:"var(--text3)"}}>{sig.detail}</div>
            </div>
          ))}
        </div>
      )}

      {/* Thresholds */}
      <div style={{marginTop:16,padding:"14px 18px",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:4}}>
        <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--amber)",textTransform:"uppercase",marginBottom:10}}>Thresholds</div>
        {[
          {range:"0 flags",color:"#3EC98A",desc:"No risk factors identified. Standard preflight procedures apply."},
          {range:"1-2 flags",color:"#FFB000",desc:"Risk factors identified. Review each flag before departure."},
          {range:"3+ flags",color:"#E84040",desc:"Multiple risk factors identified. Review all flags carefully before departure."},
        ].map(t=>(
          <div key={t.range} style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:8}}>
            <span style={{display:"flex",alignItems:"center",gap:6,minWidth:80}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:t.color,display:"inline-block",flexShrink:0}}/>
              <span style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,color:t.color}}>{t.range}</span>
            </span>
            <span style={{fontSize:11,color:"var(--text3)",lineHeight:1.5}}>{t.desc}</span>
          </div>
        ))}
      </div>

      <div style={{marginTop:16,fontSize:10,color:"var(--muted)",textAlign:"center",fontFamily:"var(--mono)",lineHeight:1.6}}>
        This score is advisory. PIC authority and responsibility remain entirely with the pilot per FAR 91.7. SOLUS surfaces data. You make the decision.
      </div>

      <div style={{marginTop:12,textAlign:"center"}}>
        <span onClick={()=>setShowAbout(true)} style={{fontSize:9,color:"var(--muted)",cursor:"pointer",fontFamily:"var(--mono)",letterSpacing:"0.08em",opacity:0.5}}>About SOLUS</span>
      </div>

      {/* Methodology Modal */}
      {showMethodology&&(
        <div className="overlay" onClick={()=>setShowMethodology(false)} style={{zIndex:10000}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:500}}>
            <button onClick={()=>setShowMethodology(false)} style={{position:"absolute",top:16,right:18,background:"none",border:"none",color:"var(--muted)",fontSize:22,cursor:"pointer",lineHeight:1}}>x</button>
            <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:"var(--amber)",marginBottom:16,textTransform:"uppercase"}}>How SOLUS Calculates Your Risk Score</div>
            <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.7,marginBottom:16}}>The SOLUS Pre-Departure Risk Score synthesizes five signals:</div>
            {[
              {n:"1",title:"PILOT CURRENCY",pts:"30 pts max",desc:"Based on FAR 61.57 requirements (day/night/IFR/tailwheel), FAR 61.56 flight review, and FAR 61.23 medical. Points increase as currency approaches expiry. Medical duration is class-aware. Source: your entered logbook dates."},
              {n:"2",title:"AIRCRAFT STATUS",pts:"25 pts max",desc:"Based on last annual inspection date per FAR 91.409 and any open squawks you have entered. An overdue annual means the aircraft is not airworthy."},
              {n:"3",title:"RECENCY GAP",pts:"15 pts max",desc:"Days since last flight. Based on human factors research showing proficiency decay after 30, 60, 90, and 120-day gaps. Source: your most recent landing date."},
              {n:"4",title:"FATIGUE / TIME-OF-DAY",pts:"10 pts max",desc:"Based on departure time. Night departures between 2200-0500 local carry peak fatigue risk. Transition hours (0500-0600, 2100-2200) carry moderate risk. Per FAA human factors guidance."},
              {n:"5",title:"TAILWHEEL CURRENCY",pts:"5 pts max",desc:"If you fly tailwheel aircraft, 3 full-stop landings in 90 days are required. FAR 61.57(a)."},
            ].map(s=>(
              <div key={s.n} style={{marginBottom:12,padding:"10px 14px",background:"rgba(255,255,255,0.02)",border:"1px solid var(--border)",borderRadius:4}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,color:"var(--amber)"}}>{s.n}. {s.title}</span>
                  <span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--muted)"}}>{s.pts}</span>
                </div>
                <div style={{fontSize:11,color:"var(--text3)",lineHeight:1.6}}>{s.desc}</div>
              </div>
            ))}
            <div style={{padding:"12px 14px",background:"rgba(255,176,0,0.06)",border:"1px solid rgba(255,176,0,0.15)",borderRadius:4,marginBottom:12}}>
              <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--amber)",letterSpacing:"0.12em",marginBottom:6,textTransform:"uppercase"}}>Thresholds</div>
              <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text2)"}}>CLEAR: 0-34 &nbsp;&nbsp; REVIEW: 35-69 &nbsp;&nbsp; HIGH: 70-100</div>
            </div>
            <div style={{fontSize:10,color:"var(--muted)",lineHeight:1.7,fontFamily:"var(--mono)"}}>
              <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--amber)",letterSpacing:"0.12em",marginBottom:6,textTransform:"uppercase"}}>Validation Status</div>
              FAR currency calculations are sourced directly from 14 CFR Part 61 and Part 91. Risk weighting is based on NTSB accident data patterns and FAA human factors research (FAA-H-8083-2). This tool has not been formally validated by an aviation medical officer or meteorologist. SOLUS is an advisory tool. PIC authority and responsibility remain with the pilot per FAR 91.7.
            </div>
          </div>
        </div>
      )}

      {/* Feedback Form */}
      {showFeedback&&(
        <div className="overlay" onClick={()=>{setShowFeedback(false);setFbSent(false);}} style={{zIndex:10000}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:420}}>
            <button onClick={()=>{setShowFeedback(false);setFbSent(false);}} style={{position:"absolute",top:16,right:18,background:"none",border:"none",color:"var(--muted)",fontSize:22,cursor:"pointer",lineHeight:1}}>x</button>
            <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:"var(--amber)",marginBottom:16,textTransform:"uppercase"}}>Report an Issue</div>
            {fbSent?(
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:13,color:"var(--green)",marginBottom:8}}>Thank you. Your feedback has been submitted.</div>
                <div style={{fontSize:11,color:"var(--muted)"}}>We review every report.</div>
              </div>
            ):(
              <>
                <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:14}}>Found an error in SOLUS output? Tell us.</div>
                <div style={{marginBottom:10}}>
                  <div className="inpl">Which tab?</div>
                  <select className="sel" value={fbTab} onChange={e=>setFbTab(e.target.value)} style={{width:"100%"}}>
                    <option value="intel">Intel</option>
                    <option value="currency">Currency</option>
                    <option value="risk">Risk Score</option>
                    <option value="weather">Weather</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div style={{marginBottom:10}}>
                  <div className="inpl">What did SOLUS get wrong?</div>
                  <textarea className="inp" rows={4} value={fbIssue} onChange={e=>setFbIssue(e.target.value)} placeholder="Describe the issue..." style={{width:"100%",resize:"vertical",fontFamily:"var(--sans)",fontSize:12}}/>
                </div>
                <div style={{marginBottom:14}}>
                  <div className="inpl">Your email (optional)</div>
                  <input className="inp" type="email" value={fbEmail} onChange={e=>setFbEmail(e.target.value)} placeholder="you@example.com" style={{width:"100%"}}/>
                </div>
                <button className="btn btn-p" disabled={!fbIssue.trim()} style={{width:"100%",padding:"11px",opacity:fbIssue.trim()?1:0.35}} onClick={()=>{
                  fetch("https://solus-backend-9wde.onrender.com/feedback",{
                    method:"POST",headers:{"Content-Type":"application/json"},
                    body:JSON.stringify({tab:fbTab,issue:fbIssue,email:fbEmail})
                  }).then(()=>{setFbSent(true);setFbIssue("");setFbEmail("");}).catch(()=>alert("Could not submit. Try again."));
                }}>Submit Feedback</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* About SOLUS Modal */}
      {showAbout&&(
        <div className="overlay" onClick={()=>setShowAbout(false)} style={{zIndex:10000}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:520}}>
            <button onClick={()=>setShowAbout(false)} style={{position:"absolute",top:16,right:18,background:"none",border:"none",color:"var(--muted)",fontSize:22,cursor:"pointer",lineHeight:1}}>x</button>
            <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:"var(--amber)",marginBottom:16,textTransform:"uppercase"}}>About SOLUS</div>
            <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.7,marginBottom:16}}>
              SOLUS Copilot is built by Almeena Shaikh, a USAF veteran, FAA private pilot, and ML engineer at Google Cloud.
            </div>
            {[
              {title:"WHAT SOLUS IS",body:"An AI pre-departure risk synthesis tool for GA pilots. It applies the FAA PAVE framework and Threat Error Management principles to your specific flight conditions using live FAA data and NTSB accident patterns."},
              {title:"WHAT SOLUS IS NOT",body:"A substitute for an official preflight weather briefing, a certified flight instructor, or a formal risk management program. All go/no-go decisions remain with the PIC."},
            ].map(s=>(
              <div key={s.title} style={{marginBottom:14}}>
                <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--amber)",letterSpacing:"0.12em",marginBottom:4,textTransform:"uppercase"}}>{s.title}</div>
                <div style={{fontSize:12,color:"var(--text3)",lineHeight:1.7}}>{s.body}</div>
              </div>
            ))}
            <div style={{marginBottom:14}}>
              <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--amber)",letterSpacing:"0.12em",marginBottom:6,textTransform:"uppercase"}}>Data Sources</div>
              <div style={{fontSize:11,color:"var(--text3)",lineHeight:1.8,fontFamily:"var(--mono)"}}>
                <div>FAR currency: 14 CFR Part 61 and Part 91</div>
                <div>Risk framework: FAA-H-8083-2 Risk Management Handbook</div>
                <div>NTSB data: NTSB aviation accident database (public)</div>
                <div>Weather: FAA Aviation Weather Center (aviationweather.gov)</div>
                <div>NOTAMs: FAA NOTAM system</div>
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--amber)",letterSpacing:"0.12em",marginBottom:4,textTransform:"uppercase"}}>Validation</div>
              <div style={{fontSize:11,color:"var(--text3)",lineHeight:1.7}}>
                Currency calculations have been tested against 14 CFR. Risk Score weighting is based on NTSB accident pattern analysis. The tool is in private beta and actively being pressure-tested by CFIs and working GA pilots. If you find an error, please report it: <a href="mailto:almeena@soluscopilot.com" style={{color:"var(--amber)"}}>almeena@soluscopilot.com</a>
              </div>
            </div>
            <div style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--mono)",lineHeight:1.5,borderTop:"1px solid var(--border)",paddingTop:12}}>
              SOLUS Copilot Inc. EIN: 41-4939987<br/>Patent Pending
            </div>
          </div>
        </div>
      )}
    </div></div>
  );
}


function WaitlistModal({onClose, triggered, ac, currDates}){
  const [form,setForm]=useState({name:"",email:"",role:"GA Pilot",org:""});
  const [submitted,setSubmitted]=useState(false);
  const [loading,setLoading]=useState(false);
  const [safetyScore,setSafetyScore]=useState(null);
  const [referralCode,setReferralCode]=useState(null);
  const [copied,setCopied]=useState(false);
  const [shadowResult,setShadowResult]=useState(null);
  const [shadowLoading,setShadowLoading]=useState(false);
  const [accessCode,setAccessCode]=useState("");
  const [accessPassword,setAccessPassword]=useState("");
  const [accessStatus,setAccessStatus]=useState(null);
  const [accessLoading,setAccessLoading]=useState(false);
  async function redeemCode(){
    if(!accessCode.trim()||!form.email||!form.name||!form.role||!form.org||!accessPassword)return setAccessStatus("All fields required: name, email, role, organization, password, and access code.");
    if(accessPassword.length<8)return setAccessStatus("Password must be at least 8 characters.");
    setAccessLoading(true);setAccessStatus(null);
    try{
      // Step 1: Redeem the access code
      const redeemRes=await fetch("https://solus-backend-9wde.onrender.com/access/redeem",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({code:accessCode.trim(),email:form.email,name:form.name,org:form.org,platform:typeof navigator!=="undefined"&&/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)?"mobile":"web"})
      });
      const redeemData=await redeemRes.json();
      if(!redeemData.success){setAccessStatus(redeemData.error||"Invalid code");setAccessLoading(false);return;}

      // Step 2: Create account with Supabase
      const isGmail=form.email.trim().toLowerCase().endsWith("@gmail.com");
      if(isGmail){
        // Gmail users: sign in with Google after code redeem
        localStorage.setItem("solus_waitlisted","1");
        localStorage.setItem("solus_allowed","1");
        localStorage.setItem("solus_entered","1");
        setAccessStatus("granted");
        setTimeout(async()=>{
          await supabaseClient.auth.signInWithOAuth({provider:"google",options:{redirectTo:window.location.origin}});
        },1200);
      }else{
        // Non-Gmail: create email/password account
        const{data:signUpData,error:signUpErr}=await supabaseClient.auth.signUp({
          email:form.email,password:accessPassword,
          options:{data:{name:form.name,role:form.role}}
        });
        if(signUpErr){setAccessStatus(signUpErr.message);setAccessLoading(false);return;}
        // Try to sign in immediately
        const{data:signInData,error:signInErr}=await supabaseClient.auth.signInWithPassword({email:form.email,password:accessPassword});
        if(signInErr){
          setAccessStatus("Account created. Check your email to confirm, then sign in.");
          localStorage.setItem("solus_waitlisted","1");
          localStorage.setItem("solus_allowed","1");
          setAccessLoading(false);return;
        }
        localStorage.setItem("solus_token",signInData.session.access_token);
        localStorage.setItem("solus_waitlisted","1");
        localStorage.setItem("solus_allowed","1");
        localStorage.setItem("solus_entered","1");
        setAccessStatus("granted");
        setTimeout(()=>window.location.reload(),1200);
      }
    }catch{setAccessStatus("Connection failed. Try again.");}
    setAccessLoading(false);
  }
  const roles=["GA Pilot","Flight Instructor (CFI)","Flight School / FBO","A&P Mechanic","Commercial Pilot","DoD / Military","Investor","Other"];
  async function submit(){
    if(!form.name||!form.email)return;
    setLoading(true);
    const refParam=new URLSearchParams(window.location.search).get("ref")||"";
    try{
      const res=await fetch("https://solus-backend-9wde.onrender.com/waitlist/submit",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        email:form.email,name:form.name,role:form.role,org:form.org,
        hasAircraft:!!(ac&&ac.nNumber),
        hasCurrency:!!(currDates&&currDates.lastBFR),
        referredBy:refParam||undefined,
        platform:typeof navigator!=="undefined"&&/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)?"mobile":"web",
      })});
      const data=await res.json();
      if(data.error)console.error("Waitlist submit error:",data.error);
      if(data.safety_score)setSafetyScore(data.safety_score);
      if(data.referral_code){setReferralCode(data.referral_code);localStorage.setItem("solus_referral_code",data.referral_code);}
    }catch(err){console.error("Waitlist submit failed:",err);}
    localStorage.setItem("solus_waitlisted","1");
    localStorage.setItem("solus_referral_name",form.name);
    localStorage.setItem("solus_referral_email",form.email);
    setLoading(false);setSubmitted(true);
  }
  async function handleShadowUpload(e){
    const file=e.target.files[0];
    if(!file)return;
    setShadowLoading(true);
    const text=await file.text();
    const lines=text.split('\n').filter(l=>l.trim());
    const headers=lines[0].split(',').map(h=>h.trim().toLowerCase().replace(/"/g,''));
    const flights=lines.slice(1).map(line=>{
      const vals=line.split(',').map(v=>v.trim().replace(/"/g,''));
      const obj={};
      headers.forEach((h,i)=>obj[h]=vals[i]||'');
      return{
        date:obj['date']||obj['flight date']||'',
        aircraft:obj['aircraft']||obj['tail number']||obj['registration']||'',
        route:obj['route']||(obj['from']?obj['from']+'-'+obj['to']:''),
        duration:obj['duration']||obj['total time']||'',
        conditions:obj['conditions']||obj['remarks']||''
      };
    }).filter(f=>f.date);
    try{
      const res=await fetch('https://solus-backend-9wde.onrender.com/shadow/analyze',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({email:form.email,flights})
      });
      const data=await res.json();
      setShadowResult(data);
    }catch(err){console.error('Shadow analyze failed:',err);}
    setShadowLoading(false);
  }
  return(
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:460,borderColor:"var(--glassBorder)"}}>
        <button onClick={onClose} style={{position:"absolute",top:16,right:18,background:"none",border:"none",color:"var(--muted)",fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        {submitted?(
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11,fontWeight:700,letterSpacing:"0.2em",color:"var(--green)",marginBottom:16,textTransform:"uppercase"}}>Account Created</div>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:10,letterSpacing:"0.18em",color:"var(--amber)",marginBottom:8,textTransform:"uppercase"}}>Your Pilot Safety Score</div>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:80,fontWeight:700,color:"var(--amber)",lineHeight:1,marginBottom:2}}>{safetyScore||"--"}</div>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11,color:"var(--text3)",marginBottom:16,letterSpacing:"0.06em"}}>/100</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:700,color:"var(--white)",marginBottom:8}}>Welcome to SOLUS.</div>
            <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.75,marginBottom:28}}>
              Your free trial is active. Explore the full flight intelligence platform.<br/>
              No spam. No drip campaigns. Just SOLUS.
            </div>
            <div style={{background:"var(--amberDim)",border:"1px solid var(--amberBorder)",borderRadius:4,padding:"14px 20px",marginBottom:24,textAlign:"left"}}>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,color:"var(--amber)",letterSpacing:"0.18em",marginBottom:6,textTransform:"uppercase"}}>Get Started</div>
              <div style={{fontSize:13,color:"var(--text2)",lineHeight:1.6}}>The web app is live at soluscopilot.com. Explore checklists, W&B, NTSB, FAR/AIM, and more.</div>
            </div>
            <div style={{background:"var(--amberDim)",border:"1px solid var(--amberBorder)",borderRadius:4,padding:"12px 16px",marginBottom:20,textAlign:"left"}}>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,color:"var(--amber)",letterSpacing:"0.18em",marginBottom:6,textTransform:"uppercase"}}>Share with a pilot</div>
              <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:10}}>Share your score with another pilot. Every referral helps the community.</div>
              {referralCode&&(
                <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12,color:"var(--amber)",background:"var(--amberDim)",border:"1px solid var(--amberBorder)",borderRadius:6,padding:"8px 12px",marginBottom:10,textAlign:"center",letterSpacing:"0.15em"}}>{referralCode}</div>
              )}
              <button className="btn btn-sm" style={{borderColor:copied?"var(--green)":"var(--amber)",color:copied?"var(--green)":"var(--amber)",width:"100%",padding:"9px",fontSize:12,transition:"all 0.15s"}}
                onClick={()=>{
                  const ref=referralCode||localStorage.getItem("solus_referral_code")||"SOLUS";
                  const score=safetyScore||"--";
                  const txt="I scored "+score+"/100 on the SOLUS Pilot Safety Assessment. Try it free and get your score: soluscopilot.com?ref="+ref;
                  if(navigator.clipboard){
                    navigator.clipboard.writeText(txt).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2000);}).catch(()=>alert(txt));
                  }else{alert(txt);}
                }}>
                {copied?"Copied!":"Share your score ↗"}
              </button>
            </div>
            <div style={{background:"var(--amberDim)",border:"1px solid var(--amberBorder)",borderRadius:4,padding:"12px 16px",marginBottom:20,textAlign:"left"}}>
              <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,color:"var(--amber)",letterSpacing:"0.18em",marginBottom:6,textTransform:"uppercase"}}>Shadow Mode</div>
              <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:10}}>Upload a CSV of your logbook and SOLUS will retroactively flag flights where currency or review issues existed.</div>
              {shadowLoading?(
                <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12,color:"var(--amber)",textAlign:"center",padding:"12px 0"}}>Analyzing your logbook...</div>
              ):!shadowResult?(
                <label style={{display:"block",textAlign:"center",cursor:"pointer"}}>
                  <input type="file" accept=".csv" onChange={handleShadowUpload} style={{display:"none"}}/>
                  <span style={{display:"inline-block",fontFamily:"'JetBrains Mono',monospace",fontSize:12,color:"var(--amber)",border:"1px solid var(--amber)",borderRadius:4,padding:"9px 20px",letterSpacing:"0.08em",cursor:"pointer",transition:"all 0.15s"}}>Upload logbook CSV</span>
                </label>
              ):(
                <div>
                  <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11,color:shadowResult.flags_found>0?"var(--amber)":"var(--green)",marginBottom:8}}>{shadowResult.summary}</div>
                  <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>{shadowResult.flights_analyzed} flights analyzed</div>
                  {shadowResult.flags.slice(0,3).map((flag,idx)=>(
                    <div key={idx} style={{borderLeft:"3px solid "+(flag.severity==="red"?"var(--red)":"var(--amber)"),background:"rgba(0,0,0,0.2)",borderRadius:2,padding:"8px 10px",marginBottom:6}}>
                      <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:flag.severity==="red"?"var(--red)":"var(--amber)",marginBottom:2}}>{flag.flag_type} / {flag.far}</div>
                      <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.5}}>{flag.message}</div>
                      <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>{flag.date}{flag.aircraft?" / "+flag.aircraft:""}{flag.route?" / "+flag.route:""}</div>
                    </div>
                  ))}
                  {shadowResult.flags.length>3&&<div style={{fontSize:10,color:"var(--text3)",textAlign:"center",marginTop:4}}>+{shadowResult.flags.length-3} more flags</div>}
                </div>
              )}
            </div>
            <button className="btn btn-p" onClick={onClose} style={{padding:"11px 32px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"0.08em"}}>Continue exploring</button>
          </div>
        ):(
          <>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,fontWeight:700,letterSpacing:"0.22em",color:"var(--amber)",marginBottom:10,textTransform:"uppercase"}}>Get Started</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:700,color:"var(--white)",marginBottom:8}}>Create your free account.</div>
                <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.65,marginBottom:20}}>
                  Sign up to unlock full access to SOLUS flight intelligence. Free trial included.
                </div>
            <div style={{display:"flex",gap:10,marginBottom:20}}>
              {[["700K","GA pilots in US"],["3-Day","free trial"],["Manual","every review"]].map(([n,l])=>(
                <div key={n} style={{flex:1,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:6,padding:"10px 8px",textAlign:"center"}}>
                  <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:13,fontWeight:700,color:"var(--amber)",marginBottom:2}}>{n}</div>
                  <div style={{fontSize:9.5,color:"var(--muted)",letterSpacing:"0.06em"}}>{l}</div>
                </div>
              ))}
            </div>
            <input type="hidden" name="form-name" value="solus-waitlist"/>
            {[["Full Name","name","text","Your name"],["Email","email","email","you@example.com"]].map(([l,f,t,ph])=>(
              <div key={f} style={{marginBottom:10}}>
                <div className="inpl">{l}</div>
                <input type={t} className="inp" value={form[f]} onChange={e=>setForm(p=>({...p,[f]:e.target.value}))} placeholder={ph}/>
              </div>
            ))}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              <div>
                <div className="inpl">I am a...</div>
                <select className="sel" value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}>{roles.map(r=><option key={r}>{r}</option>)}</select>
              </div>
              <div>
                <div className="inpl">Organization (optional)</div>
                <input type="text" className="inp" value={form.org} onChange={e=>setForm(p=>({...p,org:e.target.value}))} placeholder="School, FBO, airline..."/>
              </div>
            </div>
            <button className="btn btn-p" onClick={submit} disabled={loading||!form.name||!form.email}
              style={{width:"100%",padding:"13px",fontSize:13,fontFamily:"'JetBrains Mono',monospace",letterSpacing:"0.08em",fontWeight:700,opacity:loading||!form.name||!form.email?0.35:1,cursor:loading||!form.name||!form.email?"not-allowed":"pointer"}}>
              {loading?"Submitting...":"Sign up →"}
            </button>
            <div style={{marginTop:16,padding:"14px 0 0",borderTop:"1px solid rgba(255,176,0,0.1)"}}>
              <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--amber)",letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:10}}>Have an access code?</div>
              <div style={{fontSize:11,color:"var(--text3)",marginBottom:10}}>Fill in all fields above, then enter your code and create a password to activate your account.</div>
              <input className="inp" value={accessPassword} onChange={e=>setAccessPassword(e.target.value)} type="password" placeholder="Create a password (min 8 characters)" style={{width:"100%",marginBottom:8,fontFamily:"var(--mono)",fontSize:11}}/>
              <div style={{display:"flex",gap:8}}>
                <input className="inp" value={accessCode} onChange={e=>setAccessCode(e.target.value.toUpperCase())} placeholder="SOLUS-ALPHA-XXX" style={{flex:1,fontFamily:"var(--mono)",fontSize:12,letterSpacing:"0.06em"}}/>
                <button className="btn btn-sm" onClick={redeemCode} disabled={accessLoading} style={{borderColor:"var(--green)",color:"var(--green)",whiteSpace:"nowrap",opacity:accessLoading?0.4:1}}>
                  {accessLoading?"...":"Redeem"}
                </button>
              </div>
              {accessStatus==="granted"&&(
                <div style={{marginTop:8,padding:"8px 12px",background:"var(--greenDim)",border:"1px solid rgba(0,230,118,0.3)",borderRadius:4,fontFamily:"var(--mono)",fontSize:11,color:"var(--green,#00E676)"}}>
                  Access granted. Welcome to SOLUS, Captain.
                </div>
              )}
              {accessStatus&&accessStatus!=="granted"&&(
                <div style={{marginTop:8,fontFamily:"var(--mono)",fontSize:10,color:"var(--red)"}}>{accessStatus}</div>
              )}
            </div>
            <div style={{textAlign:"center",marginTop:12}}><div style={{fontSize:11,color:"var(--muted)",marginBottom:8}}>or sign in instantly</div><button onClick={()=>{onClose();window.dispatchEvent(new CustomEvent('solus:openSignIn'));}} style={{width:"100%",padding:"11px",marginBottom:8,background:"rgba(255,255,255,0.07)",border:"1px solid var(--glassBorder)",borderRadius:6,color:"var(--white)",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontFamily:"var(--sans)"}}>Sign in with email</button><button onClick={async()=>{const{data,error}=await supabaseClient.auth.signInWithOAuth({provider:"google",options:{redirectTo:window.location.origin}});}} style={{width:"100%",padding:"11px",background:"rgba(255,255,255,0.07)",border:"1px solid var(--glassBorder)",borderRadius:6,color:"var(--white)",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontFamily:"var(--sans)"}}><svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>Sign in with Google</button></div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── BLOG ────────────────────────────────────────────────────────────────────
const BLOG_POSTS=[
  {
    slug:"gps-spoofing-threat-2026",
    title:"The 2026 GPS Spoofing Threat: What Every GA Pilot Needs to Know",
    date:"2026-03-20",
    author:"Almeena Shaikh",
    excerpt:"GPS spoofing incidents have surged 400% since 2024. Here is what GA pilots need to understand about the threat, how to detect it in flight, and what to do when your panel stops making sense.",
    body:`GPS spoofing is no longer a military-only concern. In 2025, over 1,200 GPS anomaly reports were filed by GA pilots in the US alone, a 400% increase from 2024. The trend is accelerating into 2026.\n\nWHAT IS GPS SPOOFING?\n\nGPS spoofing is the broadcast of counterfeit GPS signals that cause a receiver to compute an incorrect position, velocity, or time. Unlike jamming (which causes a loss of signal), spoofing is insidious: your GPS continues to show a position, but it is wrong.\n\nHOW TO DETECT IT IN FLIGHT\n\n1. Cross-check GPS position against VOR/DME fixes. If your GPS says you are over a VOR but the CDI does not center, something is wrong.\n2. Monitor groundspeed. Sudden, unexplained changes in groundspeed without corresponding wind changes are a red flag.\n3. Watch for RAIM alerts. If your GPS-equipped panel shows a RAIM failure, take it seriously.\n4. Trust your eyes. If the terrain below does not match your moving map, believe the terrain.\n\nWHAT TO DO\n\n1. Report it. File a pilot report (PIREP) immediately. Use the SOLUS Airspace Intelligence feed to alert pilots nearby.\n2. Revert to conventional navigation. VOR, DME, pilotage. This is why instrument pilots still need to know how to fly without GPS.\n3. Contact ATC. They can provide radar vectors and confirm your position.\n4. Do not rely on GPS for approaches. If you suspect spoofing, request an ILS or VOR approach instead of RNAV.\n\nThe FAA is actively investigating. In the meantime, the best defense is awareness and proficiency in non-GPS navigation.\n\nFly safe, Captain.`,
  },
  {
    slug:"why-80-percent-ga-fatal-accidents-preventable",
    title:"Why 80% of GA Fatal Accidents Are Preventable",
    date:"2026-03-28",
    author:"Almeena Shaikh",
    excerpt:"323 GA pilots died last year. The NTSB data tells the same story every time: currency lapses, bad weather calls, and risk normalization. The fix is not more training. It is better data at the right time.",
    body:`323 GA pilots died in the US last year. That number has held roughly steady for a decade. The NTSB has investigated thousands of these accidents. The patterns are not subtle.\n\nTHE SAME THREE FAILURES\n\nPull any 100 fatal GA accident reports from the NTSB database. You will find the same three root causes over and over:\n\n1. Currency and proficiency gaps. A pilot who has not flown in 45 days attempts a crosswind landing. A private pilot who has not shot an approach in five months files IFR into marginal conditions. The FARs set minimums, but minimums are not the same as proficiency.\n\n2. Weather decision-making. VFR into IMC remains the single deadliest scenario in GA. It kills more pilots per year than engine failures. The weather data exists. METARs, TAFs, PIREPs, SIGMETs. But most pilots check weather once during preflight and never update their picture en route.\n\n3. Risk normalization. This one is the hardest to fix. A pilot flies with an expired medical and nothing happens. They skip the crosswind check and land fine. Each time the shortcut works, the risk threshold moves. Until it does not.\n\nWHAT THE DATA SHOWS\n\nThe FAA and NTSB have published this data for decades. The Nall Report breaks it down every year. Controlled flight into terrain. Loss of control on approach. Fuel exhaustion. These are not mechanical failures. These are information failures.\n\nThe pilot either did not have the data, did not check the data, or normalized away from the data.\n\nAUTOMATED PRE-FLIGHT RISK SCORING\n\nWhat if every pilot saw a risk score before they touched the checklist? Not a feeling. A number. Computed from their actual currency dates, their aircraft maintenance status, the live weather at departure and destination, and the NTSB accident history for their route and conditions.\n\nThat is what SOLUS does. Five signals. One score. CLEAR, REVIEW, or HIGH.\n\nThe goal is not to ground pilots. The goal is to surface the data that already exists and put it in front of the PIC before departure. Because 80% of the time, the data was there. Someone just did not look at it.\n\nThe 323 who died last year were not bad pilots. They were under-informed pilots. That is a solvable problem.`,
  },
  {
    slug:"5-far-currency-requirements-track-automatically",
    title:"The 5 FAR Currency Requirements Every GA Pilot Should Track Automatically",
    date:"2026-04-01",
    author:"Almeena Shaikh",
    excerpt:"FAR 61.56, 61.57, 61.23. Five separate currency requirements with different windows and different consequences. Spreadsheets fail. Paper logbooks fail. Here is how digital tracking works.",
    body:`Every GA pilot has five currency requirements to maintain. Miss one and you are grounded by regulation. Miss one and fly anyway, and you are flying illegally. Here they are.\n\nFAR 61.56: FLIGHT REVIEW\n\n24 calendar months. One hour of ground, one hour of flight, with a CFI. Miss it and you cannot act as PIC. Period. This one is simple but easy to lose track of because the window is so long. Most pilots remember it is "about two years" and then realize they are three weeks past due.\n\nFAR 61.57(a): DAY PASSENGER CURRENCY\n\nThree takeoffs and three landings within the preceding 90 days. Touch-and-goes count for day currency. Miss this and you can still fly solo, but you cannot carry passengers. The 90-day window rolls, so the date that matters is the oldest of your three most recent landings.\n\nFAR 61.57(b): NIGHT PASSENGER CURRENCY\n\nThree takeoffs and three full-stop landings at night within 90 days. Touch-and-goes do not count. Night is defined as one hour after sunset to one hour before sunrise. This is a separate requirement from day currency.\n\nFAR 61.23: MEDICAL CERTIFICATE\n\nFirst class: 6 months if over 40, 12 months if under 40. Second class: 12 months. Third class: 24 months (60 months for BasicMed eligible). The clock starts from the date of your exam. Most pilots carry third class, so the window is long. But it catches people who switch between classes or who age into shorter windows.\n\nFAR 61.57(c): INSTRUMENT CURRENCY\n\nSix instrument approaches, holding procedures, and intercepting/tracking courses within the preceding six calendar months. If you lapse, you have a six-month grace period to get current with a safety pilot. After that, you need an Instrument Proficiency Check.\n\nWHY SPREADSHEETS FAIL\n\nPilots track these dates in spreadsheets, on sticky notes, in the back of their logbook. The problem is none of these systems alert you proactively. You check your spreadsheet the night before a flight and discover your medical expired last week. Or you do not check at all.\n\nDigital tracking solves this by computing days-to-expiry in real time and sending alerts when windows are closing. SOLUS tracks all five requirements, computes your status as CURRENT, EXPIRING, or LAPSED for each one, and rolls that into your pre-flight risk score.\n\nYou should never be surprised by an expired currency item. The data is simple. The math is simple. The only hard part is remembering to check. Automate the check.`,
  },
  {
    slug:"building-solus-ntsb-data-real-time-risk",
    title:"Building SOLUS: From NTSB Data to Real-Time Risk Intelligence",
    date:"2026-04-02",
    author:"Almeena Shaikh",
    excerpt:"How we built a risk scoring system for GA pilots using 50 years of NTSB data, live FAA weather feeds, and an AI copilot that actually understands aviation.",
    body:`SOLUS started with a simple question: what if the data that exists across a dozen different aviation systems could be pulled together into one pre-flight brief?\n\nTHE PROBLEM\n\nA typical GA pilot checks weather on ForeFlight, currency in a logbook, NOTAMs on a separate website, and maintenance in a binder in the hangar. None of these systems talk to each other. None of them compute a combined risk picture. The pilot does that integration in their head, under time pressure, often at 5 AM before a dawn departure.\n\nTHE RISK SCORE\n\nThe SOLUS risk score is a weighted sum of five signal categories:\n\n1. Pilot currency (BFR, medical, day/night recency, IFR status). Each item is scored based on days-to-expiry and the severity of a lapse. A lapsed BFR adds 25 points. An expiring medical adds 8.\n\n2. Aircraft status. Open squawks add 15 points. An overdue annual adds 20. These are hard stops in the FARs.\n\n3. Weather complexity. We pull live METARs, TAFs, PIREPs, and SIGMETs from the FAA Aviation Weather API. Ceilings below 1000, visibility below 3, convective SIGMETs, and active icing PIREPs all contribute to the score.\n\n4. Recency gap. If you have not flown in 60+ days, your motor skills and judgment are degraded. The NTSB data is clear on this. We add points based on time since last flight.\n\n5. Airspace intelligence. GPS anomaly reports, pilot hazard submissions, and TFR proximity.\n\nThe score maps to three levels: CLEAR (under 35), REVIEW (35 to 70), HIGH (over 70). We do not make go/no-go decisions. That belongs to the PIC per FAR 91.7. We surface the data.\n\nTHE AI COPILOT\n\nSOLUS uses Anthropic Claude as its core LLM. Every query gets a system prompt that includes the pilot's currency status, aircraft profile, and live weather data. The AI does not hallucinate FARs because we seed it with the actual regulatory text.\n\nWhen a pilot asks "am I current to fly tonight with passengers," SOLUS checks FAR 61.57(b), computes the actual days since the third-oldest night landing, and gives a specific answer with the regulation reference.\n\nLIVE FAA INTEGRATION\n\nWe proxy three FAA data sources through our backend:\n\n- Aviation Weather API for METAR, TAF, and PIREPs\n- FAA NOTAM API for active NOTAMs by airport\n- FAA Aircraft Registry for N-number lookups\n\nAll data is fetched fresh on each request. No caching of safety-critical weather data.\n\nWHAT IS NEXT\n\nMyFlightbook integration for automatic logbook sync. Push notifications for currency expirations. Route-based NTSB pattern matching (show me accidents on this route in similar conditions). And a mobile app that works offline for currency and checklists.\n\nWe built SOLUS because 323 pilots died last year and 80% of those accidents trace back to information gaps. The data existed. It just was not in front of the pilot at the right time.`,
  },
  {
    slug:"far-currency-requirements-ga-pilots",
    title:"The 5 FAR Currency Requirements Every GA Pilot Gets Wrong",
    date:"2026-04-09",
    author:"Almeena Shaikh",
    excerpt:"There are five FAR currency requirements every GA pilot must maintain. Most pilots know three. The other two show up in accident reports.",
    body:`There are five FAR currency requirements every GA pilot must maintain. Most pilots know about three of them. The other two are the ones that show up in accident reports.

Here is every one of them, what the regulation actually says, and the exact mistake pilots make with each.

## 1. VFR Day Currency (FAR 61.57(a))

**What it says:** 3 takeoffs and landings in the preceding 90 days to carry passengers.

**The mistake:** Pilots count solo flights toward passenger currency. They do not count. Only flights where you performed the takeoff AND landing qualify. A simulated approach where you go around does not count.

**SOLUS flags:** Days since last qualifying landing, expiry date, passenger-carrying status.

## 2. VFR Night Currency (FAR 61.57(b))

**What it says:** 3 takeoffs and full-stop landings at night in the preceding 90 days to carry passengers at night.

**The mistake:** Touch-and-goes do not count for night currency. They must be full-stop landings. Most pilots doing night currency work in the pattern do touch-and-goes. None of them count.

**SOLUS flags:** Whether your last 3 night landings were full-stop.

## 3. Instrument Currency (FAR 61.57(c))

**What it says:** 6 instrument approaches, holding procedures, and intercepting/tracking courses within the preceding 6 months.

**The mistake:** The 6-month clock resets every time you complete the requirements, not from your last flight. If you flew 6 approaches on one flight 5 months ago, you are current but have been flying without practicing for 5 months.

**SOLUS flags:** Approach count, days since last IFR flight, days to expiry.

## 4. Flight Review (FAR 61.56)

**What it says:** A flight review with a CFI every 24 calendar months. At least 1 hour of flight training and 1 hour of ground training.

**The mistake:** Calendar months, not days. If your last BFR was January 15, 2024, it expires January 31, 2026, not January 15. You get the rest of that month. Most pilots do not know this and schedule their BFR a month early.

**SOLUS flags:** Exact expiry date using calendar month calculation, days remaining.

## 5. Medical Certificate

**What it says:** Varies by class and age. Third class for private pilots: 60 calendar months under age 40, 24 calendar months age 40 and over.

**The mistake:** BasicMed is an alternative to a third-class medical but has its own requirements. You must have held a medical after July 14, 2006, must see a physician every 48 months, and must complete an online medical course every 24 months. Pilots on BasicMed often forget the online course requirement.

**SOLUS flags:** Medical class, expiry date, BasicMed course renewal date.

All five of these requirements operate on different timeframes: 90 days, 6 months, 24 months, 24 to 60 months. Tracking them manually across a logbook and calendar is how lapses happen. SOLUS tracks all five simultaneously and tells you exactly how many days you have before each one expires.

The FAA maintains the full text of [14 CFR Part 61](https://www.ecfr.gov/current/title-14/chapter-I/subchapter-D/part-61) on the Electronic Code of Federal Regulations.

SOLUS is free to try. Sign up at [soluscopilot.com](https://soluscopilot.com).`,
  },
  {
    slug:"ntsb-database-ga-pilot-accidents",
    title:"What the NTSB Database Actually Tells Us About How GA Pilots Die",
    date:"2026-04-16",
    author:"Almeena Shaikh",
    excerpt:"After reading over 847 NTSB accident reports, here is what the database actually shows about GA pilot fatalities and the decisions that preceded them.",
    body:`I have read over 847 NTSB accident reports. Not for morbid curiosity. Because every one of them contains the same thing: a decision the pilot made, and the data that existed before they made it.

Here is what the database actually shows when you read it the way I do.

## The Accident Chain Starts 45 Minutes Before Impact

In almost every fatal GA accident, the chain of events that led to the crash started at least 45 minutes before impact. A weather briefing that showed marginal conditions. A currency that had lapsed. An aircraft with an unresolved squawk. The pilot had all of this information. None of it was synthesized into a single picture.

## Experience Makes It Worse

The most dangerous profile in the NTSB database is not the student pilot. It is the pilot with 500 to 1,500 hours who has not had an incident in years. Complacency is a function of experience, not recklessness. The accident chain almost always starts with a pilot who has done this flight before.

## The Top 5 Accident Categories (And What They Have in Common)

- **VFR into IMC:** Pilot had a weather briefing. Pressed on anyway.
- **Loss of control on approach:** Pilot overshot final, increased bank instead of going around.
- **Fuel exhaustion:** Pilot trusted the gauges. Gauges lied.
- **Runway incursion:** Pilot was programming GPS on the ground. Head down at the wrong moment.
- **Density altitude:** Pilot did not compute actual performance numbers.

What they have in common: the data to prevent every one of these accidents existed before the pilot started the engine. It was not synthesized. It was not surfaced. It did not reach the pilot at the moment of decision.

## The Flag That Would Have Changed It

For each of the 847 reports I analyzed, I asked one question: what single flag, surfaced before departure, would have changed the decision? In 73% of cases, the answer was one of five things: a currency issue, a weather complexity flag, an aircraft maintenance item, a recency gap, or a route with known accident density.

The [NTSB aviation accident database](https://www.ntsb.gov/Pages/AviationQuery.aspx) is not a record of bad pilots. It is a record of good pilots making decisions without complete information. SOLUS is the tool that closes that gap.

SOLUS is free to try. Sign up at [soluscopilot.com](https://soluscopilot.com).`,
  },
  {
    slug:"foreflight-is-not-a-preflight-brief",
    title:"Why ForeFlight Is Not a Pre-Flight Brief",
    date:"2026-04-23",
    author:"Almeena Shaikh",
    excerpt:"ForeFlight is the best data display tool in GA. But it does not synthesize your currency, aircraft status, and NTSB patterns into a risk assessment. Here is why that distinction matters.",
    body:`I use ForeFlight. Every pilot I know uses ForeFlight. This is not an argument against ForeFlight. This is a precise description of what ForeFlight does and does not do, and why the distinction matters.

## What ForeFlight Does

ForeFlight is a data display tool. It pulls METARs, TAFs, NOTAMs, TFRs, airport information, charts, and filing. It presents data clearly and beautifully. It is the best data display tool in GA.

## What ForeFlight Does Not Do

ForeFlight does not know your BFR lapsed last month.
ForeFlight does not know your aircraft had a squawk closed 3 days ago.
ForeFlight does not know 2 pilots reported turbulence on your exact route this morning.
ForeFlight does not know you have done 80% of your flying in VMC and this is your first IFR flight in 4 months.
ForeFlight does not know the last 3 accidents on your specific route involved pilots with your currency profile.

ForeFlight shows you data. It does not synthesize it.

## The Difference Between Data and a Brief

Every airline departure has a pre-flight brief. Every military sortie has a pre-flight brief. The brief is not a data dump. It is a synthesized risk assessment: here is what is different about this flight, here is the threat picture, here is the go/no-go recommendation.

GA pilots have never had this. They have ForeFlight, a logbook, a maintenance log, and a weather briefing service. None of these tools talk to each other. The synthesis happens in the pilot's head, under time pressure, before every flight.

That is where accidents happen.

The [FAA](https://www.faa.gov/data_research/accident_incident) publishes accident and incident data that confirms this pattern year after year. The information existed. The tools existed. The synthesis did not.

SOLUS is the pre-flight brief. It pulls your currency from your profile, your aircraft status from your maintenance log, live FAA data from the same sources ForeFlight uses, and 50 years of NTSB accident patterns, and synthesizes it into a single risk assessment before you ask.

SOLUS is free to try. Sign up at [soluscopilot.com](https://soluscopilot.com).`,
  },
];

function LegalPage({title,updated,children}){
  const ss={marginBottom:"1.5rem"};
  const hs={fontFamily:"'JetBrains Mono',monospace",color:"var(--white,#F0F2F5)",fontSize:"1.1rem",marginBottom:"0.5rem"};
  const ts={fontFamily:"system-ui,sans-serif",color:"var(--white,#F0F2F5)",fontSize:"0.95rem",lineHeight:"1.7",opacity:0.88};
  const ls={...ts,paddingLeft:"1.25rem",marginTop:"0.25rem"};
  return(
    <div style={{background:"var(--bg,#0B0C10)",minHeight:"100vh",padding:"2.5rem 1rem"}}>
      <div style={{maxWidth:720,margin:"0 auto"}}>
        <a href="/" style={{fontFamily:"'JetBrains Mono',monospace",color:"#66FCF1",textDecoration:"none",fontSize:"0.85rem",display:"inline-block",marginBottom:"2rem"}}>&larr; Back to SOLUS</a>
        <h1 style={{fontFamily:"'JetBrains Mono',monospace",color:"var(--white,#F0F2F5)",fontSize:"1.8rem",marginBottom:"0.25rem"}}>{title}</h1>
        <p style={{...ts,opacity:0.55,marginBottom:"2rem"}}>Last updated: {updated}</p>
        {React.Children.map(children,child=>child)}
        <p style={{...ts,opacity:0.4,marginTop:"2.5rem",fontSize:"0.8rem"}}>SOLUS Copilot Inc. | <a href="mailto:almeena@soluscopilot.com" style={{color:"#66FCF1"}}>almeena@soluscopilot.com</a></p>
      </div>
    </div>
  );
}

function Privacy(){
  const S={marginBottom:"1.5rem"};
  const H={fontFamily:"'JetBrains Mono',monospace",color:"var(--white,#F0F2F5)",fontSize:"1.1rem",marginBottom:"0.5rem"};
  const T={fontFamily:"system-ui,sans-serif",color:"var(--white,#F0F2F5)",fontSize:"0.95rem",lineHeight:"1.7",opacity:0.88};
  const L={...T,paddingLeft:"1.25rem",marginTop:"0.25rem"};
  const TH={...T,fontFamily:"'JetBrains Mono',monospace",fontSize:"0.85rem"};
  return(
    <LegalPage title="Privacy Policy" updated="April 9, 2026">
      <div style={S}>
        <h2 style={H}>1. Who We Are</h2>
        <p style={T}>SOLUS Copilot Inc. ("SOLUS," "we," "us," "our") operates the SOLUS platform at soluscopilot.com and the SOLUS mobile application. We are the data controller for the personal data described in this policy.</p>
        <p style={{...T,marginTop:"0.5rem"}}>Data Protection Contact: <a href="mailto:almeena@soluscopilot.com" style={{color:"#66FCF1"}}>almeena@soluscopilot.com</a></p>
      </div>

      <div style={S}>
        <h2 style={H}>2. What Data We Collect</h2>
        <p style={T}>We collect the following categories of personal data. For each category, we state the legal basis (GDPR) and business purpose.</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2a. Account Data</h3>
        <ul style={L}>
          <li><strong>Email address</strong> - collected at signup for authentication and communication. Legal basis: contract performance.</li>
          <li><strong>Full name</strong> - collected at signup for account identification. Legal basis: contract performance.</li>
          <li><strong>Password hash</strong> - stored by Supabase Auth. We never see or store your plaintext password. Legal basis: contract performance.</li>
          <li><strong>Organization/flight school</strong> - collected optionally at signup. Legal basis: legitimate interest (service customization).</li>
          <li><strong>Date of birth</strong> - collected optionally for medical certificate duration calculation. Legal basis: consent.</li>
        </ul>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2b. Pilot Currency Data</h3>
        <ul style={L}>
          <li><strong>Flight review (BFR) date</strong> - FAR 61.56 compliance tracking</li>
          <li><strong>Medical certificate date and class</strong> - FAR 61.23 compliance tracking</li>
          <li><strong>Day landing dates (3 most recent)</strong> - FAR 61.57(a) compliance tracking</li>
          <li><strong>Night landing and takeoff dates (3 most recent each)</strong> - FAR 61.57(b) compliance tracking</li>
          <li><strong>IFR proficiency date, approach count, hold count</strong> - FAR 61.57(c) compliance tracking</li>
          <li><strong>Tailwheel currency date</strong> - FAR 61.31(i) compliance tracking</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>Legal basis: contract performance (core service function). This data is entered voluntarily by the user.</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2c. Aircraft Data</h3>
        <ul style={L}>
          <li><strong>N-number (tail number)</strong></li>
          <li><strong>Aircraft type/model</strong></li>
          <li><strong>Total airframe time</strong></li>
          <li><strong>Engine time (SMOH)</strong></li>
          <li><strong>Last annual inspection date</strong></li>
          <li><strong>Open squawks/discrepancies</strong></li>
          <li><strong>Airworthiness determination records</strong> - PIC airworthiness check timestamps</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>Legal basis: contract performance. FAA aircraft registry data (public) may be fetched automatically by N-number.</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2d. Flight and Logbook Data</h3>
        <ul style={L}>
          <li><strong>Flight entries</strong> - date, route, aircraft, duration, landings, approaches, conditions, remarks</li>
          <li><strong>Logbook page images</strong> - uploaded photos of physical logbook pages, stored in encrypted cloud storage</li>
          <li><strong>MyFlightbook OAuth tokens</strong> - encrypted access tokens for syncing flight data from MyFlightbook</li>
          <li><strong>Extracted flight data</strong> - structured data parsed from logbook images via AI</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>Legal basis: consent (user initiates upload/sync). Logbook images are stored in Supabase Storage with per-user access controls.</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2e. AI Chat and Query Data</h3>
        <ul style={L}>
          <li><strong>Chat messages</strong> - questions you ask the SOLUS AI copilot and the responses generated</li>
          <li><strong>Flight context</strong> - departure/destination airports, departure time, fuel on board (when provided)</li>
          <li><strong>Query categories</strong> - automated classification of queries (weather, currency, emergency, etc.) for analytics</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>Legal basis: contract performance. Chat content is sent to Anthropic (Claude API) for processing. Anthropic does not use API inputs for model training per their commercial API data policy. In addition to your visible chat history, we maintain a separate internal query log that records your email address, query text, a truncated AI response (up to 5,000 characters), model used, token count, and response time. This log is used for service analytics, debugging, and abuse prevention. See Section 6 for retention details.</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2f. Post-Flight Debrief Data</h3>
        <ul style={L}>
          <li><strong>Debrief entries</strong> - flight date, route, aircraft, duration, conditions encountered, pilot notes</li>
          <li><strong>Pre-flight flag correlation</strong> - whether pre-flight flags materialized during flight</li>
          <li><strong>AI-generated debrief analysis</strong></li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>Legal basis: consent. This data forms part of the behavioral dataset used for aggregate safety research and future usage-based insurance modeling.</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2g. Airspace Reports</h3>
        <ul style={L}>
          <li><strong>Hazard reports</strong> - type, location, altitude, description, GPS coordinates</li>
          <li><strong>GPS anomaly reports</strong> - location and description of GPS spoofing/interference events</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>Legal basis: legitimate interest (community safety). Reports may be visible to other users within the Airspace Intelligence Feed.</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2h. Usage and Analytics Data</h3>
        <ul style={L}>
          <li><strong>Session data</strong> - login timestamps, tabs visited, platform (web/mobile)</li>
          <li><strong>Preflight session counts</strong> - how many pre-flight sessions per day</li>
          <li><strong>Risk flag acknowledgments</strong> - whether risk flags were reviewed before flight</li>
          <li><strong>Prompt usage counts</strong> - number of AI queries per session (for free tier gating)</li>
          <li><strong>Referral tracking</strong> - referral code usage for signups</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>Legal basis: legitimate interest (service improvement and abuse prevention).</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2i. Payment Data</h3>
        <ul style={L}>
          <li><strong>Subscription status</strong> - plan tier (free/solo/captain/fleet), active/cancelled/expired</li>
          <li><strong>RevenueCat customer ID</strong> - pseudonymous identifier for subscription management</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>Legal basis: contract performance. We do not store credit card numbers. All payment processing is handled by RevenueCat and the Apple App Store/Google Play Store.</p>

        <h3 style={{...H,fontSize:"1rem",marginTop:"1rem"}}>2j. Health-Adjacent Data and HIPAA Disclaimer</h3>
        <ul style={L}>
          <li><strong>Medical certificate date, class, and expiration</strong> - used solely to compute FAR 61.23 currency status</li>
          <li><strong>Date of birth</strong> - used solely to compute medical certificate duration per FAR 61.23(d)</li>
          <li><strong>BasicMed status</strong> - if applicable, used for BasicMed currency tracking per 14 CFR 68</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}><strong>HIPAA Disclaimer:</strong> SOLUS Copilot Inc. is not a healthcare provider, health plan, or healthcare clearinghouse. We are not a "covered entity" or "business associate" as defined by the Health Insurance Portability and Accountability Act (HIPAA). The medical certificate and date-of-birth data we collect is used exclusively for FAA regulatory currency calculations, not for healthcare purposes. While HIPAA does not apply to SOLUS, we treat this data as sensitive and apply the same encryption and access controls as all other personal data described in this policy.</p>
        <p style={{...T,marginTop:"0.5rem"}}>We do not share your medical certificate data, date of birth, or any health-adjacent information with insurers, employers, or any third party, except as described in Section 4 (data processors) or as required by law. If you discuss medical conditions in AI chat conversations, that content is processed by Anthropic per Section 4 and logged per Section 2e.</p>
      </div>

      <div style={S}>
        <h2 style={H}>3. How We Use Your Data</h2>
        <ul style={L}>
          <li><strong>Service delivery</strong> - operating SOLUS, computing risk scores, generating AI briefs, tracking currency</li>
          <li><strong>Safety research</strong> - aggregate, de-identified analysis of pre-flight flags and post-flight outcomes to improve risk models</li>
          <li><strong>Insurance data modeling</strong> - in the future, aggregate behavioral data may be used to develop usage-based insurance pricing models. Individual pilot data will never be shared with insurers without explicit opt-in consent.</li>
          <li><strong>Product improvement</strong> - understanding which features are used and how to improve them</li>
          <li><strong>Communication</strong> - currency expiration alerts, service updates, security notifications</li>
          <li><strong>Legal compliance</strong> - responding to lawful requests from authorities</li>
        </ul>
      </div>

      <div style={S}>
        <h2 style={H}>4. Data Sharing</h2>
        <p style={T}>We share data with the following third-party processors. Each operates under their own privacy policy and data processing agreements.</p>
        <ul style={L}>
          <li><strong>Supabase (Supabase Inc.)</strong> - database hosting, authentication, file storage. Data location: US (AWS). Supabase encrypts data at rest and in transit.</li>
          <li><strong>Anthropic (Anthropic PBC)</strong> - AI language model. Chat messages and flight context are sent for processing. Anthropic does not use API inputs for model training.</li>
          <li><strong>RevenueCat (RevenueCat Inc.)</strong> - subscription management. Receives email and subscription status.</li>
          <li><strong>Cloudflare (Cloudflare Inc.)</strong> - web hosting and CDN. Processes HTTP requests and IP addresses.</li>
          <li><strong>Render (Render Services Inc.)</strong> - backend hosting. Processes API requests.</li>
          <li><strong>MyFlightbook</strong> - only when you explicitly connect your account. Receives OAuth tokens to fetch your flight data.</li>
          <li><strong>FAA APIs</strong> - we query FAA public APIs (weather, NOTAMs, aircraft registry) on your behalf. No personal data is sent to the FAA.</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}><strong>We do not sell your personal data.</strong> We do not share your data with third parties for marketing or advertising purposes.</p>
      </div>

      <div style={S}>
        <h2 style={H}>5. Cookies and Local Storage</h2>
        <ul style={L}>
          <li><strong>Authentication token</strong> (localStorage: solus_token) - keeps you signed in</li>
          <li><strong>Aircraft and currency data</strong> (localStorage: solus_ac, solus_curr_dates) - local cache for offline access</li>
          <li><strong>Session preferences</strong> (localStorage: solus_entered, solus_onboarded) - UI state</li>
          <li><strong>Prompt counter</strong> (localStorage: solus_prompts_used) - deprecated, no longer used for gating</li>
          <li><strong>Supabase session cookie</strong> - authentication state management</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>We do not use tracking cookies, advertising pixels, or third-party analytics scripts. No data is sent to Google Analytics, Facebook, or any ad network.</p>
      </div>

      <div style={S}>
        <h2 style={H}>6. Data Retention</h2>
        <ul style={L}>
          <li><strong>Account data</strong> - retained while your account is active. Deleted within 30 days of account deletion request.</li>
          <li><strong>Chat history (visible)</strong> - retained for 30 days (Solo), unlimited (Captain/Fleet), or until you delete it via the app.</li>
          <li><strong>Internal query logs</strong> - retained for 90 days, then permanently deleted. These logs are separate from your visible chat history and are used for analytics and debugging.</li>
          <li><strong>Logbook images</strong> - retained until you delete them or your account.</li>
          <li><strong>Debrief data</strong> - retained until you delete it or your account.</li>
          <li><strong>Airspace reports</strong> - public reports retained for 24 hours in the feed, then archived.</li>
          <li><strong>Analytics data</strong> - retained in aggregate for up to 2 years. Individual session data deleted after 90 days.</li>
          <li><strong>Aggregate, de-identified data</strong> - may be retained indefinitely for safety research purposes.</li>
        </ul>
      </div>

      <div style={S}>
        <h2 style={H}>7. Your Rights Under GDPR</h2>
        <p style={T}>If you are in the European Economic Area (EEA), United Kingdom, or Switzerland, you have the following rights:</p>
        <ul style={L}>
          <li><strong>Right of access</strong> - request a copy of all personal data we hold about you</li>
          <li><strong>Right to rectification</strong> - correct inaccurate personal data</li>
          <li><strong>Right to erasure</strong> - request deletion of your personal data ("right to be forgotten")</li>
          <li><strong>Right to restrict processing</strong> - limit how we use your data</li>
          <li><strong>Right to data portability</strong> - receive your data in a structured, machine-readable format</li>
          <li><strong>Right to object</strong> - object to processing based on legitimate interest</li>
          <li><strong>Right to withdraw consent</strong> - withdraw consent at any time where processing is based on consent</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>To exercise any of these rights, email <a href="mailto:almeena@soluscopilot.com" style={{color:"#66FCF1"}}>almeena@soluscopilot.com</a>. We will respond within 30 days. You also have the right to lodge a complaint with your local data protection authority.</p>
      </div>

      <div style={S}>
        <h2 style={H}>8. Your Rights Under CCPA</h2>
        <p style={T}>If you are a California resident, you have the following rights under the California Consumer Privacy Act:</p>
        <ul style={L}>
          <li><strong>Right to know</strong> - what personal information we collect, use, disclose, and sell</li>
          <li><strong>Right to delete</strong> - request deletion of your personal information</li>
          <li><strong>Right to opt out of sale</strong> - we do not sell your personal information. No opt-out is necessary.</li>
          <li><strong>Right to non-discrimination</strong> - we will not discriminate against you for exercising your rights</li>
        </ul>
        <p style={{...T,marginTop:"0.5rem"}}>To exercise these rights, email <a href="mailto:almeena@soluscopilot.com" style={{color:"#66FCF1"}}>almeena@soluscopilot.com</a> or use the "Delete All Data" function in your SOLUS profile. We will respond within 45 days.</p>
        <p style={{...T,marginTop:"0.5rem"}}>Categories of personal information collected in the last 12 months: identifiers (name, email), commercial information (subscription status), internet activity (usage analytics), geolocation data (airspace reports only, when voluntarily submitted), and professional information (pilot currency, aircraft data).</p>
      </div>

      <div style={S}>
        <h2 style={H}>9. International Data Transfers</h2>
        <p style={T}>Your data is processed and stored in the United States via Supabase (AWS us-west-1), Render, and Cloudflare. If you are located outside the US, your data is transferred to the US for processing. We rely on standard contractual clauses and the data processing agreements of our service providers to ensure adequate protection.</p>
      </div>

      <div style={S}>
        <h2 style={H}>10. Data Security</h2>
        <ul style={L}>
          <li>All data encrypted in transit (TLS 1.2+) and at rest (AES-256 via Supabase)</li>
          <li>JWT-based authentication with token verification on every API request</li>
          <li>Rate limiting on all endpoints to prevent abuse</li>
          <li>Input sanitization on all user inputs to prevent injection attacks</li>
          <li>CORS enforcement restricting API access to authorized origins</li>
          <li>No plaintext passwords stored (Supabase Auth uses bcrypt hashing)</li>
          <li>Per-user access controls on logbook images (Supabase Storage RLS)</li>
        </ul>
      </div>

      <div style={S}>
        <h2 style={H}>11. Children</h2>
        <p style={T}>SOLUS is not intended for users under 16. We do not knowingly collect personal data from children. If you believe we have collected data from a child under 16, contact us immediately.</p>
      </div>

      <div style={S}>
        <h2 style={H}>12. Changes to This Policy</h2>
        <p style={T}>We may update this policy from time to time. We will notify users via email of material changes at least 30 days before they take effect. Continued use of SOLUS after the effective date constitutes acceptance of the updated policy.</p>
      </div>
    </LegalPage>
  );
}

function Terms(){
  const S={marginBottom:"1.5rem"};
  const H={fontFamily:"'JetBrains Mono',monospace",color:"var(--white,#F0F2F5)",fontSize:"1.1rem",marginBottom:"0.5rem"};
  const T={fontFamily:"system-ui,sans-serif",color:"var(--white,#F0F2F5)",fontSize:"0.95rem",lineHeight:"1.7",opacity:0.88};
  const L={...T,paddingLeft:"1.25rem",marginTop:"0.25rem"};
  return(
    <LegalPage title="Terms of Use" updated="April 9, 2026">
      <div style={S}>
        <h2 style={H}>1. Acceptance</h2>
        <p style={T}>By accessing or using SOLUS Copilot ("SOLUS," the "Service"), operated by SOLUS Copilot Inc. ("we," "us"), you agree to these Terms of Use. If you do not agree, do not use the Service.</p>
      </div>
      <div style={S}>
        <h2 style={H}>2. Service Description</h2>
        <p style={T}>SOLUS is an AI-powered flight intelligence platform for general aviation pilots. SOLUS provides pre-flight risk scoring, FAR currency tracking, weather briefing, NTSB data lookup, AI-powered flight briefing, logbook management, airworthiness verification, and post-flight debrief tools.</p>
      </div>
      <div style={S}>
        <h2 style={H}>3. Advisory Only Disclaimer</h2>
        <p style={{...T,fontWeight:"bold"}}>SOLUS IS ADVISORY ONLY. SOLUS DOES NOT REPLACE OFFICIAL FAA PUBLICATIONS, YOUR PILOT OPERATING HANDBOOK, CERTIFIED FLIGHT INSTRUCTOR GUIDANCE, OR YOUR OWN JUDGMENT AS PILOT IN COMMAND.</p>
        <ul style={L}>
          <li>All risk scores, currency calculations, weather data, NOTAM translations, performance estimates, and AI-generated content are for informational purposes only.</li>
          <li>Always verify all information with official sources including the FAA, NWS, your aircraft POH, and current FARs.</li>
          <li>All go/no-go decisions rest solely with the Pilot in Command per 14 CFR 91.7.</li>
          <li>SOLUS does not provide certified weather briefings. For legal weather briefings, contact Flight Service (1-800-WX-BRIEF).</li>
          <li>Performance calculations are rule-of-thumb estimates. Always compute actual performance from your specific aircraft POH.</li>
          <li>AI-generated content may contain errors. Do not rely on it as the sole source for any flight-critical decision.</li>
        </ul>
      </div>
      <div style={S}>
        <h2 style={H}>4. Eligibility</h2>
        <p style={T}>You must be at least 16 years old to use SOLUS. You are responsible for ensuring your use of SOLUS complies with all applicable laws and regulations.</p>
      </div>
      <div style={S}>
        <h2 style={H}>5. Accounts</h2>
        <ul style={L}>
          <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
          <li>You are responsible for all activity under your account.</li>
          <li>You agree to provide accurate and current information.</li>
          <li>We may suspend or terminate accounts that violate these terms.</li>
        </ul>
      </div>
      <div style={S}>
        <h2 style={H}>6. Subscriptions and Payments</h2>
        <ul style={L}>
          <li>SOLUS offers free and paid subscription tiers (Solo, Captain, Fleet).</li>
          <li>Paid subscriptions are billed monthly through RevenueCat, Apple App Store, or Google Play Store.</li>
          <li>You may cancel at any time. Cancellation takes effect at the end of the current billing period.</li>
          <li>We reserve the right to change pricing with 30 days notice to active subscribers.</li>
          <li>Refunds are handled per the refund policies of Apple, Google, or RevenueCat as applicable.</li>
        </ul>
      </div>
      <div style={S}>
        <h2 style={H}>7. Acceptable Use</h2>
        <p style={T}>You agree not to:</p>
        <ul style={L}>
          <li>Use SOLUS for any unlawful purpose</li>
          <li>Attempt to gain unauthorized access to any part of the Service</li>
          <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
          <li>Use automated scripts, bots, or scrapers to access the Service</li>
          <li>Submit false or misleading airspace reports</li>
          <li>Share your account credentials with others</li>
          <li>Resell access to the Service without written permission</li>
        </ul>
      </div>
      <div style={S}>
        <h2 style={H}>8. Intellectual Property</h2>
        <p style={T}>SOLUS, its design, risk scoring algorithms, system prompts, and brand assets are the property of SOLUS Copilot Inc. You retain ownership of your personal data, flight logs, and content you submit.</p>
      </div>
      <div style={S}>
        <h2 style={H}>9. Data and Privacy</h2>
        <p style={T}>Your use of SOLUS is also governed by our <a href="/privacy" style={{color:"#66FCF1"}}>Privacy Policy</a>, which describes how we collect, use, and protect your data.</p>
        <p style={{...T,marginTop:"0.5rem"}}><strong>Health-Adjacent Data:</strong> SOLUS collects FAA medical certificate dates, medical class, and date of birth for the sole purpose of computing pilot currency under 14 CFR 61.23. This data is aviation-regulatory in nature, not healthcare data. SOLUS Copilot Inc. is not a HIPAA-covered entity and does not provide medical advice, diagnoses, or treatment. Do not enter protected health information (PHI) into SOLUS, including through the AI chat. If you do, it will be processed and logged as described in our Privacy Policy.</p>
        <p style={{...T,marginTop:"0.5rem"}}><strong>AI Chat Logging:</strong> All messages you send to the SOLUS AI copilot, and all AI responses, are logged for analytics, debugging, and service improvement. Chat messages and flight context you provide are sent to Anthropic's Claude API for processing. By using the AI chat feature, you consent to this logging and third-party processing.</p>
      </div>
      <div style={S}>
        <h2 style={H}>10. Limitation of Liability</h2>
        <p style={T}>TO THE MAXIMUM EXTENT PERMITTED BY LAW, SOLUS COPILOT INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE. THIS INCLUDES, WITHOUT LIMITATION, ANY DAMAGES ARISING FROM FLIGHT DECISIONS MADE USING INFORMATION PROVIDED BY SOLUS.</p>
        <p style={{...T,marginTop:"0.5rem"}}>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM.</p>
      </div>
      <div style={S}>
        <h2 style={H}>11. Indemnification</h2>
        <p style={T}>You agree to indemnify and hold harmless SOLUS Copilot Inc. from any claims, damages, or expenses arising from your use of the Service or violation of these terms.</p>
      </div>
      <div style={S}>
        <h2 style={H}>12. Service Availability</h2>
        <p style={T}>We strive to maintain high availability but do not guarantee uninterrupted access. The Service may be unavailable due to maintenance, updates, or factors beyond our control. Do not rely on SOLUS as your sole source of pre-flight information.</p>
      </div>
      <div style={S}>
        <h2 style={H}>13. Termination</h2>
        <p style={T}>We may terminate or suspend your access at any time for violation of these terms. You may delete your account at any time via the Profile section or by emailing us. Upon termination, your data will be deleted per our Privacy Policy retention schedule.</p>
      </div>
      <div style={S}>
        <h2 style={H}>14. Governing Law</h2>
        <p style={T}>These terms are governed by the laws of the State of California, United States, without regard to conflict of law principles.</p>
      </div>
      <div style={S}>
        <h2 style={H}>15. Changes to These Terms</h2>
        <p style={T}>We may update these terms from time to time. Material changes will be communicated via email at least 30 days in advance. Continued use after the effective date constitutes acceptance.</p>
      </div>
      <div style={S}>
        <h2 style={H}>16. Contact</h2>
        <p style={T}>Questions about these terms? Email <a href="mailto:almeena@soluscopilot.com" style={{color:"#66FCF1"}}>almeena@soluscopilot.com</a>.</p>
      </div>
    </LegalPage>
  );
}

function Blog(){
  const [activePost,setActivePost]=useState(null);
  const today=new Date().toISOString().slice(0,10);
  const publishedPosts=BLOG_POSTS.filter(p=>p.date<=today);
  const post=activePost?publishedPosts.find(p=>p.slug===activePost):null;
  return(
    <div style={{minHeight:"100vh",background:"var(--bg,#0B0C10)",color:"var(--white,#F0F2F5)",fontFamily:"'Barlow',sans-serif"}}>
      <div style={{maxWidth:720,margin:"0 auto",padding:"48px 24px"}}>
        <div style={{marginBottom:40}}>
          <a href="/" style={{textDecoration:"none",display:"inline-flex",alignItems:"center",gap:12}}>
            <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:700,color:"var(--white,#F0F2F5)",letterSpacing:"0.04em"}}>SOL<em style={{color:"var(--amber,#FFB000)",fontStyle:"normal"}}>U</em>S</span>
            <span style={{fontSize:11,color:"var(--text3,#5A6070)",fontFamily:"'JetBrains Mono',monospace",letterSpacing:"0.1em"}}>BLOG</span>
          </a>
        </div>
        {post?(
          <div>
            <button onClick={()=>setActivePost(null)} style={{background:"none",border:"none",color:"var(--amber,#FFB000)",fontFamily:"'JetBrains Mono',monospace",fontSize:11,cursor:"pointer",marginBottom:24,padding:0,letterSpacing:"0.08em"}}>← All posts</button>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"var(--text3,#5A6070)",letterSpacing:"0.1em",marginBottom:4,textTransform:"uppercase"}}>{new Date(post.date).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"})}</div>
            {post.author&&<div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"var(--amber,#FFB000)",letterSpacing:"0.08em",marginBottom:12}}>{post.author}</div>}
            <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:36,fontWeight:700,color:"var(--white,#F0F2F5)",lineHeight:1.2,marginBottom:32}}>{post.title}</h1>
            {(()=>{
              const blocks=post.body.split("\n\n");
              const rendered=[];
              let i=0;
              const inlineFmt=(t)=>t
                .replace(/\*\*([^*]+)\*\*/g,'<strong style="color:#E0E0E0;font-weight:600">$1</strong>')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g,'<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#FFB000;text-decoration:underline">$1</a>');
              while(i<blocks.length){
                const b=blocks[i];
                if(b.trim().startsWith("## ")){
                  rendered.push(<div key={i} style={{fontSize:16,fontWeight:700,letterSpacing:"0.08em",color:"var(--amber,#FFB000)",fontFamily:"'JetBrains Mono', monospace",marginTop:32,marginBottom:12}}>{b.trim().replace(/^## /,"")}</div>);
                  i++;
                } else if(/^[A-Z][A-Z\s?'':!,.\-]{2,}$/.test(b.trim())){
                  rendered.push(<div key={i} style={{fontSize:10,fontWeight:700,letterSpacing:"0.15em",color:"var(--amber,#FFB000)",fontFamily:"'JetBrains Mono', monospace",marginTop:32,marginBottom:12,textTransform:"uppercase"}}>{b.trim()}</div>);
                  i++;
                } else if(b.trim().split("\n").every(l=>l.trim().startsWith("- "))){
                  const items=b.trim().split("\n").map(l=>l.trim().replace(/^- /,""));
                  rendered.push(
                    <ul key={i} style={{paddingLeft:20,margin:"12px 0",listStyleType:"disc"}}>
                      {items.map((l,j)=><li key={j} style={{color:"var(--text2,#B0B4C8)",fontSize:15,lineHeight:1.8,marginBottom:8}} dangerouslySetInnerHTML={{__html:DOMPurify.sanitize(inlineFmt(l))}}/>)}
                    </ul>
                  );
                  i++;
                } else if(/^1\./.test(b.trim())){
                  const lines=b.trim().split("\n").filter(l=>/^\d+\./.test(l.trim()));
                  rendered.push(
                    <ol key={i} className="blog-ol" style={{paddingLeft:20,margin:"12px 0"}}>
                      {lines.map((l,j)=><li key={j} style={{color:"var(--text2,#B0B4C8)",fontSize:15,lineHeight:1.8,marginBottom:8}} dangerouslySetInnerHTML={{__html:DOMPurify.sanitize(inlineFmt(l.replace(/^\d+\.\s*/,"")))}}/>)}
                    </ol>
                  );
                  i++;
                } else {
                  rendered.push(<p key={i} style={{fontSize:15,lineHeight:1.8,color:"var(--text2,#9CA3B0)",marginBottom:20}} dangerouslySetInnerHTML={{__html:DOMPurify.sanitize(inlineFmt(b))}}/>);
                  i++;
                }
              }
              return rendered;
            })()}
          </div>
        ):(
          <div>
            <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:32,fontWeight:700,color:"var(--white,#F0F2F5)",marginBottom:8}}>Flight Intelligence Blog</h1>
            <p style={{fontSize:14,color:"var(--text3,#5A6070)",marginBottom:32}}>Insights for pilots who take safety seriously.</p>
            {publishedPosts.map(p=>(
              <div key={p.slug} onClick={()=>setActivePost(p.slug)} style={{
                padding:"24px",marginBottom:16,background:"var(--surface)",
                border:"1px solid var(--border)",borderRadius:4,cursor:"pointer",
                transition:"border-color .15s",borderLeft:"3px solid var(--amber,#FFB000)",
              }}
                onMouseOver={e=>e.currentTarget.style.borderColor="var(--amberBorder)"}
                onMouseOut={e=>e.currentTarget.style.borderColor="var(--border)"}>
                <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"var(--text3,#5A6070)",letterSpacing:"0.1em",marginBottom:4,textTransform:"uppercase"}}>{new Date(p.date).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"})}{p.author&&<span style={{color:"var(--amber,#FFB000)",marginLeft:12}}>{p.author}</span>}</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:"var(--white,#F0F2F5)",marginBottom:8,lineHeight:1.3}}>{p.title}</div>
                <div style={{fontSize:13,color:"var(--text2,#9CA3B0)",lineHeight:1.6}}>{p.excerpt}</div>
                <div style={{marginTop:12,fontFamily:"'JetBrains Mono',monospace",fontSize:11,color:"var(--amber,#FFB000)",letterSpacing:"0.06em"}}>Read →</div>
              </div>
            ))}
          </div>
        )}
        <div style={{marginTop:48,paddingTop:24,borderTop:"1px solid var(--border)",textAlign:"center"}}>
          <a href="/" style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11,color:"var(--amber,#FFB000)",textDecoration:"none",letterSpacing:"0.08em"}}>← Back to SOLUS</a>
        </div>
      </div>
    </div>
  );
}

// ─── OFFLINE HOOK ────────────────────────────────────────────────────────────
function useOffline(){
  // True = user chose local mode OR browser has no connectivity
  const [offline,setOffline]=useState(!navigator.onLine);
  const [userForced,setUserForced]=useState(false);

  useEffect(()=>{
    const goOnline=()=>{if(!userForced)setOffline(false);};
    const goOffline=()=>setOffline(true);
    window.addEventListener("online",goOnline);
    window.addEventListener("offline",goOffline);
    return()=>{window.removeEventListener("online",goOnline);window.removeEventListener("offline",goOffline);};
  },[userForced]);

  const forceOffline=useCallback(()=>{setOffline(true);setUserForced(true);},[]);
  const forceOnline=useCallback(()=>{
    if(navigator.onLine){setOffline(false);setUserForced(false);}
  },[]);

  return{offline,userForced,networkDown:!navigator.onLine,forceOffline,forceOnline};
}

// ─── CONNECTION STATUS PILL ───────────────────────────────────────────────────
function ConnectionStatus({offline,userForced,networkDown,onClick}){
  const label=offline?"LOCAL":"LIVE";
  return(
    <button className={`conn-pill ${offline?"local":"live"}`} onClick={onClick} title={
      offline
        ?(networkDown?"No network detected - running in local mode":"Running in local mode - click to go live")
        :"Running live - click to switch to local mode"
    }>
      <div className="conn-dot"/>
      {label}
    </button>
  );
}

// ─── OFFLINE CONFIRM MODAL ────────────────────────────────────────────────────
function OfflineConfirmModal({onConfirm,onCancel}){
  return(
    <div className="overlay" onClick={onCancel}>
      <div className="modal" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
        <button onClick={onCancel} style={{position:"absolute",top:16,right:18,background:"none",border:"none",color:"var(--muted)",fontSize:22,cursor:"pointer",lineHeight:1}}>x</button>

        <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--amber)",marginBottom:6}}>Switching to Local Mode</div>
        <div className="modal-title" style={{fontSize:20,marginBottom:16}}>SOLUS will keep flying.</div>

        <div style={{marginBottom:16}}>
          {[
            {label:"Works the same",color:"var(--green)",items:["Checklists","Weight and Balance calculator","NTSB case database","FAR / AIM reference","Aircraft profile and maintenance log"]},
            {label:"Limited",color:"var(--amber)",items:["AI chat uses cached FAR/AIM only, no live context"]},
            {label:"Paused",color:"var(--red)",items:["Voice agent","Weather and METAR queries","Maintenance log cloud sync"]},
          ].map(group=>(
            <div key={group.label} style={{marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:group.color,marginBottom:6}}>{group.label}</div>
              {group.items.map(item=>(
                <div key={item} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:4}}>
                  <div style={{width:4,height:4,borderRadius:"50%",background:group.color,flexShrink:0,marginTop:6}}/>
                  <span style={{fontSize:13,color:"var(--text3)",lineHeight:1.5}}>{item}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={{padding:"10px 14px",background:"var(--surface2)",borderRadius:6,marginBottom:20,fontSize:12,color:"var(--muted)",lineHeight:1.6}}>
          Your data stays local and syncs automatically when you reconnect.
        </div>

        <div style={{display:"flex",gap:8}}>
          <button className="btn btn-p" onClick={onConfirm} style={{flex:1,padding:"11px",fontSize:13}}>Switch to Local</button>
          <button className="btn" onClick={onCancel} style={{flex:1,padding:"11px",fontSize:13}}>Stay Live</button>
        </div>
      </div>
    </div>
  );
}

// ─── SYNC TOAST ───────────────────────────────────────────────────────────────
function SyncToast({show}){
  if(!show)return null;
  return(
    <div style={{
      position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",
      background:"var(--surface)",border:"1px solid rgba(61,186,110,0.3)",
      borderRadius:4,padding:"10px 20px",zIndex:2000,
      display:"flex",alignItems:"center",gap:10,
      boxShadow:"0 4px 20px rgba(0,0,0,0.3)",
      animation:"fadeUp .2s ease",
    }}>
      <div style={{width:7,height:7,borderRadius:"50%",background:"var(--green)"}}/>
      <span style={{fontSize:13,fontWeight:600,color:"var(--green)"}}>Back online.</span>
      <span style={{fontSize:13,color:"var(--text3)"}}>Sync complete.</span>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────

// ─── WEATHER BRIEF ────────────────────────────────────────────────────────────
function WeatherBrief({ac,onWx}){
  const [airport,setAirport]=useState("");
  const [metar,setMetar]=useState(null);
  const [metarLoading,setMetarLoading]=useState(false);
  const [notams,setNotams]=useState([]);
  const [pireps,setPireps]=useState([]);
  const [sigmet,setSigmet]=useState(null);
  async function fetchMetar(ap){
    if(!ap||ap.length<3)return;
    setMetarLoading(true);
    setNotams([]);setPireps([]);setSigmet(null);
    try{
      // METAR
      const mr=await fetch("https://solus-backend-9wde.onrender.com/weather/metar/"+ap);
      const md=await mr.json();
      if(md&&md.length>0)setMetar(md[0]);else setMetar(null);
    }catch{setMetar(null);}
    try{
      // PIREPs
      const pr=await fetch("https://solus-backend-9wde.onrender.com/weather/pirep/"+ap);
      const pd=await pr.json();
      setPireps(Array.isArray(pd)?pd.slice(0,5):[]);
    }catch{setPireps([]);}
    try{
      // NOTAMs via Notamify (through backend)
      const nr=await fetch("https://solus-backend-9wde.onrender.com/weather/notam/"+ap);
      const nd=await nr.json();
      setNotams(nd.notams||[]);
    }catch{setNotams([]);}
    try{
      // SIGMETs/AIRMETs
      const sr=await fetch("https://aviationweather.gov/api/data/airsigmet?format=json&hazard=turb,ice,ifr&level=0");
      const sd=await sr.json();
      const active=(Array.isArray(sd)?sd:[]).filter(s=>s.rawAirSigmet);
      setSigmet(active.length>0?active[0]:null);
    }catch{setSigmet(null);}
    setMetarLoading(false);
    // Track weather checked in analytics + update session
    trackEvent('weather_checked',{airport:ap});
    const wToken=localStorage.getItem("solus_token");
    if(wToken)fetch("https://solus-backend-9wde.onrender.com/analytics/session",{
      method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+wToken},
      body:JSON.stringify({weather_checked:true})
    }).catch(()=>{});
  }
  function flightCat(vis,ceil){
    const v=parseFloat(vis)||10;const c2=parseInt(ceil)||10000;
    if(v>=3&&c2>=1000)return{cat:"VFR",color:"#3EC98A"};
    if(v>=1&&c2>=500)return{cat:"MVFR",color:"#FFB000"};
    if(v>=0.5&&c2>=200)return{cat:"IFR",color:"#E84040"};
    return{cat:"LIFR",color:"#E840A0"};
  }
  const sources=[
    {name:"1800wxbrief.com",desc:"Official FAA-certified weather briefing. Required for IFR. Logs your briefing.",url:ap=>"https://www.1800wxbrief.com/Website/weather?icaoCode="+(ap||""),tag:"OFFICIAL BRIEFING",primary:true,color:"var(--amber)"},
    {name:"aviationweather.gov: METAR/TAF",desc:"Raw NWS METAR, TAF, PIREPs. Decoded format available.",url:ap=>"https://aviationweather.gov/metar/data?ids="+(ap||"KSFO")+"&format=decoded&hours=3&taf=on&layout=on",tag:"NWS RAW DATA",color:"var(--text2)"},
    {name:"aviationweather.gov: SIGMETs",desc:"Active SIGMETs and AIRMETs: turbulence, icing, IFR conditions. Check before every flight.",url:()=>"https://aviationweather.gov/sigmet",tag:"SIGMET/AIRMET",color:"#E84040"},
    {name:"SkyVector.com",desc:"Free IFR/VFR charts with winds aloft and weather overlays.",url:()=>"https://skyvector.com/",tag:"CHARTS + WINDS",color:"var(--text3)"},
    {name:"ForeFlight Web",desc:"Full ForeFlight briefing (subscription required).",url:()=>"https://plan.foreflight.com/",tag:"FOREFLIGHT",color:"var(--text3)"},
  ];
  return(
    <div className="page"><div className="page-inner">
      <div className="page-title">Weather Briefing</div>
      <div className="page-sub">SOLUS does not replicate weather data. Use authoritative sources below. Always get a formal briefing before IFR flight.</div>
      <div style={{marginBottom:16}}>
        <div className="inpl">Departure Airport (ICAO)</div>
        <input className="inp" value={airport} onChange={e=>setAirport(e.target.value.toUpperCase())} onBlur={e=>fetchMetar(e.target.value.toUpperCase())} placeholder="e.g. KVNY" style={{maxWidth:160,marginRight:8}}/><button className="btn btn-p" onClick={()=>fetchMetar(airport)} style={{padding:"8px 16px",fontSize:12}}>Get METAR</button>
      </div>
      {metarLoading&&<div style={{fontSize:11,color:"var(--amber)",fontFamily:"var(--mono)",marginBottom:8}}>◈ Fetching METAR for {airport}...</div>}
      {metar&&(()=>{
        const clouds=metar.clouds&&metar.clouds.length>0?metar.clouds[0]:null;
        const fc=(function(vis,ceil){
          const v=parseFloat(vis)||10;const ck=parseInt(ceil)||10000;
          if(v>=3&&ck>=1000)return{cat:"VFR",color:"#3EC98A"};
          if(v>=1&&ck>=500)return{cat:"MVFR",color:"#FFB000"};
          if(v>=0.5&&ck>=200)return{cat:"IFR",color:"#E84040"};
          return{cat:"LIFR",color:"#E840A0"};
        })(metar.visib,clouds?clouds.base:null);
        return(
          <div className="card" style={{marginBottom:16,borderLeft:"3px solid "+fc.color}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--amber)",fontFamily:"var(--mono)"}}>LIVE METAR: {metar.icaoId||airport}</div>
              <div style={{fontSize:11,fontWeight:700,color:fc.color,fontFamily:"var(--mono)",background:"rgba(0,0,0,0.3)",padding:"2px 8px",borderRadius:3}}>{fc.cat}</div>
            </div>
            <div style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--text2)",lineHeight:1.8,wordBreak:"break-all",marginBottom:8}}>{metar.rawOb}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
              <div style={{fontSize:10,color:"var(--muted)"}}>Wind<br/><span style={{color:"var(--white)",fontFamily:"var(--mono)"}}>{metar.wdir===0&&metar.wspd===0?"Calm":(metar.wdir||"---")+"@"+(metar.wspd||"0")+"kt"+(metar.wgst?"/G"+metar.wgst:"")}</span></div>
              <div style={{fontSize:10,color:"var(--muted)"}}>Visibility<br/><span style={{color:"var(--white)",fontFamily:"var(--mono)"}}>{metar.visib||"---"}SM</span></div>
              <div style={{fontSize:10,color:"var(--muted)"}}>Altimeter<br/><span style={{color:"var(--white)",fontFamily:"var(--mono)"}}>{metar.altim?metar.altim.toFixed(2)+'"':"---"}</span></div>
            </div>
          </div>
        );
      })()}
      {sigmet&&(
        <div className="card" style={{marginBottom:10,borderLeft:"3px solid #E84040",background:"rgba(232,64,64,0.05)"}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--red,#E84040)",fontFamily:"var(--mono)",marginBottom:4}}>ACTIVE SIGMET/AIRMET</div>
          <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.6,fontFamily:"var(--mono)",wordBreak:"break-all"}}>{sigmet.rawAirSigmet}</div>
        </div>
      )}
      {(pireps.length>0||metarLoading===false&&metar)&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--text3)",fontFamily:"var(--mono)",marginBottom:6}}>PILOT REPORTS (PIREPs)</div>
          {pireps.length===0&&<div style={{fontSize:11,color:"var(--muted)",fontFamily:"var(--mono)"}}>No PIREPs within 100nm of {airport}</div>}
          {pireps.map((p,i)=>(
            <div key={i} className="card" style={{marginBottom:6,borderLeft:"3px solid "+(p.intIcing||p.tbInt?"#E84040":"var(--border)")}}>
              <div style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--text2)",lineHeight:1.7,wordBreak:"break-all"}}>{p.rawOb||JSON.stringify(p).slice(0,120)}</div>
            </div>
          ))}
        </div>
      )}
      {(notams.length>0||metarLoading===false&&metar)&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--text3)",fontFamily:"var(--mono)",marginBottom:6}}>NOTAMs</div>
          {notams.length===0&&<div style={{fontSize:11,color:"var(--muted)",fontFamily:"var(--mono)"}}>No active NOTAMs for {airport}</div>}
          {notams.error&&<div style={{fontSize:11,color:"var(--amber)",fontFamily:"var(--mono)",padding:"8px 10px",background:"rgba(255,176,0,0.08)",borderRadius:4,marginBottom:6}}>{notams.error}</div>}
          {notams.map((n,i)=>{
            const sevColor=n.severity==='HIGH'?'#E84040':n.severity==='MEDIUM'?'#FFB000':'var(--border)';
            const headline=n.headline||n.interpretation?.excerpt||n.raw||n.icao_message||n.message||JSON.stringify(n).slice(0,200);
            const timeInfo=n.timeInfo||(n.ends_at?'Until '+new Date(n.ends_at).toLocaleDateString('en-US',{month:'short',day:'numeric'}):'');
            return(
              <div key={i} className="card" style={{marginBottom:6,borderLeft:"3px solid "+sevColor}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                  {n.severity&&<span style={{fontSize:9,fontWeight:700,letterSpacing:"0.08em",color:sevColor,fontFamily:"var(--mono)",background:n.severity==='HIGH'?"rgba(232,64,64,0.1)":n.severity==='MEDIUM'?"rgba(255,176,0,0.1)":"transparent",padding:"1px 5px",borderRadius:2}}>{n.severity}</span>}
                  {n.category&&<span style={{fontSize:9,color:"var(--text3)",fontFamily:"var(--mono)"}}>{n.category}</span>}
                  {n.icao&&<span style={{fontSize:9,color:"var(--text3)",fontFamily:"var(--mono)"}}>{n.icao}</span>}
                </div>
                <div style={{fontSize:12,fontFamily:"var(--mono)",color:"var(--text2)",lineHeight:1.6}}>{headline}</div>
                {n.reason&&<div style={{fontSize:10,fontFamily:"var(--mono)",color:sevColor,marginTop:3,fontStyle:"italic"}}>{n.reason}</div>}
                {(timeInfo||n.affectedSummary)&&<div style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--text3)",marginTop:3}}>{[timeInfo,n.affectedSummary].filter(Boolean).join(' — ')}</div>}
              </div>
            );
          })}
        </div>
      )}
      {sigmet&&(
        <div className="card" style={{marginBottom:10,borderLeft:"3px solid #E84040",background:"rgba(232,64,64,0.05)"}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--red,#E84040)",fontFamily:"var(--mono)",marginBottom:4}}>ACTIVE SIGMET/AIRMET</div>
          <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.6,fontFamily:"var(--mono)",wordBreak:"break-all"}}>{sigmet.rawAirSigmet}</div>
        </div>
      )}
      {pireps.length>0&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--text3)",fontFamily:"var(--mono)",marginBottom:6}}>PILOT REPORTS (PIREPs)</div>
          {pireps.map((p,i)=>(
            <div key={i} className="card" style={{marginBottom:6,borderLeft:"3px solid "+(p.intIcing||p.tbInt?"#E84040":"var(--border)")}}>
              <div style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--text2)",lineHeight:1.7,wordBreak:"break-all"}}>{p.rawOb||JSON.stringify(p).slice(0,120)}</div>
            </div>
          ))}
        </div>
      )}
      {notams.length>0&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--text3)",fontFamily:"var(--mono)",marginBottom:6}}>NOTAMs</div>
          {notams.map((n,i)=>{
            const sevColor=n.severity==='HIGH'?'#E84040':n.severity==='MEDIUM'?'#FFB000':'var(--border)';
            const headline=n.headline||n.interpretation?.excerpt||n.raw||n.icao_message||n.message||JSON.stringify(n).slice(0,200);
            return(
              <div key={i} className="card" style={{marginBottom:6,borderLeft:"3px solid "+sevColor}}>
                {n.severity&&<span style={{fontSize:9,fontWeight:700,color:sevColor,fontFamily:"var(--mono)",marginRight:6}}>{n.severity}</span>}
                <div style={{fontSize:11,fontFamily:"var(--mono)",color:"var(--text2)",lineHeight:1.7}}>{headline}</div>
              </div>
            );
          })}
        </div>
      )}
      {sigmet&&(
        <div className="card" style={{marginBottom:10,borderLeft:"3px solid #E84040",background:"rgba(232,64,64,0.05)"}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--red,#E84040)",fontFamily:"var(--mono)",marginBottom:4}}>ACTIVE SIGMET/AIRMET</div>
          <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.6,fontFamily:"var(--mono)",wordBreak:"break-all"}}>{sigmet.rawAirSigmet}</div>
        </div>
      )}
      {pireps.length>0&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--text3)",fontFamily:"var(--mono)",marginBottom:6}}>PILOT REPORTS (PIREPs)</div>
          {pireps.map((p,i)=>(
            <div key={i} className="card" style={{marginBottom:6,borderLeft:"3px solid "+(p.intIcing||p.tbInt?"#E84040":"var(--border)")}}>
              <div style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--text2)",lineHeight:1.7,wordBreak:"break-all"}}>{p.rawOb||JSON.stringify(p).slice(0,120)}</div>
            </div>
          ))}
        </div>
      )}
      {notams.length>0&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--text3)",fontFamily:"var(--mono)",marginBottom:6}}>NOTAMs</div>
          {notams.map((n,i)=>{
            const sevColor=n.severity==='HIGH'?'#E84040':n.severity==='MEDIUM'?'#FFB000':'var(--border)';
            const headline=n.headline||n.interpretation?.excerpt||n.raw||n.icao_message||n.message||JSON.stringify(n).slice(0,200);
            return(
              <div key={i} className="card" style={{marginBottom:6,borderLeft:"3px solid "+sevColor}}>
                {n.severity&&<span style={{fontSize:9,fontWeight:700,color:sevColor,fontFamily:"var(--mono)",marginRight:6}}>{n.severity}</span>}
                <div style={{fontSize:11,fontFamily:"var(--mono)",color:"var(--text2)",lineHeight:1.7}}>{headline}</div>
              </div>
            );
          })}
        </div>
      )}
      <div className="card" style={{marginBottom:16,borderLeft:"3px solid var(--amber)",background:"var(--amberDim)"}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--amber)",marginBottom:4,fontFamily:"var(--mono)"}}>FAR 91.103: PREFLIGHT ACTION</div>
        <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6}}>Before any flight, PIC must review all available information: weather reports, forecasts, fuel requirements, NOTAMs, and for cross-country flights, runway lengths and aircraft performance data.</div>
      </div>
      {sources.map((src,i)=>(
        <div key={i} className="card" style={{marginBottom:10,cursor:"pointer",borderLeft:"3px solid "+(src.primary?"var(--amber)":"var(--border)")}}
          onClick={()=>{window.open(src.url(airport),"_blank");if(i===0)onWx();}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
            <div style={{fontSize:13,fontWeight:700,color:src.primary?"var(--amber)":"var(--white)"}}>{src.name}</div>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:src.color,fontFamily:"var(--mono)",background:"rgba(255,255,255,0.05)",padding:"2px 6px",borderRadius:3}}>{src.tag}</div>
          </div>
          <div style={{fontSize:11,color:"var(--muted)",lineHeight:1.5}}>{src.desc}</div>
        </div>
      ))}
      <div className="card" style={{marginTop:8}}>
        <div style={{fontSize:12,fontWeight:700,color:"var(--white)",marginBottom:4}}>Need help decoding a METAR or TAF?</div>
        <div style={{fontSize:11,color:"var(--muted)"}}>Paste it into the Intel tab. SOLUS will decode it and give you the flight category and risk flags.</div>
      </div>
      {/* Airport Quick Actions */}
      {airport&&airport.length>=3&&(
        <div style={{marginTop:16}}>
          <div className="section-label">Airport Actions: {airport.toUpperCase()}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}}>
            {[
              {l:"Full Weather Brief",u:"https://www.1800wxbrief.com",icon:"◈"},
              {l:"Check NOTAMs",u:"https://notams.aim.faa.gov/notamSearch/nsapp.html#/details?icaoCode="+airport.toUpperCase(),icon:"◈"},
              {l:"View Chart",u:"https://skyvector.com/airport/"+airport.toUpperCase(),icon:"◈"},
              {l:"Report Hazard",action:"airspace",icon:"◈"},
            ].map(btn=>(
              <button key={btn.l} className="btn btn-sm" style={{display:"flex",alignItems:"center",gap:6,justifyContent:"center"}}
                onClick={()=>{if(btn.action){/* handled by parent */}else{window.open(btn.u,"_blank");}}}>
                <span style={{color:"var(--amber)"}}>{btn.icon}</span> {btn.l}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{marginTop:16,padding:"12px 16px",background:"var(--surface2)",borderRadius:4,fontSize:11,color:"var(--muted)",lineHeight:1.7}}>
        Advisory only. 1800wxbrief.com is the FAA-accepted source for standard weather briefings.
      </div>
    </div></div>
  );
}

function ProfileAndSettings({authUser,backendFetch,entitlements}){
  const isDemo=localStorage.getItem('solus_demo_mode')==='1';
  const [plan,setPlan]=useState(isDemo?'free':'free');
  const [userEntitlements,setUserEntitlements]=useState(entitlements||{});
  const [mfbStatus,setMfbStatus]=useState(isDemo?{linked:true}:null);
  const [deleting,setDeleting]=useState(false);
  const [theme,setTheme]=useState(()=>localStorage.getItem('solus_theme')||'light');
  useEffect(()=>{
    if(isDemo)return;
    if(authUser){
      backendFetch('/user/me').then(d=>{
        if(d&&d.plan)setPlan(d.plan);
        if(d&&d.entitlements)setUserEntitlements(d.entitlements);
      }).catch(()=>{});
      backendFetch('/myflightbook/status').then(d=>setMfbStatus(d)).catch(()=>{});
    }
  },[]); // eslint-disable-line
  function signOut(){
    localStorage.removeItem('solus_token');localStorage.removeItem('solus_waitlisted');
    localStorage.removeItem('solus_allowed');localStorage.removeItem('solus_entered');
    localStorage.removeItem('solus_onboarded_complete');localStorage.removeItem('solus_jetson_unlocked');
    window.location.reload();
  }
  function toggleTheme(t){
    setTheme(t);localStorage.setItem('solus_theme',t);
    document.querySelector('.sr')?.classList.toggle('light',t==='light');
  }
  async function deleteAccount(){
    if(!window.confirm('Delete your SOLUS account and ALL data? This cannot be undone.'))return;
    if(!window.confirm('Are you absolutely sure? You will not be able to sign in again.'))return;
    setDeleting(true);
    await backendFetch('/user/all',{method:'DELETE'});
    localStorage.clear();setDeleting(false);
    alert('Your account and all data have been permanently deleted.');
    window.location.href='/';
  }
  return(
    <div style={{height:"100%",overflowY:"auto",padding:"28px 24px"}}>
      <div style={{maxWidth:620}}>
        <div className="page-title">Profile</div>
        <div className="page-sub">Account, appearance, and data management.</div>

        {/* Account Info */}
        <div className="section-label">Account</div>
        <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
          {(authUser||isDemo)?(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div>
                  <div style={{fontFamily:"var(--mono)",fontSize:13,fontWeight:600,color:"var(--text)"}}>{isDemo?"demo@soluscopilot.com":authUser?.email}</div>
                  <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)",marginTop:2}}>{isDemo?"Demo Pilot":authUser?.user_metadata?.name||'GA Pilot'}</div>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  {userEntitlements.isDesignPartner&&<div style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--green)",background:"var(--greenDim)",padding:"4px 10px",borderRadius:2,textTransform:"uppercase"}}>Design Partner{userEntitlements.partner_org?` · ${userEntitlements.partner_org}`:''}</div>}
                  {userEntitlements.isAdmin&&<div style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--sky)",background:"var(--skyDim)",padding:"4px 10px",borderRadius:2,textTransform:"uppercase"}}>Admin</div>}
                  {!userEntitlements.isDesignPartner&&!userEntitlements.isAdmin&&<div style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--amber)",background:"var(--amberDim)",padding:"4px 10px",borderRadius:2,textTransform:"uppercase"}}>{plan}</div>}
                </div>
              </div>
              {isDemo?<button className="btn btn-sm" onClick={()=>{localStorage.removeItem('solus_demo_mode');localStorage.removeItem('solus_ac');localStorage.removeItem('solus_curr_dates');localStorage.removeItem('solus_entered');localStorage.removeItem('solus_onboarded_complete');window.location.href='/';}}>Exit Demo</button>
              :<button className="btn btn-sm" onClick={signOut}>Sign Out</button>}
            </div>
          ):(
            <div>
              <div style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--text3)",marginBottom:10}}>Not signed in.</div>
              <button className="btn btn-sm" onClick={()=>window.dispatchEvent(new CustomEvent('solus:openSignIn'))} style={{borderColor:"var(--amber)",color:"var(--amber)"}}>Sign In</button>
            </div>
          )}
        </div>

        {/* Pilot Mode */}
        <div className="section-label">Pilot Mode</div>
        <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
          <div style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--text3)",marginBottom:12,lineHeight:1.6}}>Switch between GA and Jetson ONE interfaces.</div>
          <div style={{display:"flex",gap:10}}>
            <button className="btn" onClick={()=>{localStorage.setItem('solus_pilot_mode','ga');window.location.reload();}} style={{flex:1,padding:"12px",fontSize:12,background:localStorage.getItem('solus_pilot_mode')!=='jetson'?"var(--amberDim)":"transparent",borderColor:localStorage.getItem('solus_pilot_mode')!=='jetson'?"var(--amber)":"var(--border)",color:localStorage.getItem('solus_pilot_mode')!=='jetson'?"var(--amber)":"var(--text3)",fontWeight:700}}>GA PILOT</button>
            <button className="btn" onClick={()=>{const unlocked=localStorage.getItem('solus_jetson_unlocked')==='1';if(!unlocked){const pw=window.prompt('Enter Jetson access code:');if(!pw)return;if(pw.trim()==='Leclair-Jetson'){localStorage.setItem('solus_jetson_unlocked','1');}else{alert('Invalid access code.');return;}}localStorage.setItem('solus_pilot_mode','jetson');window.location.reload();}} style={{flex:1,padding:"12px",fontSize:12,background:localStorage.getItem('solus_pilot_mode')==='jetson'?"var(--greenDim)":"transparent",borderColor:localStorage.getItem('solus_pilot_mode')==='jetson'?"var(--green)":"var(--border)",color:localStorage.getItem('solus_pilot_mode')==='jetson'?"var(--green)":"var(--text3)",fontWeight:700}}>JETSON ONE</button>
          </div>
        </div>

        {/* Personal Minimums */}
        <PersonalMinimumsWeb backendFetch={backendFetch}/>

        {/* Linked Instructors */}
        <LinkedInstructorsWeb backendFetch={backendFetch}/>

        {/* Integrations */}
        <div className="section-label">Integrations</div>
        <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontFamily:"var(--mono)",fontSize:12,fontWeight:600,color:"var(--text)"}}>MyFlightbook</div>
              <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)",marginTop:2}}>{mfbStatus?.linked?"Connected":"Not connected"}</div>
            </div>
            <div style={{fontFamily:"var(--mono)",fontSize:10,color:mfbStatus?.linked?"#00E676":"var(--muted)"}}>{mfbStatus?.linked?"Linked":"--"}</div>
          </div>
        </div>

        {/* Appearance */}
        <div className="section-label">Appearance</div>
        <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>toggleTheme('dark')} style={{flex:1,padding:"12px",borderRadius:4,border:theme==='dark'?"2px solid var(--amber)":"1px solid var(--border)",background:theme==='dark'?"rgba(255,176,0,0.08)":"var(--surface)",cursor:"pointer",textAlign:"center"}}>
              <div style={{fontSize:18,marginBottom:4}}>🌙</div>
              <div style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,color:theme==='dark'?"var(--amber)":"var(--text3)",letterSpacing:"0.1em"}}>DARK</div>
            </button>
            <button onClick={()=>toggleTheme('light')} style={{flex:1,padding:"12px",borderRadius:4,border:theme==='light'?"2px solid var(--amber)":"1px solid var(--border)",background:theme==='light'?"rgba(255,176,0,0.08)":"var(--surface)",cursor:"pointer",textAlign:"center"}}>
              <div style={{fontSize:18,marginBottom:4}}>☀️</div>
              <div style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,color:theme==='light'?"var(--amber)":"var(--text3)",letterSpacing:"0.1em"}}>LIGHT</div>
            </button>
          </div>
        </div>

        {/* Feedback */}
        <div className="section-label">Feedback</div>
        <ProfileFeedback authUser={authUser}/>

        {/* Legal */}
        <div className="section-label">Legal</div>
        <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
          <div style={{display:"flex",gap:16}}>
            <a href="/privacy" style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--amber)",textDecoration:"none",letterSpacing:"0.08em"}}>Privacy Policy</a>
            <a href="/terms" style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--amber)",textDecoration:"none",letterSpacing:"0.08em"}}>Terms of Use</a>
          </div>
        </div>

        {/* Delete Account */}
        <div className="section-label">Danger Zone</div>
        <div className="card" style={{marginBottom:16,padding:"16px 18px",borderColor:"rgba(232,64,64,0.2)"}}>
          <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:12,lineHeight:1.6}}>
            Permanently delete your SOLUS account and all data. This cannot be undone.
          </div>
          <button className="btn btn-sm btn-danger" onClick={deleteAccount} disabled={deleting}>
            {deleting?"Deleting...":"Delete Account"}
          </button>
        </div>

        {/* About */}
        <div className="section-label">About</div>
        <div className="card" style={{padding:"16px 18px"}}>
          <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",lineHeight:1.8}}>
            <div>SOLUS Copilot v1.0</div>
            <div>AI Flight Intelligence for GA Pilots</div>
            <div style={{marginTop:6,color:"var(--muted)"}}>Advisory only. Verify with official sources and your POH.</div>
            <div style={{marginTop:4}}><a href="https://soluscopilot.com" style={{color:"var(--amber)",textDecoration:"none"}}>soluscopilot.com</a></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Plans({authUser}){
  const [currentPlan,setCurrentPlan]=useState("free");
  const [subLoading,setSubLoading]=useState(null);
  useEffect(()=>{
    if(authUser?.email){
      fetch("https://solus-backend-9wde.onrender.com/payments/status?email="+encodeURIComponent(authUser.email))
        .then(r=>r.json()).then(d=>{if(d.plan)setCurrentPlan(d.plan);}).catch(()=>{});
    }
  },[authUser]);
  const plans=[
    {id:"free",name:"Free",price:"$0",period:"",users:"1 pilot",desc:"Core safety tools with limited AI access.",recommended:false,features:[
      "3 AI queries",
      "Risk score (view only)",
      "All 5 FAR currency requirements",
      "Live METAR and TAF",
      "Checklists with notes",
      "W&B calculator",
      "FAR/AIM reference",
      "Data persistence (with account)",
    ]},
    {id:"solo",name:"Solo",price:"$49.99",period:"/mo",users:"1 pilot",desc:"Everything a single pilot needs for smarter pre-flights.",recommended:true,features:[
      "Unlimited AI queries",
      "Full risk signal breakdown",
      "NOTAM plain English translation",
      "Mission Brief (PAVE threat assessment)",
      "Passenger Brief for non-pilots",
      "Performance planning (DA, takeoff, fuel)",
      "Post-flight debrief with AI analysis",
      "MyFlightbook + LogTen Pro sync",
      "CSV logbook import (ForeFlight, MFB, LogTen)",
      "Logbook photo scanning (5 pages/mo)",
      "TFR route checking",
      "Airworthiness verification (FAR 91.7)",
      "NTSB pattern lookup (847 cases)",
      "30-day chat history",
      "1 aircraft profile",
      "Crowdsourced airspace hazard feed",
      "GPS spoofing/jamming alerts",
    ]},
    {id:"captain",name:"Captain",price:"$99.99",period:"/mo",users:"1 pilot + CFI tools",desc:"For pilots who fly weekly and want proactive monitoring.",recommended:false,features:[
      "Everything in Solo",
      "Priority AI response",
      "Email currency expiration alerts",
      "Unlimited logbook scanning",
      "Unlimited chat history",
      "Up to 3 aircraft profiles",
      "Agentic monitoring loop (proactive alerts)",
      "GPS anomaly proximity alerts",
      "Post-flight debrief history (unlimited)",
      "CFI student review tools",
      "Operation-type-aware risk scoring",
    ]},
    {id:"fleet",name:"Fleet",price:"$299.99",period:"/mo",users:"Up to 25 pilots",desc:"For flight schools, FBOs, and organizations.",recommended:false,features:[
      "Everything in Captain",
      "Up to 25 pilot accounts",
      "Up to 10 aircraft profiles",
      "Fleet currency dashboard",
      "Student currency monitoring",
      "Bulk pilot management",
      "Usage analytics across pilots",
      "Risk heatmap across fleet",
      "API access",
      "CSV export for insurance actuarials",
      "Dedicated support",
      "Custom risk thresholds per operation type",
    ]},
    {id:"enterprise",name:"Enterprise",price:"Custom",period:"",users:"25+ pilots",desc:"For large flight schools, Part 135 operators, universities, and military units.",recommended:false,features:[
      "Everything in Fleet",
      "Unlimited pilot accounts",
      "Unlimited aircraft profiles",
      "Dedicated account manager",
      "Custom integrations (EFB, scheduling, dispatch)",
      "Insurance actuarial data API",
      "White-label option",
      "SSO / SAML authentication",
      "SLA with uptime guarantee",
      "On-site training and onboarding",
      "Custom reporting and analytics",
    ]},
  ];
  async function handleSubscribe(planId){
    if(!authUser){alert("Sign in to subscribe.");return;}
    setSubLoading(planId);
    // RevenueCat web billing: redirect to hosted checkout
    // Replace these with your actual RevenueCat web billing URLs from the RC dashboard
    const rcUrls={
      solo:"https://billing.revenuecat.com/SOLO_PLACEHOLDER",
      captain:"https://billing.revenuecat.com/CAPTAIN_PLACEHOLDER",
      fleet:"https://billing.revenuecat.com/FLEET_PLACEHOLDER",
    };
    const url=rcUrls[planId];
    if(url&&!url.includes("PLACEHOLDER")){
      window.open(url+"?email="+encodeURIComponent(authUser.email),"_blank");
    }else{
      // Web subscriptions: redirect to mobile app
      alert("To subscribe to the "+planId.charAt(0).toUpperCase()+planId.slice(1)+" plan, download SOLUS Copilot from the App Store. Subscriptions are managed through Apple In-App Purchase.");
    }
    setSubLoading(null);
  }
  return(
    <div style={{maxWidth:960,margin:"0 auto",padding:"28px 16px",height:"100%",overflowY:"auto"}}>
      <div style={{textAlign:"center",marginBottom:36}}>
        <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:"var(--amber)",textTransform:"uppercase",marginBottom:8}}>Pricing</div>
        <div style={{fontSize:24,fontWeight:700,color:"var(--text)",marginBottom:6}}>Choose Your Plan</div>
        <div style={{fontSize:13,color:"var(--muted)"}}>All plans include a 3-day free trial. Cancel anytime.</div>
        {currentPlan!=="free"&&<div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--green)",marginTop:10}}>Current plan: {currentPlan.toUpperCase()}</div>}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
        {plans.map(p=>(
          <div key={p.id} style={{background:"var(--surface2)",border:p.recommended?"2px solid var(--amber)":"1px solid var(--border)",borderRadius:4,padding:"20px 16px",display:"flex",flexDirection:"column",position:"relative"}}>
            {p.recommended&&<div style={{fontFamily:"var(--mono)",fontSize:8,fontWeight:700,letterSpacing:"0.15em",color:"var(--bg,#0B0C10)",background:"var(--amber)",padding:"3px 8px",borderRadius:2,textTransform:"uppercase",position:"absolute",top:-10,left:16}}>Recommended</div>}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{fontSize:18,fontWeight:700,color:"var(--text)"}}>{p.name}</div>
              {p.users&&<div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--green)",letterSpacing:"0.08em",background:"rgba(0,230,118,0.1)",padding:"3px 8px",borderRadius:2}}>{p.users}</div>}
            </div>
            {p.desc&&<div style={{fontSize:11,color:"var(--muted)",marginBottom:8,lineHeight:1.5}}>{p.desc}</div>}
            <div style={{marginBottom:16}}>
              <span style={{fontFamily:"var(--mono)",fontSize:28,fontWeight:700,color:"var(--amber)"}}>{p.price}</span>
              <span style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--muted)"}}>{p.period}</span>
            </div>
            <div style={{flex:1,marginBottom:16}}>
              {p.features.map((f,i)=>(
                <div key={i} style={{display:"flex",gap:8,fontSize:12,color:"var(--text2)",marginBottom:8,lineHeight:1.5}}>
                  <span style={{color:"var(--green)",flexShrink:0}}>&#10003;</span>
                  <span>{f}</span>
                </div>
              ))}
            </div>
            {p.id==="free"?(
              <div style={{padding:"12px 0",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:2,textAlign:"center",
                fontFamily:"var(--mono)",fontSize:12,fontWeight:700,color:"var(--text3)",letterSpacing:"0.06em"}}>
                {currentPlan==="free"?"Current Plan":"Free Tier"}
              </div>
            ):p.id==="enterprise"?(
              <a href="mailto:almeena@soluscopilot.com?subject=SOLUS Enterprise Inquiry" style={{display:"block",padding:"12px 0",background:"rgba(255,176,0,0.1)",border:"1px solid var(--border)",borderRadius:2,textAlign:"center",textDecoration:"none",
                fontFamily:"var(--mono)",fontSize:12,fontWeight:700,color:"var(--amber)",letterSpacing:"0.06em"}}>
                Contact Sales
              </a>
            ):(
              <button onClick={()=>handleSubscribe(p.id)} disabled={subLoading===p.id||currentPlan===p.id}
                style={{padding:"12px 0",background:currentPlan===p.id?"var(--green)":p.recommended?"var(--amber)":"rgba(255,176,0,0.1)",
                  border:currentPlan===p.id?"none":p.recommended?"none":"1px solid var(--border)",borderRadius:2,
                  color:currentPlan===p.id?"var(--bg,#0B0C10)":p.recommended?"var(--bg,#0B0C10)":"var(--amber)",
                  fontFamily:"var(--mono)",fontSize:12,fontWeight:700,cursor:currentPlan===p.id?"default":"pointer",
                  letterSpacing:"0.06em",opacity:subLoading===p.id?0.5:1,width:"100%"}}>
                {currentPlan===p.id?"Current Plan":subLoading===p.id?"...":"Subscribe"}
              </button>
            )}
          </div>
        ))}
      </div>
      <div style={{textAlign:"center",marginTop:24,fontSize:11,color:"var(--muted)",fontFamily:"var(--mono)"}}>
        Managed by RevenueCat. Questions? almeena@soluscopilot.com
      </div>
    </div>
  );
}

// ─── LOGBOOK ─────────────────────────────────────────────────────────────────
function Logbook({backendFetch}){
  const isDemo=localStorage.getItem('solus_demo_mode')==='1';
  const [mfbLinked,setMfbLinked]=useState(isDemo);
  const [mfbSyncing,setMfbSyncing]=useState(false);
  const [flights,setFlights]=useState(()=>{
    if(!isDemo)return[];
    const d=new Date();const ago=(days)=>new Date(d.getTime()-days*864e5).toISOString().split('T')[0];
    return[
      {Date:ago(3),Route:"KVNY - KSZP",TailNumber:"N44821",TotalFlightTime:1.2,FullStopLandings:2,Comment:"Day VFR, scenic coastal route. Smooth air."},
      {Date:ago(18),Route:"KVNY - KFUL - KVNY",TailNumber:"N44821",TotalFlightTime:1.8,FullStopLandings:3,NightLandings:1,Comment:"Night currency. Full stop at KFUL, back to KVNY."},
      {Date:ago(43),Route:"KVNY - KSBA",TailNumber:"N44821",TotalFlightTime:1.5,FullStopLandings:1,Comment:"XC to Santa Barbara. Marine layer cleared by 10am."},
      {Date:ago(65),Route:"KVNY - KVNY",TailNumber:"N44821",TotalFlightTime:0.8,FullStopLandings:3,Comment:"Pattern work. 3 touch and goes."},
      {Date:ago(87),Route:"KVNY - KOXR - KVNY",TailNumber:"N44821",TotalFlightTime:1.4,FullStopLandings:2,Comment:"XC to Oxnard. Practice NDB approach."},
    ];
  });
  const [flightsLoading,setFlightsLoading]=useState(false);
  const [syncResult,setSyncResult]=useState(isDemo?"Currency auto-synced from 47 flights":null);
  const [scanning,setScanning]=useState(false);
  const [scannedFlights,setScannedFlights]=useState([]);
  const [scanNote,setScanNote]=useState(null);

  async function handleScanUpload(e){
    const files=Array.from(e.target.files||[]);
    if(!files.length)return;
    if(files.length>5){alert('Maximum 5 images at a time.');return;}
    setScanning(true);setScanNote(null);setScannedFlights([]);
    try{
      const images=await Promise.all(files.map(f=>new Promise((resolve,reject)=>{
        const reader=new FileReader();
        reader.onload=()=>{
          const base64=reader.result.split(',')[1];
          const type=f.type||'image/jpeg';
          resolve({data:base64,type});
        };
        reader.onerror=reject;
        reader.readAsDataURL(f);
      })));
      const token=localStorage.getItem('solus_token');
      const res=await fetch('https://solus-backend-9wde.onrender.com/logbook/scan',{
        method:'POST',
        headers:{'Content-Type':'application/json',...(token?{'Authorization':'Bearer '+token}:{})},
        body:JSON.stringify({images}),
      });
      const data=await res.json();
      if(data.flights&&data.flights.length>0){
        setScannedFlights(data.flights);
        setScanNote(`Extracted ${data.flights.length} flight${data.flights.length>1?'s':''} (${data.confidence} confidence). Review and save.`);
      }else{
        setScanNote(data.page_notes||data.error||'No flights found in image. Try a clearer photo.');
      }
    }catch(err){
      setScanNote('Scan failed: '+err.message);
    }
    setScanning(false);
    e.target.value='';
  }

  function saveScannedFlights(){
    // Merge scanned flights into the flights list
    const mapped=scannedFlights.map(f=>({
      Date:f.date,Route:f.route,TailNumber:f.aircraft_id,
      TotalFlightTime:f.total_time,FullStopLandings:(f.day_landings||0),
      NightLandings:(f.night_landings||0),Approaches:f.approaches,
      Comment:f.remarks||'',
    }));
    setFlights(prev=>[...mapped,...prev]);
    // Auto-fill currency from scanned flights
    const sorted=[...scannedFlights].filter(f=>f.date).sort((a,b)=>new Date(b.date)-new Date(a.date));
    const dayLandings=sorted.filter(f=>(f.day_landings||0)>0);
    const nightLandings=sorted.filter(f=>(f.night_landings||0)>0);
    const bfrFlight=sorted.find(f=>f.flight_review);
    const ifrFlight=sorted.find(f=>(f.approaches||0)>0);
    try{
      const curr=JSON.parse(localStorage.getItem('solus_curr_dates')||'{}');
      if(bfrFlight)curr.lastBFR=bfrFlight.date;
      if(dayLandings[0])curr.lastDayLanding=dayLandings[0].date;
      if(dayLandings[1])curr.dayLanding2=dayLandings[1].date;
      if(dayLandings[2])curr.dayLanding3=dayLandings[2].date;
      if(nightLandings[0]){curr.lastNightLanding=nightLandings[0].date;curr.nightTakeoff1=nightLandings[0].date;}
      if(nightLandings[1]){curr.nightLanding2=nightLandings[1].date;curr.nightTakeoff2=nightLandings[1].date;}
      if(nightLandings[2]){curr.nightLanding3=nightLandings[2].date;curr.nightTakeoff3=nightLandings[2].date;}
      if(ifrFlight){
        curr.lastIFR=ifrFlight.date;
        const sixMo=new Date();sixMo.setMonth(sixMo.getMonth()-6);
        const recentIFR=sorted.filter(f=>(f.approaches||0)>0&&new Date(f.date)>=sixMo);
        curr.ifrApproaches=String(recentIFR.reduce((s,f)=>s+(f.approaches||0),0));
        curr.ifrHolds=recentIFR.some(f=>f.holds)?'1':'0';
      }
      localStorage.setItem('solus_curr_dates',JSON.stringify(curr));
    }catch{}
    setSyncResult(`Saved ${scannedFlights.length} scanned flights. Currency dates updated.`);
    setScannedFlights([]);setScanNote(null);
    setTimeout(()=>setSyncResult(null),6000);
  }

  useEffect(()=>{
    if(isDemo)return;
    const token=localStorage.getItem('solus_token');
    if(!token)return;
    backendFetch('/myflightbook/status').then(r=>{
      if(r&&r.linked){setMfbLinked(true);loadFlights();}
    }).catch(()=>{});
    // Check URL params for MFB callback
    const params=new URLSearchParams(window.location.search);
    if(params.get('mfb_linked')==='true'){
      setMfbLinked(true);
      setSyncResult('MyFlightbook linked successfully!');
      loadFlights();
      setTimeout(()=>setSyncResult(null),6000);
      window.history.replaceState({},'',window.location.pathname);
    }
  },[]); // eslint-disable-line

  async function loadFlights(){
    setFlightsLoading(true);
    const data=await backendFetch('/myflightbook/flights');
    if(data){
      const list=Array.isArray(data)?data:(data.Flights||data.flights||[]);
      setFlights(list.slice(0,50));
    }
    setFlightsLoading(false);
  }

  async function linkMFB(){
    const token=localStorage.getItem('solus_token');
    if(!token){alert('Sign in first to link MyFlightbook.');window.dispatchEvent(new CustomEvent('solus:openSignIn'));return;}
    try{
      const res=await fetch('https://solus-backend-9wde.onrender.com/myflightbook/authorize',{headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'}});
      const data=await res.json();
      if(data&&data.url)window.location.href=data.url;
      else alert(data.error||'Failed to get authorization URL.');
    }catch(e){alert('Connection error: '+e.message);}
  }

  async function syncMFB(){
    setMfbSyncing(true);
    const data=await backendFetch('/myflightbook/sync-currency',{method:'POST'});
    if(data&&data.synced){
      setSyncResult(`Synced ${data.flights_analyzed} flights. Currency dates updated.`);
      loadFlights();
    }else{setSyncResult('Sync failed.');}
    setMfbSyncing(false);
    setTimeout(()=>setSyncResult(null),6000);
  }

  async function unlinkMFB(){
    if(!window.confirm('Unlink MyFlightbook? Your data will remain.'))return;
    await backendFetch('/myflightbook/unlink',{method:'DELETE'});
    setMfbLinked(false);setFlights([]);
  }

  function fmtDate(d){if(!d)return'--';try{return new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'});}catch{return'--';}}

  return(
    <div style={{height:"100%",overflowY:"auto",padding:"28px 24px"}}>
      <div style={{maxWidth:620}}>
        <div className="page-title">Logbook</div>
        <div className="page-sub">Your flight history synced from MyFlightbook.</div>

        {syncResult&&(
          <div style={{marginBottom:16,padding:"12px 16px",background:"var(--greenDim)",border:"1px solid rgba(0,230,118,0.25)",borderRadius:4,fontFamily:"var(--mono)",fontSize:11,color:"var(--green,#00E676)"}}>{syncResult}</div>
        )}

        {/* MFB Connection Card */}
        <div className="card" style={{marginBottom:20,padding:"16px 18px",borderLeft:"3px solid #4FC3F7"}}>
          <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--text3)",marginBottom:10,textTransform:"uppercase"}}>MyFlightbook</div>
          {mfbLinked?(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <button className="btn btn-sm" onClick={syncMFB} disabled={mfbSyncing} style={{borderColor:"var(--green,#00E676)",color:"var(--green,#00E676)"}}>
                  {mfbSyncing?"Syncing...":"Sync Flights"}
                </button>
                <span style={{fontSize:11,color:"var(--green,#00E676)",fontFamily:"var(--mono)"}}>Linked</span>
                <button onClick={unlinkMFB} style={{background:"none",border:"none",color:"var(--muted)",fontSize:10,cursor:"pointer",textDecoration:"underline",fontFamily:"var(--mono)"}}>Unlink</button>
              </div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:6}}>Pull your latest flights and auto-fill currency dates.</div>
            </div>
          ):(
            <div>
              <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:10}}>Connect your MyFlightbook account to view your flight history and auto-sync currency dates.</div>
              <button className="btn btn-sm" onClick={linkMFB} style={{borderColor:"#4FC3F7",color:"#4FC3F7"}}>Link MyFlightbook</button>
            </div>
          )}
        </div>

        {/* Scan Physical Logbook */}
        <div className="card" style={{marginBottom:20,padding:"16px 18px",borderLeft:"3px solid var(--amber)"}}>
          <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.15em",color:"var(--text3)",marginBottom:10,textTransform:"uppercase"}}>Scan Physical Logbook</div>
          <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:10}}>
            Upload photos of your paper logbook pages. SOLUS will read and extract flight entries automatically.
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <label className="btn btn-sm" style={{cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4,borderColor:"var(--amber)",color:"var(--amber)"}}>
              {scanning?"Scanning...":"Upload Logbook Photos"}
              <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={handleScanUpload} disabled={scanning}/>
            </label>
            {scanning&&<span style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--muted)"}}>Analyzing with AI vision...</span>}
          </div>
          {scanNote&&(
            <div style={{marginTop:10,padding:"10px 14px",background:scannedFlights.length>0?"var(--greenDim)":"var(--amberDim)",border:`1px solid ${scannedFlights.length>0?"rgba(0,230,118,0.25)":"rgba(255,176,0,0.25)"}`,borderRadius:4,fontFamily:"var(--mono)",fontSize:11,color:scannedFlights.length>0?"var(--green,#00E676)":"var(--amber)"}}>{scanNote}</div>
          )}
          {scannedFlights.length>0&&(
            <div style={{marginTop:12}}>
              <div style={{fontFamily:"var(--mono)",fontSize:9,letterSpacing:"0.12em",color:"var(--text3)",marginBottom:8,textTransform:"uppercase"}}>Extracted Flights (Review)</div>
              {scannedFlights.map((f,i)=>(
                <div key={i} className="card" style={{marginBottom:4,padding:"10px 12px",borderLeft:"2px solid var(--green)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,fontFamily:"var(--mono)"}}>
                    <span style={{color:"var(--text)",fontWeight:600}}>{f.date||'--'}</span>
                    <span style={{color:"var(--amber)"}}>{f.aircraft_id||'--'}</span>
                  </div>
                  <div style={{fontSize:10,color:"var(--text2)",fontFamily:"var(--mono)"}}>{f.route||'--'}</div>
                  <div style={{display:"flex",gap:12,fontSize:9,color:"var(--text3)",fontFamily:"var(--mono)",marginTop:2}}>
                    <span>{f.total_time||0} hrs</span>
                    <span>{(f.day_landings||0)+(f.night_landings||0)} ldg</span>
                    {f.approaches>0&&<span>{f.approaches} appr</span>}
                    <span>{f.conditions||''}</span>
                    {f.flight_review&&<span style={{color:"var(--green)"}}>BFR</span>}
                  </div>
                  {f.remarks&&<div style={{fontSize:9,color:"var(--muted)",fontFamily:"var(--mono)",marginTop:2,fontStyle:"italic"}}>{f.remarks}</div>}
                </div>
              ))}
              <button className="btn btn-sm" onClick={saveScannedFlights} style={{marginTop:8,borderColor:"var(--green,#00E676)",color:"var(--green,#00E676)"}}>Save {scannedFlights.length} Flights to Logbook</button>
            </div>
          )}
        </div>

        {/* Flight List */}
        {flightsLoading&&<div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--muted)",padding:20}}>Loading flights...</div>}

        {!flightsLoading&&flights.length===0&&mfbLinked&&(
          <div style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--text3)",padding:20,textAlign:"center"}}>No flights found. Click "Sync Flights" to pull from MyFlightbook.</div>
        )}

        {flights.map((f,i)=>{
          const date=fmtDate(f.Date||f.date);
          const route=f.Route||f.route||'--';
          const aircraft=f.TailNumber||f.tailNumber||f.AircraftID||'--';
          const total=f.TotalFlightTime||f.totalFlightTime||f.TotalTime||'--';
          const landings=(f.FullStopLandings||0)+(f.NightLandings||f.FullStopNightLandings||0);
          const comment=f.Comment||f.comment||'';
          return(
            <div key={i} className="card" style={{marginBottom:6,padding:"12px 14px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontFamily:"var(--mono)",fontSize:12,fontWeight:600,color:"var(--text)"}}>{date}</span>
                <span style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--amber)"}}>{aircraft}</span>
              </div>
              <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text2)",marginBottom:2}}>{route}</div>
              <div style={{display:"flex",gap:16,fontSize:10,color:"var(--text3)",fontFamily:"var(--mono)"}}>
                <span>{total} hrs</span>
                <span>{landings} ldg{landings!==1?"s":""}</span>
              </div>
              {comment&&<div style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--mono)",marginTop:4,fontStyle:"italic"}}>{comment.slice(0,100)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProfileFeedback({authUser}){
  const [msg,setMsg]=useState("");
  const [sent,setSent]=useState(false);
  const [sending,setSending]=useState(false);
  async function submit(){
    if(!msg.trim())return;
    setSending(true);
    try{
      await fetch("https://solus-backend-9wde.onrender.com/feedback",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({tab:"profile_feedback",issue:msg.trim(),email:authUser?.email||"anonymous"})
      });
      setSent(true);setMsg("");
    }catch{}
    setSending(false);
  }
  return(
    <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
      {sent?(
        <div style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--green)"}}>Thank you for your feedback.</div>
      ):(
        <>
          <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:10,lineHeight:1.6}}>
            Bug, feature request, or general feedback. We read everything.
          </div>
          <textarea value={msg} onChange={e=>setMsg(e.target.value)} placeholder="What's on your mind?" rows={3} style={{
            width:"100%",padding:"10px 12px",background:"var(--card)",border:"1px solid var(--border)",borderRadius:3,
            fontFamily:"var(--mono)",fontSize:12,color:"var(--text)",resize:"vertical",outline:"none",marginBottom:10,
          }}/>
          <button className="btn btn-sm" onClick={submit} disabled={sending||!msg.trim()} style={{borderColor:"var(--amber)",color:"var(--amber)"}}>
            {sending?"Sending...":"Send Feedback"}
          </button>
        </>
      )}
    </div>
  );
}

// ─── PROFILE ─────────────────────────────────────────────────────────────────
function Profile({authUser,backendFetch}){
  const isDemo=localStorage.getItem('solus_demo_mode')==='1';
  const [plan,setPlan]=useState(isDemo?'free':'free');
  const [mfbStatus,setMfbStatus]=useState(isDemo?{linked:true}:null);
  const [deleting,setDeleting]=useState(false);

  useEffect(()=>{
    if(isDemo)return;
    if(authUser){
      backendFetch('/user/me').then(d=>{if(d&&d.plan)setPlan(d.plan);}).catch(()=>{});
      backendFetch('/myflightbook/status').then(d=>setMfbStatus(d)).catch(()=>{});
    }
  },[]); // eslint-disable-line

  function signOut(){
    localStorage.removeItem('solus_token');
    localStorage.removeItem('solus_waitlisted');
    localStorage.removeItem('solus_allowed');
    localStorage.removeItem('solus_entered');
    window.location.reload();
  }

  async function deleteAccount(){
    if(!window.confirm('Delete your SOLUS account and ALL data? This cannot be undone.'))return;
    if(!window.confirm('Are you absolutely sure? This will permanently delete your account, aircraft, currency, chat history, and all associated data. You will not be able to sign in again.'))return;
    setDeleting(true);
    await backendFetch('/user/all',{method:'DELETE'});
    localStorage.clear();
    setDeleting(false);
    alert('Your account and all data have been permanently deleted.');
    window.location.href='/';
  }

  return(
    <div style={{height:"100%",overflowY:"auto",padding:"28px 24px"}}>
      <div style={{maxWidth:620}}>
        <div className="page-title">Profile</div>
        <div className="page-sub">Account settings and data management.</div>

        {/* Account Info */}
        <div className="section-label">Account</div>
        <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
          {(authUser||isDemo)?(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div>
                  <div style={{fontFamily:"var(--mono)",fontSize:13,fontWeight:600,color:"var(--text)"}}>{isDemo?"demo@soluscopilot.com":authUser?.email}</div>
                  <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)",marginTop:2}}>{isDemo?"Demo Pilot":authUser?.user_metadata?.name||'GA Pilot'}</div>
                </div>
                <div style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"var(--amber)",background:"var(--amberDim)",padding:"4px 10px",borderRadius:2,textTransform:"uppercase"}}>{plan}</div>
              </div>
              {isDemo?<button className="btn btn-sm" onClick={()=>{localStorage.removeItem('solus_demo_mode');localStorage.removeItem('solus_ac');localStorage.removeItem('solus_curr_dates');localStorage.removeItem('solus_entered');window.location.href='/';}}>Exit Demo</button>
              :<button className="btn btn-sm" onClick={signOut}>Sign Out</button>}
            </div>
          ):(
            <div>
              <div style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--text3)",marginBottom:10}}>Not signed in. Sign in to sync your data across devices.</div>
              <button className="btn btn-sm" onClick={()=>window.dispatchEvent(new CustomEvent('solus:openSignIn'))} style={{borderColor:"var(--amber)",color:"var(--amber)"}}>Sign In</button>
            </div>
          )}
        </div>

        {/* Integrations */}
        <div className="section-label">Integrations</div>
        <div className="card" style={{marginBottom:16,padding:"16px 18px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontFamily:"var(--mono)",fontSize:12,fontWeight:600,color:"var(--text)"}}>MyFlightbook</div>
              <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)",marginTop:2}}>
                {mfbStatus?.linked?"Connected":"Not connected"}
              </div>
            </div>
            <div style={{fontFamily:"var(--mono)",fontSize:10,color:mfbStatus?.linked?"var(--green,#00E676)":"var(--muted)"}}>
              {mfbStatus?.linked?"Linked":"--"}
            </div>
          </div>
        </div>

        {/* Account deletion moved to Settings tab */}

        {/* Feedback */}
        <div className="section-label">Feedback</div>
        <ProfileFeedback authUser={authUser}/>

        {/* App Info */}
        <div className="section-label">About</div>
        <div className="card" style={{padding:"16px 18px"}}>
          <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",lineHeight:1.8}}>
            <div>SOLUS Copilot v1.0</div>
            <div>AI Flight Intelligence for GA Pilots</div>
            <div style={{marginTop:6,color:"var(--muted)"}}>Advisory only. Verify with official sources and your POH.</div>
            <div style={{marginTop:4}}><a href="https://soluscopilot.com" style={{color:"var(--amber)",textDecoration:"none"}}>soluscopilot.com</a></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DEBRIEF ─────────────────────────────────────────────────────────────────
function Debrief({ac,currDates,flightCtx,riskScore,backendFetch}){
  const [date,setDate]=useState(new Date().toISOString().split('T')[0]);
  const [route,setRoute]=useState(flightCtx?.from&&flightCtx?.to?`${flightCtx.from} - ${flightCtx.to}`:'');
  const [aircraft,setAircraft]=useState(ac?.nNumber?`N${ac.nNumber} ${ac.type||''}`:'');
  const [duration,setDuration]=useState('');
  const [notes,setNotes]=useState('');
  const [flagsMaterialized,setFlagsMaterialized]=useState(false);
  const [conditions,setConditions]=useState([]);
  const [submitting,setSubmitting]=useState(false);
  const [history,setHistory]=useState([]);
  const [histLoading,setHistLoading]=useState(true);
  const [expandedId,setExpandedId]=useState(null);

  const COND_OPTIONS=['VMC','IMC','Night','Turbulence','Wind Shear','Precipitation','Icing','GPS Anomaly','ATC Deviation','Go-Around','Rejected Takeoff','Emergency'];
  const preflightFlags=(riskScore?.signals||[]).filter(s=>s.pts>0).map(s=>s.name+': '+s.detail);

  useEffect(()=>{
    backendFetch('/debrief/history').then(d=>{if(d&&Array.isArray(d))setHistory(d);setHistLoading(false);}).catch(()=>setHistLoading(false));
  },[]); // eslint-disable-line

  async function submit(){
    if(!duration){alert('Enter flight duration.');return;}
    setSubmitting(true);
    const data=await backendFetch('/debrief/save',{method:'POST',body:JSON.stringify({
      flight_date:date,route,aircraft,duration_hours:parseFloat(duration)||0,
      conditions,notes,preflight_flags:preflightFlags,flags_materialized:flagsMaterialized,
    })});
    if(data&&data.id){
      setHistory(prev=>[data,...prev]);
      setNotes('');setDuration('');setConditions([]);setFlagsMaterialized(false);
    }
    setSubmitting(false);
  }

  function toggleCond(c){setConditions(prev=>prev.includes(c)?prev.filter(x=>x!==c):[...prev,c]);}

  // Learning trends (5+ entries)
  const trends=history.length>=5?{
    topFlags:Object.entries(history.flatMap(h=>h.preflight_flags||[]).reduce((a,f)=>{const k=f.split(':')[0];a[k]=(a[k]||0)+1;return a;},{})).sort((a,b)=>b[1]-a[1]).slice(0,3),
    topConds:Object.entries(history.flatMap(h=>h.conditions||[]).reduce((a,c)=>{a[c]=(a[c]||0)+1;return a;},{})).sort((a,b)=>b[1]-a[1]).slice(0,3),
  }:null;

  return(
    <div style={{height:"100%",overflowY:"auto",padding:"28px 24px"}}>
      <div style={{maxWidth:620}}>
        <div className="page-title">Post-Flight Debrief</div>
        <div className="page-sub">Connect pre-flight flags to in-flight conditions. Close the learning loop.</div>

        {/* Entry Form */}
        <div className="card" style={{marginBottom:20,padding:"16px 18px",borderLeft:"3px solid var(--amber)"}}>
          <div className="section-label">New Debrief</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div><div className="inpl">Date</div><input className="inp" type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
            <div><div className="inpl">Duration (hrs)</div><input className="inp" type="number" step="0.1" value={duration} onChange={e=>setDuration(e.target.value)} placeholder="1.5"/></div>
            <div><div className="inpl">Route</div><input className="inp" value={route} onChange={e=>setRoute(e.target.value)} placeholder="KVNY - KSZP"/></div>
            <div><div className="inpl">Aircraft</div><input className="inp" value={aircraft} onChange={e=>setAircraft(e.target.value)} placeholder="N44821 C172S"/></div>
          </div>

          <div className="inpl" style={{marginTop:8}}>Conditions Encountered</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
            {COND_OPTIONS.map(c=>(
              <button key={c} className="btn btn-sm" onClick={()=>toggleCond(c)}
                style={{borderColor:conditions.includes(c)?"var(--amber)":"var(--border)",color:conditions.includes(c)?"var(--amber)":"var(--text3)",background:conditions.includes(c)?"var(--amberDim)":"transparent",fontSize:9}}>
                {c}
              </button>
            ))}
          </div>

          {preflightFlags.length>0&&(
            <div style={{marginBottom:12}}>
              <div className="inpl">Pre-Flight Flags ({preflightFlags.length})</div>
              {preflightFlags.map((f,i)=><div key={i} style={{fontSize:10,color:"var(--amber)",fontFamily:"var(--mono)",marginBottom:2}}>{f}</div>)}
              <label style={{display:"flex",alignItems:"center",gap:8,marginTop:6,fontSize:11,color:"var(--text3)",fontFamily:"var(--mono)",cursor:"pointer"}}>
                <input type="checkbox" checked={flagsMaterialized} onChange={e=>setFlagsMaterialized(e.target.checked)}/> Flags materialized in flight
              </label>
            </div>
          )}

          <div className="inpl">Notes</div>
          <textarea className="inp" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="What happened? What did you learn?" rows={3} style={{resize:"vertical"}}/>

          <button className="btn btn-p" onClick={submit} disabled={submitting} style={{width:"100%",marginTop:12,padding:"11px"}}>
            {submitting?"Generating debrief...":"Submit Debrief"}
          </button>
        </div>

        {/* Learning Trends */}
        {trends&&(
          <div className="card" style={{marginBottom:20,padding:"16px 18px",borderLeft:"3px solid var(--green)"}}>
            <div className="section-label">Learning Trends ({history.length} flights)</div>
            {trends.topFlags.length>0&&(
              <div style={{marginBottom:8}}>
                <div style={{fontSize:10,color:"var(--text3)",fontFamily:"var(--mono)",marginBottom:4}}>Most common pre-flight flags:</div>
                {trends.topFlags.map(([f,n])=><div key={f} style={{fontSize:11,color:"var(--amber)",fontFamily:"var(--mono)"}}>{f} ({n}x)</div>)}
              </div>
            )}
            {trends.topConds.length>0&&(
              <div>
                <div style={{fontSize:10,color:"var(--text3)",fontFamily:"var(--mono)",marginBottom:4}}>Most common conditions:</div>
                {trends.topConds.map(([c,n])=><div key={c} style={{fontSize:11,color:"var(--text2)",fontFamily:"var(--mono)"}}>{c} ({n}x)</div>)}
              </div>
            )}
          </div>
        )}

        {/* History */}
        <div className="section-label">Debrief History</div>
        {histLoading&&<div style={{fontSize:11,color:"var(--muted)",fontFamily:"var(--mono)"}}>Loading...</div>}
        {history.map(h=>(
          <div key={h.id} className="card" style={{marginBottom:6,padding:"12px 14px",cursor:"pointer"}} onClick={()=>setExpandedId(expandedId===h.id?null:h.id)}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontFamily:"var(--mono)",fontSize:12,fontWeight:600,color:"var(--text)"}}>{h.flight_date}</span>
              <span style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--amber)"}}>{h.aircraft||''}</span>
            </div>
            <div style={{fontSize:11,color:"var(--text2)",fontFamily:"var(--mono)"}}>{h.route||'--'} {h.duration_hours?`(${h.duration_hours}h)`:''}</div>
            {(h.conditions||[]).length>0&&<div style={{fontSize:9,color:"var(--text3)",fontFamily:"var(--mono)",marginTop:2}}>{h.conditions.join(', ')}</div>}
            {expandedId===h.id&&h.ai_debrief&&(
              <div style={{marginTop:10,padding:"10px 12px",background:"rgba(255,176,0,0.05)",borderLeft:"2px solid rgba(255,176,0,0.3)",borderRadius:"0 4px 4px 0",fontSize:12,color:"var(--text2)",fontFamily:"'Barlow',sans-serif",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{h.ai_debrief}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Card(){
  const [visible,setVisible]=useState(false);
  useEffect(()=>{
    document.title="Almeena Shaikh — SOLUS Copilot";
    const setMeta=(prop,content)=>{
      let el=document.querySelector(`meta[property="${prop}"]`)||document.querySelector(`meta[name="${prop}"]`);
      if(!el){el=document.createElement('meta');el.setAttribute(prop.startsWith('og:')?'property':'name',prop);document.head.appendChild(el);}
      el.setAttribute('content',content);
    };
    setMeta('og:title','Almeena Shaikh — Founder & CEO, SOLUS Copilot');
    setMeta('og:description','USAF veteran. Pilot. Ex-Pentagon. Ex-NASA. Ex-Google. Building behavioral intelligence for general aviation.');
    setMeta('og:url','https://soluscopilot.com/card');
    setMeta('og:type','profile');
    setMeta('description','USAF veteran. Pilot. Ex-Pentagon. Ex-NASA. Ex-Google. Building behavioral intelligence for general aviation.');
    // inject keyframes
    if(!document.getElementById('card-anim')){
      const s=document.createElement('style');s.id='card-anim';
      s.textContent=`
        @keyframes cardFadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        @keyframes cardPulse{0%,100%{box-shadow:0 0 0 0 rgba(255,176,0,0.25)}50%{box-shadow:0 0 0 8px rgba(255,176,0,0)}}
        .card-link:hover{background:#1a1a1a !important;color:#f7f5f0 !important;border-color:#1a1a1a !important}
      `;
      document.head.appendChild(s);
    }
    setTimeout(()=>setVisible(true),50);
  },[]);

  const links=[
    {label:"See the App Store",href:"https://apps.apple.com/us/app/solus-copilot/id6762177273"},
    {label:"Book a Call",href:"https://savvycal.com/almeena/solus"},
    {label:"Connect on LinkedIn",href:"https://www.linkedin.com/in/almeena-shaikh/"},
    {label:"Social Media Links",href:"https://linktr.ee/almeena.shaikh"},
  ];

  return(
    <div style={{
      margin:0,padding:0,background:"#f7f5f0",color:"#1a1a1a",minHeight:"100vh",
      fontFamily:"'Inter','Helvetica Neue',Helvetica,Arial,sans-serif",
      WebkitFontSmoothing:"antialiased",MozOsxFontSmoothing:"grayscale",
    }}>
      <div style={{
        maxWidth:480,margin:"0 auto",padding:"0 24px",
        opacity:visible?1:0,transform:visible?"translateY(0)":"translateY(24px)",
        transition:"opacity 0.7s ease, transform 0.7s ease",
      }}>

        {/* Photo + Identity */}
        <div style={{paddingTop:"10vh",textAlign:"center"}}>
          <div style={{
            width:120,height:120,margin:"0 auto 28px",borderRadius:"50%",
            overflow:"hidden",border:"3px solid #e8e3db",
            animation:"cardPulse 3s ease-in-out 1",
          }}>
            <img src="/almeena.png" alt="Almeena Shaikh"
              style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
          </div>
          <div style={{fontSize:26,fontWeight:600,letterSpacing:"-0.01em",color:"#1a1a1a",lineHeight:1.3}}>
            Almeena Shaikh
          </div>
          <div style={{fontSize:15,fontWeight:400,color:"#6b6560",marginTop:8,letterSpacing:"0.02em"}}>
            Founder &amp; CEO
          </div>
          <div style={{marginTop:16,textAlign:"center"}}>
            <img src="/solus-logo-transparent.png" alt="SOLUS" style={{height:60,width:"auto"}}/>
          </div>
        </div>

        {/* Credentials */}
        <div style={{
          textAlign:"center",marginTop:32,fontSize:13,fontWeight:400,
          color:"#a09a92",letterSpacing:"0.06em",textTransform:"uppercase",lineHeight:2,
        }}>
          USAF Veteran&ensp;&middot;&ensp;Pilot&ensp;&middot;&ensp;Ex-Pentagon&ensp;&middot;&ensp;Ex-NASA&ensp;&middot;&ensp;Ex-Google
        </div>

        {/* Divider */}
        <div style={{width:40,height:1,background:"#d9d4cc",margin:"36px auto"}}/>

        {/* Mission statement */}
        <p style={{
          fontSize:16,fontWeight:400,lineHeight:1.85,
          color:"#4a4540",textAlign:"center",maxWidth:420,margin:"0 auto",
        }}>
          Building behavioral intelligence for general aviation, so every pilot makes sharper decisions before every flight.
        </p>

        {/* Divider */}
        <div style={{width:40,height:1,background:"#d9d4cc",margin:"36px auto"}}/>

        {/* Links */}
        <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:340,margin:"0 auto"}}>
          {links.map((btn,i)=>(
            <a key={btn.label} href={btn.href} target="_blank" rel="noopener noreferrer"
              className="card-link"
              style={{
                display:"block",textAlign:"center",padding:"14px 24px",
                border:"1px solid #c4bfb7",borderRadius:2,
                color:"#1a1a1a",textDecoration:"none",
                fontSize:15,fontWeight:500,letterSpacing:"0.03em",
                background:"transparent",transition:"all 0.2s ease",
                cursor:"pointer",
                animation:`cardFadeUp 0.5s ease ${0.3+i*0.1}s both`,
              }}
            >{btn.label} &rarr;</a>
          ))}
        </div>

        {/* Footer */}
        <div style={{textAlign:"center",paddingTop:56,paddingBottom:40}}>
          <div style={{fontSize:13,color:"#c4bfb7",letterSpacing:"0.02em"}}>
            almeena@soluscopilot.com&ensp;|&ensp;soluscopilot.com
          </div>
        </div>

      </div>
    </div>
  );
}

export default function App(){
  // Simple path-based routing for /blog, /privacy, /terms
  const path=window.location.pathname;
  if(path==="/blog"||path.startsWith("/blog/"))return <Blog/>;
  if(path==="/privacy") return <Privacy/>;
  if(path==="/terms") return <Terms/>;
  if(path==="/card") return <Card/>;
  return <AppMain/>;
}

// ─── JETSON ONE COMPONENTS ────────────────────────────────────────────────────

function JetsonSortie(){
  const [draft,setDraft]=useState(()=>{
    try{const s=localStorage.getItem("solus_jetson_sortie");if(s)return JSON.parse(s);}catch{}
    return {step:0,pilotWeight:"",date:new Date().toISOString().split("T")[0],serialNumber:"",batteryId:"",batterySoc:"",flightType:"training",location:"",riskEnv:"3",riskPilot:"3",notes:""};
  });
  useEffect(()=>{try{localStorage.setItem("solus_jetson_sortie",JSON.stringify(draft));}catch{}},[draft]);
  const upd=(k,v)=>setDraft(d=>({...d,[k]:v}));
  const steps=["Pilot Weight","Date","Serial Number","Battery ID","Battery SOC","Flight Type","Location","Env Risk","Pilot Risk","Notes","Summary"];
  const jGreen="#00E676";
  const cardS={background:"var(--surface,#141922)",border:"1px solid var(--border,#1E2733)",borderRadius:6,padding:20,marginBottom:16};
  const labelS={fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.15em",color:"var(--text3)",textTransform:"uppercase",marginBottom:6};
  const inpS={width:"100%",boxSizing:"border-box",background:"var(--surface)",border:"1px solid var(--border,#1E2733)",borderRadius:4,padding:"10px 12px",color:"var(--white,#fff)",fontFamily:"var(--mono)",fontSize:13,outline:"none"};
  const btnS={fontFamily:"var(--mono)",fontSize:11,fontWeight:700,letterSpacing:"0.1em",padding:"10px 24px",borderRadius:4,cursor:"pointer",border:"none",textTransform:"uppercase"};

  function renderStepContent(){
    const s=draft.step;
    const fields=[
      {key:"pilotWeight",label:"Pilot Weight (kg)",type:"number",placeholder:"75"},
      {key:"date",label:"Date",type:"date"},
      {key:"serialNumber",label:"Jetson ONE Serial Number",type:"text",placeholder:"J1-XXXX"},
      {key:"batteryId",label:"Battery Pack ID",type:"text",placeholder:"BAT-XXXX"},
      {key:"batterySoc",label:"Battery SOC (%)",type:"number",placeholder:"100"},
      {key:"flightType",label:"Flight Type",type:"select",options:["training","proficiency","demo","photo/video","cross-country","maintenance"]},
      {key:"location",label:"Flight Location",type:"text",placeholder:"Field / GPS coords"},
      {key:"riskEnv",label:"Environmental Risk (1-5)",type:"range"},
      {key:"riskPilot",label:"Pilot Risk (1-5)",type:"range"},
      {key:"notes",label:"Notes / Remarks",type:"textarea",placeholder:"Any additional notes..."},
    ];
    if(s<10){
      const f=fields[s];
      return(<div>
        <div style={labelS}>{f.label}</div>
        {f.type==="select"?(
          <select style={inpS} value={draft[f.key]} onChange={e=>upd(f.key,e.target.value)}>
            {f.options.map(o=><option key={o} value={o}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
          </select>
        ):f.type==="range"?(
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <input type="range" min="1" max="5" value={draft[f.key]} onChange={e=>upd(f.key,e.target.value)} style={{flex:1,accentColor:jGreen}}/>
            <span style={{fontFamily:"var(--mono)",fontSize:18,color:jGreen,fontWeight:700,minWidth:20,textAlign:"center"}}>{draft[f.key]}</span>
          </div>
        ):f.type==="textarea"?(
          <textarea style={{...inpS,minHeight:80,resize:"vertical"}} value={draft[f.key]} onChange={e=>upd(f.key,e.target.value)} placeholder={f.placeholder}/>
        ):(
          <input style={inpS} type={f.type} value={draft[f.key]} onChange={e=>upd(f.key,e.target.value)} placeholder={f.placeholder}/>
        )}
      </div>);
    }
    // Summary
    return(<div>
      <div style={labelS}>Sortie Summary</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {[["Pilot Weight",draft.pilotWeight+"kg"],["Date",draft.date],["Serial",draft.serialNumber],["Battery",draft.batteryId],["SOC",draft.batterySoc+"%"],["Type",draft.flightType],["Location",draft.location],["Env Risk",draft.riskEnv+"/5"],["Pilot Risk",draft.riskPilot+"/5"]].map(([k,v])=>(
          <div key={k} style={{background:"var(--greenDim)",border:"1px solid rgba(0,230,118,0.15)",borderRadius:4,padding:"8px 10px"}}>
            <div style={{fontSize:8,color:"var(--text3)",fontFamily:"var(--mono)",letterSpacing:"0.1em",textTransform:"uppercase"}}>{k}</div>
            <div style={{fontSize:13,color:"var(--white,#fff)",fontFamily:"var(--mono)",marginTop:2}}>{v||"--"}</div>
          </div>
        ))}
      </div>
      {draft.notes&&<div style={{marginTop:10,fontSize:12,color:"var(--text2,#aaa)",fontFamily:"var(--mono)"}}>{draft.notes}</div>}
    </div>);
  }

  return(<div style={{maxWidth:520,margin:"0 auto",padding:16}}>
    <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:jGreen,marginBottom:4,textTransform:"uppercase"}}>Jetson ONE Sortie Brief</div>
    <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:16}}>Step {draft.step+1} of {steps.length}: {steps[draft.step]}</div>
    <div style={{width:"100%",height:3,background:"var(--surface)",borderRadius:2,marginBottom:20}}>
      <div style={{width:`${((draft.step+1)/steps.length)*100}%`,height:"100%",background:jGreen,borderRadius:2,transition:"width 0.3s"}}/>
    </div>
    <div style={cardS}>{renderStepContent()}</div>
    <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
      <button onClick={()=>upd("step",Math.max(0,draft.step-1))} disabled={draft.step===0} style={{...btnS,background:"var(--surface)",color:draft.step===0?"var(--muted)":"var(--white,#fff)"}}>Back</button>
      {draft.step<10?(
        <button onClick={()=>upd("step",draft.step+1)} style={{...btnS,background:jGreen,color:"var(--bg,#0B0C10)"}}>Next</button>
      ):(
        <button onClick={()=>{upd("step",0);alert("Sortie brief saved.");}} style={{...btnS,background:jGreen,color:"var(--bg,#0B0C10)"}}>Submit</button>
      )}
    </div>
  </div>);
}

function JetsonRisk(){
  const categories=["Weather","Pilot Fatigue","Battery Health","Airspace Complexity","Terrain","Wind/Gusts","Visibility","Pilot Experience","Aircraft Condition","Time Pressure"];
  const [scores,setScores]=useState(()=>{
    try{const s=localStorage.getItem("solus_jetson_risk");if(s)return JSON.parse(s);}catch{}
    return categories.reduce((a,c)=>({...a,[c]:1}),{});
  });
  useEffect(()=>{try{localStorage.setItem("solus_jetson_risk",JSON.stringify(scores));}catch{}},[scores]);
  const jGreen="#00E676";
  const scoreColor=(v)=>v<=2?jGreen:v===3?"#FFD600":v===4?"#FF8C00":"#E84040";
  const redCount=Object.values(scores).filter(v=>v===5).length;
  const total=Object.values(scores).reduce((a,b)=>a+b,0);
  const maxTotal=categories.length*5;

  return(<div style={{maxWidth:560,margin:"0 auto",padding:16}}>
    <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:jGreen,marginBottom:4,textTransform:"uppercase"}}>Jetson ONE Risk Matrix</div>
    <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:16}}>Score each category 1 (low) to 5 (high risk)</div>

    {redCount>=3&&(
      <div style={{background:"rgba(232,64,64,0.12)",border:"1px solid rgba(232,64,64,0.4)",borderRadius:6,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:18}}>!</span>
        <div>
          <div style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:700,color:"var(--red,#E84040)",letterSpacing:"0.1em"}}>FOM AUTHORIZATION REQUIRED</div>
          <div style={{fontFamily:"var(--mono)",fontSize:10,color:"rgba(232,64,64,0.7)",marginTop:2}}>{redCount} categories scored critical (5). Flight requires Flight Operations Manager approval.</div>
        </div>
      </div>
    )}

    <div style={{background:"var(--surface,#141922)",border:"1px solid var(--border,#1E2733)",borderRadius:6,padding:16,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <span style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)",letterSpacing:"0.1em",textTransform:"uppercase"}}>Total Risk Score</span>
        <span style={{fontFamily:"var(--mono)",fontSize:22,fontWeight:700,color:total/maxTotal>0.7?"#E84040":total/maxTotal>0.5?"#FF8C00":total/maxTotal>0.3?"#FFD600":jGreen}}>{total}/{maxTotal}</span>
      </div>
      <div style={{width:"100%",height:6,background:"var(--surface)",borderRadius:3}}>
        <div style={{width:`${(total/maxTotal)*100}%`,height:"100%",background:total/maxTotal>0.7?"#E84040":total/maxTotal>0.5?"#FF8C00":total/maxTotal>0.3?"#FFD600":jGreen,borderRadius:3,transition:"width 0.3s"}}/>
      </div>
    </div>

    {categories.map(cat=>(
      <div key={cat} style={{background:"var(--surface,#141922)",border:"1px solid var(--border,#1E2733)",borderRadius:6,padding:"12px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
        <div style={{flex:1,fontFamily:"var(--mono)",fontSize:12,color:"var(--white,#fff)"}}>{cat}</div>
        <div style={{display:"flex",gap:4}}>
          {[1,2,3,4,5].map(v=>(
            <button key={v} onClick={()=>setScores(s=>({...s,[cat]:v}))}
              style={{width:32,height:32,borderRadius:4,border:scores[cat]===v?`2px solid ${scoreColor(v)}`:"1px solid var(--border,#1E2733)",
                background:scores[cat]===v?`${scoreColor(v)}22`:"var(--surface)",
                color:scores[cat]===v?scoreColor(v):"var(--text3)",
                fontFamily:"var(--mono)",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              {v}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>);
}

function JetsonChecklist(){
  const phases={
    "Area Preparation":{color:"#4FC3F7",items:[
      {text:"Takeoff area — no debris, loose stones, clear of trees/powerlines",critical:true},
      {text:"Wind strength — 12 KTS MAXIMUM",critical:true},
      {text:"Aircraft position — on level ground (< 15 deg)",critical:true},
      {text:"Aircraft orientation — facing into wind",critical:false},
    ]},
    "Before Inspecting Aircraft":{color:"#81C784",note:"DO NOT MIX BATTERY BATCHES",noteColor:"#E84040",items:[
      {text:"Parachute safety pin position — SAFE",critical:true},
      {text:"Batteries (8) — fully charged and removed",critical:true,highlight:true},
      {text:"Battery terminal covers (2) — fitted & latched",critical:false},
      {text:"Propeller sleeves (8) — fitted",critical:false},
      {text:"Transport chocks (2) — REMOVE",critical:false},
      {text:"Motor arms (4) — unfold & lock",critical:true,highlight:true},
      {text:"Camera(s) — mount",critical:false},
      {text:"Helmet visor — clean, antifog",critical:false},
    ]},
    "Motor Arm Checklist":{color:"#00E676",note:"Perform this checklist for each motor arm. Repeat at Front Right, Front Left, Rear Left, and Rear Right arms.",noteColor:"#FFB000",items:[
      {text:"Arm pivot bolt — inspect bolt markings",critical:true},
      {text:"Cables — condition, securely attached",critical:true},
      {text:"MDCs (2) — condition, securely attached",critical:true},
      {text:"Propeller sleeves (2) — remove",critical:false},
      {text:"Propellers / motors (2) — condition, securely attached",critical:true,sub:"MOTOR BOLT MARKINGS (4x UPPER, 4x LOWER) · PROP BOLT MARKINGS (4x UPPER, 4x LOWER) · FREE ROTATION · RAP TEST PROP IN 90 DEG POSITION"},
      {text:"Propeller sleeves (2) — replace",critical:false},
    ]},
    "Walkaround — Front Right Arm [START HERE]":{color:"#00E676",note:"The walkaround procedure should ALWAYS start at the Front Right Arm and then progress around the aircraft in a counter-clockwise direction.",noteColor:"#FFB000",image:"/jetson-walkaround.png",items:[
      {text:"Motor arm checklist — PERFORM (see above)",critical:true,highlight:true},
    ]},
    "Walkaround — Nose":{color:"#00E676",items:[
      {text:"Front arm clamp — CLOSED, NO GAP",critical:true,highlight:true,sub:"SPRING PIN FULLY IN & MOVES FREELY"},
      {text:"Fairings — lower bolts secure (2)",critical:false},
    ]},
    "Walkaround — Front Left Arm":{color:"#00E676",items:[
      {text:"Motor arm checklist — PERFORM (see above)",critical:true,highlight:true},
    ]},
    "Walkaround — Left Side Body":{color:"#00E676",items:[
      {text:"Front fairings — rivets (4), bolts (6)",critical:false},
      {text:"Seat attachment — bolts (6)",critical:true},
      {text:"Under seat — cables/computer condition, secure",critical:true},
      {text:"Fuses (4) — inspect all 4 INBOARD",critical:true},
      {text:"GNSS sensors — cable condition, secure",critical:false},
      {text:"Rear fairings — bolts (4)",critical:false},
    ]},
    "Walkaround — Rear Left Arm":{color:"#00E676",items:[
      {text:"Motor arm checklist — PERFORM (see above)",critical:true,highlight:true},
    ]},
    "Walkaround — Tail":{color:"#00E676",items:[
      {text:"Rear arm clamp — CLOSED, NO GAP",critical:true,highlight:true,sub:"SPRING PIN FULLY IN & MOVES FREELY"},
      {text:"Fairings — bolts secure (2 upper, 2 lower)",critical:false},
      {text:"Frame / cables — condition, securely attached",critical:true},
      {text:"Radar altimeter sensor — condition, securely attached",critical:true},
    ]},
    "Walkaround — Rear Right Arm":{color:"#00E676",items:[
      {text:"Motor arm checklist — PERFORM (see above)",critical:true,highlight:true},
    ]},
    "Walkaround — Right Side Body":{color:"#00E676",items:[
      {text:"Rear fairings — bolts (4)",critical:false},
      {text:"Seat attachment — bolts (6)",critical:true},
      {text:"Under seat — cables/computer secure",critical:true},
      {text:"Fuses (4) — inspect all 4 OUTBOARD",critical:true},
      {text:"Front fairings — rivets (4), bolts (6)",critical:false},
    ]},
    "Walkaround — Cockpit":{color:"#00E676",items:[
      {text:"Parachute safety pin position — SAFE",critical:true},
      {text:"Arming module / emerg button — condition",critical:true},
      {text:"Seat harness — adjust as required",critical:false},
      {text:"Primary flight display — condition, securely attached",critical:true},
      {text:"Joystick / buttons (2) / alt switch — condition, full / free motion",critical:true},
    ]},
    "Start-Up — Connecting Batteries":{color:"#FFD600",items:[
      {text:"Preflight inspection & walkaround — COMPLETED",critical:true},
      {text:"Propeller sleeves (8) — fitted",critical:true},
      {text:"Battery terminal covers (2) — remove, inspect pins",critical:false},
      {text:"Battery connectors (8) — open fully",critical:false},
      {text:"Batteries (8) — check power status, place in position",critical:true,highlight:true},
      {text:"Battery (1 right rear) — connect and clamp",critical:true,highlight:true},
      {text:"Primary flight display — monitor boot-up (40 sec)",critical:true},
      {text:"MDC LEDs (8) — verify blinking (7x blue, 1x white)",critical:true},
      {text:"Batteries (remaining 7) — connect and latch",critical:true,highlight:true},
      {text:"Lights (front & rear) — confirm lit (if fitted)",critical:false},
      {text:"Joystick / altitude control switch — full deflection displayed",critical:true},
      {text:"Camera(s) — RECORDING",critical:false},
      {text:"Propeller sleeves (8) — remove and stow",critical:true},
    ]},
    "Start-Up — Entering Aircraft":{color:"#FFD600",note:"Enter the vehicle from the left side.",noteColor:"#FFB000",items:[
      {text:"Seat harness — FASTEN",critical:true},
      {text:"Helmet — don, chin strap, comms check",critical:true},
      {text:"Primary flight display — CHECK PARAMETERS",critical:true},
      {text:"Parachute safety pin — REMOVE, SHOW AND STOW (ARMED)",critical:true},
      {text:"Arming module / emerg button — press for > 1.5 seconds",critical:true},
      {text:"PFD message — NOT READY → READY",critical:true},
    ]},
    "Start-Up — Starting Motors":{color:"#FF8C00",items:[
      {text:"Takeoff area — CHECK / CALL \"CLEAR\"",critical:true},
      {text:"Helmet visor — DOWN",critical:true},
      {text:"Hand signal — MOTOR START",critical:false},
      {text:"Motors — START",critical:true},
      {text:"Rotation speed — STABLE AT IDLE",critical:true},
    ]},
    "After Landing":{color:"#E84040",items:[
      {text:"Motors — CONFIRM STOPPED",critical:true},
      {text:"Primary flight display — note battery time remaining",critical:false},
      {text:"Arming module / emerg button — press for > 1.5 seconds",critical:true},
      {text:"PFD message — READY → NOT READY",critical:true},
      {text:"Parachute safety pin — REMOVE, SHOW AND INSERT (SAFE)",critical:true},
      {text:"Helmet — remove",critical:false},
      {text:"Seat harness — unfasten",critical:false},
      {text:"Camera(s) — stop recording",critical:false},
      {text:"Aircraft — EXIT",critical:false},
    ]},
    "Securing Aircraft":{color:"#B0BEC5",items:[
      {text:"Propeller sleeves (8) — install",critical:false},
      {text:"Batteries (8) — disconnect, latches fully down, then remove",critical:true,highlight:true},
      {text:"Battery connector covers (2) — install, connectors latched (8)",critical:false},
      {text:"Fuses (8) — check inboard (4) & outboard (4)",critical:true},
      {text:"Front & rear arm clamp — unlock, fold motor arms (4)",critical:true,highlight:true},
      {text:"Transport chocks (2) — install",critical:false},
    ]},
  };
  const [checked,setChecked]=useState(()=>{
    try{const s=localStorage.getItem("solus_jetson_checklist");if(s)return JSON.parse(s);}catch{}
    return {};
  });
  useEffect(()=>{try{localStorage.setItem("solus_jetson_checklist",JSON.stringify(checked));}catch{}},[checked]);
  const toggle=(phase,idx)=>{const k=`${phase}-${idx}`;setChecked(c=>({...c,[k]:!c[k]}));};
  const totalItems=Object.values(phases).reduce((s,p)=>s+p.items.length,0);
  const totalDone=Object.entries(phases).reduce((s,[phase,{items}])=>s+items.filter((_,i)=>checked[`${phase}-${i}`]).length,0);

  return(<div style={{height:"100%",overflowY:"auto",padding:"28px 24px"}}>
    <div style={{maxWidth:620,margin:"0 auto"}}>
    {/* Header — matches document title block */}
    <div style={{textAlign:"center",marginBottom:20,borderBottom:"2px solid var(--green,#00E676)",paddingBottom:16}}>
      <div style={{fontFamily:"var(--mono)",fontSize:16,fontWeight:700,letterSpacing:"0.3em",color:"var(--white,#fff)",textTransform:"uppercase"}}>Jetson ONE Checklist</div>
      <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3,rgba(255,255,255,0.5))",marginTop:4,letterSpacing:"0.15em"}}>Version 3 — CAO 9 June 2026</div>
      <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--green,#00E676)",marginTop:6,letterSpacing:"0.1em"}}>{totalDone}/{totalItems} ITEMS COMPLETE</div>
    </div>

    {/* Parachute warning — per document, prominent */}
    <div style={{background:"var(--redDim,rgba(232,64,64,0.12))",border:"2px solid var(--red,#E84040)",borderRadius:4,padding:"14px 18px",marginBottom:20,textAlign:"center"}}>
      <div style={{fontFamily:"var(--mono)",fontSize:13,fontWeight:700,color:"var(--red,#E84040)",letterSpacing:"0.15em",textTransform:"uppercase"}}>⚠ WARNING</div>
      <div style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:700,color:"var(--white,#fff)",letterSpacing:"0.1em",marginTop:6}}>ALWAYS treat the Ballistic Parachute Safety Zone as Live</div>
    </div>

    <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
      <button onClick={()=>{setChecked({});localStorage.removeItem("solus_jetson_checklist");}}
        style={{fontFamily:"var(--mono)",fontSize:9,background:"var(--redDim,rgba(232,64,64,0.1))",border:"1px solid var(--red,#E84040)",color:"var(--red,#E84040)",padding:"5px 12px",borderRadius:4,cursor:"pointer",letterSpacing:"0.08em",textTransform:"uppercase"}}>Reset All</button>
    </div>

    {Object.entries(phases).map(([phase,{color,items,note,noteColor,image}])=>{
      const done=items.filter((_,i)=>checked[`${phase}-${i}`]).length;
      const allDone=done===items.length;
      return(<div key={phase} style={{marginBottom:20}}>
        {/* Section header — document style */}
        <div style={{borderBottom:"1px solid "+(allDone?"var(--green,#00E676)":"var(--border,rgba(255,255,255,0.15))"),paddingBottom:6,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
          <span style={{fontFamily:"var(--mono)",fontSize:12,fontWeight:700,color:allDone?"var(--green,#00E676)":"var(--white,#fff)",letterSpacing:"0.15em",textTransform:"uppercase"}}>{phase}</span>
          <span style={{fontFamily:"var(--mono)",fontSize:10,color:allDone?"var(--green,#00E676)":"var(--text3,rgba(255,255,255,0.35))"}}>{done}/{items.length}</span>
        </div>
        {/* Section note — instructional callout from document */}
        {note&&<div style={{background:noteColor==="#E84040"?"var(--redDim,rgba(232,64,64,0.08))":"var(--amberDim,rgba(255,176,0,0.06))",
          border:`1px solid ${noteColor==="#E84040"?"var(--red,#E84040)":"var(--amber,#FFB000)"}`,
          borderRadius:4,padding:"8px 12px",marginBottom:10,textAlign:"center"}}>
          <span style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,color:noteColor==="#E84040"?"var(--red,#E84040)":"var(--amber,#FFB000)",letterSpacing:"0.08em"}}>{note}</span>
        </div>}
        {/* Walkaround diagram */}
        {image&&<div style={{textAlign:"center",marginBottom:12,padding:12,background:"var(--surface,rgba(255,255,255,0.03))",border:"1px solid var(--border,rgba(255,255,255,0.08))",borderRadius:4}}>
          <img src={image} alt="Jetson ONE walkaround procedure" style={{maxWidth:"100%",height:"auto",borderRadius:4}}/>
        </div>}
        {/* Items — dotted leader style matching the document */}
        {items.map((item,i)=>{
          const k=`${phase}-${i}`;
          const isDone=checked[k];
          // Split item text on " — " to get label and action
          const parts=item.text.split(" — ");
          const label=parts[0];
          const action=parts[1]||"";
          return(
          <div key={i}>
          <div onClick={()=>toggle(phase,i)}
            style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:item.highlight&&!isDone?"8px 10px":"6px 0",
              borderLeft:item.highlight&&!isDone?"3px solid var(--red,#E84040)":item.critical&&!isDone?"3px solid var(--red,#E84040)":"3px solid transparent",
              paddingLeft:10,
              background:item.highlight&&!isDone?"var(--redDim,rgba(232,64,64,0.1))":"transparent",
              borderRadius:item.highlight?3:0,
              transition:"all 0.15s"}}>
            {/* Checkbox */}
            <div style={{width:16,height:16,borderRadius:2,border:isDone?"2px solid var(--green,#00E676)":"1px solid var(--border,rgba(255,255,255,0.25))",
              background:isDone?"var(--greenDim,rgba(0,230,118,0.13))":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {isDone&&<span style={{color:"var(--green,#00E676)",fontSize:11,fontWeight:700}}>✓</span>}
            </div>
            {/* Item number */}
            <span style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3,rgba(255,255,255,0.3))",minWidth:18,flexShrink:0}}>{i+1}.</span>
            {/* Label */}
            <span style={{fontFamily:"var(--mono)",fontSize:item.highlight?12:11,fontWeight:700,
              color:isDone?"var(--text3,rgba(255,255,255,0.3))":item.highlight?"var(--red,#E84040)":"var(--white,#fff)",
              whiteSpace:"nowrap",flexShrink:0}}>{label}</span>
            {/* Dotted leader */}
            <span style={{flex:1,borderBottom:"1px dotted var(--border,rgba(255,255,255,0.12))",minWidth:20,alignSelf:"flex-end",marginBottom:3}}/>
            {/* Action */}
            <span style={{fontFamily:"var(--mono)",fontSize:item.highlight?12:11,fontWeight:item.highlight?700:400,
              color:isDone?"var(--text3,rgba(255,255,255,0.3))":item.highlight?"var(--red,#E84040)":item.critical?"var(--amber,#FFB000)":"var(--text2,rgba(255,255,255,0.7))",
              whiteSpace:"nowrap",flexShrink:0,textAlign:"right"}}>{action}</span>
          </div>
          {/* Sub-details (e.g. motor arm bolt markings) */}
          {item.sub&&<div style={{paddingLeft:54,paddingBottom:4}}>
            <span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--amberLabel,rgba(255,176,0,0.5))",letterSpacing:"0.05em"}}>{item.sub}</span>
          </div>}
          </div>);
        })}
      </div>);
    })}

    {/* Footer */}
    {totalDone===totalItems&&(
      <div style={{textAlign:"center",border:"2px solid var(--green,#00E676)",borderRadius:6,padding:"20px",marginTop:12,background:"var(--greenDim,rgba(0,230,118,0.05))"}}>
        <div style={{fontFamily:"var(--mono)",fontSize:13,fontWeight:700,color:"var(--green,#00E676)",letterSpacing:"0.15em"}}>ALL PHASES COMPLETE</div>
        <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3,rgba(255,255,255,0.4))",marginTop:4}}>Aircraft ready for flight operations</div>
      </div>
    )}

    <div style={{height:40}}/>
    </div>
  </div>);
}

function JetsonHUD(){
  const jGreen="#00E676";
  const stepS={display:"flex",gap:12,marginBottom:16,alignItems:"flex-start"};
  const numS={fontFamily:"var(--mono)",fontSize:22,fontWeight:700,color:jGreen,lineHeight:1,minWidth:28};
  const titleS={fontFamily:"var(--mono)",fontSize:12,fontWeight:700,color:"var(--white,#F0F2F5)",letterSpacing:"0.08em",marginBottom:2};
  const descS={fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",lineHeight:1.5};
  const cardS={background:"var(--surface,#141922)",border:"1px solid var(--border,#1E2733)",borderRadius:6,padding:"14px 16px",marginBottom:12};

  return(<div style={{maxWidth:560,margin:"0 auto",padding:16}}>
    <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:jGreen,marginBottom:4,textTransform:"uppercase"}}>IN-FLIGHT TELEMETRY</div>
    <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:20}}>Jetson ONE Flight Companion</div>

    <div style={cardS}>
      <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.15em",color:jGreen,textTransform:"uppercase",marginBottom:8}}>JETSON ONE FLIGHT COMPANION</div>
      <div style={{height:1,background:"var(--border,#1E2733)",marginBottom:10}}/>
      <div style={descS}>In-flight telemetry, HUD display, and real-time flight data are handled by the Jetson ONE Flight Companion app during flight.</div>
      <div style={{...descS,marginTop:8}}>SOLUS handles pre-departure intelligence (risk matrix, checklist, weather brief) and post-flight debrief. The Jetson app handles everything in between.</div>
    </div>

    <div style={cardS}>
      <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.15em",color:jGreen,textTransform:"uppercase",marginBottom:8}}>WORKFLOW</div>
      <div style={{height:1,background:"var(--border,#1E2733)",marginBottom:12}}/>
      <div style={stepS}><div style={numS}>1</div><div><div style={titleS}>PRE-FLIGHT (SOLUS)</div><div style={descS}>Risk matrix, checklist, weather brief, sortie wizard</div></div></div>
      <div style={stepS}><div style={numS}>2</div><div><div style={titleS}>IN-FLIGHT (JETSON APP)</div><div style={descS}>Live HUD, telemetry, battery monitoring, alerts</div></div></div>
      <div style={stepS}><div style={numS}>3</div><div><div style={titleS}>POST-FLIGHT (SOLUS)</div><div style={descS}>Debrief connecting pre-departure flags to outcomes, pilot score update</div></div></div>
    </div>

    <div style={cardS}>
      <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.15em",color:jGreen,textTransform:"uppercase",marginBottom:8}}>DATA SYNC</div>
      <div style={{height:1,background:"var(--border,#1E2733)",marginBottom:10}}/>
      <div style={descS}>After landing, connect to Jetson Link WiFi to download flight controller logs. SOLUS will incorporate flight data into your pilot score and debrief analysis.</div>
    </div>
  </div>);
}

function JetsonScore(){
  const jGreen="#00E676";
  const [overallScore]=useState(()=>{
    try{const s=localStorage.getItem("solus_jetson_score");if(s)return parseInt(s);}catch{}
    return 0;
  });
  const components=[
    {label:"Flight Hours",score:0,max:25,desc:"Total Jetson ONE flight time"},
    {label:"Sortie Compliance",score:0,max:20,desc:"Sortie briefs completed on time"},
    {label:"Checklist Discipline",score:0,max:20,desc:"Pre/post-flight checklist completion rate"},
    {label:"Risk Management",score:0,max:15,desc:"Risk assessments within acceptable range"},
    {label:"Incident-Free Ops",score:0,max:10,desc:"Consecutive flights without safety events"},
    {label:"Currency",score:0,max:10,desc:"Recency of flights and proficiency checks"},
  ];
  const barColor=(pct)=>pct>=0.7?jGreen:pct>=0.4?"#FFD600":"#E84040";

  return(<div className="page"><div className="page-inner" style={{maxWidth:560,margin:"0 auto"}}>
    <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:jGreen,marginBottom:4,textTransform:"uppercase"}}>Jetson ONE Pilot Score</div>
    <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:20}}>Performance and proficiency dashboard</div>

    <div style={{background:"var(--surface,#141922)",border:"1px solid var(--border,#1E2733)",borderRadius:6,padding:24,marginBottom:20,textAlign:"center"}}>
      <div style={{fontFamily:"var(--mono)",fontSize:56,fontWeight:700,color:overallScore>=70?jGreen:overallScore>=40?"#FFD600":"#E84040",lineHeight:1}}>{overallScore}</div>
      <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text3)",letterSpacing:"0.15em",textTransform:"uppercase",marginTop:6}}>Overall Score / 100</div>
      <div style={{width:"80%",height:6,background:"var(--surface)",borderRadius:3,margin:"14px auto 0"}}>
        <div style={{width:`${overallScore}%`,height:"100%",background:overallScore>=70?jGreen:overallScore>=40?"#FFD600":"#E84040",borderRadius:3,transition:"width 0.3s"}}/>
      </div>
      {overallScore===0&&<div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--muted)",marginTop:10,fontStyle:"italic"}}>Complete your first sortie to begin scoring</div>}
    </div>

    <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.15em",color:"var(--text3)",textTransform:"uppercase",marginBottom:10}}>Component Breakdown</div>
    {components.map(c=>(
      <div key={c.label} style={{background:"var(--surface,#141922)",border:"1px solid var(--border,#1E2733)",borderRadius:6,padding:"12px 16px",marginBottom:8}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--white,#fff)"}}>{c.label}</span>
          <span style={{fontFamily:"var(--mono)",fontSize:12,fontWeight:700,color:barColor(c.score/c.max)}}>{c.score}/{c.max}</span>
        </div>
        <div style={{width:"100%",height:4,background:"var(--surface)",borderRadius:2}}>
          <div style={{width:c.max>0?`${(c.score/c.max)*100}%`:"0%",height:"100%",background:barColor(c.score/c.max),borderRadius:2,transition:"width 0.3s"}}/>
        </div>
        <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--muted)",marginTop:4}}>{c.desc}</div>
      </div>
    ))}

    <div style={{background:"var(--surface,#141922)",border:"1px solid var(--border,#1E2733)",borderRadius:6,padding:"14px 16px",marginTop:12}}>
      <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.15em",color:"var(--text3)",textTransform:"uppercase",marginBottom:8}}>30-Day Trend</div>
      <div style={{height:60,display:"flex",alignItems:"flex-end",gap:3}}>
        {Array.from({length:30},(_,i)=>(
          <div key={i} style={{flex:1,background:"rgba(0,230,118,0.1)",borderRadius:1,height:2,minHeight:2}}/>
        ))}
      </div>
      <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--muted)",marginTop:8,fontStyle:"italic",textAlign:"center"}}>Score history will appear after multiple sorties</div>
    </div>
  </div></div>);
}

function SubTabBar({items,active,onSelect}){
  return(
    <div style={{display:'flex',gap:8,padding:'12px 0',borderBottom:'1px solid var(--border)',marginBottom:16}}>
      {items.map(item=>(
        <button key={item.key} onClick={()=>onSelect(item.key)} style={{
          fontFamily:'var(--mono)',fontSize:10,letterSpacing:'0.1em',
          padding:'6px 14px',borderRadius:3,border:'1px solid',
          borderColor:active===item.key?'var(--amber)':'var(--border)',
          background:active===item.key?'rgba(255,176,0,0.1)':'var(--surface)',
          color:active===item.key?'var(--amber)':'var(--text3)',
          cursor:'pointer',textTransform:'uppercase',
        }}>{item.label}</button>
      ))}
    </div>
  );
}

// ─── ONBOARDING WIZARD + FREE TRIAL PAYWALL ─────────────────────────────────
function Onboarding({ authUser, onComplete }){
  const [step,setStep]=useState(1);
  const [showPaywall,setShowPaywall]=useState(false);

  // Step 1: Pilot Profile
  const [pilotType,setPilotType]=useState("");
  const [flightHours,setFlightHours]=useState("");
  const [homeAirport,setHomeAirport]=useState("");

  // Step 2: Aircraft
  const [nNumber,setNNumber]=useState("");
  const [acType,setAcType]=useState("");
  const [squawks,setSquawks]=useState("");
  const [lookupLoading,setLookupLoading]=useState(false);
  const [lookupResult,setLookupResult]=useState(null);

  // Step 3: Currency
  const [lastBFR,setLastBFR]=useState("");
  const [lastMedical,setLastMedical]=useState("");
  const [medicalClass,setMedicalClass]=useState("3rd");
  const [lastLanding,setLastLanding]=useState("");

  const pilotTypes=["GA Pilot","Flight Student","CFI/CFII","Commercial Pilot","ATP","Military","Jetson ONE/eVTOL"];

  async function lookupNNumber(nn){
    if(!nn||nn.length<2)return;
    setLookupLoading(true);
    try{
      const res=await fetch("https://solus-backend-9wde.onrender.com/registry/lookup/"+encodeURIComponent(nn.replace(/^N/i,"")));
      if(res.ok){
        const d=await res.json();
        setLookupResult(d);
        if(d.type)setAcType(d.type);
      }
    }catch{}
    setLookupLoading(false);
  }

  function saveAndFinish(){
    // Save aircraft data
    const acData={nNumber:nNumber.replace(/^N/i,""),type:acType,totalTime:"",engineTime:"",lastAnnual:"",last100hr:"",squawks};
    localStorage.setItem("solus_ac",JSON.stringify(acData));
    // Save currency data
    const currData={
      lastBFR,lastMedical,medicalClass,dob:"",
      lastDayLanding:lastLanding,dayLanding2:"",dayLanding3:"",
      lastNightLanding:"",nightLanding2:"",nightLanding3:"",
      nightTakeoff1:"",nightTakeoff2:"",nightTakeoff3:"",
      lastIFR:"",ifrApproaches:"",ifrHolds:"",ifrTracking:"",
      tailwheel:false,lastTailwheelLanding:"",
    };
    localStorage.setItem("solus_curr_dates",JSON.stringify(currData));
    // Save pilot profile
    localStorage.setItem("solus_pilot_profile",JSON.stringify({pilotType,flightHours,homeAirport}));
    // Save to backend if token available
    const token=localStorage.getItem("solus_token");
    if(token){
      fetch("https://solus-backend-9wde.onrender.com/user/aircraft",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({n_number:acData.nNumber,type:acData.type,squawks})}).catch(()=>{});
      fetch("https://solus-backend-9wde.onrender.com/user/currency",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({bfr:lastBFR,medical:lastMedical,medical_class:medicalClass,vfr_day:lastLanding})}).catch(()=>{});
    }
    // Jetson ONE/eVTOL users set jetson mode
    if(pilotType==="Jetson ONE/eVTOL"){
      localStorage.setItem("solus_pilot_mode","jetson");
    }
    // Free early access -- no paywall
    localStorage.setItem("solus_onboarded_complete","1");
    onComplete();
  }

  const trialPlans=[
    {id:"solo",name:"Solo",price:"$49.99",period:"/mo",features:["Unlimited AI queries","Full risk signal breakdown","NOTAM plain English translation","Mission Brief (PAVE assessment)","Performance planning","Post-flight debrief","1 aircraft profile"]},
    {id:"captain",name:"Captain",price:"$99.99",period:"/mo",recommended:true,features:["Everything in Solo","Priority AI response","Email currency alerts","Unlimited logbook scanning","Up to 3 aircraft profiles","Agentic monitoring loop","GPS anomaly alerts"]},
    {id:"fleet",name:"Fleet",price:"$299.99",period:"/mo",features:["Everything in Captain","Unlimited aircraft profiles","Fleet currency dashboard","Bulk pilot management","API access","CSV export for insurance","Dedicated support"]},
  ];

  function handleStartTrial(planId){
    alert("To start your free trial, download SOLUS Copilot from the App Store and subscribe through the app. Subscriptions are managed through Apple In-App Purchase.");
    localStorage.setItem("solus_onboarded_complete","1");
    localStorage.setItem("solus_trial_plan",planId);
    onComplete();
  }

  function handleSkip(){
    localStorage.setItem("solus_onboarded_complete","1");
    onComplete();
  }

  // Shared styles
  const overlayS={position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:16};
  const cardS={background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,maxWidth:480,width:"100%",padding:"32px 28px",maxHeight:"90vh",overflowY:"auto"};
  const labelS={fontFamily:"'JetBrains Mono',monospace",fontSize:10,fontWeight:700,letterSpacing:"0.15em",color:"var(--text3)",textTransform:"uppercase",marginBottom:6,display:"block"};
  const inputS={width:"100%",boxSizing:"border-box",background:"var(--surface)",border:"1px solid var(--amberBorder)",borderRadius:3,padding:"10px 12px",color:"var(--white,#fff)",fontFamily:"'JetBrains Mono',monospace",fontSize:13,outline:"none"};
  const btnS={width:"100%",padding:"12px 0",background:"var(--amber,#FFB000)",color:"var(--bg,#0B0C10)",border:"none",borderRadius:3,fontFamily:"'JetBrains Mono',monospace",fontSize:12,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",cursor:"pointer",marginTop:16};
  const dotS=(active)=>({width:8,height:8,borderRadius:"50%",background:active?"var(--amber,#FFB000)":"var(--surface)",transition:"background 0.2s"});

  // Free Trial Paywall
  if(showPaywall){
    return(
      <div style={overlayS}>
        <div style={{...cardS,maxWidth:720}}>
          <div style={{textAlign:"center",marginBottom:28}}>
            <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:"var(--amber,#FFB000)",textTransform:"uppercase",marginBottom:10}}>SOLUS COPILOT</div>
            <div style={{fontSize:22,fontWeight:700,color:"var(--white,#fff)",marginBottom:6}}>Start your 3-day free trial</div>
            <div style={{fontSize:13,color:"var(--text3)",lineHeight:1.6}}>Full access to all SOLUS flight intelligence. Cancel anytime.</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginBottom:20}}>
            {trialPlans.map(p=>(
              <div key={p.id} style={{background:"var(--surface)",border:p.recommended?"2px solid var(--amber,#FFB000)":"1px solid var(--border)",borderRadius:4,padding:"18px 14px",display:"flex",flexDirection:"column",position:"relative"}}>
                {p.recommended&&<div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:7,fontWeight:700,letterSpacing:"0.15em",color:"var(--bg,#0B0C10)",background:"var(--amber,#FFB000)",padding:"2px 7px",borderRadius:2,textTransform:"uppercase",position:"absolute",top:-9,left:12}}>Recommended</div>}
                <div style={{fontSize:16,fontWeight:700,color:"var(--white,#fff)",marginBottom:2}}>{p.name}</div>
                <div style={{fontSize:20,fontWeight:700,color:"var(--amber,#FFB000)",marginBottom:2}}>{p.price}<span style={{fontSize:11,color:"var(--text3)"}}>{p.period}</span></div>
                <div style={{flex:1,marginTop:10}}>
                  {p.features.map((f,i)=><div key={i} style={{fontSize:10,color:"var(--text2)",fontFamily:"'JetBrains Mono',monospace",marginBottom:4,lineHeight:1.5}}>+ {f}</div>)}
                </div>
                <button onClick={()=>handleStartTrial(p.id)} style={{...btnS,marginTop:12,fontSize:10,padding:"10px 0",background:p.recommended?"var(--amber,#FFB000)":"var(--amberDim)",color:p.recommended?"var(--bg,#0B0C10)":"var(--amber,#FFB000)",border:p.recommended?"none":"1px solid var(--amberBorder)"}}>Start Free Trial</button>
              </div>
            ))}
          </div>
          <div style={{textAlign:"center",fontSize:10,color:"var(--muted)",fontFamily:"'JetBrains Mono',monospace",lineHeight:1.6,marginBottom:16}}>You will not be charged during your 3-day trial. After the trial, you will be billed $49.99/mo for the Solo plan. Cancel anytime before the trial ends to avoid being charged.</div>
        </div>
      </div>
    );
  }

  return(
    <div style={overlayS}>
      <div style={cardS}>
        {/* Step indicator */}
        <div style={{display:"flex",justifyContent:"center",gap:8,marginBottom:24}}>
          <div style={dotS(step>=1)}/><div style={dotS(step>=2)}/><div style={dotS(step>=3)}/>
        </div>
        <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:"var(--amber,#FFB000)",textTransform:"uppercase",textAlign:"center",marginBottom:4}}>SOLUS COPILOT</div>
        <div style={{fontSize:18,fontWeight:700,color:"var(--white,#fff)",textAlign:"center",marginBottom:4}}>
          {step===1&&"Pilot Profile"}{step===2&&"Aircraft"}{step===3&&"Currency Quick Check"}
        </div>
        <div style={{fontSize:11,color:"var(--text3)",textAlign:"center",marginBottom:24}}>Step {step} of 3</div>

        {/* Step 1: Pilot Profile */}
        {step===1&&(
          <div>
            <label style={labelS}>What type of pilot are you?</label>
            <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:18}}>
              {pilotTypes.map(t=>(
                <label key={t} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"8px 12px",background:pilotType===t?"var(--amberDim)":"var(--surface)",border:pilotType===t?"1px solid var(--amberBorder)":"1px solid var(--border)",borderRadius:3,transition:"all 0.15s"}}>
                  <input type="radio" name="pilotType" checked={pilotType===t} onChange={()=>setPilotType(t)} style={{accentColor:"var(--amber,#FFB000)"}}/>
                  <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12,color:pilotType===t?"var(--amber,#FFB000)":"var(--text2)"}}>{t}</span>
                </label>
              ))}
            </div>
            <label style={labelS}>How many total flight hours?</label>
            <input type="text" value={flightHours} onChange={e=>setFlightHours(e.target.value)} placeholder="250" style={{...inputS,marginBottom:14}}/>
            <label style={labelS}>Home airport (ICAO)</label>
            <input type="text" value={homeAirport} onChange={e=>setHomeAirport(e.target.value.toUpperCase())} placeholder="KVNY" style={{...inputS,marginBottom:8}}/>
            <button onClick={()=>setStep(2)} style={btnS}>Next</button>
          </div>
        )}

        {/* Step 2: Aircraft */}
        {step===2&&(
          <div>
            <label style={labelS}>Primary aircraft N-number</label>
            <div style={{display:"flex",gap:8,marginBottom:4}}>
              <input type="text" value={nNumber} onChange={e=>setNNumber(e.target.value.toUpperCase())} placeholder="N44821" style={{...inputS,flex:1}}/>
              <button onClick={()=>lookupNNumber(nNumber)} disabled={lookupLoading} style={{...btnS,width:"auto",marginTop:0,padding:"10px 16px",fontSize:9,opacity:lookupLoading?0.5:1}}>{lookupLoading?"...":"LOOKUP"}</button>
            </div>
            {lookupResult&&lookupResult.name&&<div style={{fontSize:10,color:"var(--green,#00E676)",fontFamily:"'JetBrains Mono',monospace",marginBottom:10,marginTop:4}}>Found: {lookupResult.name} {lookupResult.model||""}</div>}
            <div style={{marginBottom:14}}/>
            <label style={labelS}>Aircraft type</label>
            <input type="text" value={acType} onChange={e=>setAcType(e.target.value)} placeholder="C172S" style={{...inputS,marginBottom:14}}/>
            <label style={labelS}>Open squawks <span style={{fontWeight:400,opacity:0.5}}>(optional)</span></label>
            <input type="text" value={squawks} onChange={e=>setSquawks(e.target.value)} placeholder="None" style={{...inputS,marginBottom:8}}/>
            <div style={{display:"flex",gap:10,marginTop:16}}>
              <button onClick={()=>setStep(1)} style={{...btnS,background:"var(--surface)",color:"var(--text3)",flex:"0 0 auto",width:"auto",padding:"12px 20px"}}>Back</button>
              <button onClick={()=>setStep(3)} style={{...btnS,flex:1}}>Next</button>
            </div>
          </div>
        )}

        {/* Step 3: Currency Quick Check */}
        {step===3&&(
          <div>
            <label style={labelS}>Last Flight Review (BFR)</label>
            <input type="date" value={lastBFR} onChange={e=>setLastBFR(e.target.value)} style={{...inputS,marginBottom:14}}/>
            <label style={labelS}>Last medical exam</label>
            <input type="date" value={lastMedical} onChange={e=>setLastMedical(e.target.value)} style={{...inputS,marginBottom:14}}/>
            <label style={labelS}>Medical class</label>
            <select value={medicalClass} onChange={e=>setMedicalClass(e.target.value)} style={{...inputS,marginBottom:14,appearance:"auto"}}>
              <option value="1st">1st Class</option>
              <option value="2nd">2nd Class</option>
              <option value="3rd">3rd Class</option>
              <option value="BasicMed">BasicMed</option>
            </select>
            <label style={labelS}>Last landing</label>
            <input type="date" value={lastLanding} onChange={e=>setLastLanding(e.target.value)} style={{...inputS,marginBottom:8}}/>
            <div style={{display:"flex",gap:10,marginTop:16}}>
              <button onClick={()=>setStep(2)} style={{...btnS,background:"var(--surface)",color:"var(--text3)",flex:"0 0 auto",width:"auto",padding:"12px 20px"}}>Back</button>
              <button onClick={saveAndFinish} style={{...btnS,flex:1}}>Start Free Trial</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SignInModal({show,onClose,mode,setMode,form,setForm,error,success,loading,onEmailAuth,setError,setSuccess,supabaseClient}){
  if(!show)return null;
  const m={
    overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:10000},
    card:{background:"var(--bg,#0B0C10)",border:"1px solid var(--amberBorder)",borderRadius:8,width:"100%",maxWidth:440,padding:"40px 36px",position:"relative",boxShadow:"0 24px 80px rgba(0,0,0,0.8)"},
    close:{position:"absolute",top:16,right:18,background:"none",border:"none",color:"var(--muted)",fontSize:20,cursor:"pointer",lineHeight:1},
    logo:{textAlign:"center",marginBottom:8},
    logoText:{fontFamily:"'JetBrains Mono',monospace",fontSize:28,fontWeight:700,color:"var(--white,#F0F2F5)",letterSpacing:"0.12em"},
    logoAmber:{color:"var(--amber,#FFB000)"},
    tagline:{fontFamily:"'JetBrains Mono',monospace",fontSize:8,letterSpacing:"0.3em",color:"var(--amberLabel)",textTransform:"uppercase",textAlign:"center",marginBottom:6},
    subtitle:{fontFamily:"system-ui,sans-serif",fontSize:13,color:"var(--text3)",textAlign:"center",marginBottom:28,lineHeight:1.5},
    socialBtn:{width:"100%",padding:"13px 20px",marginBottom:10,borderRadius:4,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",fontSize:12,fontWeight:600,letterSpacing:"0.06em",display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"opacity 0.15s"},
    googleBtn:{background:"#ffffff",border:"none",color:"#1a1a1a"},
    appleBtn:{background:"#000000",border:"1px solid rgba(255,255,255,0.15)",color:"#ffffff"},
    divider:{display:"flex",alignItems:"center",gap:14,margin:"20px 0"},
    dividerLine:{flex:1,height:1,background:"var(--border)"},
    dividerText:{fontFamily:"'JetBrains Mono',monospace",fontSize:8,color:"var(--amberLabel)",letterSpacing:"0.15em"},
    label:{fontFamily:"'JetBrains Mono',monospace",fontSize:8,letterSpacing:"0.18em",color:"var(--amberLabel)",textTransform:"uppercase",marginBottom:6},
    input:{width:"100%",padding:"11px 14px",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:4,fontFamily:"'JetBrains Mono',monospace",fontSize:13,color:"var(--white,#F0F2F5)",outline:"none",boxSizing:"border-box"},
    submitBtn:{width:"100%",padding:"13px",background:"var(--amber,#FFB000)",border:"none",borderRadius:4,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",fontSize:12,fontWeight:700,color:"var(--bg,#0B0C10)",letterSpacing:"0.1em",marginTop:4},
    switchBtn:{background:"none",border:"none",color:"var(--amber,#FFB000)",fontSize:11,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",marginTop:20,display:"block",textAlign:"center",width:"100%"},
    error:{marginTop:10,fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"var(--red,#E84040)",textAlign:"center"},
    success:{marginTop:10,fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"var(--green,#00E676)",textAlign:"center"},
    trial:{fontFamily:"'JetBrains Mono',monospace",fontSize:9,color:"var(--amberLabel)",textAlign:"center",marginTop:20,letterSpacing:"0.05em"},
  };
  return(
    <div style={m.overlay} onClick={onClose}>
      <div style={m.card} onClick={e=>e.stopPropagation()}>
        <button style={m.close} onClick={onClose}>x</button>
        <div style={m.logo}>
          <div style={m.logoText}>SOL<span style={m.logoAmber}>U</span>S</div>
        </div>
        <div style={m.tagline}>AI FLIGHT INTELLIGENCE</div>
        <div style={m.subtitle}>
          {mode==="register"?"Create your account to start your free trial":"Sign in to your cockpit"}
        </div>
        <button style={{...m.socialBtn,...m.googleBtn}} onClick={async()=>{await supabaseClient.auth.signInWithOAuth({provider:"google",options:{redirectTo:window.location.origin}});}}>
          Continue with Google
        </button>
        <div style={m.divider}>
          <div style={m.dividerLine}/>
          <span style={m.dividerText}>OR USE EMAIL</span>
          <div style={m.dividerLine}/>
        </div>
        {mode==="register"&&(
          <div style={{marginBottom:12}}>
            <div style={m.label}>Full Name</div>
            <input style={m.input} value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Your name"/>
          </div>
        )}
        <div style={{marginBottom:12}}>
          <div style={m.label}>Email</div>
          <input style={m.input} type="email" value={form.email} onChange={e=>{setForm(p=>({...p,email:e.target.value}));setError(null);}} placeholder="pilot@example.com"/>
        </div>
        <div style={{marginBottom:16}}>
          <div style={m.label}>Password</div>
          <input style={m.input} type="password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder={mode==="register"?"Min 8 characters":"Enter password"} onKeyDown={e=>{if(e.key==="Enter")onEmailAuth();}}/>
        </div>
        <button style={{...m.submitBtn,opacity:loading?0.5:1}} onClick={onEmailAuth} disabled={loading}>
          {loading?"Authenticating...":(mode==="register"?"Create Account":"Sign In")}
        </button>
        {error&&<div style={m.error}>{error}</div>}
        {success&&<div style={m.success}>{success}</div>}
        <button style={m.switchBtn} onClick={()=>{setMode(v=>v==="login"?"register":"login");setError(null);setSuccess(null);}}>
          {mode==="login"?"New to SOLUS? Create an account":"Already have an account? Sign in"}
        </button>
        <div style={m.trial}>3-DAY FREE TRIAL INCLUDED WITH ALL PLANS</div>
      </div>
    </div>
  );
}

function AppMain(){
  const [tab,setTab]=useState("chat");
  const [subTab,setSubTab]=useState("risk");
  const [pilotMode,setPilotMode]=useState(()=>{
    const saved=localStorage.getItem('solus_pilot_mode');
    // If saved as jetson but not unlocked, fall back to ga
    if(saved==='jetson'&&localStorage.getItem('solus_jetson_unlocked')!=='1') return 'ga';
    return saved||'ga';
  });
  const [jetsonUnlocked,setJetsonUnlocked]=useState(localStorage.getItem('solus_jetson_unlocked')==='1');
  function switchPilotMode(mode){
    if(mode==='jetson'&&!jetsonUnlocked){
      const pw=prompt('Enter Jetson access code:');
      if(!pw) return;
      if(pw.trim()==='Leclair-Jetson'){
        localStorage.setItem('solus_jetson_unlocked','1');
        setJetsonUnlocked(true);
      } else {
        alert('Invalid access code.');
        return;
      }
    }
    setPilotMode(mode);localStorage.setItem('solus_pilot_mode',mode);setTab("chat");
  }
  const isDemoMode=localStorage.getItem('solus_demo_mode')==='1';

  // Demo mode: pre-load realistic pilot data SYNCHRONOUSLY before state init
  if(isDemoMode&&!localStorage.getItem('solus_demo_seeded')){
    const d=new Date();
    const ago=(days)=>new Date(d.getTime()-days*864e5).toISOString().split('T')[0];
    const demoAc={nNumber:"12345",type:"Cessna 172S",totalTime:"250",engineTime:"312",lastAnnual:ago(243),squawks:""};
    const demoCurr={
      lastBFR:ago(707),lastMedical:ago(180),medicalClass:"3rd",dob:"1994-06-15",
      lastDayLanding:ago(47),dayLanding2:ago(52),dayLanding3:ago(60),
      lastNightLanding:ago(65),nightLanding2:ago(72),nightLanding3:ago(80),
      nightTakeoff1:ago(65),nightTakeoff2:ago(72),nightTakeoff3:ago(80),
      lastIFR:ago(152),ifrApproaches:"4",ifrHolds:"1",ifrTracking:"Yes",
      tailwheel:false,lastTailwheelLanding:"",
    };
    localStorage.setItem('solus_ac',JSON.stringify(demoAc));
    localStorage.setItem('solus_curr_dates',JSON.stringify(demoCurr));
    localStorage.setItem('solus_entered','1');
    localStorage.setItem('solus_waitlisted','1');
    localStorage.setItem('solus_allowed','1');
    localStorage.setItem('solus_demo_seeded','1');
    // Force reload so state initializers pick up the new data
    window.location.reload();
  }

  function exitDemo(){
    localStorage.removeItem('solus_demo_mode');
    localStorage.removeItem('solus_demo_seeded');
    localStorage.removeItem('solus_ac');
    localStorage.removeItem('solus_curr_dates');
    localStorage.removeItem('solus_entered');
    localStorage.removeItem('solus_waitlisted');
    localStorage.removeItem('solus_allowed');
    window.location.href='/';
  }

  const [ac,setAc]=useState(()=>{
    try{const s=localStorage.getItem("solus_ac");if(s)return JSON.parse(s);}catch{}
    return {nNumber:"",type:"",totalTime:"",engineTime:"",lastAnnual:"",last100hr:"",squawks:""};
  });
  useEffect(()=>{try{localStorage.setItem("solus_ac",JSON.stringify(ac));if(ac.nNumber)localStorage.setItem("solus_data_saved_at",new Date().toISOString());}catch{}},[ac]);
  const [currDates,setCurrDates]=useState(()=>{
    try{const s=localStorage.getItem("solus_curr_dates");if(s)return JSON.parse(s);}catch{}
    return {
    lastBFR:"",lastMedical:"",medicalClass:"3rd",dob:"",
    lastDayLanding:"",dayLanding2:"",dayLanding3:"",
    lastNightLanding:"",nightLanding2:"",nightLanding3:"",
    nightTakeoff1:"",nightTakeoff2:"",nightTakeoff3:"",
    lastIFR:"",
    ifrApproaches:"",ifrHolds:"",ifrTracking:"",
    tailwheel:false,lastTailwheelLanding:"",
  };
  });
  useEffect(()=>{try{localStorage.setItem("solus_curr_dates",JSON.stringify(currDates));if(currDates.lastBFR)localStorage.setItem("solus_data_saved_at",new Date().toISOString());}catch{}},[currDates]);
  const [flightCtx,setFlightCtx]=useState(()=>{
    try{const s=localStorage.getItem("solus_flight_ctx");if(s)return JSON.parse(s);}catch{}
    return {from:"",to:"",deptTime:"",fuel:"",burnRate:"",elapsed:""};
  });
  useEffect(()=>{try{localStorage.setItem("solus_flight_ctx",JSON.stringify(flightCtx));}catch{}},[flightCtx]);
  const [wbState,setWbState]=useState({});
  const [clState,setClState]=useState({});
  const [logs,setLogs]=useState([]);
  const [wxDone,setWxDone]=useState(false);
  const [clDone,setClDone]=useState(false);
  const [wbDone,setWbDone]=useState(false);
  const [currencyStatus,setCurrencyStatus]=useState(null);
  const riskScore=computeRiskScore(ac,currDates,flightCtx);
  // Compute currency status from currDates whenever they change
  const computedCurrencyStatus = (() => {
    if(!currDates||!currDates.lastBFR) return null;
    const today = new Date();
    function daysAgo(ds){ if(!ds)return 9999; return Math.round((today-new Date(ds))/864e5); }
    const bfr = daysAgo(currDates.lastBFR);
    const day = daysAgo(currDates.lastDayLanding);
    const med = daysAgo(currDates.lastMedical);
    if(bfr>730||day>90||med>730) return "LAPSED";
    if(bfr>650||day>80||med>660) return "EXPIRING";
    return "ALL CURRENT";
  })();
  const effectiveCurrencyStatus = computedCurrencyStatus || currencyStatus;
  const [lastQuery]=useState("");
  const [showWL,setShowWL]=useState(false);
  const [showReferral,setShowReferral]=useState(false);
  const [showSignIn,setShowSignIn]=useState(false);
  const [signInForm,setSignInForm]=useState({email:"",password:"",name:""});
  const [signInMode,setSignInMode]=useState("login"); // "login" or "register"
  const [signInLoading,setSignInLoading]=useState(false);
  const [signInError,setSignInError]=useState(null);
  const [signInSuccess,setSignInSuccess]=useState(null);
  const [onboardingComplete,setOnboardingComplete]=useState(
    localStorage.getItem('solus_onboarded_complete')==='1'
  );

  function isGmail(email){return email.trim().toLowerCase().endsWith("@gmail.com");}

  async function handleEmailAuth(){
    const {email,password,name}=signInForm;
    if(!email||!password)return setSignInError("Email and password required");
    setSignInLoading(true);setSignInError(null);setSignInSuccess(null);
    try{
      if(signInMode==="register"){
        if(password.length<8)return setSignInError("Password must be at least 8 characters");
        const{data:signUpData,error:signUpErr}=await supabaseClient.auth.signUp({
          email,password,options:{data:{name:name||email.split("@")[0],role:"GA Pilot"}}
        });
        if(signUpErr){setSignInError(signUpErr.message);}
        else{setSignInSuccess("Account created. Check your email to confirm, then sign in.");setSignInMode("login");}
      }else{
        const{data:signInData,error:signInErr}=await supabaseClient.auth.signInWithPassword({email,password});
        if(signInErr){setSignInError(signInErr.message);}
        else{
          setAuthUser(signInData.user);
          localStorage.setItem("solus_token",signInData.session.access_token);
          localStorage.setItem("solus_waitlisted","1");
          localStorage.setItem("solus_user_email",signInData.user.email||"");
          if(window.posthog)window.posthog.identify(signInData.user.email,{name:signInData.user.user_metadata?.name});
          fetch("https://solus-backend-9wde.onrender.com/auth/allowlist?email="+encodeURIComponent(email)).then(r=>r.json()).then(d=>{if(d.allowed)localStorage.setItem("solus_allowed","1");}).catch(()=>{});
          setShowSignIn(false);
          window.location.reload();
        }
      }
    }catch{setSignInError("Connection failed. Try again.");}
    setSignInLoading(false);
  }
  const [referralData,setReferralData]=useState(null);
  const [referralLoading,setReferralLoading]=useState(false);
  // skipLanding removed - gate is now: if authUser exists, show app; otherwise show landing
  const [authUser,setAuthUser]=useState(null);
  const [entitlements,setEntitlements]=useState({tier:'free',fullAccess:false,isDesignPartner:false,isAdmin:false,partner_org:null});
  const [isCFI,setIsCFI]=useState(false);

  // Listen for sign-in modal trigger from Landing page
  useEffect(()=>{
    const signInHandler=()=>setShowSignIn(true);
    window.addEventListener('solus:openSignIn',signInHandler);
    return()=>{window.removeEventListener('solus:openSignIn',signInHandler);};
  },[]);
  useEffect(()=>{
    supabaseClient.auth.getSession().then(({data:{session}})=>{
      if(session){setAuthUser(session.user);localStorage.setItem("solus_waitlisted","1");localStorage.setItem("solus_token",session.access_token);localStorage.setItem("solus_user_email",session.user.email||"");
      if(window.posthog)window.posthog.identify(session.user.email,{name:session.user.user_metadata?.name});
      // Fetch resolved entitlements from backend
      backendFetch("/user/me").then(d=>{if(d&&d.entitlements){setEntitlements(d.entitlements);if(d.entitlements.fullAccess)localStorage.setItem("solus_allowed","1");if(window.posthog)window.posthog.people.set({access_tier:d.entitlements.tier,partner_org:d.entitlements.partner_org||null,plan:d.plan||'free'});}}).catch(()=>{});
      // Check if user is a CFI
      backendFetch("/cfi/roster").then(d=>{if(Array.isArray(d))setIsCFI(true);}).catch(()=>{});
      // Load user data from backend
      backendFetch("/user/currency").then(d=>{
        if(d&&d.user_id){
          setCurrDates({
            lastBFR:d.bfr||"",lastMedical:d.medical||"",medicalClass:d.medical_class||"3rd",dob:d.dob||"",
            lastDayLanding:d.vfr_day||"",dayLanding2:d.vfr_day_2||"",dayLanding3:d.vfr_day_3||"",
            lastNightLanding:d.vfr_night||"",nightLanding2:d.vfr_night_2||"",nightLanding3:d.vfr_night_3||"",
            nightTakeoff1:d.night_takeoff_1||"",nightTakeoff2:d.night_takeoff_2||"",nightTakeoff3:d.night_takeoff_3||"",
            lastIFR:d.ifr_approach||"",
            ifrApproaches:d.ifr_approaches||"",ifrHolds:d.ifr_holds||"",ifrTracking:d.ifr_tracking||"",
            tailwheel:d.tailwheel||false,lastTailwheelLanding:d.last_tailwheel_landing||"",
          });
        }
      });
      backendFetch("/user/aircraft").then(d=>{
        if(d&&d.user_id){
          setAc({
            nNumber:d.n_number||"",type:d.type||"",totalTime:d.total_time||"",
            engineTime:d.engine_time||"",lastAnnual:d.last_annual||"",last100hr:"",squawks:d.squawks||""
          });
        }
      });
      const refParam=new URLSearchParams(window.location.search).get("ref")||undefined;
      const _isMobile=typeof navigator!=="undefined"&&/iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      fetch("https://solus-backend-9wde.onrender.com/waitlist/submit",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:session.user.email,name:session.user.user_metadata?.full_name||session.user.email,role:"GA Pilot",org:"",referredBy:refParam,platform:_isMobile?"mobile":"web"})}).then(r=>r.json()).then(d=>{if(d.referral_code)localStorage.setItem("solus_referral_code",d.referral_code);}).catch(()=>{});}
    });
    const {data:{subscription}}=supabaseClient.auth.onAuthStateChange((_,session)=>{
      if(session){setAuthUser(session.user);localStorage.setItem("solus_waitlisted","1");localStorage.setItem("solus_user_email",session.user.email||"");}
    });
    return()=>subscription.unsubscribe();
  },[]);
  // Log user session when authenticated user loads the app
  const sessionLogged=useRef(false);
  useEffect(()=>{
    if(!authUser||sessionLogged.current)return;
    sessionLogged.current=true;
    const token=localStorage.getItem("solus_token");
    if(!token)return;
    const isMobile=typeof navigator!=="undefined"&&/iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    fetch("https://solus-backend-9wde.onrender.com/analytics/session",{
      method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
      body:JSON.stringify({
        tabs_visited:[tab],
        aircraft_loaded:!!ac.nNumber,
        currency_loaded:!!currDates.lastBFR,
        weather_checked:false,
        risk_score:riskScore.score,
        platform:isMobile?"mobile":"web",
      })
    }).catch(()=>{});
  },[authUser]);
  // Track session_start and risk_score_computed on page load
  useEffect(()=>{
    trackEvent('session_start',{platform:'web',app_version:'1.0',device:navigator.userAgent.slice(0,100)});
    window.__solus_session_start=Date.now();
    // Track session_end on unload
    const handleUnload=()=>{
      const duration=Math.round((Date.now()-(window.__solus_session_start||Date.now()))/1000);
      trackEvent('session_end',{duration_seconds:duration});
    };
    window.addEventListener('beforeunload',handleUnload);
    return()=>window.removeEventListener('beforeunload',handleUnload);
  },[]);
  // Track risk score whenever it changes
  useEffect(()=>{
    if(riskScore&&riskScore.score!==null&&riskScore.score!==undefined){
      trackEvent('risk_score_computed',{score:riskScore.score,level:riskScore.level,driver:riskScore.driver});
    }
  },[riskScore.score]);
  useEffect(()=>{
    const visited=localStorage.getItem("solus_visited");
    if(!visited){
      setTimeout(()=>{
        setShowWL(true);
        localStorage.setItem("solus_visited","1");
      },3000);
    }
  },[]);
  const [wlTriggered,setWlTriggered]=useState(false);

  // Data retention expiry check on load
  useEffect(()=>{
    const ret=localStorage.getItem("solus_data_retention");
    const saved=localStorage.getItem("solus_data_saved_at");
    if(ret&&ret!=="forever"&&saved){
      const daysSinceSave=Math.round((Date.now()-new Date(saved).getTime())/864e5);
      if(daysSinceSave>Number(ret)){
        localStorage.removeItem("solus_ac");localStorage.removeItem("solus_curr_dates");localStorage.removeItem("solus_cl_notes");localStorage.removeItem("solus_flight_ctx");localStorage.removeItem("solus_data_saved_at");
        setAc({nNumber:"",type:"",totalTime:"",engineTime:"",lastAnnual:"",last100hr:"",squawks:""});
        setCurrDates({lastBFR:"",lastMedical:"",medicalClass:"3rd",dob:"",lastDayLanding:"",dayLanding2:"",dayLanding3:"",lastNightLanding:"",nightLanding2:"",nightLanding3:"",nightTakeoff1:"",nightTakeoff2:"",nightTakeoff3:"",lastIFR:"",ifrApproaches:"",ifrHolds:"",ifrTracking:"",tailwheel:false,lastTailwheelLanding:""});
        setFlightCtx({from:"",to:"",deptTime:"",fuel:"",burnRate:"",elapsed:""});
        const t=localStorage.getItem("solus_token");if(t)fetch("https://solus-backend-9wde.onrender.com/user/all",{method:"DELETE",headers:{Authorization:"Bearer "+t}}).catch(()=>{});
      }
    }
  },[]);

  async function openReferral(overrideEmail,overrideName,isRetry){
    setShowReferral(true);
    if(!isRetry)setReferralData(null);
    const email=overrideEmail||authUser?.email||localStorage.getItem("solus_referral_email");
    const name=overrideName||authUser?.user_metadata?.full_name||authUser?.email?.split("@")[0]||localStorage.getItem("solus_referral_name");
    if(!email||!name){setReferralLoading(false);return;} // Will show the form for unauthenticated users
    setReferralLoading(true);
    const controller=new AbortController();
    const timeout=setTimeout(()=>controller.abort(),10000);
    try{
      const res=await fetch("https://solus-backend-9wde.onrender.com/waitlist/generate-referral",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({email,name}),
        signal:controller.signal
      });
      clearTimeout(timeout);
      if(!res.ok)throw new Error("Status "+res.status);
      const data=await res.json();
      if(data.referral_code){
        setReferralData(data);
        localStorage.setItem("solus_referral_code",data.referral_code);
      }else if(data.error){
        console.error("Referral error:",data.error);
      }
    }catch(err){
      clearTimeout(timeout);
      console.error("Referral generate failed:",err);
      // Fallback: show code from localStorage if available
      const cached=localStorage.getItem("solus_referral_code");
      if(cached){setReferralData({referral_code:cached,name,email,total_referrals:0,signed_up_referrals:0});}
      else{
        // Auto-retry once after 3 seconds on timeout/error
        if(!isRetry){setTimeout(()=>openReferral(email,name,true),3000);}
      }
    }
    setReferralLoading(false);
  }

  const [dark,setDark]=useState(false); // Always light mode
  const [showOfflineConfirm,setShowOfflineConfirm]=useState(false);
  const [showSyncToast,setShowSyncToast]=useState(false);
  const [showSettings,setShowSettings]=useState(false);
  const [resetConfirm,setResetConfirm]=useState(null); // null | 'aircraft' | 'currency' | 'checklists' | 'flight' | 'all'
  const [dataRetention,setDataRetention]=useState(localStorage.getItem("solus_data_retention")||"forever");
  const {offline,userForced,networkDown,forceOffline,forceOnline}=useOffline();

  // Show sync toast when coming back online
  const prevOffline=useRef(offline);
  useEffect(()=>{
    if(prevOffline.current&&!offline){
      setShowSyncToast(true);
      setTimeout(()=>setShowSyncToast(false),3000);
    }
    prevOffline.current=offline;
  },[offline]);

  function handleConnectionClick(){
    if(offline){forceOnline();}
    else{setShowOfflineConfirm(true);}
  }

  // Tabs: mark Intel as PARTIAL in offline (voice disabled), all others full
  const gaTabs=[
    {k:"chat",       l:"Intel",        partial:true},
    {k:"flight-prep",l:"Flight Prep",  partial:false},
    {k:"wx-airspace",l:"Wx & Airspace",partial:false},
    {k:"library",    l:"Library",      partial:false},
    ...(isCFI?[{k:"instructor",l:"Instructor",partial:false}]:[]),
    {k:"profile",    l:"Profile",      partial:false},
  ];
  const jetsonTabs=[
    {k:"chat",        l:"Intel",      partial:true},
    {k:"sortie-hub",  l:"Sortie",     partial:false},
    {k:"flight-ops",  l:"Flight Ops", partial:false},
    {k:"jetson-score",l:"Score",      partial:false},
    {k:"profile",     l:"Profile",    partial:false},
  ];
  const subTabDefaults={
    'flight-prep':'risk','wx-airspace':'weather','library':'ntsb',
    'sortie-hub':'jetson-sortie','flight-ops':'jetson-checklist',
    'profile':'settings','instructor':'roster',
  };
  const tabs=pilotMode==="jetson"?jetsonTabs:gaTabs;

  // Landing page for unauthenticated users on root path (skip if demo mode)
  if(!isDemoMode&&!authUser&&window.location.pathname==="/"){
    return(
      <div style={{background:"var(--bg,#0B0C10)",minHeight:"100vh"}}>
        <style>{CSS}</style>
        <Landing onEnterApp={()=>{window.dispatchEvent(new CustomEvent('solus:openSignIn'));}}/>
        {/* Sign In Modal rendered here for landing page */}
        {showSignIn&&(<SignInModal show={showSignIn} onClose={()=>setShowSignIn(false)} mode={signInMode} setMode={setSignInMode} form={signInForm} setForm={setSignInForm} error={signInError} success={signInSuccess} loading={signInLoading} onEmailAuth={handleEmailAuth} setError={setSignInError} setSuccess={setSignInSuccess} supabaseClient={supabaseClient}/>)}
      </div>
    );
  }

  // Onboarding wizard for newly authenticated users (skip for demo and anonymous)
  if(authUser&&!onboardingComplete&&!isDemoMode){
    return(
      <div style={{background:"var(--bg,#0B0C10)",minHeight:"100vh"}}>
        <style>{CSS}</style>
        <Onboarding authUser={authUser} onComplete={()=>{
          localStorage.setItem('solus_onboarded_complete','1');
          setOnboardingComplete(true);
          // Reload aircraft/currency state from localStorage after onboarding saves
          try{const s=localStorage.getItem("solus_ac");if(s)setAc(JSON.parse(s));}catch{}
          try{const s=localStorage.getItem("solus_curr_dates");if(s)setCurrDates(JSON.parse(s));}catch{}
        }}/>
      </div>
    );
  }

  return(
    <div className={`sr${dark?"":" light"}`}>
      <style>{CSS}</style>

      {isDemoMode&&(
        <div style={{background:"var(--amberDim)",borderBottom:"1px solid var(--amberBorder)",padding:"5px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,color:"var(--amber,#FFB000)",textTransform:"uppercase",letterSpacing:"0.1em"}}>DEMO MODE - Sample pilot data loaded</span>
          <button onClick={exitDemo} style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,background:"var(--amberDim)",border:"1px solid var(--amberBorder)",color:"var(--amber,#FFB000)",padding:"3px 10px",borderRadius:2,cursor:"pointer",letterSpacing:"0.08em"}}>Exit Demo</button>
        </div>
      )}

      <Annunciator
        ac={ac}
        wxDone={wxDone}
        clDone={clDone}
        wbDone={wbDone}
        currencyStatus={effectiveCurrencyStatus}
        offline={offline}
        networkDown={networkDown}
        onConnectionClick={handleConnectionClick}
        riskScore={riskScore}
      />

      {/* Row 2 : Functional header */}
      <div className="hdr">
        <div className="logo">SOL<em>U</em>S</div>
        <div className="hdr-right">
          {ac.nNumber&&(
            <div className="ac-pill">
              <div className="ac-dot"/>
              N{ac.nNumber}{ac.type?` · ${ac.type}`:""}
            </div>
          )}
          <div style={{display:"flex",alignItems:"center",border:"1px solid var(--border,#1E2733)",borderRadius:3,overflow:"hidden",flexShrink:0}}>
            <button onClick={()=>switchPilotMode("ga")} style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.12em",padding:"5px 10px",border:"none",cursor:"pointer",background:pilotMode==="ga"?"var(--amberDim)":"transparent",color:pilotMode==="ga"?"var(--amber,#FFB000)":"var(--text3)",transition:"all 0.15s"}}>GA</button>
            <div style={{width:1,height:18,background:"var(--border,#1E2733)"}}/>
            <button onClick={()=>switchPilotMode("jetson")} style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.12em",padding:"5px 10px",border:"none",cursor:"pointer",background:pilotMode==="jetson"?"var(--greenDim)":"transparent",color:pilotMode==="jetson"?"var(--green,#00E676)":"var(--text3)",transition:"all 0.15s"}}>JETSON</button>
          </div>
          <span onClick={()=>openReferral()} style={{background:"var(--greenDim)",border:"1px solid rgba(0,230,118,0.35)",color:"var(--green,#00E676)",fontFamily:"var(--mono)",fontSize:10,fontWeight:700,letterSpacing:"0.1em",padding:"4px 10px",borderRadius:2,textTransform:"uppercase",cursor:"pointer"}}>REFER</span>
          <span onClick={()=>{window.location.href="/blog";}} style={{background:"var(--amberDim)",border:"1px solid var(--amberBorder)",color:"var(--amber,#FFB000)",fontFamily:"var(--mono)",fontSize:10,fontWeight:700,letterSpacing:"0.1em",padding:"4px 10px",borderRadius:2,textTransform:"uppercase",cursor:"pointer",textDecoration:"none"}}>BLOG</span>
          {authUser ? (
  <div style={{display:"flex",alignItems:"center",gap:8,marginRight:8}}>
    <div style={{width:26,height:26,borderRadius:"50%",background:"var(--amberDim)",border:"1px solid var(--amberBorder)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"var(--amber)",fontFamily:"var(--mono)",fontWeight:700,flexShrink:0}}>
      {(authUser.user_metadata?.full_name||authUser.email||"?")[0].toUpperCase()}
    </div>
    <span style={{fontSize:10,color:"var(--text3)",fontFamily:"var(--mono)",maxWidth:110,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
      {authUser.user_metadata?.full_name||authUser.email}
    </span>
    <button onClick={async()=>{await supabaseClient.auth.signOut();localStorage.removeItem("solus_token");localStorage.removeItem("solus_waitlisted");localStorage.removeItem("solus_entered");window.location.reload();}} style={{background:"none",border:"none",color:"var(--muted)",fontSize:10,cursor:"pointer",fontFamily:"var(--mono)",padding:"2px 4px",letterSpacing:"0.05em"}}>sign out</button>
  </div>
) : (
  <button onClick={()=>setShowSignIn(true)} style={{background:"rgba(255,255,255,0.07)",border:"1px solid var(--glassBorder)",borderRadius:2,color:"var(--white)",fontSize:11,cursor:"pointer",padding:"6px 12px",fontFamily:"var(--mono)",letterSpacing:"0.08em",marginRight:8}}>Sign in</button>
)}{!authUser&&!localStorage.getItem("solus_allowed")&&<button className="ea-btn" onClick={()=>setShowSignIn(true)}>Sign Up</button>}
        </div>
      </div>

      {/* Offline banner */}
      {offline&&(
        <div className="offline-banner">
          <div className="conn-dot" style={{width:7,height:7,borderRadius:"50%",background:"var(--amber)",flexShrink:0}}/>
          <span className="offline-banner-text">Local Mode</span>
          <span className="offline-banner-sub">
            {networkDown
              ?"No network detected. Checklists, W&B, NTSB, and FAR/AIM are fully available."
              :"Checklists, W&B, NTSB, and FAR/AIM are fully available. AI chat uses cached data."}
          </span>
          {!networkDown&&(
            <button onClick={forceOnline}
              style={{marginLeft:"auto",background:"none",border:"1px solid rgba(232,160,32,0.3)",
                borderRadius:4,padding:"3px 12px",color:"var(--amber)",fontSize:11,
                fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",transition:"all .15s"}}
              onMouseOver={e=>e.currentTarget.style.background="var(--amberDim)"}
              onMouseOut={e=>e.currentTarget.style.background="none"}>
              Go live
            </button>
          )}
        </div>
      )}

      {/* Nav */}
      <div className="nav">
        {tabs.map(t=>(
          <button key={t.k}
            className={`nb${tab===t.k?" on":""}${offline&&t.partial?" partial":""}`}
            onClick={()=>{setTab(t.k);if(subTabDefaults[t.k])setSubTab(subTabDefaults[t.k]);trackEvent('tab_view',{tab_name:t.k});}}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="content">
        {tab==="chat" && <Chat ac={ac} logs={logs} currDates={currDates} onWx={()=>setWxDone(true)} offline={offline} onRequestAccess={()=>setShowSignIn(true)} flightCtx={flightCtx} setFlightCtx={setFlightCtx} wxDone={wxDone} backendFetch={backendFetch} entitlements={entitlements}/>}

        {tab==="flight-prep" && <>
          <SubTabBar items={[
            {key:'risk',label:'Risk'},{key:'readiness',label:'Readiness'},{key:'currency',label:'Currency'},
            {key:'aircraft',label:'Aircraft'},{key:'wb',label:'W&B'},
            {key:'checklists',label:'Checklists'}
          ]} active={subTab} onSelect={setSubTab}/>
          {subTab==='risk' && <RiskBreakdown riskScore={riskScore} ac={ac} currDates={currDates}/>}
          {subTab==='readiness' && <PilotReadiness/>}
          {subTab==='currency' && <Currency onCurrencyChange={setCurrencyStatus} backendFetch={backendFetch} dates={currDates} setDates={setCurrDates}/>}
          {subTab==='aircraft' && <Aircraft profile={ac} setProfile={setAc} logs={logs} setLogs={setLogs} backendFetch={backendFetch}/>}
          {subTab==='wb' && <WeightBalance onResult={()=>setWbDone(true)}/>}
          {subTab==='checklists' && <Checklists onComplete={n=>{if(n==="preflight")setClDone(true);}}/>}
        </>}

        {tab==="wx-airspace" && <>
          <SubTabBar items={[
            {key:'weather',label:'Weather'},{key:'airspace',label:'Airspace'},
            {key:'performance',label:'Performance'}
          ]} active={subTab} onSelect={setSubTab}/>
          {subTab==='weather' && <WeatherBrief ac={ac} onWx={()=>setWxDone(true)}/>}
          {subTab==='airspace' && <AirspaceReports backendFetch={backendFetch}/>}
          {subTab==='performance' && <Performance/>}
        </>}

        {tab==="library" && <>
          <SubTabBar items={[
            {key:'ntsb',label:'NTSB'},{key:'regs',label:'Regs'},
            {key:'logbook',label:'Logbook'},{key:'debrief',label:'Debrief'}
          ]} active={subTab} onSelect={setSubTab}/>
          {subTab==='ntsb' && <NTSB lastQuery={lastQuery}/>}
          {subTab==='regs' && <Regs/>}
          {subTab==='logbook' && <Logbook backendFetch={backendFetch}/>}
          {subTab==='debrief' && <Debrief ac={ac} currDates={currDates} flightCtx={flightCtx} riskScore={riskScore} backendFetch={backendFetch}/>}
        </>}

        {tab==="sortie-hub" && <>
          <SubTabBar items={[
            {key:'jetson-sortie',label:'Sortie Wizard'},{key:'jetson-risk',label:'Risk Matrix'}
          ]} active={subTab} onSelect={setSubTab}/>
          {subTab==='jetson-sortie' && <JetsonSortie/>}
          {subTab==='jetson-risk' && <JetsonRisk/>}
        </>}

        {tab==="flight-ops" && <>
          <SubTabBar items={[
            {key:'jetson-checklist',label:'Checklist'},{key:'jetson-hud',label:'HUD'},
            {key:'weather',label:'Weather'},{key:'airspace',label:'Airspace'}
          ]} active={subTab} onSelect={setSubTab}/>
          {subTab==='jetson-checklist' && <JetsonChecklist/>}
          {subTab==='jetson-hud' && <JetsonHUD/>}
          {subTab==='weather' && <WeatherBrief ac={ac} onWx={()=>setWxDone(true)}/>}
          {subTab==='airspace' && <AirspaceReports backendFetch={backendFetch}/>}
        </>}

        {tab==="instructor" && <InstructorView backendFetch={backendFetch}/>}

        {tab==="jetson-score" && <JetsonScore/>}
        {tab==="profile" && <>
          <SubTabBar items={[{key:'settings',label:'Settings'},{key:'plans',label:'Plans'}]} active={subTab} onSelect={setSubTab}/>
          {subTab==='settings' && <ProfileAndSettings authUser={authUser} backendFetch={backendFetch} entitlements={entitlements}/>}
          {subTab==='plans' && <Plans authUser={authUser}/>}
        </>}
      </div>

      {/* Waitlist modal removed - now using sign-in flow */}

      {/* Referral Modal */}
      {showReferral&&(
        <div className="overlay" onClick={()=>setShowReferral(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:420,borderColor:"rgba(0,230,118,0.3)"}}>
            <button onClick={()=>setShowReferral(false)} style={{position:"absolute",top:16,right:18,background:"none",border:"none",color:"var(--muted)",fontSize:22,cursor:"pointer",lineHeight:1}}>x</button>
            <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:"var(--green,#00E676)",marginBottom:16,textTransform:"uppercase"}}>Refer a Pilot</div>

            {!authUser&&!localStorage.getItem("solus_referral_email")?(
              <div>
                <div style={{fontSize:13,color:"var(--text2)",lineHeight:1.6,marginBottom:16}}>Sign in or enter your info to get your personal referral code.</div>
                <div style={{marginBottom:10}}>
                  <div className="inpl">Name</div>
                  <input className="inp" id="ref-name" placeholder="Your name"/>
                </div>
                <div style={{marginBottom:16}}>
                  <div className="inpl">Email</div>
                  <input className="inp" id="ref-email" placeholder="you@example.com"/>
                </div>
                <button className="btn btn-p" style={{width:"100%",padding:"11px"}} onClick={()=>{
                  const n=document.getElementById("ref-name")?.value?.trim();
                  const e=document.getElementById("ref-email")?.value?.trim();
                  if(n&&e){localStorage.setItem("solus_referral_name",n);localStorage.setItem("solus_referral_email",e);openReferral(e,n);}
                }}>Get My Referral Code</button>
              </div>
            ):referralLoading?(
              <div style={{textAlign:"center",padding:"20px 0",color:"var(--muted)",fontFamily:"var(--mono)",fontSize:12}}>Generating your code...</div>
            ):referralData?(
              <div style={{textAlign:"center"}}>
                <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)",marginBottom:8}}>Your referral code</div>
                <div style={{fontFamily:"var(--mono)",fontSize:36,fontWeight:700,color:"var(--green,#00E676)",marginBottom:4,letterSpacing:"0.08em"}}>{referralData.referral_code}</div>
                <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--muted)",marginBottom:20}}>{referralData.name} · {referralData.email}</div>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20}}>
                  <div style={{background:"var(--greenDim)",border:"1px solid rgba(0,230,118,0.2)",borderRadius:4,padding:"12px 8px",textAlign:"center"}}>
                    <div style={{fontFamily:"var(--mono)",fontSize:24,fontWeight:700,color:"var(--green,#00E676)"}}>{referralData.total_referrals}</div>
                    <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--muted)",letterSpacing:"0.08em",marginTop:4}}>REFERRALS</div>
                  </div>
                  <div style={{background:"var(--greenDim)",border:"1px solid rgba(0,230,118,0.2)",borderRadius:4,padding:"12px 8px",textAlign:"center"}}>
                    <div style={{fontFamily:"var(--mono)",fontSize:24,fontWeight:700,color:"var(--green,#00E676)"}}>{referralData.signed_up_referrals}</div>
                    <div style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--muted)",letterSpacing:"0.08em",marginTop:4}}>SIGNED UP</div>
                  </div>
                </div>

                <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--muted)",marginBottom:12}}>Share this link with a pilot:</div>
                <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--amber)",background:"var(--amberDim)",border:"1px solid var(--amberBorder)",borderRadius:4,padding:"10px",marginBottom:16,wordBreak:"break-all"}}>
                  soluscopilot.com?ref={referralData.referral_code}
                </div>

                <button className="btn btn-p" style={{width:"100%",padding:"11px"}} onClick={()=>{
                  const txt="Check out SOLUS, the AI flight intelligence copilot for GA pilots. Try it free: soluscopilot.com?ref="+referralData.referral_code;
                  if(navigator.share){navigator.share({title:"SOLUS Copilot",text:txt}).catch(()=>{});}
                  else if(navigator.clipboard){navigator.clipboard.writeText(txt).then(()=>alert("Copied to clipboard!")).catch(()=>alert(txt));}
                  else{alert(txt);}
                }}>Share Referral Link</button>
              </div>
            ):(
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{color:"var(--muted)",fontSize:12,marginBottom:12}}>Backend is starting up. Please try again in a few seconds.</div>
                <button className="btn btn-p" onClick={()=>openReferral()} style={{width:"100%",padding:"11px"}}>Retry</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings&&(
        <div className="overlay" onClick={()=>{setShowSettings(false);setResetConfirm(null);}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
            <button onClick={()=>{setShowSettings(false);setResetConfirm(null);}} style={{position:"absolute",top:16,right:18,background:"none",border:"none",color:"var(--muted)",fontSize:22,cursor:"pointer",lineHeight:1}}>x</button>
            <div style={{fontFamily:"var(--mono)",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:"var(--amber)",marginBottom:20,textTransform:"uppercase"}}>Settings</div>

            {/* Data Retention */}
            <div style={{marginBottom:20}}>
              <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text2)",letterSpacing:"0.1em",marginBottom:8,textTransform:"uppercase"}}>Data Retention</div>
              <div style={{fontSize:12,color:"var(--text3)",marginBottom:10,lineHeight:1.5}}>How long should SOLUS keep your saved data? Expired data is cleared on next app load.</div>
              <select className="sel" value={dataRetention} onChange={e=>{const v=e.target.value;setDataRetention(v);localStorage.setItem("solus_data_retention",v);if(v!=="forever")localStorage.setItem("solus_data_saved_at",new Date().toISOString());}} style={{width:"100%",fontFamily:"var(--mono)",fontSize:11}}>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="365">1 year</option>
                <option value="forever">Forever (default)</option>
              </select>
            </div>

            {/* Per-tab Reset */}
            <div style={{marginBottom:20}}>
              <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--text2)",letterSpacing:"0.1em",marginBottom:8,textTransform:"uppercase"}}>Reset Individual Tabs</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[
                  {key:"aircraft",label:"Aircraft",desc:"N-number, type, times, squawks"},
                  {key:"currency",label:"Currency",desc:"BFR, medical, landings, IFR"},
                  {key:"checklists",label:"Checklists",desc:"All saved checklist notes"},
                  {key:"flight",label:"Flight Context",desc:"Route, fuel, departure time"},
                ].map(item=>(
                  <div key={item.key} style={{background:"rgba(255,255,255,0.03)",border:"1px solid var(--border)",borderRadius:4,padding:"10px 12px"}}>
                    <div style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:600,color:"var(--text)",marginBottom:2}}>{item.label}</div>
                    <div style={{fontSize:10,color:"var(--muted)",marginBottom:8}}>{item.desc}</div>
                    {resetConfirm===item.key?(
                      <div style={{display:"flex",gap:6}}>
                        <button className="btn btn-sm" onClick={async()=>{
                          if(item.key==="aircraft"){localStorage.removeItem("solus_ac");setAc({nNumber:"",type:"",totalTime:"",engineTime:"",lastAnnual:"",last100hr:"",squawks:""});const t=localStorage.getItem("solus_token");if(t)fetch("https://solus-backend-9wde.onrender.com/user/aircraft",{method:"DELETE",headers:{Authorization:"Bearer "+t}}).catch(()=>{});}
                          if(item.key==="currency"){localStorage.removeItem("solus_curr_dates");setCurrDates({lastBFR:"",lastMedical:"",medicalClass:"3rd",dob:"",lastDayLanding:"",dayLanding2:"",dayLanding3:"",lastNightLanding:"",nightLanding2:"",nightLanding3:"",nightTakeoff1:"",nightTakeoff2:"",nightTakeoff3:"",lastIFR:"",ifrApproaches:"",ifrHolds:"",ifrTracking:"",tailwheel:false,lastTailwheelLanding:""});const t=localStorage.getItem("solus_token");if(t)fetch("https://solus-backend-9wde.onrender.com/user/currency",{method:"DELETE",headers:{Authorization:"Bearer "+t}}).catch(()=>{});}
                          if(item.key==="checklists")localStorage.removeItem("solus_cl_notes");
                          if(item.key==="flight"){localStorage.removeItem("solus_flight_ctx");setFlightCtx({from:"",to:"",deptTime:"",fuel:"",burnRate:"",elapsed:""});}
                          setResetConfirm(null);
                        }} style={{borderColor:"var(--red)",color:"var(--red)",fontSize:10,flex:1}}>Confirm</button>
                        <button className="btn btn-sm" onClick={()=>setResetConfirm(null)} style={{fontSize:10,flex:1}}>Cancel</button>
                      </div>
                    ):(
                      <button className="btn btn-sm" onClick={()=>setResetConfirm(item.key)} style={{width:"100%",fontSize:10}}>Reset</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Full Reset */}
            <div style={{borderTop:"1px solid rgba(232,64,64,0.2)",paddingTop:16}}>
              <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--red)",letterSpacing:"0.1em",marginBottom:8,textTransform:"uppercase"}}>Reset All Data</div>
              <div style={{fontSize:12,color:"var(--text3)",marginBottom:10,lineHeight:1.5}}>Erase all saved data (aircraft, currency, checklists, flight context). You will stay signed in.</div>
              {resetConfirm==="all"?(
                <div style={{display:"flex",gap:8}}>
                  <button className="btn btn-p" onClick={async()=>{
                    localStorage.removeItem("solus_ac");localStorage.removeItem("solus_curr_dates");localStorage.removeItem("solus_cl_notes");localStorage.removeItem("solus_flight_ctx");localStorage.removeItem("solus_data_retention");localStorage.removeItem("solus_data_saved_at");
                    setAc({nNumber:"",type:"",totalTime:"",engineTime:"",lastAnnual:"",last100hr:"",squawks:""});
                    setCurrDates({lastBFR:"",lastMedical:"",medicalClass:"3rd",dob:"",lastDayLanding:"",dayLanding2:"",dayLanding3:"",lastNightLanding:"",nightLanding2:"",nightLanding3:"",nightTakeoff1:"",nightTakeoff2:"",nightTakeoff3:"",lastIFR:"",ifrApproaches:"",ifrHolds:"",ifrTracking:"",tailwheel:false,lastTailwheelLanding:""});
                    setFlightCtx({from:"",to:"",deptTime:"",fuel:"",burnRate:"",elapsed:""});
                    setDataRetention("forever");
                    const t=localStorage.getItem("solus_token");if(t)fetch("https://solus-backend-9wde.onrender.com/user/all",{method:"DELETE",headers:{Authorization:"Bearer "+t}}).catch(()=>{});
                    setResetConfirm(null);setShowSettings(false);
                  }} style={{flex:1,background:"var(--red)",borderColor:"var(--red)"}}>Erase Everything</button>
                  <button className="btn btn-sm" onClick={()=>setResetConfirm(null)} style={{flex:1}}>Cancel</button>
                </div>
              ):(
                <button className="btn btn-sm" onClick={()=>setResetConfirm("all")} style={{width:"100%",borderColor:"var(--red)",color:"var(--red)"}}>Reset All Data</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showSignIn&&(<SignInModal show={showSignIn} onClose={()=>setShowSignIn(false)} mode={signInMode} setMode={setSignInMode} form={signInForm} setForm={setSignInForm} error={signInError} success={signInSuccess} loading={signInLoading} onEmailAuth={handleEmailAuth} setError={setSignInError} setSuccess={setSignInSuccess} supabaseClient={supabaseClient}/>)}

      {showOfflineConfirm&&(
        <OfflineConfirmModal
          onConfirm={()=>{forceOffline();setShowOfflineConfirm(false);}}
          onCancel={()=>setShowOfflineConfirm(false)}
        />
      )}
      <SyncToast show={showSyncToast}/>
    </div>
  );
}
